import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { QRCodeSVG } from 'qrcode.react';
import { SiFacebook, SiGoogle, SiInstagram, SiYoutube } from 'react-icons/si';
import { FaLinkedinIn, FaSnapchat, FaThreads, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { TeamChat, EmployeeTasks, TeamTaskList, useTeamTasks } from './TeamTools';
import { LuHouse, LuPlus, LuLayoutGrid, LuCalendarDays, LuMessagesSquare, LuWorkflow, LuBadgeIndianRupee, LuChartNoAxesCombined, LuShare2, LuUsers, LuChevronsUpDown, LuPanelLeftClose, LuPanelLeftOpen, LuMessageCircle } from 'react-icons/lu';
import { FiMenu, FiEdit3, FiCalendar, FiMessageSquare, FiBarChart2, FiCopy, FiSun, FiMoon, FiBell, FiChevronLeft, FiChevronRight, FiChevronDown, FiX, FiRefreshCw, FiAlertCircle, FiGrid, FiList, FiTrash2, FiFileText, FiCheck, FiEye, FiClock, FiPlus, FiPaperclip, FiSmile, FiImage, FiHome, FiStar, FiTag, FiMoreHorizontal, FiSend, FiHeart, FiShare2, FiVolume2, FiVolumeX, FiPlay, FiUsers, FiPhone, FiMail, FiHelpCircle, FiSearch, FiSettings, FiPause, FiExternalLink, FiLink } from 'react-icons/fi';
import { Onboarding, ProfileMenu, Skeleton, Toast, useNotice } from './Experience';
import ClientMessages from './ClientMessages';
import SettingsPage from './SettingsPage';
import InstagramCollaborators from './InstagramCollaborators';
import { normalizeInstagramCollaborators, validateInstagramCollaboration } from './instagramCollaboration';
import './App.css';
import { conversationKey, conversationList, friendlyProviderIssue, mergeProviderAccounts, mergeProviderMessages, providerIssues, replaceProviderMessages } from './providerInbox';
import './FinalUI.css';
import './Experience.css';
import './Roles.css';
import { readWorkspaceSelection, resolveWorkspaceSelection, persistWorkspaceSelection } from './workspaceSelection';
import { providerPublishingSeries, providerReportPosts } from './reportActivity';
import { analyticsHealth } from './analyticsHealth';
import { readSession } from './sessionState';
import { PlatformPicker, PostThumbnail, shortPostText } from './PublishingControls';
import { occasionsForYear, upcomingOccasionDays, indiaToday, indianStates } from './calendarOccasions';
import { LanguagePicker, PlanPayments, SupportChat, publishedPostUrl } from './Workbench';
import './Workbench.css';
import './FiveMenu.css';
import AiCreate from './AiPreview';
import { APP_ORIGIN, initialSiteScreen } from './siteRouting';
import './DesktopSidebar.css';
import './Polish.css';
import { uploadMediaResumable } from './mediaUpload';
import { HEAT_DAYS, HEAT_SLOTS, MIN_POSTS_FOR_TIMING, buildPostingHeatmap, hourLabel, slotLabel } from './postingHeatmap';

const API_BASE = process.env.REACT_APP_API_BASE
  || (['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'http://localhost:5000'
    : window.location.hostname === 'app.eastensocial.com' ? 'https://eastensocial.com' : window.location.origin);
const isAppHost = window.location.hostname === 'app.eastensocial.com';
const isPublicHost = ['eastensocial.com', 'www.eastensocial.com'].includes(window.location.hostname);

const analyticsMemoryCache = new Map();
const ANALYTICS_CACHE_TTL_MS = 60 * 1000;
const formatVideoTime = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
};

const desktopModules = [
  { id: 'home', label: 'Home', icon: LuHouse },
  { id: 'post', label: 'Create Post', icon: LuPlus },
  { id: 'manage', label: 'Manage Posts', icon: LuLayoutGrid },
  { id: 'calendar', label: 'Calendar', icon: LuCalendarDays },
  { id: 'messaging', label: 'Inbox', icon: LuMessagesSquare },
  { id: 'automations', label: 'Automations', icon: LuWorkflow },
  { id: 'ads', label: 'Ads', icon: LuBadgeIndianRupee },
  { id: 'reports', label: 'Reports', icon: LuChartNoAxesCombined },
  { id: 'profiles', label: 'Assets', icon: LuShare2 },
  { id: 'team', label: 'Team', icon: LuUsers },
  { id: 'teamchat', label: 'Team Chat', icon: LuMessageCircle }
];

const createOptions = [
  { id: 'post', label: 'Post', icon: FiEdit3, ai: true },
  { id: 'ads', label: 'Ad', icon: FiBarChart2, ai: false },
  { id: 'bulk', label: 'Bulk scheduling', icon: FiCalendar, ai: false }
];

const primaryForModule = (module) => ({ calendar: 'manage', ads: 'manage', bulk: 'manage', post: 'create' }[module] || module);

const fallbackWorkspaces = [
  {
    id: 'easten-mediatech',
    name: 'Easten Mediatech',
    initials: 'EM',
    owner: 'Company A',
    plan: 'Growth Suite',
    color: '#246bfe'
  },
  {
    id: 'easten-pariwaar',
    name: 'Easten Pariwaar',
    initials: 'EP',
    owner: 'Company B',
    plan: 'Local Brand',
    color: '#0ea5a4'
  },
  {
    id: 'studio-north',
    name: 'Studio North',
    initials: 'SN',
    owner: 'Company C',
    plan: 'Creator Lab',
    color: '#8b5cf6'
  }
];

const platforms = [
  { id: 'facebook', label: 'Facebook', short: 'FB', color: '#1877f2', connected: true },
  { id: 'instagram', label: 'Instagram', short: 'IG', color: '#e1306c', connected: true },
  { id: 'threads', label: 'Threads', short: 'TH', color: '#111111', connected: false },
  { id: 'google', label: 'Google Business', short: 'GB', color: '#4285f4', connected: true },
  { id: 'youtube', label: 'YouTube', short: 'YT', color: '#ff0000', connected: false },
  { id: 'twitter', label: 'X (Twitter)', short: 'X', color: '#111111', connected: false, comingSoon: true },
  { id: 'linkedin', label: 'LinkedIn', short: 'IN', color: '#0a66c2', connected: false },
  { id: 'snapchat', label: 'Snapchat', short: 'SC', color: '#fffc00', connected: false, comingSoon: true }
];

const businessTypes = [
  'Advertising & Marketing', 'Agriculture', 'Artist', 'Automotive', 'Beauty & Wellness', 'Construction',
  'Education', 'Finance', 'Food & Restaurant', 'Government', 'Healthcare', 'Hospitality & Travel',
  'IT & Software', 'Legal', 'Media & Entertainment', 'Music Label', 'Nonprofit & NGO', 'Personal Brand',
  'Political Candidate', 'Political Party', 'Professional Services', 'Real Estate', 'Religious Organization',
  'Retail & Ecommerce', 'Sports & Fitness', 'Custom'
];

const inboxItems = [];

const pricingPlans = [
  {
    id: 'personal',
    name: 'Grow',
    badge: 'Start free',
    channels: '5 channels',
    price: '₹0',
    note: 'For creators, consultants and small businesses getting organized.',
    features: ['5 connected channels', 'Publishing and scheduling', 'Content calendar', 'Unified inbox']
  },
  {
    id: 'agency',
    name: 'Pro',
    badge: 'For growing teams',
    channels: '30 channels',
    price: '₹1,999',
    note: 'For agencies managing multiple brands, campaigns and approvals.',
    features: ['30 connected channels', 'Bulk scheduling', 'Client approval links', 'Reports and ads workspace']
  },
  {
    id: 'enterprise',
    name: 'Business',
    badge: 'For large agencies',
    channels: '100 channels',
    price: '₹4,999',
    note: 'For larger teams running high-volume social operations.',
    features: ['100 connected channels', 'Multiple workspaces', 'Priority onboarding', 'Advanced governance and support']
  }
];

const competitorPlatforms = [
  'Planable',
  'CoSchedule',
  'Sprout Social',
  'Buffer',
  'SocialPilot',
  'HeyOrca',
  'SocialBee',
  'SocialChamp',
  'Postly',
  'Hootsuite',
  'Later',
  'Loomly',
  'Agorapulse',
  'Sendible',
  'Metricool',
  'Zoho Social',
  'Publer',
  'Vista Social',
  'ContentStudio',
  'NapoleonCat'
];

const eastenAdvantages = [
  'Agency workspaces with client switching',
  'Post Now, Calendar, Messaging, Reports, Profiles, Bulk Scheduling and Ads in one dashboard',
  'AI caption workflow for platform-ready copy',
  'Client approval calendar and share-ready reporting',
  'India-first plans with 10 free channels',
  'Built for Meta, Google, YouTube, LinkedIn and X API workflows'
];

const exclusiveFeatures = [
  ['one-click', 'One Click Post', 'Post on 6 platforms with one click.'],
  ['manage-posts', 'Manage Posts', 'Edit and delete content you posted.'],
  ['schedule-post', 'Schedule Post', 'Schedule for each platform separately.'],
  ['calendar-view', 'Calendar View', 'View scheduled posts for the whole month.'],
  ['approval-link', 'Approval Link', 'Share calendar links and get approvals.'],
  ['single-inbox', 'Single Inbox', 'Messages from all platforms in one place.'],
  ['post-bulk', 'Post in Bulk', 'Post for a month with one-time setup.'],
  ['manage-ads', 'Manage Ads', 'Create campaigns for Meta and Google.']
];

const guidedWorkflowSteps = [
  {
    label: 'Step 1',
    title: 'Register/Login',
    text: 'Click Register Now, enter your email and desired password, verify with the one-time code received on your email, then log in to your account.',
    visualTitle: 'Create your workspace',
    chips: ['Register', 'Email OTP', 'Login']
  },
  {
    label: 'Step 2',
    title: 'Connect Profile',
    text: 'Click the profile you want to post on, authorize platform access, and confirm that the profile is connected.',
    visualTitle: 'Connect profiles',
    chips: ['Meta', 'Google', 'YouTube']
  },
  {
    label: 'Step 3',
    title: 'Post/Schedule',
    text: 'Once a profile is connected, add media, write your caption manually or with AI, choose the platform, then post now or schedule it for later.',
    visualTitle: 'Post or schedule',
    chips: ['Add media', 'Write caption', 'Schedule']
  }
];

const partnerBrands = ['AWS', 'Meta', 'LinkedIn', 'X', 'Google'];

const bestReasons = [
  ['Absolutely free', 'Built for brands, SMEs, and agencies that want serious workflow without starting cost.'],
  ['One workspace', 'Plan, post, manage, message, report, and run ads from one simple control center.'],
  ['Easten Mediatech style', 'A product experience shaped by Easten Mediatech for Indian brands that need premium execution.'],
  ['Secure cloud approach', 'Designed around AWS-grade hosting principles and separated client workspaces.']
];

const policyPages = {
  privacy: {
    title: 'Privacy Policy',
    eyebrow: 'Trust center',
    body: [
      'EastenSocial collects only the information required to create your workspace, connect social profiles, schedule content, and generate reports.',
      'Connected platform tokens and client data are handled for publishing, analytics, messaging, and approval workflows. We do not sell customer data.',
      'Access is limited to authorized users in your workspace, and future enterprise plans can include additional role-based controls.'
    ]
  },
  'data-deletion': {
    title: 'Data Deletion Policy',
    eyebrow: 'Account control',
    body: [
      'Users can request deletion of workspace data, connected profile data, saved posts, media references, reports, and account records.',
      'When a deletion request is received, EastenSocial will disconnect available platform tokens and remove stored workspace data from active systems.',
      'Some logs may remain for limited security, legal, or billing reasons where required.'
    ]
  },
  terms: {
    title: 'Terms & Conditions',
    eyebrow: 'Using EastenSocial',
    body: [
      'EastenSocial is a social media management platform for agencies, creators, and businesses.',
      'Users are responsible for the content they publish and for maintaining valid permissions on connected social profiles.',
      'Platform availability depends on third-party API access, approvals, quotas, and policy changes from Meta, Google, YouTube, LinkedIn, X and other networks.'
    ]
  },
  'legal-termination': {
    title: 'Legal & Termination',
    eyebrow: 'Account lifecycle',
    body: [
      'EastenSocial may suspend or terminate access when an account is used for unlawful activity, unauthorized social profile access, spam, platform policy violations, unpaid invoices, or security risk.',
      'Customers may request cancellation, workspace export support where available, social profile disconnection, and account deletion through the support contact listed in our public legal pages.',
      'Termination does not remove responsibility for content already published on connected platforms or for obligations that survive cancellation, including payment, audit, security, and legal compliance records.'
    ]
  },
  'user-agreement': {
    title: 'User Agreement',
    eyebrow: 'User responsibilities',
    body: [
      'By using EastenSocial, each user agrees to manage only accounts they own or are authorized to operate for a client, employer, or workspace.',
      'Users must protect login credentials, approve content carefully, follow third-party platform rules, and avoid publishing unlawful, deceptive, infringing, or harmful content.',
      'Workspace owners are responsible for inviting the right team members, assigning permissions, and removing access when a user no longer needs it.'
    ]
  },
  'social-scheduling': {
    title: 'Social Scheduling',
    eyebrow: 'Product',
    body: [
      'EastenSocial helps teams draft, approve, schedule, and publish content across connected social profiles from one workspace.',
      'Scheduling features depend on each platform API, account eligibility, permissions, media type limits, and active authorization tokens.',
      'Teams can use drafts, calendars, post history, and approval status to keep client publishing organized.'
    ]
  },
  'ai-content-writer': {
    title: 'AI Content Writer',
    eyebrow: 'Product',
    body: [
      'The AI Content Writer assists with captions, post descriptions, hashtag ideas, and content variations based on user-provided instructions and workspace business details.',
      'Users remain responsible for reviewing AI output before saving, scheduling, or publishing it.',
      'EastenSocial is designed to separate title, description, hashtags, tone, language, and platform context so teams can edit final copy clearly.'
    ]
  },
  'unified-inbox': {
    title: 'Unified Inbox',
    eyebrow: 'Product',
    body: [
      'Unified Inbox is intended to bring permitted Facebook, Instagram, client, and team messages into one operational view.',
      'Message availability depends on platform permissions, webhook subscriptions, account eligibility, and user-granted access.',
      'Automation features should be configured carefully so replies are helpful, policy-compliant, and easy for humans to review.'
    ]
  },
  'analytics-reports': {
    title: 'Analytics & Reports',
    eyebrow: 'Product',
    body: [
      'EastenSocial displays live analytics that connected platforms expose through approved APIs, including publishing activity, reach, views, engagement, followers, top posts, and eligible demographics.',
      'Some metrics vary by platform and may be unavailable because of API deprecations, account type, review status, or missing permissions.',
      'Reports can be used for client summaries, account performance checks, and operational decision-making.'
    ]
  },
  'pricing-plans': {
    title: 'Pricing Plans',
    eyebrow: 'Product',
    body: [
      'EastenSocial plans are designed around workspace needs, connected channel limits, team roles, reporting, and automation access.',
      'Final pricing, trial availability, and plan limits may change as the product evolves and platform costs change.',
      'For agency or multi-client pricing, contact the Easten Mediatech team for setup guidance.'
    ]
  },
  'parent-company': {
    title: 'Parent Company',
    eyebrow: 'Company',
    body: [
      'EastenSocial is a product operated by Easten Mediatech, a digital services company focused on practical technology for Indian businesses, agencies, and growth teams.',
      'The parent company supports product planning, implementation, onboarding, customer communication, and platform review workflows.',
      'Official business and support communication can be routed through Easten Mediatech.'
    ]
  },
  'leadership-team': {
    title: 'Leadership Team',
    eyebrow: 'Company',
    body: [
      'EastenSocial leadership is responsible for product direction, customer trust, platform compliance, and reliable delivery for agency workflows.',
      'As the company grows, this page can list named leaders, responsibilities, press profiles, and official media contacts.',
      'For verification or business enquiries, contact the company through the published contact channels.'
    ]
  },
  careers: {
    title: 'Careers',
    eyebrow: 'Company',
    body: [
      'EastenSocial looks for people who care about clean product design, reliable engineering, social media operations, customer support, and practical AI workflows.',
      'Current openings may include product, frontend, backend, support, content, sales, and partner roles as the business expands.',
      'Interested candidates can contact Easten Mediatech with their profile, portfolio, and preferred role.'
    ]
  },
  'press-kit': {
    title: 'Press Kit',
    eyebrow: 'Company',
    body: [
      'The EastenSocial press kit can include brand assets, product screenshots, company descriptions, founder information, and approved media notes.',
      'Journalists, partners, and reviewers should use official logos and product descriptions without implying endorsement by connected social platforms.',
      'For press assets or interviews, contact the company before publication.'
    ]
  },
  'press-releases': {
    title: 'Press Releases',
    eyebrow: 'Company',
    body: [
      'This page is reserved for official EastenSocial announcements, product milestones, platform integration updates, partnerships, and company news.',
      'Older releases may be archived as the product develops.',
      'For media verification, contact Easten Mediatech through the official sales or support channel.'
    ]
  },
  disclaimer: {
    title: 'Disclaimer',
    eyebrow: 'Important note',
    body: [
      'EastenSocial is built for official platform API workflows, but third-party platforms may change permissions, review requirements, limits, or features at any time.',
      'Brand logos and platform names are used to identify compatibility and integrations. They do not imply ownership by or endorsement from those companies.',
      'Reports and previews are designed to help teams make decisions, but final platform-side results can vary.'
    ]
  },
  contact: {
    title: 'Contact Us',
    eyebrow: 'Talk to us',
    body: [
      'For sales, onboarding, support, or partnership queries, contact Easten Mediatech through eastenmediatech.com or eastenconnect@gmail.com.',
      'Agencies can request custom onboarding for multiple workspaces, bulk scheduling, reporting setup, and platform approval assistance.'
    ]
  },
  about: {
    title: 'About Us',
    eyebrow: 'Parent firm',
    body: [
      'EastenSocial is created by Easten Mediatech to help agencies and local businesses manage modern social media workflows without scattered tools.',
      'The product focus is simple: connect profiles, create better content, schedule with confidence, reply faster, and report clearly.'
    ]
  },
  'contact-sales': {
    title: 'Contact Sales',
    eyebrow: 'Company',
    body: [
      'Sales queries for EastenSocial can be sent to eastenconnect@gmail.com with your business name, workspace size, required platforms, and expected channel count.',
      'Agency customers can request setup help for Meta permissions, Google Business Profile, LinkedIn, Threads, reporting, and client approval workflows.',
      'Please do not send passwords, private access tokens, or sensitive client documents by email.'
    ]
  }
};

const builderStorageKey = 'easten-social-home-builder-v1';

const builderPalette = [
  { type: 'hero', label: 'Hero' },
  { type: 'features', label: 'Feature Cards' },
  { type: 'text', label: 'Text Block' },
  { type: 'image', label: 'Image/Video Banner' },
  { type: 'table', label: 'Table' },
  { type: 'graphics', label: 'Graphics Cards' },
  { type: 'pricing', label: 'Pricing' }
];

function makeBuilderId(type) {
  return `${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createBuilderSection(type) {
  const id = makeBuilderId(type);

  if (type === 'hero') {
    return {
      id,
      type,
      kicker: 'Built by Easten Mediatech',
      title: 'EastenSocial',
      subtitle: 'Automate without limits.',
      body: 'Plan content, publish across channels, manage approvals, reply to customers, and report growth from one premium workspace.',
      buttonText: 'Get Started Now',
      secondaryButtonText: 'View Dashboard'
    };
  }

  if (type === 'features') {
    return {
      id,
      type,
      kicker: 'Complete agency suite',
      title: 'Everything your social media team needs.',
      body: 'Add, remove, and edit feature cards from the builder.',
      cards: [
        { title: 'Post Now', text: 'Publish images, videos, reels, shorts, stories and business updates.' },
        { title: 'Calendar', text: 'Plan monthly content and share approval links with clients.' },
        { title: 'Reports', text: 'Track growth, top posts, ad spend and client-ready reports.' }
      ]
    };
  }

  if (type === 'table') {
    return {
      id,
      type,
      kicker: 'Compare',
      title: 'EastenSocial vs other platforms',
      body: 'Editable comparison table.',
      rows: [
        { feature: 'Free starter channels', easten: '5 channels', other: 'Usually limited trial' },
        { feature: 'Agency workflow', easten: 'Built-in', other: 'Depends on plan' },
        { feature: 'Client approval', easten: 'Included', other: 'Often add-on' }
      ]
    };
  }

  if (type === 'image') {
    return {
      id,
      type,
      kicker: 'Product preview',
      title: 'Show a demo, screenshot, or campaign visual.',
      body: 'Paste an image/video URL or upload a small image from your computer.',
      imageUrl: '',
      caption: 'Replace this with your product demo visual.'
    };
  }

  if (type === 'graphics') {
    return {
      id,
      type,
      kicker: 'Visual proof',
      title: 'Make the page feel rich with graphics.',
      body: 'Use these editable graphic cards for metrics, badges, trust points, or process steps.',
      cards: [
        { title: '50 Channels', text: 'Scale to agency-level publishing.' },
        { title: 'AI Captions', text: 'Write faster with assistant workflows.' },
        { title: 'Client Approval', text: 'Keep review loops clean.' }
      ]
    };
  }

  if (type === 'pricing') {
    return {
      id,
      type,
      kicker: 'Plans',
      title: 'Start free, upgrade when the agency grows.',
      body: 'Edit the plan cards anytime.',
      cards: pricingPlans.map((plan) => ({ title: plan.name, text: `${plan.price} - ${plan.channels}` }))
    };
  }

  return {
    id,
    type: 'text',
    kicker: 'Content block',
    title: 'Add your headline',
    body: 'Write the section content here. You can reorder this section, duplicate it, or delete it.'
  };
}

const defaultBuilderSections = [
  createBuilderSection('hero'),
  createBuilderSection('features'),
  createBuilderSection('image'),
  createBuilderSection('table'),
  createBuilderSection('pricing'),
  createBuilderSection('graphics')
];

const profileCards = [
  { id: 'facebook', platform: 'Facebook', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#2563eb' },
  { id: 'instagram', platform: 'Instagram', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#e1306c' },
  { id: 'google', platform: 'Google Business', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#4285f4' },
  { id: 'linkedin', platform: 'LinkedIn', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#0a66c2' },
  { id: 'youtube', platform: 'YouTube', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#dc2626' },
  { id: 'threads', platform: 'Threads', handle: 'Not connected', health: 'Disconnected', sync: 'Connect now', color: '#111111' },
  { id: 'whatsapp_channel', platform: 'WhatsApp Channel', handle: 'Not added', health: 'Not added', sync: 'Add channel', color: '#1fa855' },
  { id: 'twitter', platform: 'X (Twitter)', handle: 'Paid API access', health: 'Coming soon', sync: 'Coming soon', color: '#111827', comingSoon: true },
  { id: 'snapchat', platform: 'Snapchat', handle: 'Business verification required', health: 'Coming soon', sync: 'Coming soon', color: '#f5d90a', comingSoon: true }
];

const demoAgencyUsers = [
  { id: 'agency-1', name: 'Easten Mediatech', email: 'team@eastenmediatech.com', role: 'agency', plan: 'agency', channelLimit: 30, unlimitedChannels: false, usedChannels: 8, accountStatus: 'active' },
  { id: 'agency-2', name: 'Studio North', email: 'hello@studionorth.example', role: 'agency', plan: 'personal', channelLimit: 5, unlimitedChannels: false, usedChannels: 4, accountStatus: 'active' },
  { id: 'agency-3', name: 'Growth Desk', email: 'ops@growthdesk.example', role: 'agency', plan: 'enterprise', channelLimit: 100, unlimitedChannels: false, usedChannels: 74, accountStatus: 'active' }
];

const defaultAgencyUser = demoAgencyUsers[0];
const defaultClientUser = { id: 'client-preview', name: 'Client Reviewer', email: 'client@local.preview', role: 'client', plan: 'client', channelLimit: 0, unlimitedChannels: false, usedChannels: 0, accountStatus: 'active' };

const bulkRowsSeed = [];
const adCampaigns = [];

const calendarToday = new Date();
const calendarYear = calendarToday.getFullYear();
const calendarMonthIndex = calendarToday.getMonth();

function getRedirectNotice() {
  const params = new URLSearchParams(window.location.search);
  const facebookState = params.get('facebook');
  const instagramState = params.get('instagram');

  if (instagramState === 'connected') {
    const accounts = params.get('accounts') || params.get('pages') || '0';
    const warning = params.get('warning');
    if (warning === 'no_accounts' || warning === 'no_pages') {
      return { type: 'warning', message: 'Meta login succeeded, but no connected Instagram professional account was found.' };
    }
    if (warning === 'channel_limit') {
      return { type: 'warning', message: `Instagram connected. ${accounts} account(s) added; some accounts exceeded this workspace channel allowance.` };
    }
    return { type: 'success', message: `Instagram connected. ${accounts} professional account(s) found.` };
  }

  if (instagramState) {
    return { type: 'error', message: `Instagram connection issue: ${params.get('message') || instagramState}` };
  }

  if (facebookState === 'connected') {
    const pages = params.get('pages') || '0';
    const warning = params.get('warning');

    if (warning === 'no_pages') {
      return { type: 'warning', message: 'Facebook connected, but no page came back. Reconnect with the page owner account.' };
    }

    if (warning === 'missing_permissions') {
      return { type: 'warning', message: `Facebook connected and ${pages} page(s) found, but permissions need review.` };
    }

    if (warning === 'publishing_access') {
      return {
        type: 'warning',
        message: `Facebook returned ${pages} page(s), but none supplied publish-ready Page access. Open Profiles to review each Page and reconnect after granting content access.`
      };
    }

    if (warning === 'channel_limit') {
      const skipped = params.get('skipped') || 'some';
      return { type: 'warning', message: `Facebook connected. ${pages} page(s) added; ${skipped} skipped because this account reached its channel allowance.` };
    }

    if (warning === 'workspace_conflict') {
      const skipped = params.get('skipped') || 'some';
      return { type: 'warning', message: `Facebook connected. ${pages} page(s) added; ${skipped} already belong to another workspace and were not moved.` };
    }

    return { type: 'success', message: `Facebook connected. ${pages} page(s) found.` };
  }

  if (facebookState === 'error') {
    return { type: 'error', message: `Facebook page sync failed: ${params.get('message') || 'unknown error'}` };
  }

  if (facebookState === 'config_error') {
    return { type: 'error', message: `Facebook backend config issue: ${params.get('message') || 'missing settings'}` };
  }

  if (facebookState === 'failed') {
    return { type: 'error', message: 'Facebook login failed. Try Connect Facebook again.' };
  }

  const googleState = params.get('google');

  if (googleState === 'connected') {
    return { type: 'success', message: `Google connected: ${params.get('email') || 'account synced'}.` };
  }

  if (googleState === 'config_error' || googleState === 'session_error' || googleState === 'error') {
    return { type: 'error', message: `Google connection issue: ${params.get('message') || 'unknown error'}` };
  }

  const provider = params.get('provider');
  const providerState = params.get('status');
  const providerLabels = { x: 'X (Twitter)', linkedin: 'LinkedIn', threads: 'Threads', snapchat: 'Snapchat', canva: 'Canva' };
  if (provider && providerState === 'connected') {
    return { type: 'success', message: `${providerLabels[provider] || provider} account connected successfully.` };
  }
  if (provider && providerState) {
    return { type: 'error', message: `${providerLabels[provider] || provider} connection issue: ${params.get('message') || providerState}` };
  }

  return null;
}

function cx(...names) {
  return names.filter(Boolean).join(' ');
}

function resizeSquareImage(file, size = 150) {
  return new Promise((resolve, reject) => {
    if (!file?.type?.startsWith('image/')) {
      reject(new Error('Select a PNG, JPG or WEBP image.'));
      return;
    }
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      const crop = Math.min(image.naturalWidth, image.naturalHeight);
      const sourceX = (image.naturalWidth - crop) / 2;
      const sourceY = (image.naturalHeight - crop) / 2;
      context.drawImage(image, sourceX, sourceY, crop, crop, 0, 0, size, size);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('This image could not be read.'));
    };
    image.src = objectUrl;
  });
}

function cropSquareDataUrl(source, { zoom = 1, x = 0, y = 0 } = {}, size = 1000) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      const baseCrop = Math.min(image.naturalWidth, image.naturalHeight);
      const crop = baseCrop / Math.max(1, Number(zoom) || 1);
      const maxX = Math.max(0, image.naturalWidth - crop);
      const maxY = Math.max(0, image.naturalHeight - crop);
      const sourceX = Math.min(maxX, Math.max(0, maxX / 2 + (Number(x) / 100) * (maxX / 2)));
      const sourceY = Math.min(maxY, Math.max(0, maxY / 2 + (Number(y) / 100) * (maxY / 2)));
      context.drawImage(image, sourceX, sourceY, crop, crop, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    image.onerror = () => reject(new Error('This image could not be cropped.'));
    image.src = source;
  });
}

function PlatformLogo({ id, label }) {
  const iconProps = { 'aria-hidden': true, focusable: false };
  const icons = {
    facebook: <SiFacebook {...iconProps} />,
    instagram: <SiInstagram {...iconProps} />,
    threads: <FaThreads {...iconProps} />,
    google: <SiGoogle {...iconProps} />,
    youtube: <SiYoutube {...iconProps} />,
    twitter: <FaXTwitter {...iconProps} />,
    linkedin: <FaLinkedinIn {...iconProps} />,
    snapchat: <FaSnapchat {...iconProps} />,
    whatsapp_channel: <FaWhatsapp {...iconProps} />
  };

  return <span className="platform-logo-fallback" aria-label={label}>{icons[id] || label?.slice(0, 1)}</span>;
}

function platformById(id) {
  return platforms.find((platform) => platform.id === id) || platforms[0];
}

function monthDays(baseDate = calendarToday) {
  const days = [];
  const year = baseDate.getFullYear();
  const monthIndex = baseDate.getMonth();
  const leadingBlanks = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = Math.ceil((leadingBlanks + daysInMonth) / 7) * 7;
  for (let i = 0; i < cells; i += 1) {
    const day = i - leadingBlanks + 1;
    days.push(day > 0 && day <= daysInMonth ? day : null);
  }
  return days;
}

function formatCalendarTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Now';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function toLocalDateTimeInput(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function scheduleDisplay(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return { date: 'Date unavailable', time: '', relative: '' };
  const deltaMinutes = Math.round((date.getTime() - Date.now()) / 60000);
  const absoluteMinutes = Math.abs(deltaMinutes);
  let duration = '';
  if (absoluteMinutes < 60) duration = `${Math.max(1, absoluteMinutes)} min`;
  else if (absoluteMinutes < 1440) duration = `${Math.floor(absoluteMinutes / 60)}h ${absoluteMinutes % 60}m`;
  else duration = `${Math.floor(absoluteMinutes / 1440)}d ${Math.floor((absoluteMinutes % 1440) / 60)}h`;
  return {
    date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
    time: `${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST`,
    relative: deltaMinutes >= 0 ? `in ${duration}` : `${duration} ago`
  };
}

function savedPostToCalendarEntry(post, baseDate = calendarToday) {
  const dateValue = post.scheduledTime || post.publishedAt || post.createdAt;
  const date = dateValue ? new Date(dateValue) : null;

  if (!date || Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== baseDate.getFullYear() || date.getMonth() !== baseDate.getMonth()) return null;

  const platformList = Array.isArray(post.platforms) ? post.platforms : [post.platform || 'facebook'];
  const reviewStatus = post.reviewStatus || post.approvalStatus || post.clientReviewStatus;

  return {
    day: date.getDate(),
    id: post.id || post.externalPostId || `${date.getTime()}-${post.content}`,
    platform: platformList[0] || 'facebook',
    platforms: platformList,
    status: post.status === 'published' ? 'Published' : post.status || 'Saved',
    postStatus: post.status || 'draft',
    approvalStatus: reviewStatus || (post.status === 'published' ? 'approved' : 'pending'),
    content: post.content || post.message || '',
    scheduledTime: post.scheduledTime || '',
    publishedAt: post.publishedAt || '',
    originalPost: post,
    time: formatCalendarTime(date),
    title: post.title || post.content || 'Published post',
    source: 'mongo'
  };
}

function getInitialScreen() {
  return initialSiteScreen(window.location);
}

function App() {
  const [screen, setScreen] = useState(getInitialScreen);
  const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const [currentUser, setCurrentUser] = useState(() => {
    if (!isLocalPreview) return null;
    try {
      return JSON.parse(window.localStorage.getItem('easten-current-user')) || null;
    } catch (error) {
      return null;
    }
  });
  const [authReady, setAuthReady] = useState(isLocalPreview);
  const [authError, setAuthError] = useState('');
  const [authAttempt, setAuthAttempt] = useState(0);

  useEffect(() => {
    if (!isPublicHost) return;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash.replace('#', '');
    if (params.has('facebook') || params.has('instagram') || params.has('google') || params.has('provider') || ['login', 'signup', 'dashboard', 'admin'].includes(hash)) {
      window.location.replace(`${APP_ORIGIN}/${window.location.search}${window.location.hash || '#login'}`);
    }
  }, []);

  useEffect(() => {
    if (isLocalPreview) return;
    let active = true;
    setAuthError('');
    readSession(axios, API_BASE)
      .then((user) => {
        if (!active) return;
        setCurrentUser(user);
        setAuthReady(true);
      })
      .catch(() => {
        if (active) setAuthError('Your account could not be loaded. Check your connection and retry.');
      });
    return () => { active = false; };
  }, [isLocalPreview, authAttempt]);

  useEffect(() => {
    const syncScreenFromLocation = () => setScreen(getInitialScreen());
    window.addEventListener('hashchange', syncScreenFromLocation);
    window.addEventListener('popstate', syncScreenFromLocation);
    return () => {
      window.removeEventListener('hashchange', syncScreenFromLocation);
      window.removeEventListener('popstate', syncScreenFromLocation);
    };
  }, []);

  const updateCurrentUser = (user) => {
    setCurrentUser(user || null);
    if (user) window.localStorage.setItem('easten-current-user', JSON.stringify(user));
    else window.localStorage.removeItem('easten-current-user');
  };

  const navigate = (nextScreen) => {
    if (isPublicHost && ['login', 'signup', 'dashboard', 'admin'].includes(nextScreen)) {
      window.location.assign(`${APP_ORIGIN}/#${nextScreen}`);
      return;
    }
    if (isAppHost && nextScreen === 'landing') {
      nextScreen = currentUser ? 'dashboard' : 'login';
    }
    setScreen(nextScreen);
    window.history.replaceState(null, '', nextScreen === 'landing' ? window.location.pathname : `#${nextScreen}`);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Local state is still cleared so a stale UI session cannot remain open.
    }
    updateCurrentUser(null);
    navigate(isAppHost ? 'login' : 'landing');
  };

  const protect = (content) => (
    <>
      {content}
      <PermissionSetup />
      {currentUser?.mustChangePassword && (
        <PasswordChangeModal onChanged={updateCurrentUser} />
      )}
    </>
  );

  if (!authReady && ['dashboard', 'admin'].includes(screen)) {
    return <main className="auth-shell"><div className="auth-card"><p className="site-eyebrow">Secure session</p><h1>{authError ? 'Account temporarily unavailable' : 'Checking your login...'}</h1>{authError && <><p role="alert">{authError}</p><button className="site-cta full-width" type="button" onClick={() => setAuthAttempt((value) => value + 1)}>Retry</button></>}</div></main>;
  }

  if (screen === 'dashboard') {
    if (!currentUser && !isLocalPreview) {
      return (
        <AuthPage
          mode="login"
          openDashboard={() => navigate('dashboard')}
          openAdmin={() => navigate('admin')}
          openLogin={() => navigate('login')}
          openPublicSite={() => navigate('landing')}
          openSignup={() => navigate('signup')}
          setCurrentUser={updateCurrentUser}
        />
      );
    }
    if (currentUser?.role === 'client') {
      return protect(<ClientDashboard currentUser={currentUser} setCurrentUser={updateCurrentUser} logout={logout} />);
    }
    if (currentUser?.role === 'admin') {
      return protect(<AdminDashboard currentUser={currentUser} setCurrentUser={updateCurrentUser} openPublicSite={() => navigate('landing')} logout={logout} />);
    }
    return protect(
      <DashboardApp
        currentUser={currentUser || defaultAgencyUser}
        logout={logout}
        setCurrentUser={updateCurrentUser}
      />
    );
  }

  if (screen === 'calendar-public') {
    return <PublicCalendarPage openPublicSite={() => navigate('landing')} />;
  }

  if (screen === 'admin') {
    if (!currentUser) {
      return <AuthPage mode="admin-login" openDashboard={() => navigate('dashboard')} openAdmin={() => navigate('admin')} openLogin={() => navigate('login')} openPublicSite={() => navigate('landing')} openSignup={() => navigate('signup')} setCurrentUser={updateCurrentUser} />;
    }
    if (currentUser.role !== 'admin') {
      return (
        <main className="admin-access-gate">
          <span>CO</span>
          <h1>Administrator login required</h1>
          <p><strong>{currentUser.email}</strong> is signed in as {currentUser.role}. This account cannot open the Admin Control Center.</p>
          <div className="admin-access-actions">
            <button className="product-primary" type="button" onClick={() => navigate('dashboard')}>Return to your dashboard</button>
            <button className="product-secondary" type="button" onClick={async () => {
              try { await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true }); } catch (error) { /* Clear local state if logout is temporarily unavailable. */ }
              updateCurrentUser(null);
              navigate('admin');
            }}>Sign out and use admin login</button>
          </div>
        </main>
      );
    }
    return protect(<AdminDashboard currentUser={currentUser} setCurrentUser={updateCurrentUser} openPublicSite={() => navigate('landing')} logout={logout} />);
  }

  if (screen === 'builder') {
    return <WebsiteBuilder openPublicSite={() => navigate('landing')} openDashboard={() => navigate('dashboard')} />;
  }

  if (policyPages[screen]) {
    return <LegalPage page={policyPages[screen]} openPublicSite={() => navigate('landing')} openSignup={() => navigate('signup')} />;
  }

  if (screen === 'login' || screen === 'signup') {
    return (
      <AuthPage
        mode={screen}
        openDashboard={() => navigate('dashboard')}
        openAdmin={() => navigate('admin')}
        openLogin={() => navigate('login')}
        openPublicSite={() => navigate('landing')}
        openSignup={() => navigate('signup')}
        setCurrentUser={updateCurrentUser}
      />
    );
  }

  return (
    <ProductLandingPage
      openDashboard={() => navigate('dashboard')}
      openLogin={() => navigate('login')}
      openSignup={() => navigate('signup')}
      openPolicy={(page) => navigate(page)}
      openBuilder={() => navigate('builder')}
    />
  );
}

function DashboardApp({ currentUser = defaultAgencyUser, logout, setCurrentUser }) {
  const [activeModule, setActiveModule] = useState('home');
  const [workspaceOptions, setWorkspaceOptions] = useState(fallbackWorkspaces.slice(0, 1));
  const [workspaceId, setWorkspaceId] = useState(() => readWorkspaceSelection(currentUser) || fallbackWorkspaces[0].id);
  const currentWorkspaceRef = useRef(workspaceId);
  currentWorkspaceRef.current = workspaceId;
  const [workspacesReady, setWorkspacesReady] = useState(false);
  const [title, setTitle] = useState('');
  const [createMode, setCreateMode] = useState('manual');
  const [aiEvent, setAiEvent] = useState(null);
  const [message, setMessage] = useState('');
  const [platformMessages, setPlatformMessages] = useState({});
  const [firstComment, setFirstComment] = useState('');
  const [instagramCollaborators, setInstagramCollaborators] = useState('');
  const [mediaAltText, setMediaAltText] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [scheduledAt, setScheduledAt] = useState('');
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [publishCover, setPublishCover] = useState(null);
  const [mediaMeta, setMediaMeta] = useState({ kind: 'none', orientation: 'unknown', width: 0, height: 0, name: '' });
  const [contentFormats, setContentFormats] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['facebook', 'instagram']);
  const [selectedDestinationKeys, setSelectedDestinationKeys] = useState([]);
  const [previewDevice, setPreviewDevice] = useState('mobile');
  const [notice, setNotice] = useNotice(getRedirectNotice);
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [instagramStatus, setInstagramStatus] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [externalStatus, setExternalStatus] = useState(null);
  const [, setIsLoadingStatus] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaUploadError, setMediaUploadError] = useState('');
  const uploadedMediaCache = useRef(null);
  const mediaUploadAbortRef = useRef(null);
  useEffect(() => {
    setPublishCover(selectedVideoFile?.storedCoverFileName && selectedVideoFile.storedWorkspaceId === workspaceId
      ? { fileName: selectedVideoFile.storedCoverFileName, sourceFile: selectedVideoFile, workspaceId }
      : null);
  }, [selectedVideoFile, workspaceId]);
  useEffect(() => {
    setMediaUploadError('');
    return () => {
      mediaUploadAbortRef.current?.abort();
      mediaUploadAbortRef.current = null;
    };
  }, [selectedVideoFile, workspaceId]);
  const [reportRange, setReportRange] = useState('30D');
  const [reportPlatform, setReportPlatform] = useState('All');
  const [inboxFilter, setInboxFilter] = useState('All');
  const [selectedInboxId, setSelectedInboxId] = useState(1);
  const [bulkRows, setBulkRows] = useState(bulkRowsSeed);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [publicCalendar, setPublicCalendar] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false);
  const [manageView, setManageView] = useState('published');
  const workspaceMenuRef = useRef(null);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape' || (event.type === 'pointerdown' && !workspaceMenuRef.current?.contains(event.target))) setIsWorkspaceMenuOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close); };
  }, []);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const commandSearchRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [theme, setTheme] = useState(() => window.localStorage.getItem('easten-dashboard-theme') || 'light');
  const [profileInitialTab, setProfileInitialTab] = useState('profile');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCreateStep, setMobileCreateStep] = useState(null);
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
        commandSearchRef.current?.focus();
      } else if (event.key === 'Escape') setCommandOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
  const [tourOpen, setTourOpen] = useState(() => !localStorage.getItem(`cosocial-onboarding-${currentUser.id || currentUser._id}`));
  useEffect(() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }, [activeModule]);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') setMobileCreateStep(null); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);

  const openCreate = (kind, mode = 'manual') => {
    setMobileCreateStep(null);
    setMobileMenuOpen(false);
    if (kind === 'post') setCreateMode(mode);
    setActiveModule(kind);
  };
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('facebook') || params.has('instagram') || params.has('google') || params.has('provider')) {
      setActiveModule('profiles');
    }
  }, []);

  const workspace = workspaceOptions.find((item) => item.id === workspaceId)
    || (workspacesReady ? workspaceOptions[0] : null)
    || { id: workspaceId, name: 'Loading workspace', initials: '...', color: '#273e84' };
  const workspaceRequest = { withCredentials: true, headers: { 'x-workspace-id': workspaceId } };
  const connectedPages = useMemo(() => facebookStatus?.pages || [], [facebookStatus?.pages]);
  const lastSync = facebookStatus?.lastFacebookSync || null;
  const selectedInbox = inboxItems.find((item) => item.id === selectedInboxId) || null;
  const filteredInbox = inboxItems.filter((item) => inboxFilter === 'All' || item.status === inboxFilter);
  const platformOptions = useMemo(() => platforms.map((platform) => {
    if (platform.id === 'facebook') {
      return {
        ...platform,
        connected: connectedPages.length > 0
      };
    }

    if (platform.id === 'instagram') {
      return {
        ...platform,
        connected: Boolean(instagramStatus?.connected)
      };
    }

    if (platform.id === 'google') {
      return {
        ...platform,
        connected: Boolean(googleStatus?.googleBusiness?.connected)
      };
    }

    if (platform.id === 'youtube') {
      return {
        ...platform,
        connected: Boolean(googleStatus?.youtube?.connected || googleStatus?.youtube?.hasScope)
      };
    }

    if (platform.id === 'twitter') {
      return { ...platform, connected: false, comingSoon: true };
    }

    if (platform.id === 'linkedin') {
      return {
        ...platform,
        connected: Boolean((externalStatus?.connections?.linkedin || []).some((account) => !account.needsReconnect))
      };
    }

    if (platform.id === 'threads') {
      const configured = Boolean(externalStatus?.providers?.threads?.configured);
      return {
        ...platform,
        connected: Boolean((externalStatus?.connections?.threads || []).some((account) => !account.needsReconnect)),
        comingSoon: !configured
      };
    }

    if (platform.id === 'snapchat') {
      return { ...platform, connected: false, comingSoon: true };
    }

    return {
      ...platform,
      connected: false
    };
  }), [connectedPages.length, externalStatus, googleStatus, instagramStatus]);
  const connectedChannelCount = useMemo(() => (
    connectedPages.length
    + (instagramStatus?.accounts?.length || 0)
    + (googleStatus?.googleBusiness?.locations?.length || 0)
    + (googleStatus?.youtube?.channels?.length || 0)
    + ((externalStatus?.connections?.linkedin || []).filter((account) => !account.needsReconnect).length)
    + ((externalStatus?.connections?.threads || []).filter((account) => !account.needsReconnect).length)
  ), [connectedPages.length, externalStatus, googleStatus, instagramStatus]);
  const destinationOptions = useMemo(() => [
    ...connectedPages.map((page) => ({
      key: `facebook:${page.pageId}`,
      platformId: 'facebook',
      accountId: String(page.pageId),
      label: page.name || 'Facebook Page',
      avatarUrl: page.pictureUrl || page.profileImageUrl || '',
      detail: 'Facebook Page'
    })),
    ...(instagramStatus?.accounts || []).map((account) => ({
      key: `instagram:${account.instagramBusinessId}`,
      platformId: 'instagram',
      accountId: String(account.instagramBusinessId),
      label: account.username ? `@${account.username}` : account.name || account.pageName || 'Instagram account',
      avatarUrl: account.profilePictureUrl || '',
      detail: account.pageName || 'Instagram professional account'
    })),
    ...(googleStatus?.googleBusiness?.locations || []).map((location) => ({
      key: `google:${location.googleUserId || 'account'}:${location.parent}`,
      platformId: 'google',
      accountId: String(location.parent || ''),
      label: location.title || location.storeCode || 'Google Business location',
      detail: [location.accountDisplayName, location.googleEmail].filter(Boolean).join(' · ') || 'Google Business Profile',
      connectionId: location.googleUserId || ''
    })),
    ...(googleStatus?.youtube?.channels || []).map((channel) => ({
      key: `youtube:${channel.id}`,
      platformId: 'youtube',
      accountId: String(channel.id),
      label: channel.title || 'YouTube channel',
      avatarUrl: channel.thumbnailUrl || channel.thumbnail || '',
      detail: channel.googleEmail || 'YouTube channel',
      connectionId: channel.googleUserId || ''
    })),
    ...(externalStatus?.connections?.linkedin || []).filter((account) => !account.needsReconnect).map((account) => ({
      key: `linkedin:${account.externalAccountId}`,
      platformId: 'linkedin',
      accountId: String(account.externalAccountId),
      label: account.displayName || account.username || 'LinkedIn profile',
      avatarUrl: account.avatarUrl || '',
      detail: account.metadata?.accountType === 'organization' ? 'LinkedIn organization' : 'LinkedIn member profile',
      connectionId: String(account.externalAccountId)
    })),
    ...(externalStatus?.connections?.threads || []).filter((account) => !account.needsReconnect).map((account) => ({
      key: `threads:${account.externalAccountId}`,
      platformId: 'threads',
      accountId: String(account.externalAccountId),
      label: account.username ? `@${account.username}` : account.displayName || 'Threads profile',
      avatarUrl: account.avatarUrl || '',
      detail: 'Threads profile',
      connectionId: String(account.externalAccountId)
    }))
  ].filter((destination) => destination.accountId), [connectedPages, externalStatus, googleStatus, instagramStatus]);
  const selectedDestinations = useMemo(() => destinationOptions.filter((destination) => (
    selectedDestinationKeys.includes(destination.key) && selectedPlatforms.includes(destination.platformId)
  )), [destinationOptions, selectedDestinationKeys, selectedPlatforms]);
  const channelLimit = currentUser.unlimitedChannels ? Infinity : (currentUser.channelLimit || 5);
  const canAddChannel = currentUser.unlimitedChannels || connectedChannelCount < channelLimit;
  const activePlatforms = selectedPlatforms
    .map((platformId) => platformOptions.find((platform) => platform.id === platformId) || platformById(platformId))
    .filter((platform) => platform.connected);
  const previewText = [title.trim(), message.trim()].filter(Boolean).join('\n\n') || 'Write the next post for this workspace. Live preview updates here.';

  const loadWorkspaces = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/workspaces`, { withCredentials: true });
      const rows = (response.data.workspaces || []).map((item) => ({
        ...fallbackWorkspaces[0],
        ...item,
        name: item.name || item.brandName,
        initials: item.initials || String(item.name || item.brandName || 'W').slice(0, 2).toUpperCase(),
        color: item.color || '#273e84'
      }));
      if (!rows.length) {
        setWorkspacesReady(true);
        return;
      }
      setWorkspaceOptions(rows);
      setWorkspaceId((current) => resolveWorkspaceSelection(rows, current));
      setWorkspacesReady(true);
    } catch (error) {
      if (error.response?.status !== 401) {
        setNotice({ type: 'error', message: error.response?.data?.error || 'Workspaces could not be loaded.' });
      }
    }
  };

  const loadFacebookStatus = async () => {
    try {
      setIsLoadingStatus(true);
      const response = await axios.get(`${API_BASE}/api/facebook/status`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setFacebookStatus(response.data);
    } catch (err) {
      setNotice({ type: 'error', message: `Status check failed: ${err.response?.data?.error || err.message}` });
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const loadInstagramStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/instagram/status`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setInstagramStatus(response.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        setNotice({ type: 'error', message: `Instagram status failed: ${err.response?.data?.error || err.message}` });
      }
    }
  };

  const loadGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/google/status`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setGoogleStatus(response.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        setNotice({ type: 'error', message: `Google status failed: ${err.response?.data?.error || err.message}` });
      }
    }
  };

  const loadExternalStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/connections/status`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setExternalStatus(response.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        setNotice({ type: 'error', message: `External profile status failed: ${err.response?.data?.error || err.message}` });
      }
    }
  };

  const loadPublishedPosts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/posts`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setPublishedPosts(response.data.posts || []);
    } catch (err) {
      if (err.response?.status !== 401) {
        setNotice({ type: 'error', message: `Posts load failed: ${err.response?.data?.error || err.message}` });
      }
    }
  };

  const loadNotifications = useCallback(async () => {
    if (!workspaceId || String(currentUser?.id || '').startsWith('local-')) return;
    try {
      const response = await axios.get(`${API_BASE}/api/notifications`, workspaceRequest);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setNotifications(response.data.notifications || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.warn('Notifications could not be loaded:', error.response?.data?.error || error.message);
      }
    }
  // workspaceRequest is intentionally rebuilt when the selected workspace changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, currentUser?.id]);

  const refreshStatuses = async () => {
    await Promise.all([
      loadFacebookStatus(),
      loadInstagramStatus(),
      loadGoogleStatus(),
      loadExternalStatus(),
      loadPublishedPosts()
    ]);
  };

  useEffect(() => {
    loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!workspacesReady || !workspaceId) return;
    persistWorkspaceSelection(currentUser, workspaceId);
    const selectedWorkspace = workspaceOptions.find((item) => item.id === workspaceId);
    const hour = Number(new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }).format(new Date()));
    const greeting = hour < 5 ? 'Good night' : hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : hour < 21 ? 'Good evening' : 'Good night';
    setActiveModule('home');
    setCreateMode('manual');
    setAiEvent(null);
    setTitle('');
    setMessage('');
    setPlatformMessages({});
    setFirstComment('');
    setInstagramCollaborators('');
    setMediaAltText('');
    setMediaType('image');
    setScheduledAt('');
    setSelectedVideoFile(null);
    setPublishCover(null);
    setMediaMeta({ kind: 'none', orientation: 'unknown', width: 0, height: 0, name: '' });
    setContentFormats([]);
    setSelectedPlatforms([]);
    setUploadProgress(0);
    setMediaUploadError('');
    mediaUploadAbortRef.current?.abort();
    mediaUploadAbortRef.current = null;
    uploadedMediaCache.current = null;
    setFacebookStatus(null);
    setInstagramStatus(null);
    setGoogleStatus(null);
    setExternalStatus(null);
    setPublishedPosts([]);
    setNotifications([]);
    setSelectedDestinationKeys([]);
    setReportPlatform('All');
    setSelectedInboxId(null);
    setNotice({ type: 'success', message: `${greeting}. ${selectedWorkspace?.name || 'Your workspace'} is ready.` });
    refreshStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, workspacesReady]);

  useEffect(() => {
    if (!workspacesReady || !workspaceId) return undefined;
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(timer);
  }, [loadNotifications, workspaceId, workspacesReady]);

  useEffect(() => {
    window.localStorage.setItem('easten-dashboard-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (workspace?.defaultStory) {
      setContentFormats((current) => current.includes('story') ? current : [...current, 'story']);
    }
  }, [workspace?.defaultStory, workspaceId]);

  useEffect(() => {
    setSelectedPlatforms((current) => {
      const connectedIds = platformOptions
        .filter((platform) => platform.connected)
        .map((platform) => platform.id);
      const filtered = current.filter((platformId) => connectedIds.includes(platformId));

      if (filtered.length) return filtered;
      return connectedIds.length ? [connectedIds[0]] : [];
    });
  }, [platformOptions]);

  useEffect(() => {
    const availableKeys = new Set(destinationOptions.map((destination) => destination.key));
    setSelectedDestinationKeys((current) => {
      const next = current.filter((key) => availableKeys.has(key));
      selectedPlatforms.forEach((platformId) => {
        const platformDestinations = destinationOptions.filter((destination) => destination.platformId === platformId);
        if (platformDestinations.length && !platformDestinations.some((destination) => next.includes(destination.key))) {
          platformDestinations.forEach((destination) => next.push(destination.key));
        }
      });
      return [...new Set(next)];
    });
  }, [destinationOptions, selectedPlatforms]);

  const togglePlatform = (platformId) => {
    const platform = platformOptions.find((item) => item.id === platformId);

    if (!platform?.connected) {
      setNotice({ type: 'warning', message: `${platform?.label || 'Profile'} is not connected. Connect it from Assets before publishing.` });
      setActiveModule('profiles');
      return;
    }

    setSelectedPlatforms((current) => {
      if (current.includes(platformId)) {
        if (current.length === 1) return current;
        setSelectedDestinationKeys((keys) => keys.filter((key) => !key.startsWith(`${platformId}:`)));
        return current.filter((item) => item !== platformId);
      }

      setSelectedDestinationKeys((keys) => [...new Set([
        ...keys,
        ...destinationOptions.filter((destination) => destination.platformId === platformId).map((destination) => destination.key)
      ])]);
      return [...current, platformId];
    });
  };

  const toggleDestination = (destinationKey) => {
    setSelectedDestinationKeys((current) => current.includes(destinationKey)
      ? current.filter((key) => key !== destinationKey)
      : [...current, destinationKey]);
  };

  const connectFacebook = () => {
    if (!canAddChannel) {
      setNotice({ type: 'warning', message: `Your ${currentUser.plan} allowance is full. Ask the admin to increase the channel limit.` });
      return;
    }
    window.location.href = `${API_BASE}/auth/facebook?workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const connectInstagram = () => {
    if (!canAddChannel) {
      setNotice({ type: 'warning', message: 'Channel limit reached. Ask the admin to increase your allowance.' });
      return;
    }
    window.location.href = `${API_BASE}/auth/instagram?workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const connectGoogle = (service = 'google') => {
    if (!canAddChannel) {
      setNotice({ type: 'warning', message: 'Channel limit reached. Ask the admin to increase your allowance.' });
      return;
    }
    window.location.href = `${API_BASE}/auth/google?service=${service}&workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const connectExternal = (provider) => {
    if (!canAddChannel) {
      setNotice({ type: 'warning', message: 'Channel limit reached. Ask the admin to increase your allowance.' });
      return;
    }
    window.location.href = `${API_BASE}/auth/${provider}?workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const editPostFromCalendar = (calendarPost) => {
    setCreateMode('manual');
    const savedPost = publishedPosts.find((post) => String(post.id) === String(calendarPost?.id));
    if (!savedPost) {
      if (calendarPost?.scheduledTime) {
        setScheduledAt(toLocalDateTimeInput(calendarPost.scheduledTime));
        setActiveModule('post');
        setNotice({ type: 'success', message: 'Schedule date selected in Post Now.' });
        return;
      }
      setNotice({ type: 'warning', message: 'This saved post is no longer available.' });
      return;
    }
    localStorage.removeItem(`easten-manual-draft-${workspaceId}`);
    setTitle(savedPost.title || '');
    setMessage(savedPost.content || '');
    setMediaAltText(savedPost.altText || '');
    setInstagramCollaborators((savedPost.instagramCollaborators || []).join(', '));
    setSelectedPlatforms((savedPost.platforms || []).filter((id) => platformOptions.some((platform) => platform.id === id && platform.connected)));
    setScheduledAt(toLocalDateTimeInput(savedPost.scheduledTime));
    setContentFormats(savedPost.contentFormats || []);
    setMediaType(savedPost.mediaType || 'text');
    const savedMimeType = savedPost.localMediaMimeType || '';
    const savedMedia = savedPost.localMediaFileName && savedPost.localMediaUrl
      ? {
        name: savedPost.mediaOriginalName || `Saved ${savedMimeType.startsWith('video/') ? 'video' : 'image'}${savedPost.localMediaFileName.match(/\.[a-z0-9]+$/i)?.[0] || ''}`,
        type: savedMimeType,
        size: savedPost.mediaSize || 0,
        storedFileName: savedPost.localMediaFileName,
        storedMediaUrl: savedPost.localMediaUrl,
        storedCoverFileName: savedPost.coverFileName || '',
        storedCoverUrl: savedPost.thumbnailUrl || '',
        storedWorkspaceId: workspaceId
      }
      : null;
    setSelectedVideoFile(savedMedia);
    setMediaMeta(savedMedia ? {
      kind: savedMimeType.startsWith('video/') ? 'video' : 'image',
      orientation: savedPost.mediaOrientation || 'unknown',
      width: savedPost.mediaWidth || 0,
      height: savedPost.mediaHeight || 0,
      name: savedMedia.name
    } : { kind: 'none', orientation: 'unknown', width: 0, height: 0, name: '' });
    setActiveModule('post');
    setNotice({ type: savedPost.localMediaFileName && !savedMedia ? 'warning' : 'success', message: savedPost.localMediaFileName && !savedMedia
      ? 'The saved media is unavailable. Upload it again before publishing or scheduling.'
      : 'Post opened in Post Now. Save as draft or schedule it again after editing.' });
  };

  const hasScheduleTime = Boolean(scheduledAt);
  const scheduleDate = hasScheduleTime ? new Date(scheduledAt) : null;
  const wantsSchedule = hasScheduleTime;

  const uploadSelectedMedia = useCallback(async (force = false) => {
    if (!selectedVideoFile) return null;
    if (selectedVideoFile.storedFileName) return {
      fileName: selectedVideoFile.storedFileName,
      mediaUrl: selectedVideoFile.storedMediaUrl,
      mimeType: selectedVideoFile.type,
      size: selectedVideoFile.size,
      retainedDraft: true
    };
    if (!force && uploadedMediaCache.current?.file === selectedVideoFile && uploadedMediaCache.current.workspaceId === workspaceId && uploadedMediaCache.current.promise) return uploadedMediaCache.current.promise;
    if (!force && uploadedMediaCache.current?.file === selectedVideoFile && uploadedMediaCache.current.workspaceId === workspaceId && uploadedMediaCache.current.upload) return uploadedMediaCache.current.upload;
    mediaUploadAbortRef.current?.abort();
    const controller = new AbortController();
    mediaUploadAbortRef.current = controller;
    setMediaUploadError('');
    setUploadProgress(1);
    const promise = uploadMediaResumable({
      apiBase: API_BASE,
      file: selectedVideoFile,
      workspaceId,
      signal: controller.signal,
      onProgress: setUploadProgress
    }).then((upload) => {
      setUploadProgress(100);
      if (uploadedMediaCache.current?.promise === promise) uploadedMediaCache.current = { file: selectedVideoFile, workspaceId, upload };
      if (mediaUploadAbortRef.current === controller) mediaUploadAbortRef.current = null;
      return upload;
    }).catch((error) => {
      if (uploadedMediaCache.current?.promise === promise) uploadedMediaCache.current = null;
      if (mediaUploadAbortRef.current === controller) mediaUploadAbortRef.current = null;
      if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
        setUploadProgress(0);
        setMediaUploadError(error.response?.data?.error || error.message || 'The original media upload was interrupted. Retry the upload.');
      }
      throw error;
    });
    uploadedMediaCache.current = { file: selectedVideoFile, workspaceId, promise };
    return promise;
  }, [selectedVideoFile, workspaceId]);
  const retrySelectedMediaUpload = useCallback(() => {
    uploadedMediaCache.current = null;
    return uploadSelectedMedia(true);
  }, [uploadSelectedMedia]);
  useEffect(() => {
    if (!selectedVideoFile) { setUploadProgress(0); return; }
    if (selectedVideoFile.storedFileName) { setUploadProgress(100); return; }
    uploadSelectedMedia().catch((error) => {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
      setNotice({ type: 'error', message: error.response?.data?.error || error.message || 'The original media upload was interrupted. Use Retry upload to continue.' });
    });
  }, [selectedVideoFile, uploadSelectedMedia, setNotice]);

  const discardUploadedMedia = async (upload) => {
    if (!upload?.fileName || upload.retainedDraft) return;
    if (uploadedMediaCache.current?.upload?.fileName === upload.fileName) uploadedMediaCache.current = null;
    try {
      await axios.delete(`${API_BASE}/api/media/${encodeURIComponent(upload.fileName)}`, workspaceRequest);
    } catch (error) {
      // Temporary files also expire during scheduled publishing cleanup.
    }
  };

  const postNow = async (requestedMode = 'now') => {
    try {
      if (selectedPlatforms.includes('instagram') && requestedMode !== 'draft') validateInstagramCollaboration(instagramCollaborators, contentFormats);
    } catch (error) {
      setNotice({ type: 'warning', message: error.message });
      return;
    }
    if (requestedMode === 'draft') {
      await saveDraft();
      return;
    }
    if (requestedMode === 'schedule') {
      await queuePost(true);
      return;
    }

    if (!title.trim() && !message.trim() && !Object.values(platformMessages).some((value) => String(value || '').trim())) {
      setNotice({ type: 'warning', message: 'Add a title or description before publishing.' });
      return;
    }

    const messageForPlatform = (platformId) => [title.trim(), String(platformMessages[platformId] || message).trim()].filter(Boolean).join('\n\n');

    const wantsYoutube = selectedPlatforms.includes('youtube');
    const wantsFacebook = selectedPlatforms.includes('facebook');
    const wantsInstagram = selectedPlatforms.includes('instagram');
    const wantsGoogle = selectedPlatforms.includes('google');
    const wantsX = selectedPlatforms.includes('twitter');
    const wantsLinkedIn = selectedPlatforms.includes('linkedin');
    const wantsThreads = selectedPlatforms.includes('threads');
    const facebookPageIds = selectedDestinations.filter((destination) => destination.platformId === 'facebook').map((destination) => destination.accountId);
    const instagramDestinations = selectedDestinations.filter((destination) => destination.platformId === 'instagram');
    const googleDestinations = selectedDestinations.filter((destination) => destination.platformId === 'google');
    const youtubeDestinations = selectedDestinations.filter((destination) => destination.platformId === 'youtube');
    const linkedinDestinations = selectedDestinations.filter((destination) => destination.platformId === 'linkedin');
    const threadsDestinations = selectedDestinations.filter((destination) => destination.platformId === 'threads');

    if (wantsFacebook && !facebookPageIds.length) {
      setNotice({ type: 'warning', message: 'Select at least one Facebook Page below the platform logos.' });
      return;
    }

    if (wantsYoutube && (!selectedVideoFile || mediaMeta.kind !== 'video')) {
      setNotice({ type: 'warning', message: 'Select a video in Add Media before publishing to YouTube.' });
      return;
    }

    if (wantsYoutube && !youtubeDestinations.length) {
      setNotice({ type: 'warning', message: 'Select at least one YouTube channel below the platform logos.' });
      return;
    }

    if (wantsInstagram && !instagramDestinations.length) {
      setNotice({ type: 'warning', message: 'Select at least one Instagram account below the platform logos.' });
      return;
    }

    if (wantsInstagram && (!selectedVideoFile || !['image', 'video'].includes(mediaMeta.kind))) {
      setNotice({ type: 'warning', message: 'Select an image or video in Add Media before publishing to Instagram.' });
      return;
    }

    if (wantsGoogle && !googleDestinations.length) {
      setNotice({ type: 'warning', message: 'Select at least one Google Business location below the platform logos.' });
      return;
    }

    if (wantsGoogle && selectedVideoFile && mediaMeta.kind !== 'image') {
      setNotice({ type: 'warning', message: 'Google Business currently supports text or image posts here. Remove the video or unselect Google Business.' });
      return;
    }

    if (wantsLinkedIn && !linkedinDestinations.length) {
      setNotice({ type: 'warning', message: 'Select at least one LinkedIn account below the platform logos.' });
      return;
    }

    if (wantsThreads && !threadsDestinations.length) {
      setNotice({ type: 'warning', message: 'Select at least one Threads profile below the platform logos.' });
      return;
    }

    if (wantsThreads && Array.from(messageForPlatform('threads')).length > 500) {
      setNotice({ type: 'warning', message: 'Threads text posts are limited to 500 characters. Shorten the title/description or unselect Threads.' });
      return;
    }

    let uploadedMedia = null;
    const uploadedTargets = [];
    try {
      setIsPosting(true);

      if (selectedVideoFile && (wantsYoutube || wantsFacebook || wantsInstagram || (wantsGoogle && mediaMeta.kind === 'image'))) {
        setNotice({ type: 'neutral', message: 'Preparing media for connected platforms...' });
        uploadedMedia = await uploadSelectedMedia();
      }

      if (wantsYoutube) {
        const youtubeTitle = title.trim() || message.split('\n').find((line) => line.trim()) || selectedVideoFile.name || 'EastenSocial video';
        for (const destination of youtubeDestinations) {
          setNotice({ type: 'neutral', message: `Uploading video to ${destination.label}...` });
          const youtubeResponse = await axios.post(`${API_BASE}/api/youtube/upload-stored`, {
            title: youtubeTitle,
            description: platformMessages.youtube || message,
            uploadFileName: uploadedMedia?.fileName || '',
            mimeType: uploadedMedia?.mimeType || selectedVideoFile.type || 'video/mp4',
            googleUserId: destination.connectionId || '',
            youtubeChannelId: destination.accountId,
            youtubeChannelName: destination.label
          }, workspaceRequest);
          uploadedTargets.push(`${destination.label} video ${youtubeResponse.data.videoId}`);
        }
      }

      let response = null;
      let xResponse = null;
      if (wantsFacebook) {
        setNotice({ type: 'neutral', message: wantsYoutube ? 'YouTube done. Publishing Facebook post...' : 'Publishing Facebook post...' });
        response = await axios.post(`${API_BASE}/api/post-to-fb`, {
          mediaType: selectedVideoFile ? mediaType : 'text',
          title,
          message: messageForPlatform('facebook'),
          firstComment,
          altText: mediaAltText,
          platformCaptions: platformMessages,
          contentFormats,
          mediaOrientation: mediaMeta.orientation,
          workspaceId,
          platforms: ['facebook'],
          pageIds: facebookPageIds,
          destinations: selectedDestinations,
          mediaUrl: uploadedMedia?.mediaUrl || '',
          mimeType: uploadedMedia?.mimeType || '',
          uploadFileName: uploadedMedia?.fileName || '',
          scheduledTime: null
        }, workspaceRequest);
        const facebookCount = response.data.posts?.length || (response.data.post ? 1 : 0);
        uploadedTargets.push(`${facebookCount} Facebook Page${facebookCount === 1 ? '' : 's'}`);
      }

      let instagramResponse = null;
      if (wantsInstagram) {
        setNotice({ type: 'neutral', message: 'Publishing to selected Instagram accounts...' });
        instagramResponse = await axios.post(`${API_BASE}/api/instagram/publish`, {
          instagramCollaborators: normalizeInstagramCollaborators(instagramCollaborators),
          title,
          message: messageForPlatform('instagram'),
          firstComment,
          altText: mediaAltText,
          platformCaptions: platformMessages,
          destinations: instagramDestinations,
          contentFormats,
          mediaOrientation: mediaMeta.orientation,
          mediaUrl: uploadedMedia?.mediaUrl || '',
          mimeType: uploadedMedia?.mimeType || '',
          uploadFileName: uploadedMedia?.fileName || '',
          coverFileName: publishCover?.sourceFile === selectedVideoFile && publishCover?.workspaceId === workspaceId ? publishCover.fileName : '',
          workspaceId
        }, workspaceRequest);
        const instagramCount = instagramResponse.data.posts?.length || (instagramResponse.data.post ? 1 : 0);
        uploadedTargets.push(`${instagramCount} Instagram account${instagramCount === 1 ? '' : 's'}`);
      }

      let linkedinResponse = null;
      if (wantsLinkedIn) {
        setNotice({ type: 'neutral', message: 'Publishing to selected LinkedIn accounts...' });
        linkedinResponse = await axios.post(`${API_BASE}/api/linkedin/post`, {
          title,
          message: messageForPlatform('linkedin'),
          firstComment,
          altText: mediaAltText,
          platformCaptions: platformMessages,
          destinations: linkedinDestinations,
          workspaceId
        }, workspaceRequest);
        const linkedinCount = linkedinResponse.data.posts?.length || 0;
        uploadedTargets.push(`${linkedinCount} LinkedIn profile${linkedinCount === 1 ? '' : 's'}`);
      }

      let threadsResponse = null;
      if (wantsThreads) {
        setNotice({ type: 'neutral', message: 'Publishing to selected Threads profiles...' });
        threadsResponse = await axios.post(`${API_BASE}/api/threads/post`, {
          title,
          message: messageForPlatform('threads'),
          platformCaptions: platformMessages,
          destinations: threadsDestinations,
          workspaceId
        }, workspaceRequest);
        const threadsCount = threadsResponse.data.posts?.length || 0;
        uploadedTargets.push(`${threadsCount} Threads profile${threadsCount === 1 ? '' : 's'}`);
      }

      let googleBusinessResponse = null;
      if (wantsGoogle) {
        setNotice({ type: 'neutral', message: 'Publishing to selected Google Business locations...' });
        googleBusinessResponse = await axios.post(`${API_BASE}/api/google-business/post`, {
          title,
          message: messageForPlatform('google'),
          platformCaptions: platformMessages,
          destinations: googleDestinations,
          mediaUrl: uploadedMedia?.mediaUrl || '',
          mimeType: uploadedMedia?.mimeType || '',
          workspaceId
        }, workspaceRequest);
        const googleCount = googleBusinessResponse.data.posts?.length || 0;
        uploadedTargets.push(`${googleCount} Google Business location${googleCount === 1 ? '' : 's'}`);
      }

      if (wantsX) {
        setNotice({ type: 'neutral', message: 'Publishing text post to connected X profiles...' });
        xResponse = await axios.post(`${API_BASE}/api/x/post`, { message: messageForPlatform('twitter'), firstComment, altText: mediaAltText, platformCaptions: platformMessages, workspaceId }, workspaceRequest);
        const xPostCount = xResponse.data.posts?.length || 0;
        uploadedTargets.push(`${xPostCount} X profile${xPostCount === 1 ? '' : 's'}`);
      }

      if (!uploadedTargets.length) {
        setNotice({ type: 'warning', message: 'Selected profiles are connected for account sync, but their publishing endpoint is not ready yet. Nothing was posted.' });
        return;
      }

      setNotice({
        type: 'success',
        message: `Published: ${uploadedTargets.join(' + ')}. Saved to MongoDB.${wantsInstagram && instagramResponse && normalizeInstagramCollaborators(instagramCollaborators).length ? ' Instagram collaboration invitation requested; acceptance is pending.' : ''}`
      });
      setTitle('');
      setMessage('');
      setPlatformMessages({});
      setFirstComment('');
      setInstagramCollaborators('');
      setMediaAltText('');
      setSelectedVideoFile(null);
      setMediaMeta({ kind: 'none', orientation: 'unknown', width: 0, height: 0, name: '' });
      setContentFormats([]);
      if (response?.data?.posts?.length || response?.data?.post) {
        const facebookPosts = response.data.posts || [response.data.post];
        setPublishedPosts((current) => [...facebookPosts, ...current]);
      }
      if (xResponse?.data?.posts?.length) {
        setPublishedPosts((current) => [...xResponse.data.posts, ...current]);
      }
      if (instagramResponse?.data?.posts?.length || instagramResponse?.data?.post) {
        const instagramPosts = instagramResponse.data.posts || [instagramResponse.data.post];
        setPublishedPosts((current) => [...instagramPosts, ...current]);
      }
      if (linkedinResponse?.data?.posts?.length) {
        setPublishedPosts((current) => [...linkedinResponse.data.posts, ...current]);
      }
      if (threadsResponse?.data?.posts?.length) {
        setPublishedPosts((current) => [...threadsResponse.data.posts, ...current]);
      }
      if (googleBusinessResponse?.data?.posts?.length) {
        setPublishedPosts((current) => [...googleBusinessResponse.data.posts, ...current]);
      }
      if (wantsFacebook) await loadFacebookStatus();
      await loadPublishedPosts();
    } catch (err) {
      const failureMessage = err.response?.data?.error || err.message;
      setNotice({
        type: uploadedTargets.length ? 'warning' : 'error',
        message: uploadedTargets.length
          ? `Partial success: ${uploadedTargets.join(' + ')} published. Remaining destination failed: ${failureMessage}`
          : `Post failed: ${failureMessage}`
      });
      if (uploadedTargets.length) await loadPublishedPosts();
    } finally {
      await discardUploadedMedia(uploadedMedia);
      setIsPosting(false);
    }
  };

  const queuePost = async (forceSchedule = false) => {
    try {
      if (selectedPlatforms.includes('instagram')) validateInstagramCollaboration(instagramCollaborators, contentFormats);
    } catch (error) {
      setNotice({ type: 'warning', message: error.message });
      return;
    }
    if (!title.trim() && !message.trim() && !Object.values(platformMessages).some((value) => String(value || '').trim())) {
      setNotice({ type: 'warning', message: 'Add a title or description before scheduling.' });
      return;
    }

    const messageForPlatform = (platformId) => [title.trim(), String(platformMessages[platformId] || message).trim()].filter(Boolean).join('\n\n');

    if (forceSchedule || wantsSchedule) {
      if (!scheduledAt) {
        setNotice({ type: 'warning', message: 'Select a publishing date and time before scheduling.' });
        return;
      }

      if (!scheduleDate || Number.isNaN(scheduleDate.getTime())) {
        setNotice({ type: 'warning', message: 'Select a valid publishing date and time.' });
        return;
      }

      if (scheduleDate <= new Date(Date.now() + 60000)) {
        setNotice({ type: 'warning', message: 'The scheduled time must be at least one minute in the future.' });
        return;
      }

      const wantsYoutube = selectedPlatforms.includes('youtube');
      const wantsFacebook = selectedPlatforms.includes('facebook');
      const wantsInstagram = selectedPlatforms.includes('instagram');
      const wantsGoogle = selectedPlatforms.includes('google');
      const wantsLinkedIn = selectedPlatforms.includes('linkedin');
      const wantsThreads = selectedPlatforms.includes('threads');
      const facebookPageIds = selectedDestinations.filter((destination) => destination.platformId === 'facebook').map((destination) => destination.accountId);
      const instagramDestinations = selectedDestinations.filter((destination) => destination.platformId === 'instagram');
      const googleDestinations = selectedDestinations.filter((destination) => destination.platformId === 'google');
      const youtubeDestinations = selectedDestinations.filter((destination) => destination.platformId === 'youtube');
      const linkedinDestinations = selectedDestinations.filter((destination) => destination.platformId === 'linkedin');
      const threadsDestinations = selectedDestinations.filter((destination) => destination.platformId === 'threads');

      if (wantsYoutube && (!selectedVideoFile || mediaMeta.kind !== 'video')) {
        setNotice({ type: 'warning', message: 'Select a video in Add Media before scheduling to YouTube.' });
        return;
      }

      if (wantsYoutube && !youtubeDestinations.length) {
        setNotice({ type: 'warning', message: 'Select at least one YouTube channel below the platform logos.' });
        return;
      }

      if (wantsInstagram && !instagramDestinations.length) {
        setNotice({ type: 'warning', message: 'Select at least one Instagram account below the platform logos.' });
        return;
      }

      if (wantsInstagram && (!selectedVideoFile || !['image', 'video'].includes(mediaMeta.kind))) {
        setNotice({ type: 'warning', message: 'Select an image or video in Add Media before scheduling to Instagram.' });
        return;
      }

      if (wantsLinkedIn && !linkedinDestinations.length) {
        setNotice({ type: 'warning', message: 'Select at least one LinkedIn account below the platform logos.' });
        return;
      }

      if (wantsThreads && !threadsDestinations.length) {
        setNotice({ type: 'warning', message: 'Select at least one Threads profile below the platform logos.' });
        return;
      }

      if (wantsThreads && Array.from(messageForPlatform('threads')).length > 500) {
        setNotice({ type: 'warning', message: 'Threads text posts are limited to 500 characters. Shorten the title/description or unselect Threads.' });
        return;
      }

      if (wantsGoogle && !googleDestinations.length) {
        setNotice({ type: 'warning', message: 'Select at least one Google Business location below the platform logos.' });
        return;
      }

      if (wantsGoogle && selectedVideoFile && mediaMeta.kind !== 'image') {
        setNotice({ type: 'warning', message: 'Google Business currently supports text or image posts here. Remove the video or unselect Google Business.' });
        return;
      }

      if (!wantsYoutube && !wantsFacebook && !wantsInstagram && !wantsGoogle && !wantsLinkedIn && !wantsThreads) {
        setNotice({ type: 'warning', message: 'Select at least one connected publishing platform before scheduling.' });
        return;
      }

      if (wantsFacebook && !facebookPageIds.length) {
        setNotice({ type: 'warning', message: 'Select at least one Facebook Page below the platform logos.' });
        return;
      }

      const scheduledPosts = [];
      const scheduledTargets = [];
      let acceptedTime = new Date(scheduleDate).toLocaleString();
      let uploadedMedia = null;
      let mediaRetainedForSchedule = false;

      try {
        setIsPosting(true);

        if (selectedVideoFile && (wantsYoutube || wantsFacebook || wantsInstagram || (wantsGoogle && mediaMeta.kind === 'image'))) {
          setNotice({ type: 'neutral', message: 'Preparing scheduled media...' });
          uploadedMedia = await uploadSelectedMedia();
        }

        try {
          if (wantsYoutube) {
            const youtubeTitle = title.trim() || message.split('\n').find((line) => line.trim()) || selectedVideoFile.name || 'EastenSocial video';
            for (const destination of youtubeDestinations) {
              setNotice({ type: 'neutral', message: `Scheduling video for ${destination.label}...` });
              const youtubeResponse = await axios.post(`${API_BASE}/api/youtube/schedule`, {
                title: youtubeTitle,
                description: platformMessages.youtube || message,
                uploadFileName: uploadedMedia?.fileName || '',
                mimeType: uploadedMedia?.mimeType || selectedVideoFile.type || 'video/mp4',
                scheduledTime: scheduleDate.toISOString(),
                googleUserId: destination.connectionId || '',
                youtubeChannelId: destination.accountId,
                youtubeChannelName: destination.label
              }, workspaceRequest);
              if (youtubeResponse.data.post) scheduledPosts.push(youtubeResponse.data.post);
              scheduledTargets.push(destination.label);
              mediaRetainedForSchedule = true;
              if (youtubeResponse.data.scheduledTime) acceptedTime = new Date(youtubeResponse.data.scheduledTime).toLocaleString();
            }
          }

          if (wantsInstagram) {
            setNotice({ type: 'neutral', message: 'Scheduling selected Instagram accounts...' });
            const instagramResponse = await axios.post(`${API_BASE}/api/instagram/schedule`, {
              instagramCollaborators: normalizeInstagramCollaborators(instagramCollaborators),
              title,
              message: messageForPlatform('instagram'),
              firstComment,
              altText: mediaAltText,
              platformCaptions: platformMessages,
              destinations: instagramDestinations,
              contentFormats,
              mediaOrientation: mediaMeta.orientation,
              mimeType: uploadedMedia?.mimeType || '',
              uploadFileName: uploadedMedia?.fileName || '',
              coverFileName: publishCover?.sourceFile === selectedVideoFile && publishCover?.workspaceId === workspaceId ? publishCover.fileName : '',
              scheduledTime: scheduleDate.toISOString(),
              workspaceId
            }, workspaceRequest);
            scheduledPosts.push(...(instagramResponse.data.posts || (instagramResponse.data.post ? [instagramResponse.data.post] : [])));
            scheduledTargets.push(`${instagramDestinations.length} Instagram account${instagramDestinations.length === 1 ? '' : 's'}`);
            mediaRetainedForSchedule = true;
            if (instagramResponse.data.scheduledTime) acceptedTime = new Date(instagramResponse.data.scheduledTime).toLocaleString();
          }

          if (wantsFacebook) {
            setNotice({ type: 'neutral', message: wantsYoutube ? 'YouTube scheduled. Scheduling Facebook Pages...' : 'Scheduling Facebook Pages...' });
            const facebookResponse = await axios.post(`${API_BASE}/api/schedule-post`, {
              mediaType: selectedVideoFile ? mediaType : 'text',
              title,
              message: messageForPlatform('facebook'),
              firstComment,
              altText: mediaAltText,
              platformCaptions: platformMessages,
              contentFormats,
              mediaOrientation: mediaMeta.orientation,
              workspaceId,
              platforms: ['facebook'],
              pageIds: facebookPageIds,
              destinations: selectedDestinations,
              mediaUrl: uploadedMedia?.mediaUrl || '',
              mimeType: uploadedMedia?.mimeType || '',
              uploadFileName: uploadedMedia?.fileName || '',
              scheduledTime: scheduleDate.toISOString()
            }, workspaceRequest);
            scheduledPosts.push(...(facebookResponse.data.posts || (facebookResponse.data.post ? [facebookResponse.data.post] : [])));
            scheduledTargets.push(facebookResponse.data.pageName || `${facebookPageIds.length} Facebook Page${facebookPageIds.length === 1 ? '' : 's'}`);
            if (uploadedMedia) mediaRetainedForSchedule = true;
            if (facebookResponse.data.scheduledTime) acceptedTime = new Date(facebookResponse.data.scheduledTime).toLocaleString();
          }

          if (wantsLinkedIn) {
            setNotice({ type: 'neutral', message: 'Scheduling selected LinkedIn accounts...' });
            const linkedinResponse = await axios.post(`${API_BASE}/api/linkedin/schedule`, {
              title,
              message: messageForPlatform('linkedin'),
              firstComment,
              altText: mediaAltText,
              platformCaptions: platformMessages,
              destinations: linkedinDestinations,
              scheduledTime: scheduleDate.toISOString(),
              workspaceId
            }, workspaceRequest);
            scheduledPosts.push(...(linkedinResponse.data.posts || []));
            scheduledTargets.push(`${linkedinDestinations.length} LinkedIn profile${linkedinDestinations.length === 1 ? '' : 's'}`);
            if (linkedinResponse.data.scheduledTime) acceptedTime = new Date(linkedinResponse.data.scheduledTime).toLocaleString();
          }

          if (wantsThreads) {
            setNotice({ type: 'neutral', message: 'Scheduling selected Threads profiles...' });
            const threadsResponse = await axios.post(`${API_BASE}/api/threads/schedule`, {
              title,
              message: messageForPlatform('threads'),
              platformCaptions: platformMessages,
              destinations: threadsDestinations,
              scheduledTime: scheduleDate.toISOString(),
              workspaceId
            }, workspaceRequest);
            scheduledPosts.push(...(threadsResponse.data.posts || []));
            scheduledTargets.push(`${threadsDestinations.length} Threads profile${threadsDestinations.length === 1 ? '' : 's'}`);
            if (threadsResponse.data.scheduledTime) acceptedTime = new Date(threadsResponse.data.scheduledTime).toLocaleString();
          }

          if (wantsGoogle) {
            setNotice({ type: 'neutral', message: 'Scheduling selected Google Business locations...' });
            const googleBusinessResponse = await axios.post(`${API_BASE}/api/google-business/schedule`, {
              title,
              message: messageForPlatform('google'),
              platformCaptions: platformMessages,
              destinations: googleDestinations,
              mimeType: uploadedMedia?.mimeType || '',
              uploadFileName: uploadedMedia?.fileName || '',
              scheduledTime: scheduleDate.toISOString(),
              workspaceId
            }, workspaceRequest);
            scheduledPosts.push(...(googleBusinessResponse.data.posts || []));
            scheduledTargets.push(`${googleDestinations.length} Google Business location${googleDestinations.length === 1 ? '' : 's'}`);
            if (uploadedMedia) mediaRetainedForSchedule = true;
            if (googleBusinessResponse.data.scheduledTime) acceptedTime = new Date(googleBusinessResponse.data.scheduledTime).toLocaleString();
          }

          setNotice({ type: 'success', message: `Post scheduled for ${acceptedTime} on ${scheduledTargets.join(' + ')}. It will publish automatically.` });
          setTitle('');
          setMessage('');
          setPlatformMessages({});
          setFirstComment('');
          setInstagramCollaborators('');
          setMediaAltText('');
          if (wantsYoutube || wantsFacebook || wantsInstagram || wantsGoogle || wantsLinkedIn || wantsThreads) setSelectedVideoFile(null);
          if (scheduledPosts.length) {
            setPublishedPosts((current) => [...scheduledPosts, ...current]);
          }
          await loadPublishedPosts();
          return;
        } finally {
          if (uploadedMedia && !mediaRetainedForSchedule) await discardUploadedMedia(uploadedMedia);
        }
      } catch (err) {
        const failureMessage = err.response?.data?.error || err.message;
        setNotice({
          type: scheduledTargets.length ? 'warning' : 'error',
          message: scheduledTargets.length
            ? `Partial success: ${scheduledTargets.join(' + ')} scheduled. Remaining destination failed: ${failureMessage}`
            : `Schedule failed: ${failureMessage}`
        });
        if (scheduledPosts.length) {
          setPublishedPosts((current) => [...scheduledPosts, ...current]);
          await loadPublishedPosts();
        }
        return;
      } finally {
        setIsPosting(false);
      }
    }

    await saveDraft();
  };

  const saveDraft = async () => {
    if (!title.trim() && !message.trim() && !Object.values(platformMessages).some((value) => String(value || '').trim())) {
      setNotice({ type: 'warning', message: 'Add a title or description before saving the draft.' });
      return;
    }
    try {
      setIsPosting(true);
      const uploadedMedia = selectedVideoFile ? await uploadSelectedMedia() : null;
      const response = await axios.post(`${API_BASE}/api/posts/draft`, {
        instagramCollaborators: selectedPlatforms.includes('instagram') ? normalizeInstagramCollaborators(instagramCollaborators) : [],
        title,
        description: message,
        platformCaptions: platformMessages,
        firstComment,
        altText: mediaAltText,
        platforms: selectedPlatforms,
        destinations: selectedDestinations,
        contentFormats,
        mediaType: selectedVideoFile ? mediaType : 'text',
        mediaOrientation: mediaMeta.orientation,
        mediaWidth: mediaMeta.width,
        mediaHeight: mediaMeta.height,
        mediaOriginalName: selectedVideoFile?.name || '',
        uploadFileName: uploadedMedia?.fileName || '',
        mimeType: uploadedMedia?.mimeType || '',
        coverFileName: publishCover?.sourceFile === selectedVideoFile && publishCover?.workspaceId === workspaceId ? publishCover.fileName : '',
        workspaceId
      }, workspaceRequest);
      setPublishedPosts((current) => [response.data.post, ...current]);
      setNotice({ type: 'success', message: 'Draft saved to this workspace.' });
      setTitle('');
      setMessage('');
      setPlatformMessages({});
      setFirstComment('');
      setInstagramCollaborators('');
      setMediaAltText('');
      setSelectedVideoFile(null);
      setMediaMeta({ kind: 'none', orientation: 'unknown', width: 0, height: 0, name: '' });
      setContentFormats([]);
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Draft could not be saved.' });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div data-module={activeModule} className={cx('mp-app', isSidebarCollapsed && 'sidebar-collapsed', theme === 'dark' && 'dark-mode', mobileMenuOpen && 'mobile-menu-open')}>
      {mobileMenuOpen && <button className="mobile-menu-backdrop" type="button" aria-label="Close navigation" onClick={() => setMobileMenuOpen(false)} />}
      <aside className={cx('mp-side', isSidebarCollapsed && 'collapsed')}>
        <div className="mp-brand">
          <img src="/brand/easten-social-logo.png" alt="EastenSocial" />
        </div>

        <section className="workspace-switcher" aria-label="Workspace switcher" ref={workspaceMenuRef}>
          <button
            className="workspace-select-wrap workspace-menu-trigger"
            title={workspace.name}
            aria-expanded={isWorkspaceMenuOpen}
            type="button"
            onClick={() => {
              setIsWorkspaceMenuOpen((value) => !value);
            }}
          >
            <span className="workspace-avatar" style={{ background: workspace.color }}>
              {workspace.profileImageUrl ? <img src={workspace.profileImageUrl} alt="" /> : workspace.initials}
            </span>
            <span className="workspace-select">
              <strong>{workspace.name}</strong>
              <small>{connectedChannelCount} connected channels</small>
            </span>
            <LuChevronsUpDown aria-hidden="true" />
          </button>
          {isWorkspaceMenuOpen && (
            <div className="workspace-menu">
              {workspaceOptions.map((item) => (
                <button
                  className={cx(workspaceId === item.id && 'active')}
                  key={item.id}
                  onClick={() => {
                    if (item.id !== workspaceId) setWorkspaceId(item.id);
                    setIsWorkspaceMenuOpen(false);
                  }}
                  type="button"
                >
                  <span className="workspace-avatar" style={{ background: item.color }}>{item.profileImageUrl ? <img src={item.profileImageUrl} alt="" /> : item.initials}</span>
                  <span>
                    <strong>{item.name}</strong>
                    <small>{workspaceId === item.id ? 'Current workspace' : 'Switch workspace'}</small>
                  </span>
                  {workspaceId === item.id && <FiCheck aria-label="Selected" />}
                </button>
              ))}
              <button className="workspace-add" type="button" onClick={() => { setIsWorkspaceMenuOpen(false); setProfileInitialTab('workspaces'); setIsProfileOpen(true); }}>
                <span className="workspace-avatar workspace-add-icon">+</span>
                <span><strong>Add workspace</strong><small>Keep each business separate</small></span>
              </button>
            </div>
          )}
        </section>

        <nav className="module-nav" aria-label="Main navigation">
          {desktopModules.filter((item) => !['team', 'teamchat'].includes(item.id) || ['agency', 'employee'].includes(currentUser.role)).map((item) => (
            <button
              className={cx('module-link', item.id === 'post' && 'create-link', ['team', 'teamchat'].includes(item.id) && 'team-link', activeModule === item.id && 'active')}
              key={item.id}
              onClick={() => { if (item.id === 'post') setCreateMode('manual'); setActiveModule(item.id); if (item.id === 'manage') setManageView('published'); setMobileMenuOpen(false); }}
              aria-current={activeModule === item.id ? 'page' : undefined}
              title={item.label}
              type="button"
            >
              <i><item.icon aria-hidden="true" /></i>
              <span>{item.id === 'team' && currentUser.role === 'employee' ? 'My Tasks' : item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-profile-row"><button className={cx('sidebar-settings', activeModule === 'settings' && 'active')} type="button" onClick={() => { setActiveModule('settings'); setMobileMenuOpen(false); }} title="Settings" aria-current={activeModule === 'settings' ? 'page' : undefined}>
              <FiSettings aria-hidden="true" /><span>Settings</span>
            </button><button
              className="collapse-btn bottom-collapse"
              type="button"
              onClick={() => setIsSidebarCollapsed((value) => !value)}
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <LuPanelLeftOpen /> : <LuPanelLeftClose />}
            </button></div>
        </div>
      </aside>

      <main className={cx('mp-main', activeModule === 'post' ? 'composer-focused' : 'module-focused')}>
        <header className="command-bar">
          <div className="mobile-app-logo" aria-label="EastenSocial"><img src="/brand/easten-social-logo.png" alt="EastenSocial" /></div>
          <div className="command-search" role="search" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setCommandOpen(false); }}>
            <FiSearch aria-hidden="true" />
            <input ref={commandSearchRef} aria-label="Search app" placeholder="Search pages, workspaces, posts" value={commandQuery} onFocus={() => setCommandOpen(true)} onChange={(event) => setCommandQuery(event.target.value)} />
            <kbd>Ctrl+K</kbd>
            {commandOpen && <div className="command-results" role="listbox" aria-label="Search results">
              {desktopModules.filter((item) => !['team', 'teamchat'].includes(item.id) || ['agency', 'employee'].includes(currentUser.role)).filter((item) => item.label.toLowerCase().includes(commandQuery.toLowerCase())).slice(0, 8).map((item) => <button type="button" key={item.id} role="option" aria-selected="false" onClick={() => { setActiveModule(item.id); setCommandQuery(''); setCommandOpen(false); }}><item.icon aria-hidden="true" /><span>{item.label}</span></button>)}
              {workspaceOptions.filter((item) => item.name.toLowerCase().includes(commandQuery.toLowerCase())).slice(0, 4).map((item) => <button type="button" key={item.id} role="option" aria-selected="false" onClick={() => { if (item.id !== workspaceId) setWorkspaceId(item.id); setCommandQuery(''); setCommandOpen(false); }}><LuLayoutGrid aria-hidden="true" /><span>{item.name}</span></button>)}
              {commandQuery.trim() && publishedPosts.filter((post) => String(post.title || post.content || '').toLowerCase().includes(commandQuery.trim().toLowerCase())).slice(0, 5).map((post) => <button type="button" key={post.id || post.externalPostId} role="option" aria-selected="false" onClick={() => { setActiveModule('manage'); setCommandQuery(''); setCommandOpen(false); }}><FiFileText aria-hidden="true" /><span>{shortPostText(post.title || post.content || 'Post', 8)}</span></button>)}
            </div>}
          </div>
          <div className="command-actions">
            <button className="command-plain-icon" type="button" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}>{theme === 'dark' ? <FiSun aria-hidden="true" /> : <FiMoon aria-hidden="true" />}</button>
            {currentUser.role !== 'client' && <ClientMessages apiBase={API_BASE} workspaceId={workspaceId} />}
            <div className="notification-center-wrap">
              <button className="command-plain-icon" type="button" aria-label="Open notifications" title="Notifications" onClick={() => setIsNotificationOpen((value) => !value)}>
                <FiBell aria-hidden="true" />
                {notifications.some((item) => !item.readAt) && <span className="notification-dot" />}
              </button>
              {isNotificationOpen && <NotificationCenter notifications={notifications} onClose={() => setIsNotificationOpen(false)} onNavigate={setActiveModule} onRefresh={loadNotifications} workspaceId={workspaceId} />}
            </div>
            <div className="header-profile-slot mobile-profile-menu"><ProfileMenu user={{ ...currentUser, name: currentUser.companyName || currentUser.name }} onProfile={() => { setProfileInitialTab('profile'); setIsProfileOpen(true); }} onSecurity={() => { setProfileInitialTab('security'); setIsProfileOpen(true); }} onBilling={() => { setProfileInitialTab('billing'); setIsProfileOpen(true); }} onLogout={logout} /></div>
          </div>
        </header>

        <Toast notice={notice} onDismiss={setNotice} dark={theme === 'dark'} />

        {activeModule === 'home' && (
          <HomeDashboard
            connectedChannelCount={connectedChannelCount}
            onNavigate={(module) => { if (module === 'post') setCreateMode('manual'); setActiveModule(module); }}
            onCreateEvent={(event) => { setAiEvent({ ...event, workspaceId, id: `${event.id}-${Date.now()}` }); setCreateMode('ai'); setActiveModule('post'); }}
            onSelectWorkspace={setWorkspaceId}
            publishedPosts={publishedPosts}
            platforms={platformOptions}
            workspace={workspace}
            workspaceId={workspaceId}
            workspaceOptions={workspaceOptions}
          />
        )}

        {activeModule === 'post' && <nav className="create-mode-tabs" aria-label="Create post mode"><button type="button" className={createMode !== 'ai' ? 'active' : ''} onClick={() => setCreateMode('manual')}>Create Manual</button><button type="button" className={createMode === 'ai' ? 'active' : ''} onClick={() => setCreateMode('ai')}>Create with AI</button></nav>}
        {activeModule === 'post' && createMode === 'ai' && <AiCreate apiBase={API_BASE} workspace={workspace} workspaceId={workspaceId} workspaces={workspaceOptions} onWorkspace={setWorkspaceId} destinations={destinationOptions} initialEvent={aiEvent} onEventConsumed={() => setAiEvent(null)} onManual={() => { setAiEvent(null); setCreateMode('manual'); }} onNotice={setNotice} />}

        {activeModule === 'post' && createMode === 'manual' && (
          <PostNow
            onAiGenerated={loadWorkspaces}
            activePlatforms={activePlatforms}
            contentFormats={contentFormats}
            currentUser={currentUser}
            isPosting={isPosting}
            mediaMeta={mediaMeta}
            mediaType={mediaType}
            mediaAltText={mediaAltText}
            message={message}
            firstComment={firstComment}
            instagramCollaborators={instagramCollaborators}
            setInstagramCollaborators={setInstagramCollaborators}
            platformMessages={platformMessages}
            platforms={platformOptions}
            previewDevice={previewDevice}
            previewText={previewText}
            scheduledAt={scheduledAt}
            selectedPlatforms={selectedPlatforms}
            destinationOptions={destinationOptions}
            selectedDestinationKeys={selectedDestinationKeys}
            setContentFormats={setContentFormats}
            setMediaMeta={setMediaMeta}
            setMediaType={setMediaType}
            setMediaAltText={setMediaAltText}
            setMessage={setMessage}
            setFirstComment={setFirstComment}
            setPlatformMessages={setPlatformMessages}
            setPreviewDevice={setPreviewDevice}
            setScheduledAt={setScheduledAt}
            selectedVideoFile={selectedVideoFile}
            setSelectedVideoFile={setSelectedVideoFile}
            togglePlatform={togglePlatform}
            toggleDestination={toggleDestination}
            postNow={postNow}
            setNotice={setNotice}
            setTitle={setTitle}
            title={title}
            uploadProgress={uploadProgress}
            uploadSelectedMedia={uploadSelectedMedia}
            mediaUploadError={mediaUploadError}
            retrySelectedMediaUpload={retrySelectedMediaUpload}
            publishCover={publishCover}
            setPublishCover={setPublishCover}
            workspace={workspace}
            openWorkspaceSettings={() => { setProfileInitialTab('workspaces'); setIsProfileOpen(true); }}
          />
        )}

        {activeModule === 'manage' && (
          <ManagePosts
            view={manageView}
            setView={setManageView}
            onResume={editPostFromCalendar}
            posts={publishedPosts}
            setActiveModule={setActiveModule}
            setNotice={setNotice}
            setPublishedPosts={setPublishedPosts}
            workspaceId={workspaceId}
          />
        )}

        {activeModule === 'calendar' && (
          <CalendarView
            publicCalendar={publicCalendar}
            publishedPosts={publishedPosts}
            setNotice={setNotice}
            setPublicCalendar={setPublicCalendar}
            setPublishedPosts={setPublishedPosts}
            workspaceId={workspaceId}
            workspace={workspace}
            onEditPost={editPostFromCalendar}
            onFestivalCreate={(event) => {
              setAiEvent({ ...event, workspaceId, id: `${event.id}-${Date.now()}` });
              setCreateMode('ai');
              setActiveModule('post');
            }}
          />
        )}

        {(activeModule === 'messaging' || activeModule === 'automations') && (
          <Messaging
            key={`${workspaceId}-${activeModule}`}
            filteredInbox={filteredInbox}
            inboxFilter={inboxFilter}
            selectedInbox={selectedInbox}
            selectedInboxId={selectedInboxId}
            onNavigate={setActiveModule}
            setNotice={setNotice}
            setInboxFilter={setInboxFilter}
            setSelectedInboxId={setSelectedInboxId}
            initialMode={activeModule === 'automations' ? 'automations' : 'messages'}
            workspaceId={workspaceId}
          />
        )}

        {activeModule === 'reports' && (
          <Reports
            key={workspaceId}
            platformOptions={platformOptions}
            publishedPosts={publishedPosts}
            reportPlatform={reportPlatform}
            reportRange={reportRange}
            setNotice={setNotice}
            setReportPlatform={setReportPlatform}
            setReportRange={setReportRange}
            workspaceId={workspaceId}
            businessName={workspace?.name || ''}
          />
        )}

        {activeModule === 'profiles' && (
          <Profiles
            connectedPages={connectedPages}
            connectFacebook={connectFacebook}
            connectGoogle={connectGoogle}
            connectInstagram={connectInstagram}
            connectExternal={connectExternal}
            connectedChannelCount={connectedChannelCount}
            channelLimit={channelLimit}
            canAddChannel={canAddChannel}
            currentUser={currentUser}
            externalStatus={externalStatus}
            facebookStatus={facebookStatus}
            googleStatus={googleStatus}
            instagramStatus={instagramStatus}
            lastSync={lastSync}
            setNotice={setNotice}
            workspaceId={workspaceId}
          />
        )}

        {activeModule === 'settings' && <SettingsPage apiBase={API_BASE} workspaceId={workspaceId} workspaces={workspaceOptions} setNotice={setNotice} onManageWorkspaces={(tab) => { setProfileInitialTab(tab); setIsProfileOpen(true); }} />}
        {activeModule === 'team' && currentUser.role === 'agency' && <TeamManagement setNotice={setNotice} workspaces={workspaceOptions} onOpenChat={() => setActiveModule('teamchat')} />}
        {activeModule === 'team' && currentUser.role === 'employee' && <EmployeeTasks apiBase={API_BASE} setNotice={setNotice} />}
        {activeModule === 'teamchat' && ['agency', 'employee'].includes(currentUser.role) && <TeamChat apiBase={API_BASE} setNotice={setNotice} />}

        {activeModule === 'bulk' && (
          <BulkScheduling
            bulkRows={bulkRows}
            setNotice={setNotice}
            setBulkRows={setBulkRows}
            workspaceId={workspaceId}
          />
        )}

        {activeModule === 'ads' && <Ads setNotice={setNotice} workspaceId={workspaceId} />}
      </main>
      <button className="floating-support-btn" type="button" aria-label="Open support" title="Help & support" onClick={() => setIsSupportOpen(true)}><FiHelpCircle aria-hidden="true" /></button>
      {mobileCreateStep && <div className="five-create-overlay"><button className="five-create-scrim" type="button" aria-label="Close create menu" onClick={() => setMobileCreateStep(null)} /><section className="five-create-sheet" role="dialog" aria-modal="true" aria-label="Create menu">
        <div className="five-create-sheet-head"><strong>{mobileCreateStep === 'kind' ? 'Create' : `Create ${createOptions.find((item) => item.id === mobileCreateStep)?.label || ''}`}</strong><button type="button" aria-label="Close" onClick={() => setMobileCreateStep(null)}><FiX /></button></div>
        {mobileCreateStep === 'kind' ? createOptions.map(({ id, label, icon: Icon }) => <button className="five-create-choice" key={id} type="button" onClick={() => setMobileCreateStep(id)}><Icon aria-hidden="true" /><span>{label}</span><FiChevronRight aria-hidden="true" /></button>) : <>
          <button className="five-create-choice" type="button" onClick={() => openCreate(mobileCreateStep, 'manual')}><FiEdit3 aria-hidden="true" /><span>Create manually</span><FiChevronRight aria-hidden="true" /></button>
          {mobileCreateStep === 'post' && <button className="five-create-choice" type="button" onClick={() => openCreate('post', 'ai')}><FiStar aria-hidden="true" /><span>Create with AI</span><FiChevronRight aria-hidden="true" /></button>}
          <button className="five-create-back" type="button" onClick={() => setMobileCreateStep('kind')}><FiChevronLeft aria-hidden="true" /> Back</button>
        </>}
      </section></div>}
      <nav className="mobile-navigation five-mobile-navigation" aria-label="Quick navigation">
        <button type="button" className={primaryForModule(activeModule) === 'home' ? 'active' : ''} aria-current={primaryForModule(activeModule) === 'home' ? 'page' : undefined} onClick={() => setActiveModule('home')}><FiHome aria-hidden="true" /><span>Home</span></button>
        <button type="button" className={primaryForModule(activeModule) === 'manage' ? 'active' : ''} aria-current={primaryForModule(activeModule) === 'manage' ? 'page' : undefined} onClick={() => { setActiveModule('manage'); setManageView('published'); }}><FiCalendar aria-hidden="true" /><span>Planner</span></button>
        <button className={cx('five-mobile-create', primaryForModule(activeModule) === 'create' && 'active')} type="button" aria-label="Create" aria-expanded={Boolean(mobileCreateStep)} onClick={() => setMobileCreateStep((step) => step ? null : 'kind')}><FiPlus aria-hidden="true" /><span>Create</span></button>
        <button type="button" className={primaryForModule(activeModule) === 'messaging' ? 'active' : ''} aria-current={primaryForModule(activeModule) === 'messaging' ? 'page' : undefined} onClick={() => setActiveModule('messaging')}><FiMessageSquare aria-hidden="true" /><span>Inbox</span></button>
        <button type="button" className={primaryForModule(activeModule) === 'reports' ? 'active' : ''} aria-current={primaryForModule(activeModule) === 'reports' ? 'page' : undefined} onClick={() => setActiveModule('reports')}><FiBarChart2 aria-hidden="true" /><span>Reports</span></button>
      </nav>
      {workspacesReady && tourOpen && !isProfileOpen && !(activeModule === 'post' && createMode === 'ai') && <Onboarding userId={currentUser.id || currentUser._id} onClose={() => setTourOpen(false)} onNavigate={(id) => { if (id === 'workspace') { setProfileInitialTab('workspaces'); setIsProfileOpen(true); } else setActiveModule(id); }} />}
      {isProfileOpen && (
        <ProfileModal
          currentUser={currentUser}
          initialTab={profileInitialTab}
          logout={logout}
          onClose={() => setIsProfileOpen(false)}
          onProfileUpdated={(user) => {
            setCurrentUser(user);
            setNotice({ type: 'success', message: `${user.name} profile saved.` });
          }}
          onWorkspacesChanged={(rows, selectedId) => {
            setWorkspaceOptions(rows);
            if (selectedId) setWorkspaceId(selectedId);
          }}
          selectedWorkspaceId={workspaceId}
          workspaces={workspaceOptions}
        />
      )}
      {isSupportOpen && <SupportChat apiBase={API_BASE} key={workspaceId} onClose={() => setIsSupportOpen(false)} workspaceId={workspaceId} />}
    </div>
  );
}

// Kept temporarily as the migration source for legacy landing-page content.
// eslint-disable-next-line no-unused-vars
function LandingPage({ openDashboard, openLogin, openSignup, openPolicy }) {
  const [comparisonTarget, setComparisonTarget] = useState(competitorPlatforms[0]);
  const [activeWorkflowIndex, setActiveWorkflowIndex] = useState(0);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const activeStep = guidedWorkflowSteps[activeWorkflowIndex] || guidedWorkflowSteps[0];

  useEffect(() => {
    if (window.localStorage.getItem('easten-signup-popup-dismissed') === 'yes') return undefined;
    const timer = window.setTimeout(() => setShowSignupPrompt(true), 10000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const section = document.getElementById('how-it-works');
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const total = Math.max(section.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-rect.top / total, 0), 0.999);
      setActiveWorkflowIndex(Math.floor(progress * guidedWorkflowSteps.length));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeSignupPrompt = () => {
    window.localStorage.setItem('easten-signup-popup-dismissed', 'yes');
    setShowSignupPrompt(false);
  };

  return (
    <main className="site-shell easten-home">
      <nav className="site-nav easten-nav">
        <button className="site-logo" type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="EastenSocial home">
          <img src="/brand/easten-social-logo.png" alt="EastenSocial" />
        </button>
        <div className="site-links">
          <a href="#top">Home</a>
          <a href="#features">Features</a>
          <a href="#how-it-works">How it works</a>
          <a href="#compare">Compare</a>
          <button className="site-login" type="button" onClick={openLogin}>Login</button>
          <button className="site-cta" type="button" onClick={openSignup}>Register Now</button>
        </div>
      </nav>

      <section className="easten-hero refined-hero" id="top">
        <div className="easten-hero-copy">
          <h1>Automate</h1>
          <h2>at absolutely zero cost.</h2>
          <p>
            India's first free social media management tool built for brands, SMEs, and agencies.
            Plan, post, manage, track, and report from one simple workspace.
          </p>
          <button className="hero-pill-cta" type="button" onClick={openSignup}>
            <span>Get started now</span>
            <i aria-hidden="true">→</i>
          </button>
          <div className="hero-powered">
            <strong>Powered By:</strong>
            <div>
              <img src="/brand/aws.png" alt="AWS" />
              <img src="/brand/meta.png" alt="Meta" />
              <img src="/brand/google.png" alt="Google" />
              <img src="/brand/linkedin.png" alt="LinkedIn" />
            </div>
            <small>eastensocial.com, a product by Easten Mediatech</small>
          </div>
        </div>

        <div className="hero-product-stage refined-visual" aria-label="EastenSocial product preview">
          <div className="stage-topbar"><span /><span /><span /><strong>Agency Command Center</strong></div>
          <div className="stage-grid">
            <div className="stage-sidebar">{['Post', 'Manage', 'Calendar', 'Inbox', 'Reports'].map((item) => <b key={item}>{item}</b>)}</div>
            <div className="stage-compose">
              <small>Post Now</small>
              <strong>Compose Everywhere with one click</strong>
              <p>Build one campaign and adapt it for feed, reels, stories, shorts and business updates.</p>
              <div className="brand-dot-row">{['f', 'IG', 'G', 'YT', 'in', 'X'].map((item) => <i key={item}>{item}</i>)}</div>
            </div>
            <div className="stage-preview"><strong>Live preview</strong><span>Instagram</span><p>Launch caption preview with image, video or story-ready layout.</p><b /></div>
            <div className="stage-chart">{[44, 70, 58, 86, 76].map((height) => <span style={{ height: `${height}%` }} key={height} />)}</div>
          </div>
        </div>
      </section>

      <section className="home-section feature-panorama" id="features">
        <div className="section-title">
          <h2>Exclusive Features</h2>
        </div>
        <div className="feature-panorama-grid exclusive-grid">
          {exclusiveFeatures.map(([icon, title, text]) => (
            <article key={title}>
              <span className={`feature-icon ${icon}`} aria-hidden="true"></span>
              <strong>{title}</strong>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section partner-band">
        <div className="section-title">
          <h2>Our Partner</h2>
        </div>
        <div className="partner-row">
          {partnerBrands.map((brand) => <span className={`partner-logo ${brand.toLowerCase().replace(/[^a-z]/g, '')}`} key={brand}>{brand}</span>)}
        </div>
      </section>

      <section className="guided-workflow" id="how-it-works">
        <div className="guided-sticky">
          <div className="workflow-copy">
            <p className="site-eyebrow">How it works</p>
            <h2>Easier than you expect.</h2>
            <p>Inspired by the easiest publishing tools, here is the step-by-step guide to use our platform.</p>
            <div className="workflow-step-card">
              <span>{activeStep.label}</span>
              <strong>{activeStep.title}</strong>
              <p>{activeStep.text}</p>
            </div>
          </div>
          <div className="workflow-screen drop-up-visual" key={activeWorkflowIndex}>
            <div className="workflow-browser">
              <div className="stage-topbar"><span /><span /><span /><strong>{activeStep.visualTitle}</strong></div>
              <div className="workflow-dashboard-shot">
                <aside>{['Login', 'Profile', 'Post', 'Report'].map((item) => <b key={item}>{item}</b>)}</aside>
              <section>
                <small>{activeStep.label}</small>
                <h3>{activeStep.title}</h3>
                <p>{activeStep.text}</p>
                <div>{activeStep.chips.map((card) => <span key={card}>{card}</span>)}</div>
              </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section comparison-lab" id="compare">
        <div>
          <p className="site-eyebrow">Compare before you choose</p>
          <h2>We vs<br />Top Platforms</h2>
          <p>Select any platform and compare the work that matters most for Indian agencies.</p>
          <label className="compare-select"><span>Compare with</span><select value={comparisonTarget} onChange={(event) => setComparisonTarget(event.target.value)}>{competitorPlatforms.map((name) => <option key={name}>{name}</option>)}</select></label>
        </div>
        <div className="comparison-table three-col-compare">
          <div className="comparison-head"><strong>Features</strong><strong>EastenSocial</strong><strong>{comparisonTarget}</strong></div>
          {eastenAdvantages.map((feature, index) => (
            <div className="comparison-row" key={feature}><span>{feature}</span><span className="tick-mark">✓</span><span>{index < 3 ? '✓' : 'Plan dependent'}</span></div>
          ))}
        </div>
      </section>

      <section className="home-section best-section">
        <div className="section-title">
          <h2>Why We Are the Best</h2>
        </div>
        <div className="best-grid">
          {bestReasons.map(([title, text]) => <article key={title}><strong>{title}</strong><p>{text}</p></article>)}
        </div>
      </section>

      <section className="home-section upcoming-suite">
        <div><p className="site-eyebrow">Upcoming business tools</p><h2>More Easten tools are coming.</h2><p>Social is the first layer. The wider Easten ecosystem can grow into CRM, WhatsApp API, invoicing, HR, payroll and client portals.</p></div>
        <div className="upcoming-grid">{['WhatsApp CRM', 'AI Content Studio', 'Client Portal', 'Invoice Suite', 'HR & Payroll', 'Lead Automation'].map((item) => <span key={item}>{item}</span>)}</div>
      </section>

      <section className="home-section security-band">
        <div><p className="site-eyebrow">Security and scale</p><h2>A serious workspace for serious client data.</h2><p>EastenSocial is designed around secure cloud infrastructure, role-based agency workflows, platform authorization, and clean data separation between workspaces.</p></div>
        <div className="security-cards">{['AWS-grade infrastructure approach', 'Workspace-level data separation', 'OAuth based platform access', 'Client approval workflow'].map((item) => <span key={item}>{item}</span>)}</div>
      </section>

      <section className="modern-final home-final">
        <h2>Make your agency look bigger, faster, and more organized.</h2>
        <div><button className="site-cta" type="button" onClick={openSignup}>Register Now</button></div>
      </section>

      <footer className="site-footer easten-footer">
        <div className="footer-main">
          <div className="footer-brand">
            <span className="footer-logo">ES</span>
            <strong>EastenSocial</strong>
            <p>
              EastenSocial is a social media automation product by Easten Mediatech,
              built for brands, SMEs, and agencies that need simple, reliable workflows.
            </p>
            <div className="follow-row"><b>Follow Us:</b>{['f', 'IG', 'YT', 'in', 'X'].map((item) => <span key={item}>{item}</span>)}</div>
          </div>
          <div className="footer-links">
            <h3>Legal</h3>
            <button type="button" onClick={() => openPolicy('privacy')}>Privacy Policies</button>
            <button type="button" onClick={() => openPolicy('terms')}>Terms & Conditions</button>
            <button type="button" onClick={() => openPolicy('data-deletion')}>Data Deletion Policies</button>
            <button type="button" onClick={() => openPolicy('legal-termination')}>Legal & Termination</button>
            <button type="button" onClick={() => openPolicy('user-agreement')}>User Agreement</button>
          </div>
          <div className="footer-links right-links">
            <h3>Product</h3>
            <button type="button" onClick={() => openPolicy('social-scheduling')}>Social Scheduling</button>
            <button type="button" onClick={() => openPolicy('ai-content-writer')}>AI Content Writer</button>
            <button type="button" onClick={() => openPolicy('unified-inbox')}>Unified Inbox</button>
            <button type="button" onClick={() => openPolicy('analytics-reports')}>Analytics & Reports</button>
            <button type="button" onClick={() => openPolicy('pricing-plans')}>Pricing Plans</button>
          </div>
          <div className="footer-links right-links">
            <h3>Company</h3>
            <button type="button" onClick={() => openPolicy('about')}>About Us</button>
            <button type="button" onClick={() => openPolicy('parent-company')}>Parent Company</button>
            <button type="button" onClick={() => openPolicy('careers')}>Join Our Team</button>
            <button type="button" onClick={() => openPolicy('press-releases')}>Press Releases</button>
            <button type="button" onClick={() => openPolicy('contact')}>Contact Us</button>
          </div>
        </div>
        <div className="footer-bottom">eastensocial.com © 2026, All Rights Reserved. A product by <a href="https://www.eastenmediatech.com" target="_blank" rel="noreferrer">Easten Mediatech</a>.</div>
      </footer>

      {showSignupPrompt && (
        <div className="signup-popup" role="dialog" aria-label="Create EastenSocial account">
          <button type="button" aria-label="Close signup popup" onClick={closeSignupPrompt}>x</button>
          <strong>Start your free 5-channel workspace</strong>
          <p>Build your first campaign, connect profiles, and see how EastenSocial can organize your agency workflow.</p>
          <input placeholder="Work email" type="email" />
          <button className="site-cta" type="button" onClick={openSignup}>Register Now</button>
        </div>
      )}
    </main>
  );
}

function ProductLandingPage({ openLogin, openSignup, openPolicy }) {
  useEffect(() => {
    const handleHomepageNavigation = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'cosocial:navigate') return;
      const target = event.data.target;
      if (target === 'login') openLogin();
      else if (target === 'signup') openSignup();
      else if (policyPages[target]) openPolicy(target);
    };
    window.addEventListener('message', handleHomepageNavigation);
    return () => window.removeEventListener('message', handleHomepageNavigation);
  }, [openLogin, openSignup, openPolicy]);

  return (
    <main className="exact-home-shell">
      <iframe
        className="exact-home-frame"
        src="/cosocial-home.html"
        title="EastenSocial homepage"
      />
    </main>
  );
}

function LegalPage({ page, openPublicSite, openSignup }) {
  return (
    <main className="legal-shell">
      <nav className="site-nav easten-nav legal-nav">
        <button className="site-logo" type="button" onClick={openPublicSite}><img src="/brand/easten-social-logo.png" alt="EastenSocial" /></button>
        <div className="site-links"><button className="site-login" type="button" onClick={openPublicSite}>Home</button><button className="site-cta" type="button" onClick={openSignup}>Get Started Now</button></div>
      </nav>
      <section className="legal-card">
        <p className="site-eyebrow">{page.eyebrow}</p>
        <h1>{page.title}</h1>
        {page.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      </section>
    </main>
  );
}

function WebsiteBuilder({ openPublicSite, openDashboard }) {
  const [sections, setSections] = useState(() => {
    try {
      const saved = window.localStorage.getItem(builderStorageKey);
      return saved ? JSON.parse(saved) : defaultBuilderSections;
    } catch (error) {
      return defaultBuilderSections;
    }
  });
  const [selectedId, setSelectedId] = useState(sections[0]?.id || '');
  const [draggedId, setDraggedId] = useState(null);
  const selectedSection = sections.find((section) => section.id === selectedId) || sections[0];

  useEffect(() => {
    window.localStorage.setItem(builderStorageKey, JSON.stringify(sections));
  }, [sections]);

  const selectSection = (id) => setSelectedId(id);
  const updateSelected = (patch) => {
    setSections((current) => current.map((section) => (section.id === selectedSection.id ? { ...section, ...patch } : section)));
  };
  const updateCard = (index, patch) => {
    updateSelected({
      cards: selectedSection.cards.map((card, cardIndex) => (cardIndex === index ? { ...card, ...patch } : card))
    });
  };
  const updateRow = (index, patch) => {
    updateSelected({
      rows: selectedSection.rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    });
  };
  const addSection = (type) => {
    const section = createBuilderSection(type);
    setSections((current) => [...current, section]);
    setSelectedId(section.id);
  };
  const duplicateSection = (id) => {
    const original = sections.find((section) => section.id === id);
    if (!original) return;
    const copy = { ...original, id: makeBuilderId(original.type), title: `${original.title} copy` };
    setSections((current) => {
      const index = current.findIndex((section) => section.id === id);
      const next = [...current];
      next.splice(index + 1, 0, copy);
      return next;
    });
    setSelectedId(copy.id);
  };
  const deleteSection = (id) => {
    if (sections.length === 1) return;
    setSections((current) => current.filter((section) => section.id !== id));
    if (selectedId === id) setSelectedId(sections.find((section) => section.id !== id)?.id || '');
  };
  const onDropSection = (targetId) => {
    if (!draggedId || draggedId === targetId) return;
    setSections((current) => {
      const dragged = current.find((section) => section.id === draggedId);
      if (!dragged) return current;
      const withoutDragged = current.filter((section) => section.id !== draggedId);
      const targetIndex = withoutDragged.findIndex((section) => section.id === targetId);
      const next = [...withoutDragged];
      next.splice(targetIndex, 0, dragged);
      return next;
    });
    setDraggedId(null);
  };
  const resetBuilder = () => {
    const freshSections = defaultBuilderSections.map((section) => ({ ...section, id: makeBuilderId(section.type) }));
    setSections(freshSections);
    setSelectedId(freshSections[0]?.id || '');
  };

  return (
    <main className="builder-shell">
      <header className="builder-top">
        <button className="site-logo" type="button" onClick={openPublicSite}><span>ES</span><strong>Website Builder</strong></button>
        <div>
          <button className="site-login" type="button" onClick={openPublicSite}>View Homepage</button>
          <button className="site-login" type="button" onClick={openDashboard}>Dashboard</button>
          <button className="site-cta" type="button" onClick={() => window.localStorage.setItem(builderStorageKey, JSON.stringify(sections))}>Save Design</button>
        </div>
      </header>

      <section className="builder-grid">
        <aside className="builder-palette">
          <strong>Add blocks</strong>
          {builderPalette.map((item) => (
            <button type="button" key={item.type} onClick={() => addSection(item.type)}>+ {item.label}</button>
          ))}
          <button className="builder-reset" type="button" onClick={resetBuilder}>Reset demo layout</button>
          <small>Drag sections in the middle preview. Select any section to edit text, cards, tables, images and graphics.</small>
        </aside>

        <section className="builder-preview">
          {sections.map((section, index) => (
            <article
              className={cx('builder-section-card', selectedId === section.id && 'selected')}
              draggable
              key={section.id}
              onClick={() => selectSection(section.id)}
              onDragStart={() => setDraggedId(section.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => onDropSection(section.id)}
            >
              <div className="builder-section-tools">
                <span>Drag {index + 1}</span>
                <button type="button" onClick={(event) => { event.stopPropagation(); duplicateSection(section.id); }}>Duplicate</button>
                <button type="button" onClick={(event) => { event.stopPropagation(); deleteSection(section.id); }}>Delete</button>
              </div>
              <BuilderPreviewSection section={section} />
            </article>
          ))}
        </section>

        <aside className="builder-editor">
          {selectedSection && (
            <BuilderEditor
              section={selectedSection}
              updateSelected={updateSelected}
              updateCard={updateCard}
              updateRow={updateRow}
            />
          )}
        </aside>
      </section>
    </main>
  );
}

function BuilderPreviewSection({ section }) {
  if (section.type === 'hero') {
    return (
      <div className="builder-hero-preview">
        <p className="site-eyebrow">{section.kicker}</p>
        <h1>{section.title}</h1>
        <h2>{section.subtitle}</h2>
        <p>{section.body}</p>
        <div><button type="button">{section.buttonText}</button><button type="button">{section.secondaryButtonText}</button></div>
      </div>
    );
  }

  if (section.type === 'table') {
    return (
      <div className="builder-table-preview">
        <p className="site-eyebrow">{section.kicker}</p>
        <h2>{section.title}</h2>
        <p>{section.body}</p>
        <table>
          <tbody>
            {section.rows.map((row) => (
              <tr key={`${row.feature}-${row.easten}`}><td>{row.feature}</td><td>{row.easten}</td><td>{row.other}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (section.type === 'image') {
    return (
      <div className="builder-image-preview">
        <div>
          <p className="site-eyebrow">{section.kicker}</p>
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          <small>{section.caption}</small>
        </div>
        {section.imageUrl ? <img src={section.imageUrl} alt={section.caption || section.title} /> : <div className="builder-image-placeholder">Image / video visual</div>}
      </div>
    );
  }

  if (section.type === 'text') {
    return (
      <div className="builder-text-preview">
        <p className="site-eyebrow">{section.kicker}</p>
        <h2>{section.title}</h2>
        <p>{section.body}</p>
      </div>
    );
  }

  return (
    <div className="builder-cards-preview">
      <p className="site-eyebrow">{section.kicker}</p>
      <h2>{section.title}</h2>
      <p>{section.body}</p>
      <div>
        {(section.cards || []).map((card) => <article key={`${card.title}-${card.text}`}><strong>{card.title}</strong><span>{card.text}</span></article>)}
      </div>
    </div>
  );
}

function BuilderEditor({ section, updateSelected, updateCard, updateRow }) {
  const onImageFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateSelected({ imageUrl: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h2>Edit {section.type}</h2>
      <BuilderInput label="Small label" value={section.kicker || ''} onChange={(value) => updateSelected({ kicker: value })} />
      <BuilderInput label="Heading" value={section.title || ''} onChange={(value) => updateSelected({ title: value })} />
      {section.type === 'hero' && <BuilderInput label="Subheading" value={section.subtitle || ''} onChange={(value) => updateSelected({ subtitle: value })} />}
      <BuilderTextarea label="Body text" value={section.body || ''} onChange={(value) => updateSelected({ body: value })} />
      {section.type === 'hero' && (
        <>
          <BuilderInput label="Main button" value={section.buttonText || ''} onChange={(value) => updateSelected({ buttonText: value })} />
          <BuilderInput label="Second button" value={section.secondaryButtonText || ''} onChange={(value) => updateSelected({ secondaryButtonText: value })} />
        </>
      )}
      {section.type === 'image' && (
        <>
          <BuilderInput label="Image or video URL" value={section.imageUrl || ''} onChange={(value) => updateSelected({ imageUrl: value })} />
          <label className="builder-field"><span>Upload image</span><input type="file" accept="image/*" onChange={onImageFile} /></label>
          <BuilderInput label="Caption" value={section.caption || ''} onChange={(value) => updateSelected({ caption: value })} />
          <button type="button" onClick={() => updateSelected({ imageUrl: '' })}>Remove image</button>
        </>
      )}
      {section.cards && (
        <div className="builder-edit-list">
          <strong>Cards / graphics</strong>
          {section.cards.map((card, index) => (
            <div className="builder-edit-item" key={`${card.title}-${index}`}>
              <BuilderInput label="Title" value={card.title} onChange={(value) => updateCard(index, { title: value })} />
              <BuilderTextarea label="Text" value={card.text} onChange={(value) => updateCard(index, { text: value })} />
              <button type="button" onClick={() => updateSelected({ cards: section.cards.filter((_, cardIndex) => cardIndex !== index) })}>Remove card</button>
            </div>
          ))}
          <button type="button" onClick={() => updateSelected({ cards: [...section.cards, { title: 'New card', text: 'Edit this card text.' }] })}>Add card</button>
        </div>
      )}
      {section.rows && (
        <div className="builder-edit-list">
          <strong>Table rows</strong>
          {section.rows.map((row, index) => (
            <div className="builder-edit-item" key={`${row.feature}-${index}`}>
              <BuilderInput label="Feature" value={row.feature} onChange={(value) => updateRow(index, { feature: value })} />
              <BuilderInput label="EastenSocial" value={row.easten} onChange={(value) => updateRow(index, { easten: value })} />
              <BuilderInput label="Other platform" value={row.other} onChange={(value) => updateRow(index, { other: value })} />
              <button type="button" onClick={() => updateSelected({ rows: section.rows.filter((_, rowIndex) => rowIndex !== index) })}>Remove row</button>
            </div>
          ))}
          <button type="button" onClick={() => updateSelected({ rows: [...section.rows, { feature: 'New feature', easten: 'Included', other: 'Compare' }] })}>Add row</button>
        </div>
      )}
    </div>
  );
}

function BuilderInput({ label, value, onChange }) {
  return (
    <label className="builder-field">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function BuilderTextarea({ label, value, onChange }) {
  return (
    <label className="builder-field">
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function ProfileModal({ currentUser, initialTab, logout, onClose, onOpenAdmin, onProfileUpdated, onWorkspacesChanged, selectedWorkspaceId, workspaces }) {
  const [tab, setTab] = useState(initialTab || 'profile');
  const [profile, setProfile] = useState({
    name: currentUser.name || '',
    username: currentUser.username || '',
    companyName: currentUser.companyName || currentUser.name || '',
    phone: currentUser.phone || '',
    profileImageUrl: currentUser.profileImageUrl || '',
    twoFactorEnabled: Boolean(currentUser.twoFactorEnabled),
    totpEnabled: Boolean(currentUser.totpEnabled)
  });
  const [workspaceDrafts, setWorkspaceDrafts] = useState(workspaces);
  const [openWorkspaceId, setOpenWorkspaceId] = useState('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [status, setStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [locationWorkspaceId, setLocationWorkspaceId] = useState('');
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [workspaceClients, setWorkspaceClients] = useState([]);
  const [clientEmail, setClientEmail] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [workspaceDeleteTarget, setWorkspaceDeleteTarget] = useState(null);
  const [workspaceDeletePassword, setWorkspaceDeletePassword] = useState('');
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpPassword, setTotpPassword] = useState('');
  const [emailChange, setEmailChange] = useState({ stage: '', newEmail: '', currentPassword: '', oldCode: '', newCode: '' });
  const [editingProfileField, setEditingProfileField] = useState('');
  const [profileCrop, setProfileCrop] = useState(null);

  useEffect(() => setTab(initialTab || 'profile'), [initialTab]);
  useEffect(() => setWorkspaceDrafts(workspaces), [workspaces]);
  useEffect(() => {
    if (tab !== 'workspaces' || !selectedWorkspaceId) return;
    axios.get(`${API_BASE}/api/workspaces/${selectedWorkspaceId}/clients`, { withCredentials: true })
      .then((response) => setWorkspaceClients(response.data.clients || []))
      .catch((error) => setStatus(error.response?.data?.error || 'Workspace clients could not be loaded.'));
  }, [selectedWorkspaceId, tab]);

  const updateProfile = (key, value) => setProfile((current) => ({ ...current, [key]: value }));
  const uploadProfileImage = async (file) => {
    if (!file?.type?.startsWith('image/')) return setStatus('Select a PNG, JPG or WEBP image.');
    const reader = new FileReader();
    reader.onload = () => setProfileCrop({ source: reader.result, zoom: 1, x: 0, y: 0 });
    reader.onerror = () => setStatus('This image could not be read.');
    reader.readAsDataURL(file);
  };
  const applyProfileCrop = async () => {
    try {
      const profileImageUrl = await cropSquareDataUrl(profileCrop.source, profileCrop);
      updateProfile('profileImageUrl', profileImageUrl);
      setProfileCrop(null);
      setStatus('Logo cropped to 1000 × 1000. Select Save to upload it.');
    } catch (error) { setStatus(error.message); }
  };
  const uploadWorkspaceImage = async (workspaceId, file) => {
    try {
      const profileImageUrl = await resizeSquareImage(file);
      setWorkspaceDrafts((rows) => rows.map((row) => row.id === workspaceId ? { ...row, profileImageUrl } : row));
      setStatus('Workspace image cropped to 150 x 150. Save the workspace to keep it.');
    } catch (error) {
      setStatus(error.message);
    }
  };
  const saveProfile = async () => {
    try {
      setIsSaving(true);
      setStatus('');
      const response = await axios.patch(`${API_BASE}/api/profile`, profile, { withCredentials: true });
      setProfile((current) => ({ ...current, ...response.data.user }));
      onProfileUpdated(response.data.user);
      setStatus('Profile saved successfully.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Profile could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };
  const saveCurrentTab = () => {
    if (tab === 'workspaces') {
      const openWorkspace = workspaceDrafts.find((item) => item.id === openWorkspaceId);
      if (openWorkspace) saveWorkspace(openWorkspace);
      else setStatus('Open a workspace before saving its settings.');
      return;
    }
    saveProfile();
  };
  const checkUsername = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/profile/username-available`, { params: { username: profile.username }, withCredentials: true });
      setStatus(response.data.available ? `@${response.data.username} is available.` : response.data.message || 'This username is already taken.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Username could not be checked.');
    }
  };
  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/workspaces`, { name: newWorkspaceName.trim() }, { withCredentials: true });
      const rows = [...workspaceDrafts, { ...fallbackWorkspaces[0], ...response.data.workspace }];
      setWorkspaceDrafts(rows);
      setNewWorkspaceName('');
      onWorkspacesChanged(rows, response.data.workspace.id);
      setStatus('New workspace created. Its profiles and posts are kept separate.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Workspace could not be created.');
    } finally {
      setIsSaving(false);
    }
  };
  const saveWorkspace = async (workspace) => {
    try {
      setIsSaving(true);
      const response = await axios.patch(`${API_BASE}/api/workspaces/${workspace.id}`, {
        name: workspace.name,
        businessName: workspace.businessName || '',
        profileImageUrl: workspace.profileImageUrl || '',
        businessType: workspace.businessType || '',
        customBusinessType: workspace.customBusinessType || '',
        businessDescription: workspace.businessDescription || '',
        defaultDescription: workspace.defaultDescription || '',
        contactDetails: workspace.contactDetails || '',
        contactNumber: workspace.contactNumber || '',
        contactEmail: workspace.contactEmail || '',
        address: workspace.address || '',
        businessState: workspace.businessState || '',
        preferredLanguage: workspace.preferredLanguage || 'English',
        website: workspace.website || '',
        contactHours: workspace.contactHours || '',
        cloudDataUrl: workspace.cloudDataUrl || '',
        defaultStory: Boolean(workspace.defaultStory),
        autoWriteAfterUpload: Boolean(workspace.autoWriteAfterUpload),
        socialLinks: workspace.socialLinks || [],
        lockedLocation: workspace.lockedLocation || null
      }, { withCredentials: true });
      const rows = workspaceDrafts.map((item) => item.id === workspace.id ? { ...fallbackWorkspaces[0], ...item, ...response.data.workspace } : item);
      setWorkspaceDrafts(rows);
      onWorkspacesChanged(rows, selectedWorkspaceId);
      setStatus(`${response.data.workspace.name} saved.`);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Workspace could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };
  const socialPlatformOptions = [
    ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['whatsapp', 'WhatsApp'], ['twitter', 'X (Twitter)'],
    ['youtube', 'YouTube'], ['linkedin', 'LinkedIn'], ['snapchat', 'Snapchat']
  ];
  const updateWorkspaceSocialLinks = (workspaceId, updater) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === workspaceId ? { ...row, socialLinks: updater(row.socialLinks || []) } : row));
  const addWorkspaceSocialLink = (workspaceId) => updateWorkspaceSocialLinks(workspaceId, (links) => links.length >= 5 ? links : [...links, { platform: socialPlatformOptions.find(([id]) => !links.some((item) => item.platform === id))?.[0] || 'instagram', url: '' }]);
  const searchLocation = async (workspaceId) => {
    if (locationQuery.trim().length < 3) {
      setStatus('Type at least 3 characters to search a location.');
      return;
    }
    try {
      setIsSearchingLocation(true);
      setLocationWorkspaceId(workspaceId);
      const response = await axios.get(`${API_BASE}/api/location/search`, {
        params: { q: locationQuery.trim() },
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      setLocationResults(response.data.results || []);
      setStatus(response.data.results?.length ? 'Select one result to lock it to this workspace.' : 'No matching location found.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Location search failed.');
    } finally {
      setIsSearchingLocation(false);
    }
  };
  const deleteWorkspace = async () => {
    const workspace = workspaceDeleteTarget;
    if (!workspace || !workspaceDeletePassword) return;
    try {
      setIsSaving(true);
      await axios.delete(`${API_BASE}/api/workspaces/${workspace.id}`, {
        params: { confirm: true },
        data: { password: workspaceDeletePassword },
        withCredentials: true
      });
      const rows = workspaceDrafts.filter((item) => item.id !== workspace.id);
      const nextId = selectedWorkspaceId === workspace.id ? rows[0]?.id : selectedWorkspaceId;
      setWorkspaceDrafts(rows);
      onWorkspacesChanged(rows, nextId);
      setStatus(`${workspace.name} and its contained data were deleted.`);
      setWorkspaceDeleteTarget(null);
      setWorkspaceDeletePassword('');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Workspace could not be deleted.');
    } finally {
      setIsSaving(false);
    }
  };
  const assignClient = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/workspaces/${selectedWorkspaceId}/clients`, { email: clientEmail.trim() }, { withCredentials: true });
      setWorkspaceClients((rows) => rows.some((item) => item.id === response.data.client.id) ? rows : [...rows, response.data.client]);
      setClientEmail('');
      setStatus(`${response.data.client.name} can now review this workspace.`);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Client could not be assigned.');
    } finally {
      setIsSaving(false);
    }
  };
  const removeClient = async (client) => {
    try {
      setIsSaving(true);
      await axios.delete(`${API_BASE}/api/workspaces/${selectedWorkspaceId}/clients/${client.id}`, { withCredentials: true });
      setWorkspaceClients((rows) => rows.filter((item) => item.id !== client.id));
      setStatus(`${client.name} no longer has access to this workspace.`);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Client access could not be removed.');
    } finally {
      setIsSaving(false);
    }
  };
  const startTotpSetup = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/profile/totp/setup`, {}, { withCredentials: true });
      setTotpSetup(response.data);
      setTotpCode('');
      setStatus('Scan the QR code with your authenticator app, then enter its current 6-digit code.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Authenticator setup could not be started.');
    } finally {
      setIsSaving(false);
    }
  };
  const verifyTotpSetup = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/profile/totp/verify`, { code: totpCode }, { withCredentials: true });
      setProfile((current) => ({ ...current, ...response.data.user }));
      onProfileUpdated(response.data.user);
      setTotpSetup(null);
      setTotpCode('');
      setStatus('Authenticator 2FA is active. Future logins will require its current code.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Authenticator code could not be verified.');
    } finally {
      setIsSaving(false);
    }
  };
  const disableTotp = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/profile/totp/disable`, { password: totpPassword, code: totpCode }, { withCredentials: true });
      setProfile((current) => ({ ...current, ...response.data.user }));
      onProfileUpdated(response.data.user);
      setTotpPassword('');
      setTotpCode('');
      setStatus('Authenticator 2FA has been disabled.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Authenticator 2FA could not be disabled.');
    } finally {
      setIsSaving(false);
    }
  };
  const startEmailChange = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/profile/email-change/start`, {
        newEmail: emailChange.newEmail,
        currentPassword: emailChange.currentPassword
      }, { withCredentials: true });
      setEmailChange((current) => ({ ...current, stage: 'verify', currentPassword: '' }));
      setStatus(response.data.message);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Email change could not be started.');
    } finally {
      setIsSaving(false);
    }
  };
  const confirmEmailChange = async () => {
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/profile/email-change/confirm`, {
        oldCode: emailChange.oldCode,
        newCode: emailChange.newCode
      }, { withCredentials: true });
      onProfileUpdated(response.data.user);
      setEmailChange({ stage: '', newEmail: '', currentPassword: '', oldCode: '', newCode: '' });
      setStatus(response.data.message);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Email address could not be verified.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="profile-modal" role="dialog" aria-modal="true" aria-label="My Profile">
        <header>
          <div><p>ACCOUNT CENTER</p><h2>My Profile</h2></div>
          <button type="button" aria-label="Close profile" onClick={onClose}>×</button>
        </header>
        <nav className="profile-modal-tabs">
          <button className={tab === 'profile' ? 'active' : ''} type="button" onClick={() => setTab('profile')}>Profile</button>
          <button className={tab === 'security' ? 'active' : ''} type="button" onClick={() => setTab('security')}>Security</button>
          <button className={tab === 'workspaces' ? 'active' : ''} type="button" onClick={() => setTab('workspaces')}>Workspaces</button>
          <button className={tab === 'billing' ? 'active' : ''} type="button" onClick={() => setTab('billing')}>Plan &amp; Payments</button>
        </nav>

        {tab === 'billing' && <PlanPayments user={currentUser} />}

        {tab === 'profile' && <div className="profile-modal-body profile-overview">
          <div className="profile-avatar-editor">
            <span className="profile-image-preview">{profile.profileImageUrl ? <img src={profile.profileImageUrl} alt="Profile" /> : String(profile.companyName || profile.name || 'A').slice(0, 1).toUpperCase()}{profile.profileImageUrl && <button type="button" aria-label="Remove profile picture" title="Remove" onClick={() => updateProfile('profileImageUrl', '')}><FiX /></button>}</span>
            <label className="profile-change-logo"><FiEdit3 /><span>Change Logo</span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => uploadProfileImage(event.target.files?.[0])} /></label>
            <small>Recommended 1000 × 1000 px. Other ratios are cropped to a square before saving.</small>
          </div>
          <div className="profile-agency-name">
            {editingProfileField === 'companyName' ? <input autoFocus value={profile.companyName} onChange={(event) => updateProfile('companyName', event.target.value)} onBlur={() => setEditingProfileField('')} /> : <><h3>{profile.companyName || profile.name || 'Agency'}</h3><button type="button" aria-label="Edit agency name" title="Edit" onClick={() => setEditingProfileField('companyName')}><FiEdit3 /></button></>}
          </div>
          <div className="profile-username-box"><span>@</span><input aria-label="Username" value={profile.username} onChange={(event) => updateProfile('username', event.target.value)} placeholder="username" /><button type="button" aria-label="Check username" title="Check availability" onClick={checkUsername}><FiCheck /></button></div>
          <div className="profile-contact-grid">
            <section><FiPhone /><div><small>Mobile Number</small><input value={profile.phone} onChange={(event) => updateProfile('phone', event.target.value)} placeholder="Add mobile number" /></div>{profile.phone && <button type="button" aria-label="Remove mobile number" title="Remove" onClick={() => updateProfile('phone', '')}><FiX /></button>}</section>
            <section><FiMail /><div><small>Email Address</small><strong>{currentUser.email}</strong><span>{currentUser.emailVerified === false ? 'Verification pending' : 'Email verified'}</span></div><button type="button" aria-label="Change email address" title="Change email" onClick={() => setEmailChange((current) => ({ ...current, stage: current.stage ? '' : 'start' }))}><FiEdit3 /></button></section>
          </div>
          {emailChange.stage === 'start' && <div className="email-change-panel full-field"><strong>Verify a new email</strong><div><input type="email" value={emailChange.newEmail} onChange={(event) => setEmailChange((current) => ({ ...current, newEmail: event.target.value }))} placeholder="New email address" /><input type="password" value={emailChange.currentPassword} onChange={(event) => setEmailChange((current) => ({ ...current, currentPassword: event.target.value }))} placeholder="Current password" /><button className="btn primary" type="button" disabled={isSaving || !emailChange.newEmail.includes('@') || !emailChange.currentPassword} onClick={startEmailChange}>Send both codes</button></div><small>A code will be sent to your current and new email address.</small></div>}
          {emailChange.stage === 'verify' && <div className="email-change-panel full-field"><strong>Enter both email codes</strong><div><input inputMode="numeric" maxLength="6" value={emailChange.oldCode} onChange={(event) => setEmailChange((current) => ({ ...current, oldCode: event.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="Code from old email" /><input inputMode="numeric" maxLength="6" value={emailChange.newCode} onChange={(event) => setEmailChange((current) => ({ ...current, newCode: event.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="Code from new email" /><button className="btn primary" type="button" disabled={isSaving || emailChange.oldCode.length !== 6 || emailChange.newCode.length !== 6} onClick={confirmEmailChange}>Verify and change</button></div></div>}
        </div>}

        {tab === 'security' && <div className="profile-modal-body security-settings">
          <div className="security-row"><div><strong>Email verification</strong><small>New accounts must enter the code sent to their email before login.</small></div><span className="security-ready">Active</span></div>
          <div className="security-row"><div><strong>Password</strong><small>Change your account password securely. All roles use the same password policy.</small></div><button className="btn secondary" type="button" onClick={() => setPasswordOpen(true)}>Change password</button></div>
          <label className="security-row"><div><strong>Email two-factor login</strong><small>Send an email code after every correct password login.</small></div><input type="checkbox" checked={profile.twoFactorEnabled} disabled={profile.totpEnabled} onChange={(event) => updateProfile('twoFactorEnabled', event.target.checked)} /></label>
          <div className="security-row authenticator-security-row"><div><strong>Authenticator app</strong><small>Use a rotating code from Google Authenticator, Microsoft Authenticator or Authy.</small></div>{profile.totpEnabled ? <span className="security-ready">Active</span> : <button className="btn secondary" type="button" disabled={isSaving} onClick={startTotpSetup}>Set up</button>}</div>
          {totpSetup && !profile.totpEnabled && <div className="totp-setup-panel">
            <div className="totp-qr-layout">
              <div className="totp-qr-code">
                <QRCodeSVG value={totpSetup.otpauthUrl} size={184} level="M" marginSize={2} title="EastenSocial authenticator setup QR code" />
              </div>
              <div className="totp-qr-copy">
                <strong>Scan this QR code</strong>
                <small>Open Google Authenticator, Microsoft Authenticator or Authy, tap add account, then choose Scan a QR code.</small>
                <small>The code changes every 30 seconds. Enter the current code below to finish setup.</small>
              </div>
            </div>
            <details className="totp-manual-key">
              <summary>Cannot scan? Use a setup key</summary>
              <strong>{totpSetup.secret}</strong>
              <small>Choose a time-based key in your authenticator app.</small>
            </details>
            <div className="totp-verify-row"><input inputMode="numeric" autoComplete="one-time-code" maxLength="6" value={totpCode} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" /><button className="btn primary" type="button" disabled={isSaving || totpCode.length !== 6} onClick={verifyTotpSetup}>Verify and enable</button></div>
          </div>}
          {profile.totpEnabled && <div className="totp-setup-panel danger"><span>Disable authenticator 2FA</span><small>Confirm with your current password and current authenticator code.</small><div><input type="password" value={totpPassword} onChange={(event) => setTotpPassword(event.target.value)} placeholder="Current password" /><input inputMode="numeric" maxLength="6" value={totpCode} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" /><button className="btn secondary" type="button" disabled={isSaving || !totpPassword || totpCode.length !== 6} onClick={disableTotp}>Disable</button></div></div>}
          <div className="security-row"><div><strong>Phone verification</strong><small>An SMS provider must be connected before phone OTP can be enabled.</small></div><span>Setup required</span></div>
        </div>}

        {tab === 'workspaces' && <div className="profile-modal-body workspace-manager">
          <div className="workspace-create"><input value={newWorkspaceName} onChange={(event) => setNewWorkspaceName(event.target.value)} placeholder="New workspace name" /><button className="btn primary" type="button" disabled={isSaving || !newWorkspaceName.trim()} onClick={createWorkspace}>Add workspace</button></div>
          <p className="workspace-isolation-note">Every workspace keeps its connected profiles, posts, calendar and reports separate.</p>
          {workspaceDrafts.map((item) => <article className={cx('workspace-settings-card', item.id === selectedWorkspaceId && 'selected', openWorkspaceId === item.id && 'expanded')} key={item.id}>
            <button className="workspace-accordion-trigger" type="button" aria-expanded={openWorkspaceId === item.id} onClick={() => { const next = openWorkspaceId === item.id ? '' : item.id; setOpenWorkspaceId(next); if (next) onWorkspacesChanged(workspaceDrafts, next); }}><span className="workspace-avatar">{item.profileImageUrl ? <img src={item.profileImageUrl} alt="" /> : item.initials}</span><span><strong>{item.name}</strong><small>{item.businessType || 'Workspace settings'}</small></span><FiChevronDown /></button>
            {openWorkspaceId === item.id && <div className="workspace-accordion-content">
            <div className="workspace-setting-head modern-logo-control">
              <span className="workspace-avatar">{item.profileImageUrl ? <img src={item.profileImageUrl} alt="" /> : item.initials}</span>
              <label><span>Workspace Logo</span><small>Square PNG, JPG or WEBP. Transparent logos work best.</small><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => uploadWorkspaceImage(item.id, event.target.files?.[0])} /></label>
              {item.profileImageUrl && <button className="icon-action" type="button" aria-label="Remove workspace logo" title="Remove logo" onClick={() => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, profileImageUrl: '' } : row))}><FiX /></button>}
              <button className="icon-action danger" type="button" aria-label="Delete workspace" title="Delete workspace" disabled={workspaceDrafts.length <= 1} onClick={() => { setWorkspaceDeleteTarget(item); setWorkspaceDeletePassword(''); setStatus(''); }}><FiTrash2 /></button>
            </div>
            <div className="workspace-fields-grid workspace-fields-three">
              <label><span>Workspace Name</span><input value={item.name} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, name: event.target.value } : row))} /></label>
              <label><span>Business Name</span><input value={item.businessName || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, businessName: event.target.value } : row))} placeholder="Public business or brand name" /></label>
              <label><span>Business type</span><select value={item.businessType || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, businessType: event.target.value } : row))}><option value="">Select type</option>{businessTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
              <label><span>Custom Name</span><input disabled={item.businessType !== 'Custom'} value={item.customBusinessType || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, customBusinessType: event.target.value } : row))} placeholder={item.businessType === 'Custom' ? 'Enter category' : 'Select Custom to use'} /></label>
              <label className="span-two"><span>Business Description</span><textarea value={item.businessDescription || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, businessDescription: event.target.value } : row))} placeholder="What this business offers, its audience and tone" /></label>
              <label><span>Address</span><textarea value={item.address || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, address: event.target.value } : row))} placeholder="Business or service address" /></label>
              <label><span>Contact number</span><input type="tel" value={item.contactNumber || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, contactNumber: event.target.value } : row))} placeholder="+91 98765 43210" /></label>
              <label><span>Contact email</span><input type="email" value={item.contactEmail || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, contactEmail: event.target.value } : row))} placeholder="hello@business.com" /></label>
              <label><span>Website</span><input type="url" value={item.website || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, website: event.target.value } : row))} placeholder="https://yourbusiness.com" /></label>
              <section className="workspace-social-links full-field">
                <header><span><strong>Social media links</strong><small>Add up to five public profile links for this business.</small></span><button className="icon-action" type="button" aria-label="Add social media link" title="Add social media link" disabled={(item.socialLinks || []).length >= 5} onClick={() => addWorkspaceSocialLink(item.id)}><FiPlus /></button></header>
                <div>{(item.socialLinks || []).map((link, index) => <div className="workspace-social-link" key={`${link.platform}-${index}`}><select aria-label={`Social platform ${index + 1}`} value={link.platform} onChange={(event) => updateWorkspaceSocialLinks(item.id, (links) => links.map((entry, entryIndex) => entryIndex === index ? { ...entry, platform: event.target.value } : entry))}>{socialPlatformOptions.map(([id, label]) => <option key={id} value={id} disabled={(item.socialLinks || []).some((entry, entryIndex) => entryIndex !== index && entry.platform === id)}>{label}</option>)}</select><input aria-label={`Social link ${index + 1}`} type="url" value={link.url} onChange={(event) => updateWorkspaceSocialLinks(item.id, (links) => links.map((entry, entryIndex) => entryIndex === index ? { ...entry, url: event.target.value } : entry))} placeholder="https://..." /><button className="icon-action danger" type="button" aria-label={`Remove social link ${index + 1}`} title="Remove link" onClick={() => updateWorkspaceSocialLinks(item.id, (links) => links.filter((_, entryIndex) => entryIndex !== index))}><FiX /></button></div>)}</div>
                {!(item.socialLinks || []).length && <small>No public social links added.</small>}
              </section>
              <label><span>State for regional events</span><select value={item.businessState || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, businessState: event.target.value } : row))}><option value="">Select state</option>{indianStates.map((state) => <option key={state} value={state}>{state}</option>)}</select></label>
              <label><span>Preferred Language</span><select value={item.preferredLanguage || 'English'} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, preferredLanguage: event.target.value } : row))}>{['English', 'Hindi', 'Punjabi', 'Hinglish', 'Bengali', 'Gujarati', 'Marathi', 'Tamil', 'Telugu', 'Urdu'].map((language) => <option key={language}>{language}</option>)}</select></label>
              {item.contactDetails && <label className="full-field"><span>Legacy contact notes</span><textarea value={item.contactDetails || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, contactDetails: event.target.value } : row))} placeholder="Older contact notes kept for compatibility" /></label>}
              <label><span>Client contact hours</span><input value={item.contactHours || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, contactHours: event.target.value } : row))} placeholder="Mon-Sat, 10:00 AM-6:00 PM" /></label>
              <label><span>Client Cloud Data link</span><input type="url" value={item.cloudDataUrl || ''} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, cloudDataUrl: event.target.value } : row))} placeholder="https://drive.google.com/..." /></label>
              <label className="workspace-toggle full-field"><input type="checkbox" checked={Boolean(item.defaultStory)} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, defaultStory: event.target.checked } : row))} /><span>Always enable Story in Post Now</span></label>
              <label className="workspace-toggle full-field"><input type="checkbox" checked={Boolean(item.autoWriteAfterUpload)} onChange={(event) => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, autoWriteAfterUpload: event.target.checked } : row))} /><span><strong>Auto Write after Upload</strong><small>Automatically prepare an editable title, caption and relevant hashtags after media analysis.</small></span></label>
              <div className="workspace-location full-field">
                <span>Locked location</span>
                {item.lockedLocation?.label && <div className="locked-location"><strong>{item.lockedLocation.label}</strong><button type="button" onClick={() => setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, lockedLocation: null } : row))}>Unlock</button></div>}
                <div className="location-search-row"><input value={locationWorkspaceId === item.id ? locationQuery : ''} onChange={(event) => { setLocationWorkspaceId(item.id); setLocationQuery(event.target.value); }} placeholder="Search city, area or business address" /><button type="button" disabled={isSearchingLocation} onClick={() => searchLocation(item.id)}>{isSearchingLocation && locationWorkspaceId === item.id ? 'Searching...' : 'Search'}</button></div>
                {locationWorkspaceId === item.id && locationResults.length > 0 && <div className="location-results">{locationResults.map((location) => <button key={`${location.placeId}-${location.latitude}`} type="button" onClick={() => { setWorkspaceDrafts((rows) => rows.map((row) => row.id === item.id ? { ...row, lockedLocation: location } : row)); setLocationResults([]); }}>{location.label}</button>)}</div>}
                <small>Search by OpenStreetMap. Data © OpenStreetMap contributors.</small>
              </div>
            </div>
            <section className="workspace-inline-clients"><div><strong>Assigned Clients</strong><small>{workspaceClients.length} / 5 client reviewers</small></div><div className="workspace-client-add"><input type="email" value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} placeholder="client@company.com" /><button className="btn primary" type="button" disabled={isSaving || workspaceClients.length >= 5 || !clientEmail.includes('@')} onClick={assignClient}>Assign</button></div><div className="workspace-client-list">{workspaceClients.map((client) => <article key={client.id}><span>{String(client.name || 'C').slice(0, 1)}</span><div><strong>{client.name}</strong><small>{client.email}</small></div><button className="danger-link" type="button" onClick={() => removeClient(client)}>Remove</button></article>)}{!workspaceClients.length && <small>No client assigned to this workspace.</small>}</div></section>
            </div>}
          </article>)}
          {workspaceDeleteTarget && <div className="workspace-delete-confirmation" role="alertdialog" aria-modal="true" aria-label="Confirm workspace deletion">
            <div><strong>Delete {workspaceDeleteTarget.name}?</strong><small>This permanently removes its connected accounts, posts, calendar data and reports. Enter your current password to continue.</small></div>
            <input type="password" autoComplete="current-password" value={workspaceDeletePassword} onChange={(event) => setWorkspaceDeletePassword(event.target.value)} placeholder="Current password" />
            <div><button className="btn secondary" type="button" disabled={isSaving} onClick={() => { setWorkspaceDeleteTarget(null); setWorkspaceDeletePassword(''); }}>Cancel</button><button className="btn danger" type="button" disabled={isSaving || !workspaceDeletePassword} onClick={deleteWorkspace}>{isSaving ? 'Deleting...' : 'Delete workspace'}</button></div>
          </div>}
        </div>}

        {profileCrop && <div className="profile-crop-backdrop" role="dialog" aria-modal="true" aria-label="Crop profile logo"><section className="profile-crop-dialog"><header><strong>Crop Logo</strong><button type="button" aria-label="Close crop" onClick={() => setProfileCrop(null)}><FiX /></button></header><div className="profile-crop-frame"><img src={profileCrop.source} alt="Crop preview" style={{ transform: `translate(${profileCrop.x * -0.35}%, ${profileCrop.y * -0.35}%) scale(${profileCrop.zoom})` }} /></div><label><span>Zoom</span><input type="range" min="1" max="3" step="0.05" value={profileCrop.zoom} onChange={(event) => setProfileCrop((current) => ({ ...current, zoom: Number(event.target.value) }))} /></label><div className="profile-crop-position"><label><span>Horizontal</span><input type="range" min="-100" max="100" value={profileCrop.x} onChange={(event) => setProfileCrop((current) => ({ ...current, x: Number(event.target.value) }))} /></label><label><span>Vertical</span><input type="range" min="-100" max="100" value={profileCrop.y} onChange={(event) => setProfileCrop((current) => ({ ...current, y: Number(event.target.value) }))} /></label></div><footer><button className="btn subtle" type="button" onClick={() => setProfileCrop(null)}>Cancel</button><button className="btn primary" type="button" onClick={applyProfileCrop}>Apply Crop</button></footer></section></div>}
        {status && <p className="profile-modal-status">{status}</p>}
        {passwordOpen && <PasswordChangeModal forced={false} onClose={() => setPasswordOpen(false)} onChanged={(user) => { onProfileUpdated(user); setPasswordOpen(false); setStatus('Password changed successfully.'); }} />}
        <footer><button className="btn primary profile-footer-save" type="button" disabled={isSaving || tab === 'billing'} onClick={saveCurrentTab}>{isSaving ? 'Saving...' : 'Save'}</button></footer>
      </section>
    </div>
  );
}


function PublicCalendarPage({ openPublicSite }) {
  const token = new URLSearchParams(window.location.search).get('calendarToken') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [comment, setComment] = useState('');
  const [selectedPostId, setSelectedPostId] = useState('');
  const [month, setMonth] = useState(() => new Date(calendarYear, calendarMonthIndex, 1));
  const [isBusy, setIsBusy] = useState(false);

  const loadCalendar = useCallback(async () => {
    if (!token) {
      setError('This calendar link is incomplete. Ask the workspace owner for a new link.');
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/api/calendar/public/${encodeURIComponent(token)}`);
      setData(response.data);
      const firstDate = response.data.posts?.map((post) => post.scheduledTime || post.publishedAt || post.createdAt).find(Boolean);
      if (firstDate) {
        const date = new Date(firstDate);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Calendar could not be loaded.');
    }
  }, [token]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const latestReviews = useMemo(() => {
    const map = {};
    (data?.reviews || []).forEach((review) => { map[String(review.postId)] = review; });
    return map;
  }, [data]);
  const monthCells = useMemo(() => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const leading = new Date(year, monthIndex, 1).getDay();
    const total = new Date(year, monthIndex + 1, 0).getDate();
    return Array.from({ length: Math.ceil((leading + total) / 7) * 7 }, (_, index) => {
      const day = index - leading + 1;
      return day > 0 && day <= total ? day : null;
    });
  }, [month]);
  const postsForDay = (day) => (data?.posts || []).filter((post) => {
    const date = new Date(post.scheduledTime || post.publishedAt || post.createdAt);
    return date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth() && date.getDate() === day;
  });
  const reviewPost = async (postId, status) => {
    if (!reviewerName.trim()) {
      setError('Please add your name before reviewing a post.');
      return;
    }
    try {
      setIsBusy(true);
      setError('');
      await axios.post(`${API_BASE}/api/calendar/public/${encodeURIComponent(token)}/reviews`, { postId, status, comment, reviewerName });
      setComment('');
      setSelectedPostId('');
      await loadCalendar();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Review could not be submitted.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <main className="public-calendar-page">
      <header className="public-calendar-header">
        <button className="site-logo" type="button" onClick={openPublicSite}><img src="/brand/easten-social-logo.png" alt="" /><strong>EastenSocial</strong></button>
        <div><p>CLIENT APPROVAL CALENDAR</p><h1>{data?.workspace?.name || 'Shared calendar'}</h1></div>
        <button className="site-login" type="button" onClick={openPublicSite}>EastenSocial</button>
      </header>
      {error && <div className="public-calendar-error">{error}</div>}
      {!data && !error && <div className="public-calendar-loading">Loading approval calendar...</div>}
      {data && <section className="public-calendar-shell">
        <div className="public-calendar-main">
          <div className="public-calendar-toolbar"><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>Previous</button><h2>{month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h2><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>Next</button></div>
          <div className="public-calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <strong className="weekday" key={day}>{day}</strong>)}
            {monthCells.map((day, index) => <div className="public-day-cell" key={`${day || 'blank'}-${index}`}>
              {day && <span>{day}</span>}
              {day && postsForDay(day).map((post) => {
                const review = latestReviews[String(post.id)];
                return <button className={cx('public-post-chip', review?.status)} key={post.id} type="button" onClick={() => setSelectedPostId(String(post.id))}><strong>{post.title || post.content}</strong><small>{review?.status || post.status}</small></button>;
              })}
            </div>)}
          </div>
        </div>
        <aside className="public-review-panel">
          <p>REVIEW A POST</p><h2>{selectedPostId ? 'Approve or request changes' : 'Select a calendar post'}</h2>
          <label><span>Your name</span><input value={reviewerName} onChange={(event) => setReviewerName(event.target.value)} placeholder="Client reviewer" /></label>
          <label><span>Comment</span><textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Optional for approval; required for changes" /></label>
          <div className="public-review-actions"><button disabled={!selectedPostId || isBusy} type="button" onClick={() => reviewPost(selectedPostId, 'approved')}>Approve</button><button disabled={!selectedPostId || isBusy} type="button" onClick={() => reviewPost(selectedPostId, 'changes')}>Request changes</button><button disabled={!selectedPostId || isBusy} type="button" onClick={() => reviewPost(selectedPostId, 'declined')}>Decline</button></div>
          <small>Link expires {new Date(data.expiresAt).toLocaleDateString('en-IN')}.</small>
        </aside>
      </section>}
    </main>
  );
}

export function ClientDashboard({ currentUser, setCurrentUser, logout }) {
  const [data, setData] = useState({ assigned: false, workspaces: [], workspace: null, posts: [], reviews: [], reports: [], tickets: [] });
  const [workspaceId, setWorkspaceId] = useState('');
  const [activeView, setActiveView] = useState('calendar');
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [notice, setNotice] = useNotice();
  const [isLoading, setIsLoading] = useState(true);
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [ratingDrafts, setRatingDrafts] = useState({});
  const [ticket, setTicket] = useState({ subject: '', message: '', attachment: null });
  const [selectedPost, setSelectedPost] = useState(null);
  const [reviewPending, setReviewPending] = useState(false);
  const reviewDialog = useRef(null);
  const reviewTrigger = useRef(null);
  const [reportMode, setReportMode] = useState('progress');
  const [theme, setTheme] = useState(() => window.localStorage.getItem('cosocial-dashboard-theme') || window.localStorage.getItem('easten-dashboard-theme') || 'light');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem('cosocial-dashboard-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!selectedPost) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    reviewDialog.current?.querySelector('button')?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') setSelectedPost(null);
      if (event.key !== 'Tab') return;
      const controls = Array.from(reviewDialog.current?.querySelectorAll('button:not(:disabled), textarea, video[controls]') || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      reviewTrigger.current?.focus();
    };
  }, [selectedPost]);

  const loadChat = useCallback(async (selectedWorkspaceId) => {
    if (!selectedWorkspaceId || String(currentUser?.id || '').startsWith('client-preview')) return;
    try {
      const response = await axios.get(`${API_BASE}/api/chat/workspaces/${selectedWorkspaceId}`, { withCredentials: true });
      setChatMessages(response.data.messages || []);
    } catch (error) {
      if (error.response?.status !== 404) setNotice(error.response?.data?.error || 'Chat could not be refreshed.');
    }
  }, [currentUser?.id, setNotice]);

  const loadDashboard = useCallback(async (selectedWorkspaceId = '', background = false) => {
    if (String(currentUser?.id || '').startsWith('client-preview')) {
      setData({ assigned: false, workspaces: [], workspace: null, posts: [], reviews: [], reports: [], tickets: [] });
      setIsLoading(false);
      return;
    }
    try {
      if (!background) setIsLoading(true);
      const response = await axios.get(`${API_BASE}/api/client/dashboard`, {
        params: selectedWorkspaceId ? { workspaceId: selectedWorkspaceId } : {},
        withCredentials: true
      });
      setData(response.data);
      const nextWorkspaceId = response.data.workspace?.id || '';
      setWorkspaceId(nextWorkspaceId);
      await loadChat(nextWorkspaceId);
    } catch (error) {
      setNotice(error.response?.data?.error || 'Client dashboard could not be loaded.');
    } finally {
      if (!background) setIsLoading(false);
    }
  }, [currentUser?.id, loadChat, setNotice]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (!workspaceId || String(currentUser?.id || '').startsWith('client-preview')) return undefined;
    const refresh = () => loadDashboard(workspaceId, true);
    const timer = window.setInterval(refresh, 5000);
    const handleVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentUser?.id, loadDashboard, workspaceId]);

  const latestReviewByPost = useMemo(() => {
    const rows = {};
    (data.reviews || []).forEach((review) => { rows[String(review.postId)] = review; });
    return rows;
  }, [data.reviews]);
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const calendarCells = [
    ...Array(monthStart.getDay()).fill(null),
    ...Array.from({ length: monthEnd.getDate() }, (_, index) => index + 1)
  ];
  const postsForDay = (day) => (data.posts || []).filter((post) => {
    const dateValue = post.scheduledTime || post.publishedAt || post.createdAt;
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth() && date.getDate() === day;
  });
  const reviewPost = async (postId, status) => {
    if (reviewPending) return;
    const comment = String(reviewDrafts[postId] || '').trim();
    if (status === 'changes' && !comment) {
      setNotice('Request changes ke saath comment likhna required hai.');
      return;
    }
    try {
      setReviewPending(true);
      await axios.post(`${API_BASE}/api/client/workspaces/${workspaceId}/reviews`, { postId, status, comment }, { withCredentials: true });
      setReviewDrafts((current) => ({ ...current, [postId]: '' }));
      setNotice({ type: 'success', message: `Post marked ${status}.` });
      await loadDashboard(workspaceId, true);
    } catch (error) {
      setNotice(error.response?.data?.error || 'Review could not be saved.');
    } finally {
      setReviewPending(false);
    }
  };
  const rateReport = async (reportId) => {
    const draft = ratingDrafts[reportId] || {};
    try {
      await axios.post(`${API_BASE}/api/client/reports/${reportId}/rating`, {
        score: Number(draft.score || 0),
        suggestion: draft.suggestion || ''
      }, { withCredentials: true });
      setNotice({ type: 'success', message: 'Report feedback saved. Thank you.' });
      await loadDashboard(workspaceId);
    } catch (error) {
      setNotice(error.response?.data?.error || 'Report feedback could not be saved.');
    }
  };
  const createTicket = async () => {
    try {
      await axios.post(`${API_BASE}/api/support/tickets`, ticket, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      setTicket({ subject: '', message: '', attachment: null });
      setNotice({ type: 'success', message: 'Support ticket created.' });
      await loadDashboard(workspaceId);
    } catch (error) {
      setNotice(error.response?.data?.error || 'Support ticket could not be created.');
    }
  };
  const sendChatMessage = async () => {
    const message = chatDraft.trim();
    if (!message || !workspaceId) return;
    try {
      const response = await axios.post(`${API_BASE}/api/chat/workspaces/${workspaceId}`, { message }, { withCredentials: true });
      setChatMessages((current) => [...current, response.data.message]);
      setChatDraft('');
    } catch (error) {
      setNotice(error.response?.data?.error || 'Message could not be sent.');
    }
  };
  const selectTicketAttachment = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setNotice('Screenshot must be a JPG or PNG under 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setTicket((current) => ({ ...current, attachment: { name: file.name, mimeType: file.type, dataUrl: reader.result } }));
    reader.readAsDataURL(file);
  };
  const postMediaUrl = (post) => post?.localMediaFileName ? `${API_BASE}/uploads/${encodeURIComponent(post.localMediaFileName)}` : '';
  const clientViews = [['calendar', 'Calendar'], ['reports', 'Reports'], ['chat', 'Chat'], ['support', 'Support']];
  const agendaDays = calendarCells.filter(Boolean).map((day) => ({ day, posts: postsForDay(day) })).filter((entry) => entry.posts.length);
  const openPost = (post, event) => { reviewTrigger.current = event.currentTarget; setSelectedPost(post); };
  const postButton = (post) => <button className="client-post-open" type="button" key={post.id} onClick={(event) => openPost(post, event)}><strong>{post.title || post.content}</strong><small>{latestReviewByPost[String(post.id)]?.status || 'Awaiting review'}</small></button>;

  return (
    <main className={cx('client-shell', theme === 'dark' && 'dark-mode')}>
      <aside className="client-sidebar">
        <img className="client-brand-logo" src="/brand/easten-social-logo.png" alt="EastenSocial" />
        <div className="client-identity"><span>{currentUser?.profileImageUrl ? <img src={currentUser.profileImageUrl} alt="" /> : String(currentUser?.name || 'C').slice(0, 1)}</span><strong>{currentUser?.name}</strong><small>Client reviewer</small></div>
        <nav>{[['calendar', 'Calendar'], ['reports', 'Reports'], ['chat', 'Chat'], ['support', 'Support']].map(([id, label]) => <button className={activeView === id ? 'active' : ''} type="button" key={id} onClick={() => setActiveView(id)}><ModuleIcon id={id === 'chat' ? 'messaging' : id === 'support' ? 'support' : id} /><span>{label}</span></button>)}</nav>
        <div className="client-sidebar-actions"><button type="button" onClick={() => setProfileOpen(true)}><ModuleIcon id="profiles" /><span>My Profile</span></button><button type="button" onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}><ModuleIcon id="theme" /><span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span></button><button className="client-logout" type="button" onClick={logout}><ModuleIcon id="logout" /><span>Log out</span></button></div>
      </aside>
      <section className="client-main">
        <header className="client-topbar"><div><p>CLIENT WORKSPACE</p><h1>{data.workspace?.name || 'Your approval dashboard'}</h1></div><div className="client-top-actions">{data.workspaces?.length > 0 && <select aria-label="Workspace" value={workspaceId} onChange={(event) => { setSelectedPost(null); loadDashboard(event.target.value); }}>{data.workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}</select>}<button className="role-icon-button" type="button" title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}>{theme === 'dark' ? <FiSun /> : <FiMoon />}</button><ProfileMenu user={currentUser} onSettings={() => setProfileOpen(true)} onLogout={logout} /></div></header>
        <Toast notice={notice} onDismiss={setNotice} dark={theme === 'dark'} />
        {isLoading && <Skeleton label="Loading client workspace" rows={5} />}
        {!isLoading && !data.assigned && <div className="client-empty"><span><ModuleIcon id="calendar" /></span><h2>Your client account is ready.</h2><p>Nothing will appear here until an agency owner assigns your email to a workspace. Share <strong>{currentUser?.email}</strong> with your agency.</p></div>}
        {!isLoading && data.assigned && activeView === 'calendar' && <>
          <div className="client-summary"><article><span>Scheduled</span><strong>{data.posts.filter((post) => post.status === 'scheduled').length}</strong></article><article><span>Awaiting review</span><strong>{data.posts.filter((post) => !latestReviewByPost[String(post.id)]).length}</strong></article><article><span>Approved</span><strong>{Object.values(latestReviewByPost).filter((review) => review.status === 'approved').length}</strong></article></div>
          <section className="client-calendar-panel">
            <div className="client-calendar-toolbar"><button type="button" aria-label="Previous month" title="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><FiChevronLeft /></button><h2>{month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h2><button type="button" aria-label="Next month" title="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><FiChevronRight /></button></div>
            <div className="client-calendar-grid">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <strong className="client-weekday" key={day}>{day}</strong>)}{calendarCells.map((day, index) => <div className="client-day" key={`${day || 'blank'}-${index}`}>{day && <span>{day}</span>}{day && postsForDay(day).map((post) => <article className={latestReviewByPost[String(post.id)]?.status || ''} key={post.id}>{postButton(post)}</article>)}</div>)}</div>
            <div className="client-month-agenda">{agendaDays.map(({ day, posts }) => <div key={day}><strong>{new Date(month.getFullYear(), month.getMonth(), day).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</strong>{posts.map(postButton)}</div>)}{!agendaDays.length && <div className="client-empty compact"><strong>No posts this month.</strong></div>}</div>
          </section>
        </>}
        {!isLoading && data.assigned && activeView === 'reports' && <section className="client-reports"><div className="client-section-head"><div><p>REPORT CENTER</p><h2>Last six months</h2></div><div className="client-report-switch"><button className={reportMode === 'progress' ? 'active' : ''} type="button" onClick={() => setReportMode('progress')}>Progress report</button><button className={reportMode === 'ads' ? 'active' : ''} type="button" onClick={() => setReportMode('ads')}>Ads report</button></div></div>{data.reports.length ? data.reports.map((report) => <article key={report.id}><header><div><strong>{new Date(`${report.month}-01T00:00:00Z`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</strong><small>Generated {new Date(report.generatedAt).toLocaleDateString('en-IN')}</small></div><em>{reportMode === 'ads' ? 'Ad performance' : `${report.metrics.connectedChannels || 0} channels`}</em></header>{reportMode === 'progress' ? <div className="client-report-metrics"><span><b>{report.metrics.published || 0}</b> Published</span><span><b>{report.metrics.scheduled || 0}</b> Scheduled</span><span><b>{report.metrics.approvals || 0}</b> Approved</span><span><b>{report.metrics.changesRequested || 0}</b> Changes</span></div> : <div className="client-report-metrics"><span><b>{report.metrics.adSpend || '—'}</b> Spend</span><span><b>{report.metrics.adReach || '—'}</b> Reach</span><span><b>{report.metrics.adLeads || '—'}</b> Leads</span><span><b>{report.metrics.adReturn || '—'}</b> Return</span></div>}<div className="client-rating"><select value={ratingDrafts[report.id]?.score || report.rating?.score || ''} onChange={(event) => setRatingDrafts((current) => ({ ...current, [report.id]: { ...current[report.id], score: event.target.value } }))}><option value="">Rate service</option>{[5, 4, 3, 2, 1].map((score) => <option key={score} value={score}>{score} / 5</option>)}</select><input value={ratingDrafts[report.id]?.suggestion ?? report.rating?.suggestion ?? ''} onChange={(event) => setRatingDrafts((current) => ({ ...current, [report.id]: { ...current[report.id], suggestion: event.target.value } }))} placeholder="Suggestion for your agency" /><button type="button" onClick={() => rateReport(report.id)}>Save feedback</button></div></article>) : <div className="client-empty compact"><strong>Your first monthly report will appear here after the month closes.</strong></div>}</section>}
        {!isLoading && data.assigned && activeView === 'chat' && <section className="client-chat"><div className="client-section-head"><div><p>DIRECT CHAT</p><h2>Talk to your agency</h2></div><span>{data.workspace?.contactHours || 'Agency response hours are not set.'}</span></div><div className="client-chat-thread">{chatMessages.map((item) => <article className={item.senderRole === 'client' ? 'mine' : ''} key={item.id}><div><strong>{item.senderName}</strong><small>{new Date(item.createdAt).toLocaleString('en-IN')}</small></div><p>{item.message}</p></article>)}{!chatMessages.length && <div className="client-empty compact"><strong>No messages yet.</strong><small>Start a conversation with your agency.</small></div>}</div><div className="client-chat-compose"><textarea maxLength="2000" value={chatDraft} onChange={(event) => setChatDraft(event.target.value)} placeholder="Write a message to your agency..." /><button type="button" disabled={!chatDraft.trim()} onClick={sendChatMessage}>Send message</button></div></section>}
        {!isLoading && data.assigned && activeView === 'support' && <section className="client-support"><div className="client-section-head"><div><p>SUPPORT</p><h2>Ask your agency for help</h2></div><span>{data.workspace?.contactHours || 'Agency response hours are not set.'}</span></div>{data.workspace?.cloudDataUrl && <a className="client-cloud-link" href={data.workspace.cloudDataUrl} target="_blank" rel="noreferrer">Access your Cloud Data</a>}<div className="client-support-grid"><div className="client-ticket-form"><label><span>Subject</span><input value={ticket.subject} onChange={(event) => setTicket((current) => ({ ...current, subject: event.target.value }))} /></label><label><span>Describe the issue</span><textarea value={ticket.message} onChange={(event) => setTicket((current) => ({ ...current, message: event.target.value }))} /></label><label><span>Screenshot (optional)</span><input type="file" accept="image/jpeg,image/png" onChange={selectTicketAttachment} /><small>{ticket.attachment?.name || 'JPG or PNG, maximum 2 MB'}</small></label><button type="button" disabled={!ticket.subject.trim() || !ticket.message.trim()} onClick={createTicket}>Create support ticket</button></div><div className="client-ticket-list">{data.tickets.map((item) => <article key={item._id}><strong>{item.subject}</strong><p>{item.message}</p>{item.attachment?.dataUrl && <a href={item.attachment.dataUrl} target="_blank" rel="noreferrer">View screenshot</a>}<span className={item.status}>{item.status}</span><small>{new Date(item.createdAt).toLocaleString('en-IN')}</small></article>)}{!data.tickets.length && <div className="client-empty compact"><strong>No support tickets yet.</strong></div>}</div></div></section>}
      </section>
      <nav className="client-quick-nav" aria-label="Client navigation">{clientViews.map(([id, label]) => <button type="button" key={id} className={activeView === id ? 'active' : ''} aria-current={activeView === id ? 'page' : undefined} onClick={() => { setActiveView(id); window.scrollTo({ top: 0 }); }}><ModuleIcon id={id === 'chat' ? 'messaging' : id} /><span>{label}</span></button>)}</nav>
      {selectedPost && <div className="client-post-modal" role="dialog" aria-modal="true" aria-label="Post preview" onClick={() => setSelectedPost(null)}><section ref={reviewDialog} onClick={(event) => event.stopPropagation()}><header><div><p>POST PREVIEW</p><h2>{selectedPost.title || 'Scheduled content'}</h2><small>{(selectedPost.platforms || []).join(', ') || 'No platform selected'}</small></div><button type="button" aria-label="Close preview" onClick={() => setSelectedPost(null)}><FiX /></button></header>{postMediaUrl(selectedPost) && (selectedPost.mediaType === 'video' ? <video className="client-post-media" src={postMediaUrl(selectedPost)} controls /> : <img className="client-post-media" src={postMediaUrl(selectedPost)} alt="Scheduled post" />)}<p className="client-post-copy">{selectedPost.content}</p><div className="client-review-form"><small>{latestReviewByPost[String(selectedPost.id)]?.status || 'Awaiting review'}</small><label><span>Comment or requested change</span><textarea rows={3} disabled={reviewPending} value={reviewDrafts[selectedPost.id] || ''} onChange={(event) => setReviewDrafts((current) => ({ ...current, [selectedPost.id]: event.target.value }))} /></label><div className="client-review-actions" aria-busy={reviewPending}><button type="button" disabled={reviewPending} onClick={() => reviewPost(selectedPost.id, 'approved')}>Approve</button><button type="button" disabled={reviewPending} onClick={() => reviewPost(selectedPost.id, 'changes')}>Request changes</button><button type="button" disabled={reviewPending} onClick={() => reviewPost(selectedPost.id, 'declined')}>Decline</button></div></div></section></div>}
      {profileOpen && <PasswordChangeModal forced={false} onClose={() => setProfileOpen(false)} onChanged={(user) => { setCurrentUser(user); setProfileOpen(false); setNotice({ type: 'success', message: 'Password changed successfully.' }); }} />}
    </main>
  );
}

function AuthPage({ mode, openAdmin, openDashboard, openLogin, openPublicSite, openSignup, setCurrentUser }) {
  const isSignup = mode === 'signup';
  const isAdminLogin = mode === 'admin-login';
  const [form, setForm] = useState({ name: '', email: '', password: '', plan: 'personal', accountType: 'agency' });
  const [recoveryStage, setRecoveryStage] = useState('');
  const [recovery, setRecovery] = useState({ email: '', code: '', newPassword: '', confirmPassword: '' });
  const [verification, setVerification] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const emailInvalid = isSignup && emailTouched && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updateRecovery = (key, value) => setRecovery((current) => ({ ...current, [key]: value }));
  const submitRecovery = async (event) => {
    event.preventDefault();
    setError('');
    try {
      setIsSubmitting(true);
      if (recoveryStage === 'request') {
        const response = await axios.post(`${API_BASE}/api/auth/forgot-password`, { email: recovery.email }, { withCredentials: true });
        setRecoveryStage('reset');
        setError(response.data.message);
        return;
      }
      if (recovery.newPassword !== recovery.confirmPassword) {
        setError('New passwords do not match.');
        return;
      }
      const response = await axios.post(`${API_BASE}/api/auth/reset-password`, {
        email: recovery.email,
        code: recovery.code,
        newPassword: recovery.newPassword
      }, { withCredentials: true });
      setRecoveryStage('');
      setRecovery({ email: '', code: '', newPassword: '', confirmPassword: '' });
      setError(response.data.message);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setEmailTouched(true);
    if (isSignup && !verification && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    try {
      setIsSubmitting(true);
      if (verification) {
        const response = await axios.post(`${API_BASE}/api/auth/verify-code`, {
          email: verification.email,
          purpose: verification.purpose,
          code: verificationCode
        }, { withCredentials: true });
        if (isAdminLogin && response.data.user?.role !== 'admin') {
          await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true }).catch(() => null);
          setCurrentUser(null);
          setVerification(null);
          setVerificationCode('');
          setError('This account is not authorized for the Admin Control Center. Use an administrator account.');
          return;
        }
        setCurrentUser(response.data.user);
        if (response.data.user?.role === 'admin') openAdmin();
        else openDashboard();
        return;
      }
      const endpoint = isSignup ? 'register' : 'login';
      const response = await axios.post(`${API_BASE}/api/auth/${endpoint}`, form, { withCredentials: true });
      if (response.data.verificationRequired) {
        setVerification({
          email: response.data.email,
          purpose: response.data.purpose,
          message: response.data.message,
          method: response.data.verificationMethod || 'email'
        });
        return;
      }
      if (isAdminLogin && response.data.user?.role !== 'admin') {
        await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true }).catch(() => null);
        setCurrentUser(null);
        setError('This account is not authorized for the Admin Control Center. Use an administrator account.');
        return;
      }
      setCurrentUser(response.data.user);
      if (response.data.user?.role === 'admin') openAdmin();
      else openDashboard();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewAs = (role) => {
    const user = role === 'admin'
      ? { id: 'local-admin', name: 'EastenSocial Admin', email: 'admin@local.preview', role: 'admin', plan: 'enterprise', channelLimit: 0, unlimitedChannels: true, usedChannels: 0, accountStatus: 'active' }
      : role === 'client' ? defaultClientUser : defaultAgencyUser;
    setCurrentUser(user);
    if (role === 'admin') openAdmin();
    else openDashboard();
  };

  return (
    <main className="auth-shell">
      <video className="auth-background-media" autoPlay muted loop playsInline poster="/brand/cosocial-product-demo.jpg" aria-hidden="true">
        <source src="/brand/cosocial-product-demo.mp4" type="video/mp4" />
      </video>
      <div className="auth-background-shade" aria-hidden="true" />
      <button className="site-logo auth-logo" type="button" onClick={openPublicSite}>
        <img src="/brand/easten-social-logo.png" alt="" />
        <strong>EastenSocial</strong>
      </button>
      <form className="auth-card" onSubmit={recoveryStage ? submitRecovery : submit}>
        {recoveryStage ? <>
          <p className="site-eyebrow">Secure account recovery</p>
          <h1>{recoveryStage === 'request' ? 'Reset your password' : 'Enter the reset code'}</h1>
          <p className="auth-verify-copy">{recoveryStage === 'request' ? 'We will send a six-digit code to your verified email.' : `Use the code sent to ${recovery.email}.`}</p>
          <label><span>Email</span><input type="email" value={recovery.email} onChange={(event) => updateRecovery('email', event.target.value)} disabled={recoveryStage === 'reset'} required /></label>
          {recoveryStage === 'reset' && <>
            <label><span>Reset code</span><input className="otp-input" inputMode="numeric" maxLength="6" pattern="[0-9]{6}" value={recovery.code} onChange={(event) => updateRecovery('code', event.target.value.replace(/\D/g, '').slice(0, 6))} required /></label>
            <label><span>New password</span><input type="password" autoComplete="new-password" placeholder="10+ characters with letters, number and symbol" value={recovery.newPassword} onChange={(event) => updateRecovery('newPassword', event.target.value)} required /></label>
            <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={recovery.confirmPassword} onChange={(event) => updateRecovery('confirmPassword', event.target.value)} required /></label>
          </>}
          {error && <p className={recoveryStage === 'reset' && error.startsWith('If this email') ? 'auth-info' : 'auth-error'}>{error}</p>}
          <button className="site-cta full-width" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Please wait...' : recoveryStage === 'request' ? 'Send reset code' : 'Save new password'}</button>
          <button className="auth-back-link" type="button" onClick={() => { setRecoveryStage(''); setError(''); }}>Back to login</button>
        </> : <>
        <p className="site-eyebrow">{verification ? (verification.method === 'totp' ? 'Two-factor authentication' : 'Verify your email') : isSignup ? 'Create account' : isAdminLogin ? 'Restricted administration' : 'Welcome back'}</p>
        <h1>{verification ? 'Enter your 6-digit code' : isSignup ? (form.accountType === 'client' ? 'Create your client account' : 'Start your agency workspace') : isAdminLogin ? 'Admin Control Center' : 'Login to EastenSocial'}</h1>
        {isAdminLogin && !verification && <p className="auth-verify-copy">Use the authorized EastenSocial administrator account. Agency and client accounts are blocked from this area.</p>}
        {verification ? <>
          <p className="auth-verify-copy">{verification.message}<br /><strong>{verification.email}</strong></p>
          <label>
            <span>Verification code</span>
            <input className="otp-input" inputMode="numeric" maxLength="6" pattern="[0-9]{6}" placeholder="000000" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))} required />
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button className="site-cta full-width" type="submit" disabled={isSubmitting || verificationCode.length !== 6}>{isSubmitting ? 'Verifying...' : 'Verify and continue'}</button>
          {verification.method !== 'totp' && <button className="site-login full-width" type="button" onClick={async () => {
            try {
              setError('');
              const response = await axios.post(`${API_BASE}/api/auth/resend-code`, verification, { withCredentials: true });
              setError(response.data.message);
            } catch (requestError) {
              setError(requestError.response?.data?.error || requestError.message);
            }
          }}>Send a new code</button>}
          <button className="auth-back-link" type="button" onClick={() => { setVerification(null); setVerificationCode(''); setError(''); }}>Back</button>
        </> : <>
        <label>
          <span>{isSignup ? 'Email' : 'Email / Username / Phone'}</span>
          <input aria-invalid={emailInvalid} autoComplete="username" placeholder={isSignup ? 'you@company.com' : 'Email, username or phone'} type={isSignup ? 'email' : 'text'} value={form.email} onBlur={() => setEmailTouched(true)} onChange={(event) => update('email', event.target.value)} required />
          {emailInvalid && <small className="field-error">Enter a valid email address.</small>}
        </label>
        <label className="auth-password-field">
          <span>Password</span>
          <div><input placeholder={isSignup ? '10+ characters with letters, number and symbol' : 'Your password'} type={showPassword ? 'text' : 'password'} value={form.password} onChange={(event) => update('password', event.target.value)} required /><button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)}><ModuleIcon id={showPassword ? 'eyeOff' : 'eye'} /></button></div>
        </label>
        {isSignup && <small className="auth-password-rule">Minimum 10 characters: at least 6 letters, uppercase, lowercase, a number and one symbol from @ # $ % &amp; * ( ) _</small>}
        {isSignup && (
          <label>
            <span>{form.accountType === 'client' ? 'Your name' : 'Company name'}</span>
            <input placeholder={form.accountType === 'client' ? 'Full name' : 'Agency or brand name'} value={form.name} onChange={(event) => update('name', event.target.value)} required />
          </label>
        )}
        {isSignup && <div className="auth-account-choice"><button className={form.accountType === 'agency' ? 'active' : ''} type="button" onClick={() => update('accountType', 'agency')}><strong>Agency owner</strong><span>Manage brands and publishing</span></button><button className={form.accountType === 'client' ? 'active' : ''} type="button" onClick={() => update('accountType', 'client')}><strong>Client reviewer</strong><span>Approvals, reports and support</span></button></div>}
        {isSignup && form.accountType === 'agency' && <div className="auth-plan-choice single"><button className="active" type="button"><strong>Grow</strong><span>5 channels · Free</span></button></div>}
        {error && <p className="auth-error">{error}</p>}
        <button className="site-cta full-width" type="submit" disabled={isSubmitting}>
          {isSubmitting && <span className="auth-button-spinner" aria-hidden="true" />}{isSubmitting ? 'Please wait...' : isSignup ? (form.accountType === 'client' ? 'Create client account' : 'Create free workspace') : isAdminLogin ? 'Secure admin login' : 'Log in'}
        </button>
        {!isAdminLogin && <button className="site-login full-width" type="button" onClick={isSignup ? openLogin : openSignup}>
          {isSignup ? 'Already have an account?' : 'New here? Sign up'}
        </button>}
        {isAdminLogin && <button className="site-login full-width" type="button" onClick={openLogin}>Agency or client login</button>}
        {!isSignup && <button className="auth-back-link" type="button" onClick={() => { setRecoveryStage('request'); setRecovery((current) => ({ ...current, email: form.email })); setError(''); }}>Forgot password?</button>}
        {isLocalPreview && <div className="local-preview-actions"><span>Local preview</span><button type="button" onClick={() => previewAs('agency')}>Agency dashboard</button><button type="button" onClick={() => previewAs('client')}>Client dashboard</button><button type="button" onClick={() => previewAs('admin')}>Admin panel</button></div>}
        </>}
        </>}
      </form>
      <PermissionSetup />
    </main>
  );
}

function PermissionSetup() {
  const [open, setOpen] = useState(() => {
    try {
      const installed = window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true
        || document.referrer.startsWith('android-app://');
      return installed && window.localStorage.getItem('cosocial-permission-guide-seen') !== '1';
    }
    catch { return false; }
  });
  const [micStatus, setMicStatus] = useState('');
  if (!open || isPublicHost) return null;

  const dismiss = () => {
    try { window.localStorage.setItem('cosocial-permission-guide-seen', '1'); } catch {}
    setOpen(false);
  };
  const checkMicrophone = async () => {
    setMicStatus('Waiting for microphone access...');
    const promptTimer = window.setTimeout(() => setMicStatus('Check the browser microphone prompt. If none appears, open site settings and allow microphone for this site.'), 8000);
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setMicStatus('Microphone needs HTTPS and a supported browser.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      dismiss();
    } catch (error) {
      setMicStatus(error.name === 'NotAllowedError'
        ? `Microphone is blocked for ${window.location.hostname}. Open browser site settings, allow microphone, then retry.`
        : error.name === 'NotFoundError' ? 'No microphone was found on this device.' : 'Microphone is unavailable. Check device settings and retry.');
    } finally {
      window.clearTimeout(promptTimer);
    }
  };

  return <div className="permission-guide-backdrop"><section className="permission-guide" role="dialog" aria-modal="true" aria-labelledby="permission-guide-title">
    <h2 id="permission-guide-title">Set up device access</h2>
    <p>Allow only what you use. You can change access later in your device settings.</p>
    <ul>
      <li><strong>Microphone</strong><span>Needed to record voice instructions for Create with AI.</span></li>
      <li><strong>Photos and videos</strong><span>Select files with your device picker when adding media. Full gallery access is not needed.</span></li>
      <li><strong>Notifications</strong><span>In-app alerts work without device access. Push notifications are not active yet.</span></li>
      <li><strong>Location</strong><span>Not needed for current features; we do not request it.</span></li>
    </ul>
    {micStatus && <p role="status" className="permission-guide-status">{micStatus}</p>}
    <div className="permission-guide-actions"><button type="button" className="site-login" onClick={dismiss}>Continue without microphone</button><button type="button" className="site-cta" onClick={checkMicrophone}>Allow microphone</button></div>
  </section></div>;
}

function PasswordChangeModal({ onChanged, onClose, forced = true }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    try {
      setSaving(true);
      const response = await axios.post(`${API_BASE}/api/auth/change-password`, {
        currentPassword,
        newPassword
      }, { withCredentials: true });
      onChanged(response.data.user);
      if (onClose) onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-modal-backdrop password-change-backdrop" role="presentation" onMouseDown={(event) => { if (!forced && event.target === event.currentTarget && onClose) onClose(); }}>
      <form className="auth-card password-change-card" onSubmit={submit}>
        {!forced && <button className="password-change-close" type="button" aria-label="Close password change" onClick={onClose}>x</button>}
        <p className="site-eyebrow">{forced ? 'First login security' : 'Account security'}</p>
        <h1>{forced ? 'Create your private password' : 'Change password'}</h1>
        <p className="auth-verify-copy">{forced ? 'The password shared with you is temporary. Change it before using the dashboard.' : 'Enter your current password, then choose a strong new password.'}</p>
        <label><span>{forced ? 'Temporary password' : 'Current password'}</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>
        <label><span>New password</span><input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="10+ characters with letters, number and symbol" required /></label>
        <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label>
        {error && <p className="auth-error">{error}</p>}
        <button className="site-cta full-width" type="submit" disabled={saving}>{saving ? 'Securing account...' : forced ? 'Change password and continue' : 'Save new password'}</button>
        {!forced && <button className="auth-back-link" type="button" onClick={() => { if (onClose) onClose(); window.location.hash = 'login'; }}>Forgot current password? Verify by email</button>}
      </form>
    </div>
  );
}

const homepageEditorGroups = {
  texts: [
    ['announcement', 'Announcement'], ['announcementCta', 'Announcement action'], ['signIn', 'Sign-in button'], ['getStarted', 'Header CTA'],
    ['heroBadge', 'Hero badge'], ['heroTitle', 'Hero heading'], ['heroDescription', 'Hero description'], ['primaryCta', 'Primary CTA'], ['secondaryCta', 'Secondary CTA'],
    ['stat1Value', 'Stat 1 value'], ['stat1Label', 'Stat 1 label'], ['stat2Value', 'Stat 2 value'], ['stat2Label', 'Stat 2 label'], ['stat3Value', 'Stat 3 value'], ['stat3Label', 'Stat 3 label'],
    ['trustedTitle', 'Partner strip heading'], ['featuresTitle', 'Features heading'], ['featuresDescription', 'Features description'],
    ['feature1Title', 'Feature 1 title'], ['feature1Description', 'Feature 1 description'], ['feature2Title', 'Feature 2 title'], ['feature2Description', 'Feature 2 description'], ['feature3Title', 'Feature 3 title'], ['feature3Description', 'Feature 3 description'],
    ['servicesTitle', 'Services heading'], ['servicesDescription', 'Services description'], ['aboutTitle', 'About heading'], ['aboutDescription', 'About description'],
    ['service1Title', 'Service 1 title'], ['service1Description', 'Service 1 description'], ['service2Title', 'Service 2 title'], ['service2Description', 'Service 2 description'], ['service3Title', 'Service 3 title'], ['service3Description', 'Service 3 description'],
    ['pricingTitle', 'Pricing heading'], ['pricingDescription', 'Pricing description'], ['rolesTitle', 'Roles heading'], ['rolesDescription', 'Roles description'],
    ['role1Title', 'Agency role title'], ['role1Subtitle', 'Agency role subtitle'], ['role1Description', 'Agency role description'], ['role2Title', 'Client role title'], ['role2Subtitle', 'Client role subtitle'], ['role2Description', 'Client role description'], ['role3Title', 'Admin role title'], ['role3Subtitle', 'Admin role subtitle'], ['role3Description', 'Admin role description'],
    ['stepsTitle', 'Workflow heading'], ['stepsDescription', 'Workflow description'], ['proofTitle', 'Proof heading'], ['proofDescription', 'Proof description'],
    ['step1Title', 'Step 1 title'], ['step1Description', 'Step 1 description'], ['step2Title', 'Step 2 title'], ['step2Description', 'Step 2 description'], ['step3Title', 'Step 3 title'], ['step3Description', 'Step 3 description'],
    ['proof1Title', 'Proof card 1 title'], ['proof1Description', 'Proof card 1 description'], ['proof2Title', 'Proof card 2 title'], ['proof2Description', 'Proof card 2 description'], ['proof3Title', 'Proof card 3 title'], ['proof3Description', 'Proof card 3 description'],
    ['guidesTitle', 'Guides heading'], ['guidesDescription', 'Guides description'], ['contactTitle', 'Contact heading'], ['contactDescription', 'Contact description'],
    ['guide1Kicker', 'Guide 1 label'], ['guide1Title', 'Guide 1 title'], ['guide1Description', 'Guide 1 description'], ['guide2Kicker', 'Guide 2 label'], ['guide2Title', 'Guide 2 title'], ['guide2Description', 'Guide 2 description'], ['guide3Kicker', 'Guide 3 label'], ['guide3Title', 'Guide 3 title'], ['guide3Description', 'Guide 3 description'],
    ['contactButton', 'Contact button'], ['footerDescription', 'Footer description'], ['footerNewsletterText', 'Newsletter description'], ['footerNewsletterButton', 'Newsletter button'], ['footerCopyright', 'Copyright line']
  ],
  links: [
    ['announcementCta', 'Announcement link'], ['signIn', 'Sign-in link'], ['getStarted', 'Header CTA link'], ['primaryCta', 'Primary CTA link'], ['secondaryCta', 'Secondary CTA link'],
    ['x', 'X link'], ['linkedin', 'LinkedIn link'], ['instagram', 'Instagram link'], ['youtube', 'YouTube link'],
    ['privacy', 'Privacy link'], ['terms', 'Terms link'], ['dataDeletion', 'Data deletion link'], ['legalTermination', 'Legal termination link'], ['userAgreement', 'User agreement link'], ['security', 'Security link']
  ],
  images: [
    ['role1', 'Agency role image'], ['role2', 'Client role image'], ['role3', 'Admin role image'], ['guide1', 'Guide image 1'], ['guide2', 'Guide image 2'], ['guide3', 'Guide image 3']
  ],
  icons: [['feature1', 'Feature icon 1'], ['feature2', 'Feature icon 2'], ['feature3', 'Feature icon 3'], ['contact', 'Contact icon']],
  sections: [
    ['trusted', 'Partner strip'], ['features', 'Feature highlights'], ['services', 'Services'], ['about', 'About'], ['pricing', 'Pricing'], ['roles', 'Role dashboards'],
    ['steps', 'Workflow steps'], ['proof', 'Customer value'], ['guides', 'Guides'], ['contact', 'Contact'], ['footer', 'Footer']
  ]
};

function readableHomepageField(key) {
  return String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function completeHomepageEditorFields(group, settings) {
  const knownFields = homepageEditorGroups[group] || [];
  const knownLabels = new Map(knownFields);
  const storedKeys = Object.keys(settings.homepage?.[group] || {});
  const allKeys = [...new Set([...knownFields.map(([key]) => key), ...storedKeys])];
  return allKeys.map((key) => [key, knownLabels.get(key) || readableHomepageField(key)]);
}

function normalizeIntegrationHealth(value) {
  if (value === true) return { ready: true, detail: 'Ready' };
  if (value === false || value == null) return { ready: false, detail: 'Setup required' };
  if (typeof value === 'string') return { ready: false, detail: value };
  if (typeof value !== 'object') return { ready: false, detail: 'Setup required' };

  const ready = Boolean(value.ready ?? value.configured);
  const detail = ready
    ? String(value.provider || value.status || 'Ready')
    : String(value.reason || value.provider || value.status || 'Setup required');
  return { ready, detail };
}

export function AdminDashboard({ currentUser, setCurrentUser, openPublicSite, logout }) {
  const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const hasAdminAccess = currentUser?.role === 'admin';
  const [users, setUsers] = useState(isLocalPreview ? demoAgencyUsers : []);
  const [selectedId, setSelectedId] = useState(isLocalPreview ? demoAgencyUsers[0].id : null);
  const [editor, setEditor] = useState(isLocalPreview ? demoAgencyUsers[0] : null);
  const [notice, setNotice] = useNotice();
  const [isLoading, setIsLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [settings, setSettings] = useState({
    brandName: 'EastenSocial',
    logoUrl: '/brand/easten-social-logo.png',
    supportEmail: 'support@eastensocial.com',
    homepage: { texts: {}, links: {}, images: {}, icons: {}, sections: {} },
    plans: pricingPlans.map((plan) => ({ ...plan, channelLimit: plan.id === 'personal' ? 5 : plan.id === 'agency' ? 30 : 100, unlimitedChannels: false, multipleWorkspaces: true }))
  });
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [adminSection, setAdminSection] = useState('overview');
  const [adminWorkspaces, setAdminWorkspaces] = useState([]);
  const [adminPosts, setAdminPosts] = useState([]);
  const [adminConnections, setAdminConnections] = useState([]);
  const [workspaceEditor, setWorkspaceEditor] = useState(null);
  const [postEditor, setPostEditor] = useState(null);
  const [adminConfirmAction, setAdminConfirmAction] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('cosocial-dashboard-theme') || 'light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => { localStorage.setItem('cosocial-dashboard-theme', theme); }, [theme]);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  const selectedUser = users.find((user) => user.id === selectedId) || users[0];

  useEffect(() => {
    if (!hasAdminAccess || currentUser?.role !== 'admin' || isLocalPreview) return undefined;
    let active = true;
    setIsLoading(true);
    Promise.all([
      axios.get(`${API_BASE}/api/admin/users`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/admin/support/tickets`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/admin/site-settings`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/health`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/admin/workspaces`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/admin/posts`, { withCredentials: true }),
      axios.get(`${API_BASE}/api/admin/connections`, { withCredentials: true })
    ])
      .then(([response, ticketResponse, settingsResponse, healthResponse, workspaceResponse, postResponse, connectionResponse]) => {
        if (!active) return;
        const liveUsers = response.data.users || [];
        setUsers(liveUsers);
        setTickets(ticketResponse.data.tickets || []);
        if (settingsResponse.data.settings) setSettings(settingsResponse.data.settings);
        setHealth(healthResponse.data);
        setAdminWorkspaces(workspaceResponse.data.workspaces || []);
        setAdminPosts(postResponse.data.posts || []);
        setAdminConnections(connectionResponse.data.connections || []);
        setSelectedId(liveUsers[0]?.id || null);
        setEditor(liveUsers[0] || null);
        if (!liveUsers.length) setNotice({ type: 'neutral', message: 'No user accounts have registered yet.' });
      })
      .catch((error) => {
        setUsers([]);
        setSelectedId(null);
        setEditor(null);
        setNotice(error.response?.data?.error || 'Live user list is currently unavailable.');
      })
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, [currentUser?.role, hasAdminAccess, isLocalPreview, setNotice]);

  useEffect(() => {
    if (selectedUser) setEditor({ ...selectedUser });
  }, [selectedId, selectedUser]);

  if (!hasAdminAccess) {
    return (
      <main className="admin-access-gate">
        <span>CO</span>
        <h1>Admin access required</h1>
        <p>This control center is available only to the EastenSocial administrator.</p>
        <button className="product-primary" type="button" onClick={openPublicSite}>Return home</button>
      </main>
    );
  }

  const usedTotal = users.reduce((sum, user) => sum + Number(user.usedChannels || 0), 0);
  const enterpriseCount = users.filter((user) => user.plan === 'enterprise').length;
  const suspendedCount = users.filter((user) => user.accountStatus === 'suspended').length;
  const pickUser = (user) => {
    setSelectedId(user.id);
    setEditor({ ...user });
    setNotice('');
    setTemporaryPassword('');
    setDeleteConfirmation('');
  };
  const jumpTo = (section) => {
    setAdminSection(section);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };
  const changePlan = (plan) => {
    const defaults = { personal: 5, agency: 30, enterprise: 100 };
    setEditor((current) => ({ ...current, plan, channelLimit: defaults[plan], unlimitedChannels: false }));
  };
  const saveAllowance = async () => {
    const updated = {
      ...editor,
      channelLimit: editor.unlimitedChannels ? 0 : Math.max(1, Number(editor.channelLimit || 1))
    };
    try {
      setIsLoading(true);
      if (!String(updated.id).startsWith('agency-')) {
        const response = await axios.patch(`${API_BASE}/api/admin/users/${updated.id}/allowance`, updated, { withCredentials: true });
        Object.assign(updated, response.data.user);
      }
      setUsers((current) => current.map((user) => (user.id === updated.id ? updated : user)));
      setEditor(updated);
      setNotice({ type: 'success', message: `${updated.name} allowance saved.` });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Allowance could not be saved.');
    } finally {
      setIsLoading(false);
    }
  };
  const resetUserPassword = async () => {
    try {
      setIsLoading(true);
      await axios.post(`${API_BASE}/api/admin/users/${editor.id}/reset-password`, { temporaryPassword }, { withCredentials: true });
      setTemporaryPassword('');
      setNotice({ type: 'success', message: `Temporary password set for ${editor.name}. They must change it at the next login.` });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Temporary password could not be set.');
    } finally {
      setIsLoading(false);
    }
  };
  const removeUserAccount = async () => {
    if (!editor || deleteConfirmation.trim().toLowerCase() !== String(editor.email || '').trim().toLowerCase()) {
      setNotice('Enter the selected user email exactly to confirm removal.');
      return;
    }
    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE}/api/admin/users/${editor.id}`, { data: { confirmEmail: deleteConfirmation }, withCredentials: true });
      const remaining = users.filter((user) => user.id !== editor.id);
      setUsers(remaining);
      setSelectedId(remaining[0]?.id || null);
      setEditor(remaining[0] || null);
      setDeleteConfirmation('');
      setNotice({ type: 'success', message: 'Account access removed and personal login data anonymized. Operational audit records were retained.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'User account could not be removed.');
    } finally {
      setIsLoading(false);
    }
  };
  const updatePlan = (planId, patch) => setSettings((current) => ({
    ...current,
    plans: (current.plans || []).map((plan) => plan.id === planId ? { ...plan, ...patch } : plan)
  }));
  const updateHomepage = (group, key, value) => setSettings((current) => ({
    ...current,
    homepage: {
      ...(current.homepage || {}),
      [group]: { ...(current.homepage?.[group] || {}), [key]: value }
    }
  }));
  const saveSiteSettings = async () => {
    try {
      setSettingsSaving(true);
      const response = await axios.put(`${API_BASE}/api/admin/site-settings`, settings, { withCredentials: true });
      setSettings(response.data.settings);
      setNotice({ type: 'success', message: 'Homepage, brand and plan settings saved.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Product settings could not be saved.');
    } finally {
      setSettingsSaving(false);
    }
  };
  const updateTicketStatus = async (ticket, status) => {
    try {
      const response = await axios.patch(`${API_BASE}/api/admin/support/tickets/${ticket._id}`, { status }, { withCredentials: true });
      setTickets((current) => current.map((item) => item._id === ticket._id ? response.data.ticket : item));
      setNotice({ type: 'success', message: `Ticket marked ${status}.` });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Ticket could not be updated.');
    }
  };
  const saveAdminWorkspace = async () => {
    if (!workspaceEditor) return;
    try {
      setIsLoading(true);
      const response = await axios.patch(`${API_BASE}/api/admin/workspaces/${workspaceEditor.id}`, workspaceEditor, { withCredentials: true });
      setAdminWorkspaces((current) => current.map((workspace) => workspace.id === workspaceEditor.id ? { ...workspace, ...response.data.workspace } : workspace));
      setWorkspaceEditor((current) => ({ ...current, ...response.data.workspace }));
      setNotice({ type: 'success', message: 'Workspace details saved.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Workspace could not be updated.');
    } finally {
      setIsLoading(false);
    }
  };
  const deleteAdminWorkspace = async () => {
    if (!workspaceEditor || window.prompt(`Type ${workspaceEditor.name} to permanently delete this workspace and its operational data.`) !== workspaceEditor.name) return;
    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE}/api/admin/workspaces/${workspaceEditor.id}`, { data: { confirmName: workspaceEditor.name }, withCredentials: true });
      setAdminWorkspaces((current) => current.filter((workspace) => workspace.id !== workspaceEditor.id));
      setAdminPosts((current) => current.filter((post) => String(post.workspace?.id || post.workspaceId) !== String(workspaceEditor.id)));
      setAdminConnections((current) => current.filter((connection) => String(connection.workspace?.id) !== String(workspaceEditor.id)));
      setWorkspaceEditor(null);
      setNotice({ type: 'success', message: 'Workspace and its associated operational data were permanently removed.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Workspace could not be removed.');
    } finally {
      setIsLoading(false);
    }
  };
  const saveAdminPost = async () => {
    if (!postEditor) return;
    try {
      setIsLoading(true);
      const response = await axios.patch(`${API_BASE}/api/admin/posts/${postEditor.id}`, postEditor, { withCredentials: true });
      setAdminPosts((current) => current.map((post) => String(post.id) === String(postEditor.id) ? { ...post, ...response.data.post } : post));
      setPostEditor((current) => ({ ...current, ...response.data.post }));
      setNotice({ type: 'success', message: 'Post record updated.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Post could not be updated.');
    } finally {
      setIsLoading(false);
    }
  };
  const deleteAdminPost = async () => {
    if (!postEditor) return;
    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE}/api/admin/posts/${postEditor.id}`, { data: { confirm: true }, withCredentials: true });
      setAdminPosts((current) => current.filter((post) => String(post.id) !== String(postEditor.id)));
      setPostEditor(null);
      setNotice({ type: 'success', message: 'Post record permanently deleted.' });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Post could not be deleted.');
    } finally {
      setIsLoading(false);
    }
  };
  const disconnectAdminConnection = async (connection) => {
    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE}/api/admin/connections/${connection.kind}/${connection.id}`, { data: { confirm: true }, withCredentials: true });
      setAdminConnections((current) => current.filter((item) => !(item.kind === connection.kind && item.id === connection.id)));
      setNotice({ type: 'success', message: `${connection.provider} connection removed.` });
    } catch (error) {
      setNotice(error.response?.data?.error || 'Connection could not be removed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main data-admin-section={adminSection} className={cx('control-admin', theme === 'dark' && 'dark-mode', mobileMenuOpen && 'role-menu-open')}>
      {mobileMenuOpen && <button className="role-menu-backdrop" aria-label="Close admin navigation" type="button" onClick={() => setMobileMenuOpen(false)} />}
      <aside className="control-sidebar" aria-label="Admin navigation">
        <button className="product-brand inverse-brand" type="button" onClick={openPublicSite}><img src="/brand/easten-social-logo.png" alt="EastenSocial" /></button>
        <div className="control-admin-label">ADMIN CONTROL</div>
        <button className={adminSection === 'overview' ? 'active' : ''} type="button" onClick={() => jumpTo('overview', 'admin-overview')}><ModuleIcon id="reports" /><span>Overview</span></button>
        <button className={adminSection === 'users' ? 'active' : ''} type="button" onClick={() => jumpTo('users', 'admin-users')}><ModuleIcon id="profiles" /><span>User accounts</span></button>
        <button className={adminSection === 'workspaces' ? 'active' : ''} type="button" onClick={() => jumpTo('workspaces', 'admin-workspaces')}><ModuleIcon id="manage" /><span>Workspaces</span></button>
        <button className={adminSection === 'content' ? 'active' : ''} type="button" onClick={() => jumpTo('content', 'admin-content')}><ModuleIcon id="post" /><span>Content</span></button>
        <button className={adminSection === 'connections' ? 'active' : ''} type="button" onClick={() => jumpTo('connections', 'admin-connections')}><ModuleIcon id="profiles" /><span>Connections</span></button>
        <button className={adminSection === 'website' ? 'active' : ''} type="button" onClick={() => jumpTo('website', 'admin-website')}><ModuleIcon id="manage" /><span>Website and plans</span></button>
        <button className={adminSection === 'support' ? 'active' : ''} type="button" onClick={() => jumpTo('support', 'admin-support')}><ModuleIcon id="support" /><span>Support queue</span></button>
        <button className={adminSection === 'health' ? 'active' : ''} type="button" onClick={() => jumpTo('health', 'admin-health')}><ModuleIcon id="profiles" /><span>Platform health</span></button>
        <div className="control-sidebar-bottom"><span>{currentUser?.name || 'EastenSocial Admin'}</span><small>{currentUser?.email || 'Administrator'}</small><button type="button" onClick={logout}>Log out</button></div>
      </aside>

      <section className="control-main">
        <header className="control-top"><button className="role-menu-toggle" aria-label="Open admin navigation" aria-expanded={mobileMenuOpen} type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}><FiMenu /></button><div><p>ADMIN PANEL</p><h1>EastenSocial Control Center</h1></div><div className="control-top-actions"><button className="role-icon-button" type="button" title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>{theme === 'dark' ? <FiSun /> : <FiMoon />}</button><button className="product-secondary" type="button" onClick={openPublicSite}>Public website</button><ProfileMenu user={currentUser} onSettings={() => setProfileOpen(true)} onLogout={logout} /></div></header>
        <Toast notice={notice} onDismiss={setNotice} dark={theme === 'dark'} />
        {isLoading && <Skeleton label="Loading admin data" />}

        <div className="control-stats" id="admin-overview" hidden={adminSection !== 'overview'}>
          <article><span>Registered users</span><strong>{users.length}</strong><small>{users.length - suspendedCount} active accounts</small></article>
          <article><span>Connected channels</span><strong>{usedTotal}</strong><small>Across all workspaces</small></article>
          <article><span>Enterprise accounts</span><strong>{enterpriseCount}</strong><small>Custom allowance</small></article>
          <article><span>Suspended</span><strong>{suspendedCount}</strong><small>Publishing disabled</small></article>
        </div>

        <section className="admin-health-strip" id="admin-health" hidden={!['overview', 'health'].includes(adminSection)}>
          <div><p>PLATFORM HEALTH</p><h2>Integration readiness</h2></div>
          <div className="admin-health-items">
            {Object.entries(health?.readiness || {}).map(([key, value]) => {
              const status = normalizeIntegrationHealth(value);
              return <span className={status.ready ? 'ready' : 'pending'} key={key}><b>{readableHomepageField(key)}</b><small>{status.detail}</small></span>;
            })}
            {!health && <span className="pending"><b>Loading integrations</b><small>Checking server configuration</small></span>}
          </div>
        </section>

        <div className="control-grid" id="admin-users" hidden={adminSection !== 'users'}>
          <section className="control-users">
            <div className="control-panel-head"><div><p>USERS</p><h2>Access and usage</h2></div><span>{isLoading ? 'Syncing...' : `${users.length} accounts`}</span></div>
            <div className="control-user-head"><span>Owner</span><span>Plan</span><span>Channels</span><span>Status</span></div>
            {users.map((user) => (
              <button className={user.id === selectedId ? 'selected' : ''} type="button" key={user.id} onClick={() => pickUser(user)}>
                <span className="control-owner"><i>{String(user.name || 'A').slice(0, 2).toUpperCase()}</i><b>{user.name}</b><small>{user.email}</small></span>
                <span className="control-plan">{user.role === 'client' ? 'client' : user.plan}</span>
                <span className="control-usage"><b>{user.usedChannels || 0} / {user.unlimitedChannels ? 'Unlimited' : user.channelLimit}</b><i><em style={{ width: `${user.unlimitedChannels ? 16 : Math.min(((user.usedChannels || 0) / Math.max(user.channelLimit || 1, 1)) * 100, 100)}%` }} /></i></span>
                <span className={user.accountStatus === 'suspended' ? 'status suspended' : 'status'}>{user.accountStatus}</span>
              </button>
            ))}
          </section>

          {editor && <aside className="allowance-editor">
            <div className="control-panel-head"><div><p>ALLOWANCE</p><h2>{editor.name}</h2></div><span>{editor.usedChannels || 0} in use</span></div>
            <label><span>Account name</span><input type="text" maxLength="80" value={editor.name || ''} onChange={(event) => setEditor((current) => ({ ...current, name: event.target.value }))} /></label>
            <label><span>Company / organization</span><input type="text" maxLength="120" value={editor.companyName || ''} onChange={(event) => setEditor((current) => ({ ...current, companyName: event.target.value }))} /></label>
            {editor.role !== 'client' && <><label><span>Plan</span><select value={editor.plan} onChange={(event) => changePlan(event.target.value)}><option value="personal">Grow - 5 channels</option><option value="agency">Pro - 30 channels</option><option value="enterprise">Business - 100 channels</option></select></label>
            <label><span>Channel limit</span><input min="1" max="100000" type="number" value={editor.unlimitedChannels ? '' : editor.channelLimit} disabled={editor.unlimitedChannels} onChange={(event) => setEditor((current) => ({ ...current, channelLimit: event.target.value }))} /></label>
            <label className="allowance-toggle"><span><b>Unlimited channels</b><small>Available for Enterprise accounts</small></span><input type="checkbox" checked={Boolean(editor.unlimitedChannels)} disabled={editor.plan !== 'enterprise'} onChange={(event) => setEditor((current) => ({ ...current, unlimitedChannels: event.target.checked }))} /></label></>}
            <label><span>Account status</span><select value={editor.accountStatus} onChange={(event) => setEditor((current) => ({ ...current, accountStatus: event.target.value }))}><option value="active">Active</option><option value="suspended">Suspended</option></select></label>
            <div className="allowance-summary"><span>{editor.role === 'client' ? 'Client access' : 'Publishing capacity'}</span><strong>{editor.role === 'client' ? 'Calendar, reports and support' : editor.unlimitedChannels ? 'Unlimited connected channels' : `${editor.channelLimit} connected channels`}</strong><small>{editor.role === 'client' ? 'Workspace access is assigned by an agency owner.' : 'The agency owner can add profiles only up to this allowance.'}</small></div>
            <button className="product-primary" type="button" disabled={isLoading} onClick={saveAllowance}>{isLoading ? 'Saving...' : 'Save allowance'}</button>
            {!String(editor.id).startsWith('agency-') && <div className="admin-password-reset">
              <label><span>Temporary password</span><input type="password" autoComplete="new-password" placeholder="10+ characters with letters, number and symbol" value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} /></label>
              <small>Current passwords are never visible. The user must replace this temporary password after signing in.</small>
              <button className="product-secondary" type="button" disabled={isLoading || !temporaryPassword} onClick={resetUserPassword}>Set temporary password</button>
            </div>}
            {!String(editor.id).startsWith('agency-') && <div className="admin-account-danger">
              <strong>Remove account access</strong>
              <small>This signs the user out permanently and anonymizes login data while retaining operational audit records.</small>
              <input value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} placeholder={`Type ${editor.email} to confirm`} />
              <button type="button" disabled={isLoading || deleteConfirmation.trim().toLowerCase() !== String(editor.email || '').trim().toLowerCase()} onClick={removeUserAccount}>Remove user account</button>
            </div>}
          </aside>}
        </div>

        <section className="admin-resource-section" id="admin-workspaces" hidden={adminSection !== 'workspaces'}>
          <div className="control-panel-head"><div><p>WORKSPACES</p><h2>Business workspaces</h2></div><span>{adminWorkspaces.length} total</span></div>
          <div className="admin-resource-layout">
            <div className="admin-resource-list">
              {adminWorkspaces.map((workspace) => <button className={workspaceEditor?.id === workspace.id ? 'selected' : ''} type="button" key={workspace.id} onClick={() => setWorkspaceEditor({ ...workspace })}>
                <span><strong>{workspace.name}</strong><small>{workspace.owner?.name || 'Unknown owner'} · {workspace.owner?.email || 'No email'}</small></span>
                <span><b>{workspace.counts?.connections || 0} channels</b><small>{workspace.counts?.posts || 0} posts · {workspace.counts?.clients || 0} clients</small></span>
              </button>)}
              {!adminWorkspaces.length && <div className="empty-box"><strong>No workspaces found</strong><small>Registered agency workspaces will appear here.</small></div>}
            </div>
            {workspaceEditor && <aside className="admin-resource-editor">
              <h3>Edit workspace</h3>
              <label><span>Name</span><input value={workspaceEditor.name || ''} onChange={(event) => setWorkspaceEditor((current) => ({ ...current, name: event.target.value }))} /></label>
              <label><span>Business type</span><input value={workspaceEditor.businessType || ''} onChange={(event) => setWorkspaceEditor((current) => ({ ...current, businessType: event.target.value }))} /></label>
              <label><span>Business description</span><textarea value={workspaceEditor.businessDescription || ''} onChange={(event) => setWorkspaceEditor((current) => ({ ...current, businessDescription: event.target.value }))} /></label>
              <label><span>Contact details</span><input value={workspaceEditor.contactDetails || ''} onChange={(event) => setWorkspaceEditor((current) => ({ ...current, contactDetails: event.target.value }))} /></label>
              <label><span>Website</span><input value={workspaceEditor.website || ''} onChange={(event) => setWorkspaceEditor((current) => ({ ...current, website: event.target.value }))} /></label>
              <div className="admin-resource-actions"><button className="product-primary" type="button" disabled={isLoading} onClick={saveAdminWorkspace}>Save workspace</button><button className="admin-delete-action" type="button" disabled={isLoading} onClick={deleteAdminWorkspace}>Delete workspace</button></div>
            </aside>}
          </div>
        </section>

        <section className="admin-resource-section" id="admin-content" hidden={adminSection !== 'content'}>
          <div className="control-panel-head"><div><p>CONTENT</p><h2>Post moderation</h2></div><span>{adminPosts.length} recent records</span></div>
          <div className="admin-resource-layout">
            <div className="admin-resource-list compact-list">
              {adminPosts.map((post) => <button className={String(postEditor?.id) === String(post.id) ? 'selected' : ''} type="button" key={String(post.id)} onClick={() => setPostEditor({ ...post })}>
                <span><strong>{post.title || String(post.content || '').slice(0, 70) || 'Untitled post'}</strong><small>{post.workspace?.name || 'Unknown workspace'} · {post.owner?.email || 'Unknown owner'}</small></span>
                <span><b>{post.status}</b><small>{new Date(post.createdAt).toLocaleString('en-IN')}</small></span>
              </button>)}
              {!adminPosts.length && <div className="empty-box"><strong>No post records</strong><small>Published, scheduled and draft posts will appear here.</small></div>}
            </div>
            {postEditor && <aside className="admin-resource-editor">
              <h3>Edit post record</h3>
              <label><span>Title</span><input maxLength="60" value={postEditor.title || ''} onChange={(event) => setPostEditor((current) => ({ ...current, title: event.target.value }))} /></label>
              <label><span>Description</span><textarea value={postEditor.content || ''} onChange={(event) => setPostEditor((current) => ({ ...current, content: event.target.value }))} /></label>
              <label><span>Status</span><select value={postEditor.status || 'pending'} onChange={(event) => setPostEditor((current) => ({ ...current, status: event.target.value }))}><option value="draft">Draft</option><option value="pending">Pending</option><option value="scheduled">Scheduled</option><option value="published">Published</option><option value="failed">Failed</option></select></label>
              <small>Changing a database record does not edit content that is already live on an external social platform.</small>
              <div className="admin-resource-actions"><button className="product-primary" type="button" disabled={isLoading} onClick={saveAdminPost}>Save post</button><button className="admin-delete-action" type="button" disabled={isLoading} onClick={() => setAdminConfirmAction({ type: 'post' })}>Delete record</button></div>
            </aside>}
          </div>
        </section>

        <section className="admin-resource-section" id="admin-connections" hidden={adminSection !== 'connections'}>
          <div className="control-panel-head"><div><p>CONNECTIONS</p><h2>Connected profiles</h2></div><span>{adminConnections.length} accounts</span></div>
          <div className="admin-connection-list">
            {adminConnections.map((connection) => <article key={`${connection.kind}-${connection.id}-${connection.accountId}`}>
              <i>{String(connection.provider || 'C').slice(0, 2).toUpperCase()}</i>
              <span><strong>{connection.label}</strong><small>{connection.provider} · {connection.workspace?.name || 'Unknown workspace'} · {connection.owner?.email || 'Unknown owner'}</small></span>
              <em>{connection.status}</em>
              <button type="button" disabled={isLoading} onClick={() => setAdminConfirmAction({ type: 'connection', connection })}>Disconnect</button>
            </article>)}
            {!adminConnections.length && <div className="empty-box"><strong>No connected profiles</strong><small>Sanitized connection metadata will appear here. Tokens are never exposed.</small></div>}
          </div>
        </section>

        <div className="admin-operations-grid" hidden={!['support', 'website'].includes(adminSection)}>
          <section className="admin-ticket-queue" id="admin-support" hidden={adminSection !== 'support'}>
            <div className="control-panel-head"><div><p>SUPPORT</p><h2>Ticket queue</h2></div><span>{tickets.filter((ticket) => ticket.status === 'open').length} open</span></div>
            <div className="admin-ticket-list">
              {tickets.map((ticket) => <article key={ticket._id}>
                <div><strong>{ticket.subject}</strong><small>{ticket.ownerId?.name || 'User'} · {ticket.workspaceId?.brandName || 'Workspace'} · {new Date(ticket.createdAt).toLocaleString('en-IN')}</small></div>
                <p>{ticket.message}</p>
                {ticket.attachment?.dataUrl && <a href={ticket.attachment.dataUrl} target="_blank" rel="noreferrer">View screenshot</a>}
                <button className={ticket.status === 'resolved' ? 'subtle-action' : 'resolve-action'} type="button" onClick={() => updateTicketStatus(ticket, ticket.status === 'resolved' ? 'open' : 'resolved')}>{ticket.status === 'resolved' ? 'Reopen' : 'Resolve'}</button>
              </article>)}
              {!tickets.length && <div className="empty-box"><strong>No support tickets</strong><small>New agency and client requests will appear here.</small></div>}
            </div>
          </section>

          <section className="admin-product-settings" id="admin-website" hidden={adminSection !== 'website'}>
            <div className="control-panel-head"><div><p>WHITE LABEL</p><h2>Brand and plans</h2></div><span>Database backed</span></div>
            <div className="admin-brand-fields">
              <label><span>Brand name</span><input value={settings.brandName || ''} onChange={(event) => setSettings((current) => ({ ...current, brandName: event.target.value }))} /></label>
              <label><span>Logo URL</span><input value={settings.logoUrl || ''} onChange={(event) => setSettings((current) => ({ ...current, logoUrl: event.target.value }))} /></label>
              <label><span>Support email</span><input type="email" value={settings.supportEmail || ''} onChange={(event) => setSettings((current) => ({ ...current, supportEmail: event.target.value }))} /></label>
            </div>
            <div className="admin-plan-editor">
              {(settings.plans || []).map((plan) => <article key={plan.id}>
                <header><strong>{plan.name}</strong><span>{plan.id}</span></header>
                <label><span>Display name</span><input value={plan.name || ''} onChange={(event) => updatePlan(plan.id, { name: event.target.value })} /></label>
                <label><span>Price label</span><input value={plan.price || ''} onChange={(event) => updatePlan(plan.id, { price: event.target.value })} /></label>
                <label><span>Channel limit</span><input type="number" min="0" max="100000" disabled={plan.unlimitedChannels} value={plan.unlimitedChannels ? '' : plan.channelLimit} onChange={(event) => updatePlan(plan.id, { channelLimit: event.target.value })} /></label>
                {plan.id === 'enterprise' && <label className="admin-check"><input type="checkbox" checked={Boolean(plan.unlimitedChannels)} onChange={(event) => updatePlan(plan.id, { unlimitedChannels: event.target.checked })} /><span>Unlimited channels</span></label>}
                <label><span>Features (one per line)</span><textarea value={(plan.features || []).join('\n')} onChange={(event) => updatePlan(plan.id, { features: event.target.value.split('\n') })} /></label>
              </article>)}
            </div>
            <div className="admin-homepage-editor">
              <div className="admin-editor-heading">
                <div><p>HOMEPAGE EDITOR</p><h3>Edit website content without touching code</h3></div>
                <small>Layout, responsiveness and security rules stay protected.</small>
              </div>
              <details open>
                <summary>Text content</summary>
                <div className="admin-editor-fields text-fields">
                  {completeHomepageEditorFields('texts', settings).map(([key, label]) => <label key={key}><span>{label}</span>{/Description|heading|Copyright/i.test(label) ? <textarea value={settings.homepage?.texts?.[key] || ''} onChange={(event) => updateHomepage('texts', key, event.target.value)} /> : <input value={settings.homepage?.texts?.[key] || ''} onChange={(event) => updateHomepage('texts', key, event.target.value)} />}</label>)}
                </div>
              </details>
              <details>
                <summary>Buttons and links</summary>
                <div className="admin-editor-fields">
                  {completeHomepageEditorFields('links', settings).map(([key, label]) => <label key={key}><span>{label}</span><input value={settings.homepage?.links?.[key] || ''} placeholder="https:// or #section" onChange={(event) => updateHomepage('links', key, event.target.value)} /></label>)}
                </div>
              </details>
              <details>
                <summary>Images and icons</summary>
                <div className="admin-editor-fields">
                  {completeHomepageEditorFields('images', settings).map(([key, label]) => <label key={key}><span>{label}</span><input value={settings.homepage?.images?.[key] || ''} placeholder="HTTPS image URL" onChange={(event) => updateHomepage('images', key, event.target.value)} /></label>)}
                  {completeHomepageEditorFields('icons', settings).map(([key, label]) => <label key={key}><span>{label}</span><input value={settings.homepage?.icons?.[key] || ''} placeholder="fa-solid fa-icon-name" onChange={(event) => updateHomepage('icons', key, event.target.value)} /></label>)}
                </div>
              </details>
              <details>
                <summary>Visible sections</summary>
                <div className="admin-section-switches">
                  {completeHomepageEditorFields('sections', settings).map(([key, label]) => <label key={key}><span>{label}</span><input type="checkbox" checked={settings.homepage?.sections?.[key] !== false} onChange={(event) => updateHomepage('sections', key, event.target.checked)} /></label>)}
                </div>
              </details>
            </div>
            <button className="product-primary" type="button" disabled={settingsSaving} onClick={saveSiteSettings}>{settingsSaving ? 'Saving...' : 'Save website settings'}</button>
          </section>
        </div>
      </section>
      {profileOpen && <PasswordChangeModal forced={false} onClose={() => setProfileOpen(false)} onChanged={(user) => { setCurrentUser(user); setProfileOpen(false); setNotice({ type: 'success', message: 'Password changed successfully.' }); }} />}
      {adminConfirmAction && <ConfirmDialog
        title={adminConfirmAction.type === 'post' ? 'Delete post record?' : 'Disconnect profile?'}
        message={adminConfirmAction.type === 'post' ? 'This permanently removes the saved record. A post already live on a social platform will remain there.' : `Disconnect ${adminConfirmAction.connection.label} from ${adminConfirmAction.connection.workspace?.name || 'this workspace'}?`}
        confirmLabel={adminConfirmAction.type === 'post' ? 'Delete record' : 'Disconnect'}
        onCancel={() => setAdminConfirmAction(null)}
        onConfirm={async () => { const action = adminConfirmAction; setAdminConfirmAction(null); if (action.type === 'post') await deleteAdminPost(); else await disconnectAdminConnection(action.connection); }}
      />}
    </main>
  );
}

function ModuleIcon({ id }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const icons = {
    home: <><path {...common} d="M3 11l9-8 9 8M5 10v10h14V10M9 20v-6h6v6" /></>,
    post: <path {...common} d="M4 17l4 3 12-14M4 7h8M4 12h6" />,
    manage: <path {...common} d="M5 5h14v14H5zM8 9h8M8 13h5" />,
    calendar: <path {...common} d="M7 3v4M17 3v4M4 9h16M5 5h14v15H5z" />,
    messaging: <path {...common} d="M4 5h16v11H8l-4 4zM8 9h8M8 13h5" />,
    reports: <path {...common} d="M5 19V9M10 19V5M15 19v-7M20 19H3" />,
    profiles: <path {...common} d="M8 10a4 4 0 118 0M4 20a8 8 0 0116 0" />,
    bulk: <path {...common} d="M6 4h9l3 3v13H6zM14 4v4h4M9 12h6M9 16h6" />,
    ads: <path {...common} d="M4 12h4l9-5v10l-9-5H4zM8 12v5" />,
    support: <><path {...common} d="M4 13v-2a8 8 0 0116 0v2M4 13h3v6H5a2 2 0 01-2-2v-2a2 2 0 012-2zM20 13h-3v6h2a2 2 0 002-2v-2a2 2 0 00-2-2zM17 19c-1 2-3 2-5 2" /></>,
    notifications: <><path {...common} d="M18 8a6 6 0 00-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></>,
    eye: <><path {...common} d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" /><circle {...common} cx="12" cy="12" r="2.5" /></>,
    eyeOff: <><path {...common} d="M3 3l18 18M10.5 6.2A11 11 0 0112 6c6.5 0 10 6 10 6a18 18 0 01-3 3.7M6.2 6.2C3.5 8 2 12 2 12s3.5 6 10 6a10 10 0 004.2-.9" /></>,
    theme: <><path {...common} d="M12 3a9 9 0 100 18 7 7 0 010-18z" /><path {...common} d="M12 3v18" /></>,
    logout: <><path {...common} d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" /></>
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{icons[id] || icons.post}</svg>;
}

function NotificationCenter({ notifications, onClose, onNavigate, onRefresh, workspaceId }) {
  const markRead = async (notification) => {
    try {
      if (!notification.readAt) {
        await axios.patch(`${API_BASE}/api/notifications/${notification._id}/read`, {}, {
          withCredentials: true,
          headers: { 'x-workspace-id': workspaceId }
        });
        await onRefresh();
      }
      const destination = String(notification.link || '').replace(/^#/, '');
      if (destination) onNavigate?.(destination === 'dashboard' ? 'home' : destination);
      onClose?.();
    } catch (error) {
      console.warn('Notification could not be marked read:', error.response?.data?.error || error.message);
    }
  };

  return (
    <section className="notification-popover" aria-label="Notifications">
      <header><div><strong>Notifications</strong><small>{notifications.filter((item) => !item.readAt).length} unread</small></div><button type="button" onClick={onClose} aria-label="Close notifications">x</button></header>
      <div className="notification-list">
        {notifications.map((item) => (
          <button className={cx('notification-item', !item.readAt && 'unread')} key={item._id} type="button" onClick={() => markRead(item)}>
            <i><ModuleIcon id={item.type === 'support' ? 'support' : item.type === 'calendar' ? 'calendar' : 'notifications'} /></i>
            <span><strong>{item.title}</strong><small>{item.message}</small><time>{new Date(item.createdAt).toLocaleString('en-IN')}</time></span>
          </button>
        ))}
        {!notifications.length && <div className="empty-box compact-empty"><strong>All caught up</strong><small>Approvals, tickets and account alerts will appear here.</small></div>}
      </div>
    </section>
  );
}

function PostNow(props) {
  const {
    instagramCollaborators,
    setInstagramCollaborators,
    activePlatforms,
    contentFormats,
    destinationOptions,
    isPosting,
    mediaMeta,
    mediaType,
    mediaAltText,
    message,
    firstComment,
    platformMessages,
    platforms: allPlatforms,
    previewDevice,
    previewText,
    scheduledAt,
    selectedVideoFile,
    selectedPlatforms,
    selectedDestinationKeys,
    setContentFormats,
    setMediaMeta,
    setMediaType,
    setMediaAltText,
    setMessage,
    setFirstComment,
    setPlatformMessages,
    setPreviewDevice,
    setScheduledAt,
    setSelectedVideoFile,
    togglePlatform,
    toggleDestination,
    postNow,
    setNotice,
    setTitle,
    title,
    uploadProgress,
    uploadSelectedMedia,
    mediaUploadError,
    retrySelectedMediaUpload,
    publishCover,
    setPublishCover,
    workspace,
    openWorkspaceSettings
  } = props;
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const aiRequestRef = useRef(null);
  const [aiWriterOpen, setAiWriterOpen] = useState(false);
  const [aiWriter, setAiWriter] = useState({
    selectedLength: 'Medium',
    tone: 'Professional',
    languages: ['English'],
    includeBusinessName: true,
    includeBusinessInfo: true,
    includeContactDetails: false,
    includeWebsite: false,
    postAbout: ''
  });
  const [aiOptions, setAiOptions] = useState([]);
  const [selectedAiOption, setSelectedAiOption] = useState(-1);
  const [aiPreview, setAiPreview] = useState('');
  const [activeCaptionPlatform, setActiveCaptionPlatform] = useState('shared');
  const [aiWriterError, setAiWriterError] = useState('');
  const [aiHistory, setAiHistory] = useState([]);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  const [mediaLimits, setMediaLimits] = useState(null);
  const [manualAiJob, setManualAiJob] = useState(null);
  const [externalMediaConsent, setExternalMediaConsent] = useState(false);
  const [manualAiRequiresConsent, setManualAiRequiresConsent] = useState(true);
  const [isPreparingAi, setIsPreparingAi] = useState(false);
  const [isGeneratingHashtags, setIsGeneratingHashtags] = useState(false);
  const [pendingMediaHashtags, setPendingMediaHashtags] = useState(false);
  const [previewThumbnailUrl, setPreviewThumbnailUrl] = useState('');
  const [selectedThumbnailFrame, setSelectedThumbnailFrame] = useState(null);
  const [customThumbnailFrame, setCustomThumbnailFrame] = useState(null);
  const [thumbnailGenerateOpen, setThumbnailGenerateOpen] = useState(false);
  const [thumbnailTitle, setThumbnailTitle] = useState('');
  const [thumbnailStyle, setThumbnailStyle] = useState('Professional');
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [isMediaDragging, setIsMediaDragging] = useState(false);
  const [draftSaveState, setDraftSaveState] = useState('Saved');
  const [showCaptionEmojiPicker, setShowCaptionEmojiPicker] = useState(false);
  const [isGeneratingAltText, setIsGeneratingAltText] = useState(false);
  const captionTextareaRef = useRef(null);
  const aiJobFileRef = useRef(null);
  const applyAiResultRef = useRef(false);
  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/api/manual-ai/config`, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } })
      .then(({ data }) => { if (active) setManualAiRequiresConsent(Boolean(data.requiresExternalMediaConsent)); })
      .catch(() => { if (active) setManualAiRequiresConsent(true); });
    return () => { active = false; };
  }, [workspace.id]);
  useEffect(() => {
    let active = true;
    setManualAiJob(null);
    setPreviewThumbnailUrl('');
    setSelectedThumbnailFrame(null);
    setCustomThumbnailFrame(null);
    setThumbnailGenerateOpen(false);
    setThumbnailTitle('');
    setThumbnailStyle('Professional');
    setPendingMediaHashtags(false);
    setIsGeneratingDescription(false);
    setExternalMediaConsent(false);
    aiJobFileRef.current = null;
    if (selectedVideoFile?.storedFileName && selectedVideoFile.storedWorkspaceId === workspace.id) {
      axios.get(`${API_BASE}/api/manual-ai/jobs/by-source/${encodeURIComponent(selectedVideoFile.storedFileName)}`, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } })
        .then(({ data }) => {
          if (!active) return;
          aiJobFileRef.current = selectedVideoFile;
          setManualAiJob(data);
          if (data.result?.thumbnailTitle) setThumbnailTitle(data.result.thumbnailTitle);
        }).catch(() => {});
    }
    return () => { active = false; };
  }, [selectedVideoFile, workspace.id]);
  useEffect(() => {
    if (!manualAiJob?.id || !['queued', 'processing'].includes(manualAiJob.status)) return undefined;
    let stopped = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}`, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
        if (stopped || aiJobFileRef.current !== selectedVideoFile) return;
        setManualAiJob(data);
        if (data.status === 'ready') {
          if (data.result?.thumbnailTitle) setThumbnailTitle(data.result.thumbnailTitle.split(/\s+/).slice(0, 8).join(' '));
          const bestFrame = data.frames?.[0];
          if (bestFrame) {
            const bestTimestamp = Number(data.result?.suggestedFrames?.[0]?.timestamp) || 0;
            setSelectedThumbnailFrame((current) => current || {
              url: bestFrame,
              sourceName: decodeURIComponent(new URL(bestFrame, API_BASE).pathname.split('/').pop() || ''),
              timestamp: bestTimestamp
            });
            setPreviewThumbnailUrl((current) => current || (/^https?:/i.test(bestFrame) ? bestFrame : `${API_BASE}${bestFrame}`));
          }
          const options = Array.isArray(data.result?.options) ? data.result.options : [];
          if (workspace.autoWriteAfterUpload) {
            const selected = options[0] || { title: data.result?.title, text: data.result?.caption };
            if (selected.title) setTitle(selected.title.slice(0, 60));
            if (data.result?.altText) setMediaAltText(data.result.altText);
            if (selected.text) {
              if (activeCaptionPlatform === 'shared') setMessage(selected.text);
              else setPlatformMessages((current) => ({ ...current, [activeCaptionPlatform]: selected.text }));
            }
            setPendingMediaHashtags(true);
            setAiWriterOpen(false);
            setNotice({ type: 'success', message: 'Title, caption and hashtags are being prepared automatically.' });
          } else if (applyAiResultRef.current && options.length) {
            setAiOptions(options);
            setSelectedAiOption(0);
            setAiPreview(options[0].text || '');
            setAiWriterOpen(true);
            setNotice({ type: 'success', message: 'Five AI options are ready. Select one to insert.' });
          } else if (applyAiResultRef.current) {
            setAiWriterError('The writing provider returned no selectable options. Retry generation.');
          }
          applyAiResultRef.current = false;
          setIsGeneratingDescription(false);
        } else if (['failed', 'cancelled'].includes(data.status)) {
          setIsGeneratingDescription(false);
          setAiWriterError(data.error || 'AI writing could not be completed. Retry when ready.');
        }
      } catch (error) {
        if (!stopped) setNotice({ type: 'error', message: error.response?.data?.error || 'AI analysis status could not be refreshed.' });
      }
    }, 2200);
    return () => { stopped = true; clearTimeout(timer); };
  }, [manualAiJob, workspace.id, workspace.autoWriteAfterUpload, selectedVideoFile, setNotice, setTitle, setMediaAltText, activeCaptionPlatform, setMessage, setPlatformMessages]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/media/limits`, { withCredentials: true }).then(({ data }) => { if (!cancelled) setMediaLimits(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    try {
      setAiHistory(JSON.parse(localStorage.getItem(`cosocial-ai-history-${workspace.id}`) || '[]'));
    } catch {
      setAiHistory([]);
    }
    setActiveCaptionPlatform('shared');
  }, [workspace.id]);
  const activeCaption = activeCaptionPlatform === 'shared' ? message : (platformMessages[activeCaptionPlatform] ?? message);
  const descriptionWords = activeCaption.trim() ? activeCaption.trim().split(/\s+/).length : 0;
  const formatOptions = [
    { value: 'text', label: 'Text only' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
    { value: 'reel', label: 'Reel' },
    { value: 'short', label: 'Short' },
    { value: 'story', label: 'Story' }
  ];
  const allowedFormats = mediaMeta.kind === 'image'
    ? ['image', 'story']
    : mediaMeta.kind === 'video'
      ? mediaMeta.orientation === 'horizontal' ? ['video', 'story'] : ['reel', 'short', 'story']
      : ['text'];

  useEffect(() => {
    if (!selectedVideoFile) {
      setMediaPreviewUrl('');
      return undefined;
    }
    if (selectedVideoFile.storedMediaUrl) {
      setMediaPreviewUrl(selectedVideoFile.storedMediaUrl);
      const media = selectedVideoFile.type.startsWith('video/') ? document.createElement('video') : new Image();
      const eventName = selectedVideoFile.type.startsWith('video/') ? 'loadedmetadata' : 'load';
      const onMetadata = () => {
        const width = media.videoWidth || media.naturalWidth || 0;
        const height = media.videoHeight || media.naturalHeight || 0;
        if (width && height) setMediaMeta((current) => ({ ...current, width, height }));
      };
      media.addEventListener(eventName, onMetadata);
      media.preload = 'metadata';
      media.src = selectedVideoFile.storedMediaUrl;
      return () => media.removeEventListener(eventName, onMetadata);
    }
    const url = URL.createObjectURL(selectedVideoFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedVideoFile, setMediaMeta]);

  const inspectMediaFile = (file, input = null) => {
    if (!file) return;
    const kind = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'unsupported';
    if (kind === 'unsupported') {
      setNotice({ type: 'warning', message: 'Please select a JPG, PNG, WEBP or video file.' });
      return;
    }
    const maxMb = mediaLimits?.postMaxMb || 1900;
    const maxBytes = maxMb * 1024 * 1024;
    if (kind === 'video' && file.size > maxBytes) {
      setNotice({ type: 'warning', message: `Video is too large. Maximum allowed size is ${maxMb} MB.` });
      if (input) input.value = '';
      return;
    }
    setSelectedVideoFile(file);
    const objectUrl = URL.createObjectURL(file);
    const finish = (width, height) => {
      const ratio = width / Math.max(height, 1);
      const orientation = ratio > 1.12 ? 'horizontal' : ratio < 0.88 ? 'vertical' : 'square';
      const defaults = kind === 'image'
        ? ['image']
        : orientation === 'horizontal' ? ['video'] : ['reel', 'short'];
      if (workspace.defaultStory) defaults.push('story');
      setMediaMeta({ kind, orientation, width, height, name: file.name });
      setMediaType(kind === 'image' ? 'image' : defaults[0]);
      setContentFormats(defaults);
      setNotice({ type: 'success', message: `${file.name} detected as ${orientation} ${kind}. Compatible formats are ready.` });
      URL.revokeObjectURL(objectUrl);
    };
    if (kind === 'image') {
      const image = new Image();
      image.onload = () => finish(image.naturalWidth, image.naturalHeight);
      image.onerror = () => finish(1, 1);
      image.src = objectUrl;
    } else {
      const video = document.createElement('video');
      video.onloadedmetadata = () => finish(video.videoWidth, video.videoHeight);
      video.onerror = () => finish(16, 9);
      video.preload = 'metadata';
      video.src = objectUrl;
    }
  };
  const inspectMedia = (event) => inspectMediaFile(event.target.files?.[0], event.target);
  const dropMedia = (event) => {
    event.preventDefault();
    setIsMediaDragging(false);
    inspectMediaFile(event.dataTransfer.files?.[0]);
  };
  const toggleFormat = (format) => {
    if (!allowedFormats.includes(format)) return;
    if (['reel', 'short'].includes(format) && mediaMeta.width && mediaMeta.height) {
      const ratio = mediaMeta.width / mediaMeta.height;
      if (Math.abs(ratio - 9 / 16) > 0.035) {
        setNotice({ type: 'warning', message: 'Reels and Shorts require a 9:16 vertical asset. Crop the media before publishing.' });
        return;
      }
    }
    if (format === 'story') {
      if (workspace.defaultStory) return;
      setContentFormats((current) => current.includes('story') ? current.filter((item) => item !== 'story') : [...current, 'story']);
      return;
    }
    const requiredFormat = mediaMeta.kind === 'image' ? 'image' : mediaMeta.orientation === 'horizontal' ? 'video' : null;
    if (requiredFormat === format) return;
    setContentFormats((current) => {
      const next = current.includes(format) ? current.filter((item) => item !== format) : [...current, format];
      const publishingFormats = next.filter((item) => item !== 'story');
      if (!publishingFormats.length) next.push(format);
      setMediaType(next.find((item) => item !== 'story') || 'story');
      return [...new Set(next)];
    });
  };
  const startManualAnalysis = async (applyText = false) => {
    if (!selectedVideoFile || isPreparingAi || ['queued', 'processing'].includes(manualAiJob?.status)) return;
    // Starting from Write with AI, Generate Hashtags, or the workspace auto-write
    // setting is the user's explicit request to analyse the selected media preview.
    const effectiveExternalMediaConsent = true;
    if (!externalMediaConsent) setExternalMediaConsent(true);
    applyAiResultRef.current = Boolean(applyText || workspace.autoWriteAfterUpload);
    setIsPreparingAi(true);
    if (applyAiResultRef.current) setIsGeneratingDescription(true);
    aiJobFileRef.current = selectedVideoFile;
    try {
      const upload = await uploadSelectedMedia();
      if (aiJobFileRef.current !== selectedVideoFile) return;
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs`, {
        fileName: upload.fileName,
        instruction: aiWriter.postAbout.trim() || title.trim(),
        tone: aiWriter.tone,
        length: aiWriter.selectedLength,
        languages: aiWriter.languages,
        includeBusinessName: Boolean(aiWriter.includeBusinessName),
        includeBusinessInfo: Boolean(aiWriter.includeBusinessInfo),
        includeContactDetails: Boolean(aiWriter.includeContactDetails),
        includeWebsite: Boolean(aiWriter.includeWebsite),
        externalMediaConsent: effectiveExternalMediaConsent,
        platforms: activePlatforms.map((platform) => platform.id)
      }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setManualAiJob(data);
    } catch (error) {
      setIsGeneratingDescription(false);
      setNotice({ type: 'error', message: error.response?.data?.error || 'Could not start media analysis. Original upload is kept for retry.' });
    } finally { setIsPreparingAi(false); }
  };
  useEffect(() => {
    if (!workspace.autoWriteAfterUpload || !selectedVideoFile || uploadProgress !== 100 || manualAiJob?.id || isPreparingAi) return;
    startManualAnalysis(true);
    // Auto analysis begins once per selected upload only when the workspace opted in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoFile, uploadProgress, manualAiJob?.id, isPreparingAi, workspace.autoWriteAfterUpload]);
  const retryManualAnalysis = async () => {
    if (!manualAiJob?.id) return startManualAnalysis();
    try {
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/retry`, {}, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setManualAiJob(data);
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Could not retry AI analysis.' }); }
  };
  const cancelManualAnalysis = async () => {
    if (!manualAiJob?.id) return;
    try {
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/cancel`, {}, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setManualAiJob(data);
      setIsGeneratingDescription(false);
      setAiWriterError('Generation cancelled. Your uploaded original is still safe.');
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Could not cancel analysis.' }); }
  };
  const uploadCustomThumbnail = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedVideoFile) return;
    const form = new FormData();
    form.append('file', file);
    try {
      if (manualAiJob?.id) {
        const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/custom-thumbnail`, form, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
        const next = { url: data.imageUrl, sourceName: frameSourceName(data.imageUrl), timestamp: 0 };
        setSelectedThumbnailFrame(next);
        setPreviewThumbnailUrl(manualAiAssetUrl(data.imageUrl));
        setCustomThumbnailFrame(next);
        await attachThumbnailSource(next.sourceName, data.imageUrl);
        setNotice({ type: 'success', message: 'Custom thumbnail uploaded and selected.' });
        event.target.value = '';
        return;
      }
      const upload = await uploadSelectedMedia();
      form.append('fileName', upload.fileName);
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/custom-thumbnail`, form, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setPublishCover({ fileName: data.coverFileName, sourceFile: selectedVideoFile, workspaceId: workspace.id });
      const customUrl = URL.createObjectURL(file);
      setCustomThumbnailFrame({ url: customUrl, sourceName: '', timestamp: 0 });
      setSelectedThumbnailFrame({ url: customUrl, sourceName: '', timestamp: 0 });
      setPreviewThumbnailUrl(customUrl);
      setNotice({ type: 'success', message: 'Video thumbnail uploaded and attached to this post.' });
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Custom thumbnail upload failed.' }); }
    event.target.value = '';
  };
  const manualAiAssetUrl = (value) => !value ? '' : /^(https?:|blob:|data:)/i.test(value) ? value : `${API_BASE}${value}`;
  const frameSourceName = (value) => decodeURIComponent(new URL(value, API_BASE).pathname.split('/').pop() || '');
  const chooseThumbnailFrame = (url, timestamp = 0) => {
    setSelectedThumbnailFrame({ url, sourceName: frameSourceName(url), timestamp });
    setPreviewThumbnailUrl(manualAiAssetUrl(url));
  };
  const attachThumbnailSource = async (sourceName, imageUrl) => {
    const target = mediaType === 'short' ? 'youtube' : mediaType === 'reel' || mediaType === 'story' ? 'reel' : 'feed';
    const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/publish-thumbnail`, { sourceName, target }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
    setPublishCover({ fileName: data.coverFileName, sourceFile: selectedVideoFile, workspaceId: workspace.id });
    setPreviewThumbnailUrl(manualAiAssetUrl(imageUrl || data.coverUrl));
  };
  const useSelectedFrame = async () => {
    if (customThumbnailFrame?.url === selectedThumbnailFrame?.url && publishCover?.sourceFile === selectedVideoFile) {
      setNotice({ type: 'success', message: 'Uploaded thumbnail is selected.' });
      return;
    }
    if (!selectedThumbnailFrame?.sourceName || !manualAiJob?.id) return;
    try {
      await attachThumbnailSource(selectedThumbnailFrame.sourceName, selectedThumbnailFrame.url);
      setNotice({ type: 'success', message: 'Selected original-video frame is ready as the post thumbnail.' });
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Selected frame could not be attached.' }); }
  };
  const generateHiggsfieldThumbnail = async () => {
    if (!manualAiJob?.id) { setNotice({ type: 'neutral', message: 'Thumbnail frames are still being prepared.' }); return; }
    if (!selectedThumbnailFrame?.sourceName) { setNotice({ type: 'warning', message: 'Select a suggested frame or upload a thumbnail first.' }); return; }
    const cleanTitle = thumbnailTitle.trim().split(/\s+/).slice(0, 8).join(' ');
    if (!cleanTitle && !manualAiRequiresConsent) { setNotice({ type: 'warning', message: 'Add a thumbnail title of up to eight words.' }); return; }
    setIsGeneratingThumbnail(true);
    try {
      const target = mediaType === 'short' ? 'youtube' : mediaType === 'reel' || mediaType === 'story' ? 'reel' : 'feed';
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/generate-thumbnail`, {
        sourceName: selectedThumbnailFrame.sourceName, title: cleanTitle, target,
        generationMode: 'creative', designStyle: thumbnailStyle
      }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setManualAiJob((current) => ({ ...current, generated: [...new Set([...(current?.generated || []), data.imageUrl])] }));
      setSelectedThumbnailFrame({ url: data.imageUrl, sourceName: data.sourceName, timestamp: selectedThumbnailFrame.timestamp || 0 });
      await attachThumbnailSource(data.sourceName, data.imageUrl);
      setThumbnailGenerateOpen(false);
      setNotice({ type: data.fallback ? 'warning' : 'success', message: data.fallback ? 'Image editing is unavailable. A thumbnail was made from your original frame with the selected title.' : data.model?.startsWith('gemini-') ? 'Gemini thumbnail ready. Check the subject and title before using it.' : 'Thumbnail ready. Review it before publishing.' });
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'AI thumbnail could not be generated.' }); }
    finally { setIsGeneratingThumbnail(false); }
  };
  const createAutoDescription = async () => {
    const missing = [];
    const businessType = workspace.businessType === 'Custom' ? workspace.customBusinessType : workspace.businessType;
    if (aiWriter.includeBusinessName && !workspace.name?.trim()) missing.push('Business name');
    if (aiWriter.includeBusinessInfo && (!businessType?.trim() || !workspace.businessDescription?.trim())) missing.push('Business type and description');
    if (aiWriter.includeContactDetails && ![workspace.contactNumber, workspace.contactEmail, workspace.address, workspace.contactDetails].some((value) => value?.trim())) missing.push('Contact details');
    if (aiWriter.includeWebsite && !workspace.website?.trim()) missing.push('Website');
    if (!aiWriter.languages.length) missing.push('At least one language');
    if (!title.trim() && !message.trim() && !aiWriter.postAbout.trim() && !selectedVideoFile) missing.push('Post idea or media');
    if (missing.length) {
      setAiWriterError(`Required: ${missing.join(', ')}.${missing.some((item) => ['Business name', 'Business type and description', 'Contact details', 'Website'].includes(item)) ? ' Add missing details in My Profile > Workspace.' : ''}`);
      return;
    }
    if (selectedVideoFile) {
      if (manualAiJob?.status === 'ready' && !(selectedVideoFile.type.startsWith('video/') && manualAiJob.result?.audioIntegrated !== true)) {
        setIsGeneratingDescription(true);
        setAiWriterError('');
        try {
          const { data } = await axios.post(`${API_BASE}/api/manual-ai/jobs/${manualAiJob.id}/text`, {
            instruction: aiWriter.postAbout.trim() || title.trim(), existingText: currentCaption.trim(),
            length: aiWriter.selectedLength, languages: aiWriter.languages,
            includeBusinessName: Boolean(aiWriter.includeBusinessName),
            includeBusinessInfo: Boolean(aiWriter.includeBusinessInfo),
            includeContactDetails: Boolean(aiWriter.includeContactDetails),
            includeWebsite: Boolean(aiWriter.includeWebsite)
          }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
          const options = Array.isArray(data.result?.options) ? data.result.options : [];
          if (!options.length) throw new Error('The writing provider returned no selectable options.');
          setManualAiJob(data);
          setAiOptions(options);
          setSelectedAiOption(0);
          setAiPreview(options[0].text || '');
          setNotice({ type: 'success', message: 'Five AI options are ready. Select one to insert.' });
        } catch (error) {
          const messageText = error.response?.data?.error || error.message || 'AI descriptions could not be generated. Please try again.';
          setAiWriterError(messageText);
          setNotice({ type: 'error', message: messageText });
        } finally { setIsGeneratingDescription(false); }
      } else {
        // Clicking Generate is the user's explicit approval to analyse this selected media.
        await startManualAnalysis(true);
      }
      return;
    }
    const formData = new FormData();
    if (selectedVideoFile) formData.append('mediaFile', selectedVideoFile);
    if (mediaLimits && selectedVideoFile?.size > mediaLimits.aiMaxMb * 1024 * 1024) {
      setAiWriterError(`AI media limit is ${mediaLimits.aiMaxMb} MB. Select a smaller file.`);
      return;
    }
    formData.append('roughTitle', title.trim());
    formData.append('roughDescription', currentCaption.trim());
    formData.append('postAboutText', aiWriter.postAbout.trim());
    formData.append('length', aiWriter.selectedLength);
    formData.append('businessName', workspace.name || '');
    formData.append('businessInfo', [businessType, workspace.businessDescription].filter(Boolean).join('\n'));
    formData.append('contactDetails', [workspace.contactNumber, workspace.contactEmail, workspace.address, workspace.contactDetails].filter(Boolean).join('\n'));
    formData.append('websiteUrl', workspace.website || '');
    formData.append('tone', aiWriter.tone);
    formData.append('selectedLanguages', JSON.stringify(aiWriter.languages));
    formData.append('selectedPlatforms', JSON.stringify(activePlatforms.map((platform) => platform.label || platform.id)));
    formData.append('includeBusinessName', String(aiWriter.includeBusinessName));
    formData.append('includeBusinessInfo', String(aiWriter.includeBusinessInfo));
    formData.append('includeContactDetails', String(aiWriter.includeContactDetails));
    formData.append('includeWebsite', String(aiWriter.includeWebsite));

    setIsGeneratingDescription(true);
    aiRequestRef.current?.abort();
    const controller = new AbortController();
    aiRequestRef.current = controller;
    setAiWriterError('');
    setAiOptions([]);
    setSelectedAiOption(-1);
    try {
      const response = await axios.post(`${API_BASE}/api/auto-description`, formData, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspace.id },
        signal: controller.signal
      });
      const suggestions = response.data.suggestions || (response.data.options || []).map((text) => ({ title: '', text }));
      setAiOptions(suggestions);
      setSelectedAiOption(suggestions.length ? 0 : -1);
      setAiPreview(suggestions[0]?.text || '');
      const historyItem = { id: Date.now(), brief: aiWriter.postAbout || title || 'Media-based caption', tone: aiWriter.tone, length: aiWriter.selectedLength };
      setAiHistory((current) => {
        const next = [historyItem, ...current].slice(0, 8);
        localStorage.setItem(`cosocial-ai-history-${workspace.id}`, JSON.stringify(next));
        return next;
      });
      props.onAiGenerated?.();
      setNotice({ type: 'success', message: 'Five AI descriptions are ready. Select one and insert it.' });
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        setAiWriterError('Generation cancelled. Your post content is unchanged.');
        return;
      }
      const messageText = error.response?.data?.error || 'AI descriptions could not be generated. Please try again.';
      setAiWriterError(messageText);
      setNotice({ type: 'error', message: messageText });
    } finally {
      if (aiRequestRef.current === controller) aiRequestRef.current = null;
      setIsGeneratingDescription(false);
    }
  };
  const createAutoHashtags = async () => {
    if (selectedVideoFile && manualAiJob?.status !== 'ready') {
      setPendingMediaHashtags(true);
      if (!manualAiJob || ['failed', 'cancelled'].includes(manualAiJob.status)) await (manualAiJob ? retryManualAnalysis() : startManualAnalysis(false));
      setNotice({ type: 'neutral', message: 'Media analysis is preparing. Hashtags will be added when ready.' });
      return;
    }
    setIsGeneratingHashtags(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/hashtags`, {
        title, caption: currentCaption || title,
        jobId: manualAiJob?.status === 'ready' ? manualAiJob.id : undefined
      }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      const cleanCaption = currentCaption.replace(/(?:\s*#[\p{L}\p{N}_]+)+\s*$/gu, '').trimEnd();
      setCurrentCaption(`${cleanCaption}${cleanCaption ? '\n\n' : ''}${data.hashtags.join(' ')}`);
      setNotice({ type: 'success', message: `${data.hashtags.length} contextual hashtags added. You can edit them.` });
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Hashtags could not be generated.' }); }
    finally { setIsGeneratingHashtags(false); }
  };

  const toggleAiLanguage = (language) => setAiWriter((current) => {
    if (current.languages.includes(language)) return { ...current, languages: current.languages.filter((item) => item !== language) };
    if (current.languages.length >= 3) {
      setAiWriterError('Select a maximum of 3 languages.');
      return current;
    }
    setAiWriterError('');
    return { ...current, languages: [...current.languages, language] };
  });
  const insertAiDescription = () => {
    const selected = aiOptions[selectedAiOption];
    if (!selected || !aiPreview.trim()) return;
    if (activeCaptionPlatform === 'shared') setMessage(aiPreview.trim());
    else setPlatformMessages((current) => ({ ...current, [activeCaptionPlatform]: aiPreview.trim() }));
    if (selected.title) setTitle(selected.title.slice(0, 60));
    if (manualAiJob?.result?.altText) setMediaAltText(manualAiJob.result.altText);
    setAiWriterOpen(false);
    setNotice({ type: 'success', message: 'Selected AI description inserted.' });
  };
  const currentCaption = activeCaptionPlatform === 'shared' ? message : (platformMessages[activeCaptionPlatform] ?? message);
  const captionForPendingHashtags = useRef('');
  captionForPendingHashtags.current = currentCaption || title;
  const pendingHashtagRequest = useRef('');
  const setCurrentCaption = (value) => {
    if (activeCaptionPlatform === 'shared') setMessage(value);
    else setPlatformMessages((current) => ({ ...current, [activeCaptionPlatform]: value }));
  };
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`easten-manual-draft-${workspace.id}`) || 'null');
      if (!saved) return;
      setTitle(String(saved.title || '').slice(0, 60));
      setMessage(String(saved.message || ''));
      setPlatformMessages(saved.platformMessages && typeof saved.platformMessages === 'object' ? saved.platformMessages : {});
      setMediaAltText(String(saved.mediaAltText || '').slice(0, 1000));
      setFirstComment(String(saved.firstComment || '').slice(0, 2000));
      setInstagramCollaborators(typeof saved.instagramCollaborators === 'string' ? saved.instagramCollaborators.slice(0, 200) : (Array.isArray(saved.instagramCollaborators) ? saved.instagramCollaborators.join(', ').slice(0, 200) : ''));
      setDraftSaveState('Restored');
    } catch {
      localStorage.removeItem(`easten-manual-draft-${workspace.id}`);
    }
  }, [workspace.id, setFirstComment, setInstagramCollaborators, setMediaAltText, setMessage, setPlatformMessages, setTitle]);
  useEffect(() => {
    const storageKey = `easten-manual-draft-${workspace.id}`;
    const hasCopy = Boolean(title.trim() || message.trim() || Object.values(platformMessages).some((value) => String(value || '').trim()) || mediaAltText.trim() || firstComment.trim() || instagramCollaborators.trim());
    if (!hasCopy) {
      localStorage.removeItem(storageKey);
      setDraftSaveState('Saved');
      return undefined;
    }
    setDraftSaveState('Saving...');
    const timer = setTimeout(() => {
      localStorage.setItem(storageKey, JSON.stringify({ title, message, platformMessages, mediaAltText, firstComment, instagramCollaborators, savedAt: new Date().toISOString() }));
      setDraftSaveState('Saved ✓');
    }, 10000);
    return () => clearTimeout(timer);
  }, [workspace.id, title, message, platformMessages, mediaAltText, firstComment, instagramCollaborators]);
  const insertCaptionText = (text) => {
    const element = captionTextareaRef.current;
    const start = element?.selectionStart ?? currentCaption.length;
    const end = element?.selectionEnd ?? start;
    const next = `${currentCaption.slice(0, start)}${text}${currentCaption.slice(end)}`;
    setCurrentCaption(next);
    window.requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(start + text.length, start + text.length);
    });
  };
  const generateAltText = async () => {
    if (!selectedVideoFile && !title.trim() && !currentCaption.trim()) {
      setNotice({ type: 'warning', message: 'Add media or post copy before generating alt text.' });
      return;
    }
    setIsGeneratingAltText(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/manual-ai/alt-text`, {
        jobId: manualAiJob?.id || '', title, caption: currentCaption
      }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } });
      setMediaAltText(data.altText || '');
      setNotice({ type: 'success', message: 'Accessible alt text generated. Review it before publishing.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Alt text could not be generated.' });
    } finally {
      setIsGeneratingAltText(false);
    }
  };
  useEffect(() => {
    if (!pendingMediaHashtags || manualAiJob?.status !== 'ready' || pendingHashtagRequest.current === manualAiJob.id) return undefined;
    let cancelled = false;
    pendingHashtagRequest.current = manualAiJob.id;
    setIsGeneratingHashtags(true);
    axios.post(`${API_BASE}/api/manual-ai/hashtags`, {
      title, caption: captionForPendingHashtags.current, jobId: manualAiJob.id
    }, { withCredentials: true, headers: { 'x-workspace-id': workspace.id } }).then(({ data }) => {
      if (cancelled) return;
      const append = (caption) => {
        const clean = String(caption || '').replace(/(?:\s*#[\p{L}\p{N}_]+)+\s*$/gu, '').trimEnd();
        return `${clean}${clean ? '\n\n' : ''}${data.hashtags.join(' ')}`;
      };
      if (activeCaptionPlatform === 'shared') setMessage(append);
      else setPlatformMessages((current) => ({ ...current, [activeCaptionPlatform]: append(current[activeCaptionPlatform] ?? captionForPendingHashtags.current) }));
      setNotice({ type: 'success', message: 'Media-aware hashtags added. You can edit them.' });
    }).catch((error) => {
      if (!cancelled) setNotice({ type: 'error', message: error.response?.data?.error || 'Media-aware hashtags could not be generated.' });
    }).finally(() => {
      pendingHashtagRequest.current = '';
      if (!cancelled) { setPendingMediaHashtags(false); setIsGeneratingHashtags(false); }
    });
    return () => { cancelled = true; };
  }, [pendingMediaHashtags, manualAiJob?.id, manualAiJob?.status, workspace.id, activeCaptionPlatform, title, setMessage, setPlatformMessages, setNotice]);
  const thumbnailFrameOptions = [...new Set([...(manualAiJob?.frames || []).slice(0, 5), ...(manualAiJob?.generated || [])])];
  return (<>
    <section className="two-column post-workbench">
      <div className="glass-panel composer">
        <div className="composer-section-title"><PanelTitle title="Create a post" /><span className={cx('draft-save-indicator', draftSaveState === 'Saving...' && 'saving')}><FiCheck /> {draftSaveState}</span></div>

        <PlatformPicker platforms={allPlatforms} accounts={destinationOptions} selectedPlatforms={selectedPlatforms} selectedKeys={selectedDestinationKeys} togglePlatform={togglePlatform} toggleAccount={toggleDestination} Logo={PlatformLogo} />

        <div className="media-source-row">
          <div className={cx('smart-media-card', isMediaDragging && 'dragging')}
            onDragEnter={(event) => { event.preventDefault(); setIsMediaDragging(true); }}
            onDragOver={(event) => { event.preventDefault(); setIsMediaDragging(true); }}
            onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setIsMediaDragging(false); }}
            onDrop={dropMedia}>
            <label className="smart-media-upload">
              <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" onChange={inspectMedia} />
              <span className="smart-media-icon">+</span>
              <span><strong>{isMediaDragging ? 'Drop media here' : selectedVideoFile ? selectedVideoFile.name : 'Add Media'}</strong><small>{selectedVideoFile ? 'Click or drop another file to replace it' : 'Click or drag and drop · video up to 2 GB'}</small></span>
              <em>{mediaMeta.kind === 'none' ? 'Choose file' : `${mediaMeta.orientation} ${mediaMeta.kind}`}</em>
            </label>
            {selectedVideoFile && <div className="media-upload-summary"><span>{mediaMeta.width} × {mediaMeta.height}px</span><span>{(selectedVideoFile.size / 1024 / 1024).toFixed(1)} MB</span><span className={['reel', 'short'].includes(mediaType) && Math.abs((mediaMeta.width / Math.max(mediaMeta.height, 1)) - 9 / 16) > 0.035 ? 'warning' : 'ready'}>{['reel', 'short'].includes(mediaType) ? '9:16 check' : 'Ready'}</span><strong>{uploadProgress > 0 && uploadProgress < 100 ? `Uploading original · ${uploadProgress}%` : manualAiJob?.stage === 'processing_media' ? mediaMeta.kind === 'video' ? 'Preparing 720p AI copy' : 'Preparing compressed AI copy' : manualAiJob?.stage === 'analysing_video' ? 'Analysing video' : manualAiJob?.stage === 'analysing_image' ? 'Analysing image' : manualAiJob?.stage === 'transcribing' ? 'Reading spoken context' : manualAiJob?.stage === 'generating_text' ? 'Writing five options' : manualAiJob?.stage === 'extracting_frames' ? 'Extracting thumbnail frames' : manualAiJob?.stage === 'generating_thumbnail' ? 'Generating thumbnail' : manualAiJob?.status === 'ready' ? 'AI writing and frames ready' : uploadProgress === 100 ? 'Original safely uploaded' : 'Ready to upload'}</strong>{uploadProgress > 0 && uploadProgress < 100 && <progress max="100" value={uploadProgress} />}</div>}
            {mediaUploadError && <div className="media-upload-error original-upload-error" role="alert"><span><FiAlertCircle /> <b>Original upload interrupted</b><small>{mediaUploadError}</small></span><button type="button" onClick={() => retrySelectedMediaUpload().catch(() => {})}><FiRefreshCw /> Retry upload</button></div>}
            {manualAiJob && ['failed', 'cancelled'].includes(manualAiJob.status) && <div className="media-upload-error" role="status"><span>{manualAiJob.error || 'Media analysis stopped.'}</span><button type="button" onClick={retryManualAnalysis}>Retry</button></div>}
            {manualAiJob && ['queued', 'processing'].includes(manualAiJob.status) && <button className="media-analysis-cancel" type="button" onClick={cancelManualAnalysis}>Cancel analysis</button>}
          </div>
          <div className="format-toggle-group" role="group" aria-label="Publishing formats">{formatOptions.map((format) => {
            const selected = selectedVideoFile ? contentFormats.includes(format.value) : format.value === 'text';
            const disabled = selectedVideoFile ? !allowedFormats.includes(format.value) : format.value !== 'text';
            return <button className={selected ? 'active' : ''} aria-pressed={selected} disabled={disabled} key={format.value} type="button" onClick={() => toggleFormat(format.value)}>{format.label}{selected && <FiCheck />}</button>;
          })}</div>
        </div>
        <div className="composer-body-grid">
          <div className="composer-copy-column">
            <label className="title-box">
              <span>Title <em>{title.length} / 60</em></span>
              <input maxLength="60" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Add a clear title" />
            </label>

            <label className="caption-box">
              <span>Description / Caption <span className="caption-heading-tools"><button type="button" onClick={() => { setAiWriterOpen(true); setAiWriterError(''); setAiOptions([]); setSelectedAiOption(-1); }}>Write with AI</button><button type="button" onClick={createAutoHashtags} disabled={isGeneratingHashtags}>{isGeneratingHashtags ? 'Generating...' : 'Generate Hashtags'}</button><span className="caption-emoji-wrap"><button className="caption-icon-action" type="button" aria-label="Add emoji" title="Add emoji" aria-expanded={showCaptionEmojiPicker} onClick={(event) => { event.preventDefault(); setShowCaptionEmojiPicker((open) => !open); }}><FiSmile /></button>{showCaptionEmojiPicker && <span className="caption-emoji-picker" role="menu" aria-label="Choose an emoji">{['😀', '😊', '👍', '🙏', '❤️', '🎉', '✅', '📍', '📞', '✨'].map((emoji) => <button key={emoji} type="button" role="menuitem" onClick={(event) => { event.preventDefault(); insertCaptionText(emoji); setShowCaptionEmojiPicker(false); }}>{emoji}</button>)}</span>}</span><button className="caption-icon-action" type="button" aria-label="Add GIF" title="GIFs attach as post media" onClick={(event) => { event.preventDefault(); setNotice({ type: 'neutral', message: 'GIFs must be attached as post media. GIPHY search will activate after a backend GIPHY key is configured.' }); }}><FiImage /></button><button className="caption-copy-action" type="button" aria-label="Copy description" title="Copy description" disabled={!currentCaption} onClick={async (event) => { event.preventDefault(); try { await navigator.clipboard.writeText(currentCaption); setNotice({ type: 'success', message: 'Description copied.' }); } catch { setNotice({ type: 'warning', message: 'Clipboard access is unavailable. Select the description to copy it.' }); } }}><FiCopy /></button><em>{descriptionWords} / 2500</em></span></span>
              <textarea ref={captionTextareaRef} value={currentCaption} onChange={(event) => { const next = event.target.value; if (!next.trim() || next.trim().split(/\s+/).length <= 2500) setCurrentCaption(next); }} placeholder="Write the caption, description and hashtags..." />
            </label>
          </div>

          {mediaMeta.kind === 'video' && <section className="thumbnail-studio compact-thumbnail-studio" aria-labelledby="thumbnail-studio-title">
            <header className="thumbnail-studio-head"><span><strong id="thumbnail-studio-title">Create Thumbnail</strong><small>Select a suggested frame or upload your own image.</small></span><em className={publishCover?.sourceFile === selectedVideoFile && publishCover?.workspaceId === workspace.id ? 'ready' : ''}>{publishCover?.sourceFile === selectedVideoFile && publishCover?.workspaceId === workspace.id ? <><FiCheck /> Ready</> : `${Math.min(manualAiJob?.frames?.length || 0, manualAiRequiresConsent ? 5 : 10)}/${manualAiRequiresConsent ? 5 : 10} frames`}</em></header>
            <div className={cx('thumbnail-frame-strip', 'compact-ten', mediaMeta.orientation === 'horizontal' ? 'landscape' : mediaMeta.orientation === 'square' ? 'square' : 'portrait')}>
              {thumbnailFrameOptions.map((frame) => { const isGenerated = (manualAiJob?.generated || []).includes(frame); const frameIndex = (manualAiJob?.frames || []).indexOf(frame); const timestamp = isGenerated ? 0 : manualAiJob.result?.suggestedFrames?.[frameIndex]?.timestamp || 0; return <button className={selectedThumbnailFrame?.url === frame ? 'selected' : ''} aria-label={isGenerated ? 'Choose generated thumbnail' : `Choose frame at ${formatVideoTime(timestamp)}`} aria-pressed={selectedThumbnailFrame?.url === frame} key={frame} type="button" onClick={() => chooseThumbnailFrame(frame, timestamp)}><img src={manualAiAssetUrl(frame)} alt="" /><span>{isGenerated ? 'AI' : formatVideoTime(timestamp)}</span>{selectedThumbnailFrame?.url === frame && <FiCheck />}</button>; })}
              {customThumbnailFrame && <button className={selectedThumbnailFrame?.url === customThumbnailFrame.url ? 'selected' : ''} aria-label="Choose uploaded thumbnail" aria-pressed={selectedThumbnailFrame?.url === customThumbnailFrame.url} type="button" onClick={() => { setSelectedThumbnailFrame(customThumbnailFrame); setPreviewThumbnailUrl(manualAiAssetUrl(customThumbnailFrame.url)); }}><img src={manualAiAssetUrl(customThumbnailFrame.url)} alt="" /><span>Upload</span>{selectedThumbnailFrame?.url === customThumbnailFrame.url && <FiCheck />}</button>}
              <label className="thumbnail-upload-tile" title="Upload your own thumbnail"><FiPaperclip /><span>Upload</span><input aria-label="Upload thumbnail image" type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadCustomThumbnail} /></label>
            </div>
            {!thumbnailFrameOptions.length && !customThumbnailFrame && <div className="thumbnail-frame-status">{manualAiJob?.status === 'failed' ? 'Frame suggestions unavailable. Upload a thumbnail or retry analysis.' : 'Preparing thumbnail frames...'}</div>}
            <div className="thumbnail-action-row"><button className="btn subtle" type="button" disabled={!selectedThumbnailFrame} onClick={useSelectedFrame}>Use as Thumbnail</button><button className="btn success" type="button" title={!manualAiJob?.id ? 'Frames are being prepared' : !(manualAiJob?.frames || []).includes(selectedThumbnailFrame?.url) ? 'Select an original frame first' : 'Generate thumbnail'} disabled={isGeneratingThumbnail || !manualAiJob?.id || !(manualAiJob?.frames || []).includes(selectedThumbnailFrame?.url)} onClick={() => setThumbnailGenerateOpen(true)}>Generate Thumbnail</button><button className="btn subtle" type="button" title="Design Own is coming soon" disabled>Design Own</button></div>
            {thumbnailGenerateOpen && <div className="thumbnail-generate-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !isGeneratingThumbnail) setThumbnailGenerateOpen(false); }}><section className="thumbnail-generate-dialog" role="dialog" aria-modal="true" aria-labelledby="thumbnail-generate-title"><header><h3 id="thumbnail-generate-title">Generate Thumbnail</h3><button className="icon-action" type="button" aria-label="Close thumbnail settings" disabled={isGeneratingThumbnail} onClick={() => setThumbnailGenerateOpen(false)}><FiX /></button></header><div className="thumbnail-generate-source"><img src={manualAiAssetUrl(selectedThumbnailFrame?.url)} alt="Selected video frame" /><span>Selected frame</span></div><label>Thumbnail title<input value={thumbnailTitle} onChange={(event) => setThumbnailTitle(event.target.value.split(/\s+/).slice(0, 8).join(' '))} placeholder="Add a short title" /></label>{manualAiJob?.result?.thumbnailTitles?.length > 0 && <div className="thumbnail-title-suggestions" role="group" aria-label="Suggested thumbnail titles">{manualAiJob.result.thumbnailTitles.slice(0, 2).map((suggestion) => <button key={suggestion} type="button" aria-pressed={thumbnailTitle === suggestion} onClick={() => setThumbnailTitle(suggestion)}>{suggestion}</button>)}</div>}<label>AI thumbnail style<select aria-label="Thumbnail style" value={thumbnailStyle} onChange={(event) => setThumbnailStyle(event.target.value)}>{['Professional', 'Cinematic', 'Bold News', 'Educational', 'Modern Minimal', 'Vibrant Social'].map((style) => <option key={style}>{style}</option>)}</select></label><footer><button className="btn subtle" type="button" disabled={isGeneratingThumbnail} onClick={() => setThumbnailGenerateOpen(false)}>Cancel</button><button className="btn success" type="button" disabled={isGeneratingThumbnail || !thumbnailTitle.trim()} onClick={generateHiggsfieldThumbnail}>{isGeneratingThumbnail ? 'Generating...' : 'Generate'}</button></footer></section></div>}
          </section>}
        </div>
        {workspace.lockedLocation?.label && <span className="post-location-chip" title={workspace.lockedLocation.label}>Location locked</span>}

        <details className="composer-advanced" open={instagramCollaborators ? true : undefined}>
          <summary><span>Advanced post options</span><small>{selectedPlatforms.includes('instagram') ? 'Collaborators, accessibility and first comment' : 'Accessibility and first comment'}</small></summary>
          {selectedPlatforms.includes('instagram') && <InstagramCollaborators value={instagramCollaborators} onChange={setInstagramCollaborators} contentFormats={contentFormats} disabled={isPosting} />}
          <div><label><span>Media alt text <small>Describe the visual for people using screen readers.</small><button className="alt-text-ai" type="button" disabled={isGeneratingAltText} onClick={(event) => { event.preventDefault(); generateAltText(); }}>{isGeneratingAltText ? 'Writing...' : 'Write with AI'}</button></span><input maxLength="1000" value={mediaAltText} onChange={(event) => setMediaAltText(event.target.value)} placeholder="Example: Students celebrating in the school courtyard" /></label><label><span>First comment <small>Add a link or supporting note without crowding the caption.</small></span><input maxLength="2000" value={firstComment} onChange={(event) => setFirstComment(event.target.value)} placeholder="Optional link, offer or follow-up note" /></label></div>
        </details>

        <div className="composer-action-bar">
          <div className="toolbar-schedule schedule-input-card">
            <span><FiCalendar /> Publish later</span>
            <DatePicker aria-label="Publish date and time" selected={scheduledAt ? new Date(scheduledAt) : null} onChange={(date) => setScheduledAt(date ? toLocalDateTimeInput(date) : '')} showTimeSelect timeIntervals={15} minDate={new Date()} dateFormat="dd MMM yyyy, h:mm aa" placeholderText="Choose date and time" isClearable />
          </div>
          <div className="publish-actions">
            <button className="btn success" type="button" onClick={() => postNow('now')} disabled={isPosting}>{isPosting ? 'Working...' : 'Post'}</button>
            <button className="btn primary" type="button" onClick={() => postNow('schedule')} disabled={isPosting || !scheduledAt}>Schedule for later</button>
            <button className="btn subtle" type="button" onClick={() => postNow('draft')} disabled={isPosting}>Draft</button>
          </div>
        </div>
        {selectedVideoFile && <div className="selected-media-line"><strong>{selectedVideoFile.name}</strong><span>{mediaMeta.width} × {mediaMeta.height}px</span></div>}
      </div>

      <aside className="preview-stack">
        <div className="glass-panel multi-preview-panel">
          <div className="panel-head compact-head">
            <div>
              <h2>Post Preview</h2>
            </div>
            <div className="toggle-pair">
              <button className={cx(previewDevice === 'mobile' && 'active')} onClick={() => setPreviewDevice('mobile')} type="button">Mobile</button>
              <button className={cx(previewDevice === 'desktop' && 'active')} onClick={() => setPreviewDevice('desktop')} type="button">Desktop</button>
            </div>
          </div>

          <div className="platform-preview-list">
            {(activePlatforms.length ? activePlatforms : allPlatforms.filter((platform) => platform.connected)).map((platform) => (
              <PlatformLivePreview
                key={platform.id}
                mediaKind={mediaMeta.kind}
                mediaHeight={mediaMeta.height}
                mediaPreviewUrl={mediaPreviewUrl}
                thumbnailPoster={previewThumbnailUrl}
                mediaType={mediaType}
                mediaWidth={mediaMeta.width}
                platform={platform}
                previewDevice={previewDevice}
                previewText={[title.trim(), String(platformMessages[platform.id] || message).trim()].filter(Boolean).join('\n\n') || previewText}
                workspace={workspace}
              />
            ))}

            {!activePlatforms.length && (
              <div className="empty-box centered-empty preview-empty">
                <strong>No platform selected</strong>
                <small>Connect publishing profiles to preview each selected platform here.</small>
              </div>
            )}
          </div>
        </div>
      </aside>
    </section>
    {aiWriterOpen && <div className="ai-writer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !isGeneratingDescription) setAiWriterOpen(false); }}>
      <section className="ai-writer-dialog" role="dialog" aria-modal="true" aria-labelledby="ai-writer-title">
        <header><div><p className="kicker">AI writing studio</p><h2 id="ai-writer-title">Create title and caption</h2><span>Uses this workspace, selected platforms and uploaded media.</span></div><button type="button" aria-label={isGeneratingDescription ? 'Cancel AI generation' : 'Close AI writer'} onClick={() => { if (isGeneratingDescription) { if (selectedVideoFile && manualAiJob?.id) cancelManualAnalysis(); else aiRequestRef.current?.abort(); } else setAiWriterOpen(false); }}><FiX /></button></header>
        {aiHistory.length > 0 && <label className="ai-history-select"><span>Prompt history</span><select defaultValue=""><option value="" disabled>Recent prompts</option>{aiHistory.map((item) => <option key={item.id} value={item.id}>{item.brief.slice(0, 64)} · {item.tone}</option>)}</select></label>}
        <div className="ai-writer-grid">
          <fieldset><legend>Length</legend><div className="ai-choice-row">{['Short', 'Medium', 'Long'].map((item) => <button aria-pressed={aiWriter.selectedLength === item} className={aiWriter.selectedLength === item ? 'active' : ''} key={item} type="button" onClick={() => setAiWriter((current) => ({ ...current, selectedLength: item }))}>{item}</button>)}</div></fieldset>
          <fieldset><legend>Tone</legend><select aria-label="Tone" value={aiWriter.tone} onChange={(event) => setAiWriter((current) => ({ ...current, tone: event.target.value }))}>{['Professional', 'Casual', 'Friendly', 'Funny', 'Inspirational', 'Educational', 'Luxury', 'Urgent'].map((item) => <option key={item}>{item}</option>)}</select></fieldset>
        </div>
        <fieldset className="ai-language-field"><legend>Language (maximum 3)</legend><LanguagePicker value={aiWriter.languages} onToggle={toggleAiLanguage} /></fieldset>
        <fieldset className="ai-context-options"><legend>Include workspace details</legend>{[
          ['includeBusinessName', 'Business Name'],
          ['includeBusinessInfo', 'Business Info'],
          ['includeContactDetails', 'Contact Details'],
          ['includeWebsite', 'Website']
        ].map(([key, label]) => <label key={key}><input type="checkbox" checked={Boolean(aiWriter[key])} onChange={(event) => setAiWriter((current) => ({ ...current, [key]: event.target.checked }))} /><span>{label}</span></label>)}</fieldset>
        <label className="ai-post-brief"><span>What should this post say?</span><textarea rows={3} value={aiWriter.postAbout} onChange={(event) => setAiWriter((current) => ({ ...current, postAbout: event.target.value.slice(0, 3000) }))} placeholder="Add the idea, offer or important details. Uploaded media is processed automatically." /><small>{aiWriter.postAbout.length} / 3000</small></label>
        {selectedVideoFile && manualAiRequiresConsent && <div className="ai-analysis-disclosure" role="note"><FiCheck /><span><strong>Media-aware writing</strong><small>Generate analyses a reduced video preview with NVIDIA and transcribes its audio with Whisper. Your original publishing file remains unchanged in EastenSocial.</small></span></div>}
        {aiWriterError && <div className="ai-writer-error">{aiWriterError}{aiWriterError.includes('My Profile') && <button type="button" onClick={() => { setAiWriterOpen(false); if (openWorkspaceSettings) openWorkspaceSettings(); }}>Open Workspace settings</button>}</div>}
        <button className={cx('btn ai-generate-button', isGeneratingDescription ? 'danger' : 'primary')} type="button" onClick={isGeneratingDescription ? () => { if (selectedVideoFile && manualAiJob?.id) cancelManualAnalysis(); else aiRequestRef.current?.abort(); } : createAutoDescription}>{isGeneratingDescription ? 'Cancel generation' : 'Generate title and caption'}</button>
        {isGeneratingDescription && <Skeleton rows={5} label="Generating AI suggestions" />}
        {aiOptions.length > 0 && <div className="ai-description-options"><div><strong>Choose one description</strong><span>Nothing is inserted until you confirm.</span></div>{aiOptions.map((option, index) => <button className={selectedAiOption === index ? 'selected' : ''} key={index} type="button" onClick={() => { setSelectedAiOption(index); setAiPreview(option.text); }}><i>{index + 1}</i><span className="ai-option-copy"><strong>{option.title}</strong><span>{option.text}</span></span></button>)}</div>}
        {aiOptions.length > 0 && <label className="ai-preview-editor"><span>Edit preview</span><textarea value={aiPreview} onChange={(event) => setAiPreview(event.target.value)} /></label>}
        {aiOptions.length > 0 && <footer><button className="btn subtle" type="button" onClick={createAutoDescription} disabled={isGeneratingDescription}>Regenerate</button><button className="btn success" type="button" onClick={insertAiDescription} disabled={selectedAiOption < 0 || !aiPreview.trim()}>Insert directly to post</button></footer>}
      </section>
    </div>}
  </>);
}

function PlatformLivePreview({ mediaHeight, mediaKind, mediaPreviewUrl, thumbnailPoster, mediaType, mediaWidth, platform, previewDevice, previewText, workspace }) {
  const previewVideoRef = useRef(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [isPreviewMuted, setIsPreviewMuted] = useState(true);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(0);
  useEffect(() => {
    setIsPreviewPlaying(false);
    setPreviewTime(0);
    setPreviewDuration(0);
  }, [mediaPreviewUrl]);
  const togglePreviewPlayback = async () => {
    const video = previewVideoRef.current;
    if (!video) return;
    if (video.paused) {
      try { await video.play(); }
      catch { setIsPreviewPlaying(false); }
    } else video.pause();
  };
  const surfaceMap = {
    instagram: mediaType === 'reel' ? 'Instagram Reel' : 'Instagram feed',
    facebook: 'Facebook feed',
    youtube: mediaType === 'short' ? 'YouTube Short' : 'YouTube video',
    google: 'Google Business update',
    linkedin: 'LinkedIn post'
  };

  return (
    <article className="platform-live-card" style={{ '--platform': platform.color }}>
      <div className="platform-live-head">
        <span className="platform-logo"><PlatformLogo id={platform.id} label={platform.label} /></span>
        <div>
          <strong>{platform.label}</strong>
          <small>{surfaceMap[platform.id] || 'Social post'} - {previewDevice}</small>
        </div>
        <em>{platform.connected ? 'Ready' : 'Connect'}</em>
      </div>

      <div className={cx('phone-preview compact-phone', previewDevice)}>
        <div className="phone-top" />
        <div className="social-post">
          <div className="post-profile">
            <span style={{ background: workspace.color }}>{workspace.initials}</span>
            <div>
              <strong>{workspace.name}</strong>
              <small>{platform.label}</small>
            </div>
          </div>
          <p>{previewText}</p>
          <div
            className={cx('media-preview', mediaPreviewUrl && 'has-media')}
            style={mediaPreviewUrl && mediaWidth && mediaHeight ? { '--media-ratio': `${mediaWidth} / ${mediaHeight}` } : undefined}
          >
            {mediaPreviewUrl && mediaKind === 'image' && <img src={mediaPreviewUrl} alt={`${platform.label} post preview`} />}
            {mediaPreviewUrl && mediaKind === 'video' && <>
              <video ref={previewVideoRef} src={mediaPreviewUrl} poster={thumbnailPoster || undefined} muted={isPreviewMuted} playsInline preload="metadata" onLoadedMetadata={(event) => setPreviewDuration(Number(event.currentTarget.duration) || 0)} onTimeUpdate={(event) => setPreviewTime(Number(event.currentTarget.currentTime) || 0)} onPlay={() => setIsPreviewPlaying(true)} onPause={() => setIsPreviewPlaying(false)} onEnded={() => setIsPreviewPlaying(false)} />
              {!isPreviewPlaying && thumbnailPoster && <img className="preview-pause-poster" src={thumbnailPoster} alt="Selected video thumbnail" />}
              <div className="preview-video-controls"><button type="button" aria-label={isPreviewMuted ? 'Unmute preview' : 'Mute preview'} title={isPreviewMuted ? 'Unmute' : 'Mute'} onClick={() => setIsPreviewMuted((value) => !value)}>{isPreviewMuted ? <FiVolumeX /> : <FiVolume2 />}</button><button type="button" aria-label={isPreviewPlaying ? 'Pause preview' : 'Play preview'} title={isPreviewPlaying ? 'Pause' : 'Play'} onClick={togglePreviewPlayback}>{isPreviewPlaying ? <FiPause /> : <FiPlay />}</button></div>
              {previewDuration > 0 && <input className="preview-video-progress" type="range" min="0" max={previewDuration} step="0.1" value={Math.min(previewTime, previewDuration)} aria-label="Seek video preview" onChange={(event) => { const time = Number(event.target.value); if (previewVideoRef.current) previewVideoRef.current.currentTime = time; setPreviewTime(time); }} />}
            </>}
            {!mediaPreviewUrl && <span>{mediaType} preview</span>}
          </div>
          <div className="post-actions">
            <span><FiHeart /> Like</span>
            <span><FiMessageSquare /> {platform.id === 'google' ? 'Call' : 'Comment'}</span>
            <span><FiShare2 /> {platform.id === 'youtube' ? 'Subscribe' : 'Share'}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function analyticsPointValue(value) {
  if (Number.isFinite(Number(value))) return Number(value);
  if (value && typeof value === 'object') return Object.values(value).reduce((total, item) => total + analyticsPointValue(item), 0);
  return 0;
}

function aggregateAccountTrend(accounts, metricNames, { postSeries = false, activeDays = false } = {}) {
  const valuesByDate = new Map();
  accounts.forEach((account) => {
    const points = postSeries
      ? (account.postSeries || [])
      : metricNames.map((name) => account.series?.[name]).find((series) => Array.isArray(series) && series.length) || [];
    points.forEach((point, index) => {
      const rawDate = point.end_time || point.date || point.timestamp || point.day || `${index}`;
      const date = /^\d+$/.test(String(rawDate)) ? String(rawDate) : String(rawDate).slice(0, 10);
      const rawValue = analyticsPointValue(point.value ?? point.total ?? point.count ?? 0);
      const value = activeDays ? (rawValue > 0 ? 1 : 0) : rawValue;
      valuesByDate.set(date, Number(valuesByDate.get(date) || 0) + value);
    });
  });
  return Array.from(valuesByDate, ([date, value]) => ({ date, value })).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
}

function trendDelta(series = []) {
  if (series.length < 4) return null;
  const middle = Math.floor(series.length / 2);
  const previous = series.slice(0, middle).reduce((total, point) => total + Number(point.value || 0), 0);
  const current = series.slice(middle).reduce((total, point) => total + Number(point.value || 0), 0);
  if (!previous) return current ? 100 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function DashboardSparkline({ series = [], tone = 'neutral' }) {
  const values = series.map((point) => Number(point.value || 0));
  if (values.length < 2) return <span className="hd-spark-empty" aria-hidden="true" />;
  const width = 120;
  const height = 36;
  const pad = 3;
  const min = Math.min(...values);
  const range = (Math.max(...values) - min) || 1;
  const line = values.map((value, index) => `${((index / (values.length - 1)) * width).toFixed(1)},${(pad + (height - pad * 2) * (1 - (value - min) / range)).toFixed(1)}`).join(' ');
  return <svg className={`hd-spark ${tone}`} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Trend for the last 30 days"><polygon points={`0,${height} ${line} ${width},${height}`} /><polyline points={line} /></svg>;
}

function DashboardMetricCard({ icon: Icon, label, value, series, note = '' }) {
  const delta = Array.isArray(series) ? trendDelta(series) : undefined;
  const flat = delta != null && Math.abs(delta) < 0.05;
  const tone = delta == null || flat ? 'neutral' : delta > 0 ? 'positive' : 'negative';
  return (
    <article className="hd-kpi">
      <span className="hd-kpi-label"><Icon aria-hidden="true" />{label}</span>
      <strong className="hd-kpi-value">{value}</strong>
      {delta === undefined
        ? <small className="hd-kpi-note">{note}</small>
        : delta === null
          ? <small className="hd-kpi-note">Not enough data yet</small>
          : <small className={`hd-kpi-delta ${tone}`}><b>{flat ? '–' : delta > 0 ? '▲' : '▼'} {flat ? '' : delta > 0 ? '+' : '−'}{Math.abs(delta).toFixed(1)}%</b> vs last period</small>}
      {delta !== undefined && <DashboardSparkline series={series} tone={tone} />}
    </article>
  );
}

function HomeDashboard({ connectedChannelCount, onNavigate, onCreateEvent, onSelectWorkspace, platforms = [], publishedPosts = [], workspace, workspaceId, workspaceOptions = [] }) {
  const [summary, setSummary] = useState({ followers: null, postsCount: null, activeDays: null, views: null, engagement: null, topPosts: [], timingPosts: [], trends: {}, loading: true, stale: false });
  const cutoff = useMemo(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), []);
  const recentPosts = useMemo(() => publishedPosts
    .filter((post) => post.status === 'published' && new Date(post.publishedAt || post.createdAt || 0) >= cutoff)
    .sort((a, b) => new Date(b.publishedAt || b.createdAt || 0) - new Date(a.publishedAt || a.createdAt || 0)), [cutoff, publishedPosts]);
  const bestPostingWindow = useMemo(() => buildPostingHeatmap(summary.timingPosts || []), [summary.timingPosts]);
  const topRecentPosts = useMemo(() => (summary.topPosts.length ? summary.topPosts : recentPosts)
    .slice()
    .filter((post) => Number(post.views ?? post.viewCount ?? post.metrics?.views ?? post.summary?.views ?? 0) > 0)
    .sort((a, b) => Number(b.views ?? b.viewCount ?? b.metrics?.views ?? b.summary?.views ?? 0) - Number(a.views ?? a.viewCount ?? a.metrics?.views ?? a.summary?.views ?? 0))
    .slice(0, 10), [recentPosts, summary.topPosts]);
  const upcomingPosts = useMemo(() => publishedPosts
    .filter((post) => {
      const scheduledAt = new Date(post.scheduledTime || 0);
      return post.status === 'scheduled' && scheduledAt > new Date() && scheduledAt <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    })
    .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime))
    .slice(0, 5), [publishedPosts]);
  const todayKey = indiaToday();
  const upcomingDays = useMemo(() => upcomingOccasionDays(todayKey, 7, { state: workspace.businessState }), [todayKey, workspace.businessState]);
  const savedActiveDays = new Set(recentPosts.map((post) => new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }))).size;
  const postViews = (post) => Number(post.views ?? post.viewCount ?? post.metrics?.views ?? post.summary?.views ?? 0);
  const failedPosts = publishedPosts.filter((post) => post.status === 'failed');
  const failureReason = String(failedPosts.find((post) => post.errorMessage)?.errorMessage || 'Open Manage Posts to see the reason and retry.').replace(/\s+/g, ' ').trim();
  const attentionItems = [
    failedPosts.length > 0 && { icon: FiAlertCircle, title: `${failedPosts.length} post${failedPosts.length === 1 ? '' : 's'} failed to publish`, detail: failureReason.length > 120 ? `${failureReason.slice(0, 117)}…` : failureReason, module: 'manage' },
    summary.issue && { icon: FiAlertCircle, title: summary.issue.split('. ')[0].replace(/\.$/, ''), detail: 'Open Assets and reconnect them to restore live data.', module: 'profiles' },
    connectedChannelCount === 0 && { icon: FiGrid, title: 'No publishing account connected', detail: 'Connect a profile to publish and see reports.', module: 'profiles' }
  ].filter(Boolean);
  useEffect(() => {
    let active = true;
    const cacheKey = `${workspaceId}:all:30`;
    const cached = analyticsMemoryCache.get(cacheKey);
    const readSummary = (data) => {
      const networks = data?.networks || [];
      const accounts = (networks || []).flatMap((network) => network.accounts || []);
      const total = (key) => accounts.some((account) => account.summary?.[key] != null)
        ? accounts.reduce((value, account) => value + Number(account.summary?.[key] || 0), 0) : null;
      const followersAvailable = accounts.some((account) => account.followers != null || account.summary?.followers != null);
      const postsAvailable = accounts.some((account) => account.postsCount != null);
      const postingDates = new Set(accounts.flatMap((account) => account.postingDates || []));
      const trends = {
        followers: aggregateAccountTrend(accounts, ['follower_count', 'page_daily_follows', 'page_fan_adds']),
        activeDays: aggregateAccountTrend(accounts, [], { postSeries: true, activeDays: true }),
        postsCount: aggregateAccountTrend(accounts, [], { postSeries: true }),
        views: aggregateAccountTrend(accounts, ['views', 'page_media_view', 'impressions']),
        engagement: aggregateAccountTrend(accounts, ['total_interactions', 'page_post_engagements', 'media_engagements'])
      };
      return {
        followers: followersAvailable ? accounts.reduce((value, account) => value + Number(account.followers ?? account.summary?.followers ?? 0), 0) : null,
        postsCount: postsAvailable ? accounts.reduce((value, account) => value + Number(account.postsCount || 0), 0) : null,
        activeDays: postingDates.size || (accounts.some((account) => account.activePostingDays != null) ? Math.max(...accounts.map((account) => Number(account.activePostingDays || 0))) : null),
        views: total('views'),
        engagement: total('engagement'),
        topPosts: data?.topPosts || [],
        timingPosts: accounts.flatMap((account) => account.topPosts || []),
        trends,
        loading: false,
        ...analyticsHealth(data)
      };
    };
    const refreshSummary = () => axios.get(`${API_BASE}/api/analytics/summary`, {
      params: { days: 30, platform: 'all' },
      withCredentials: true,
      headers: { 'x-workspace-id': workspaceId },
      timeout: 20000
    }).then(({ data }) => {
      analyticsMemoryCache.set(cacheKey, { cachedAt: Date.now(), data });
      if (active) setSummary(readSummary(data));
    }).catch(() => active && setSummary((current) => ({ ...current, loading: false })));
    if (cached) setSummary(readSummary(cached.data));
    if (!cached || Date.now() - cached.cachedAt >= ANALYTICS_CACHE_TTL_MS) {
      setSummary((current) => ({ ...current, loading: true }));
      refreshSummary();
    }
    const refreshTimer = window.setInterval(refreshSummary, 5 * 60 * 1000);
    return () => { active = false; window.clearInterval(refreshTimer); };
  }, [workspaceId]);

  return (
    <section className="home-dashboard">
      <div className="mobile-workspace-picker">
        <span className="workspace-avatar" style={{ background: workspace.color }} aria-hidden="true">{workspace.profileImageUrl ? <img src={workspace.profileImageUrl} alt="" /> : workspace.initials}</span>
        <label><span className="sr-only">Switch workspace</span><select aria-label="Switch workspace" value={workspaceId} onChange={(event) => onSelectWorkspace(event.target.value)}>{workspaceOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><small>{connectedChannelCount} connected channels</small></label>
        <FiChevronDown aria-hidden="true" />
      </div>
      <div className="hd-kpis" aria-label="Workspace performance summary">
        <DashboardMetricCard icon={FiGrid} label="Connected Assets" note="In this workspace" value={connectedChannelCount.toLocaleString('en-IN')} />
        <DashboardMetricCard icon={FiHeart} label="Followers" series={summary.trends?.followers || []} value={summary.followers == null ? summary.loading ? '…' : '—' : summary.followers.toLocaleString('en-IN')} />
        <DashboardMetricCard icon={FiCalendar} label="Active Days" series={summary.trends?.activeDays || []} value={(summary.activeDays ?? savedActiveDays).toLocaleString('en-IN')} />
        <DashboardMetricCard icon={FiFileText} label="Published Posts" series={summary.trends?.postsCount || []} value={(summary.postsCount ?? recentPosts.length).toLocaleString('en-IN')} />
        <DashboardMetricCard icon={FiEye} label="Overall Views" series={summary.trends?.views || []} value={summary.views == null ? summary.loading ? '…' : '—' : summary.views.toLocaleString('en-IN')} />
        <DashboardMetricCard icon={FiMessageSquare} label="Engagement" series={summary.trends?.engagement || []} value={summary.engagement == null ? summary.loading ? '…' : '—' : summary.engagement.toLocaleString('en-IN')} />
      </div>
      <div className="hd-grid">
        <div className="hd-main">
          {upcomingDays[0] && <button className="desktop-next-occasion hd-event" type="button" onClick={() => onNavigate('calendar')}>
            <time dateTime={upcomingDays[0].date}><strong>{upcomingDays[0].date.slice(8)}</strong><small>{new Date(`${upcomingDays[0].date}T00:00:00`).toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</small></time>
            <span><small>Upcoming event</small><strong>{upcomingDays[0].events.map((event) => event.name).join(', ')}</strong></span><FiChevronRight aria-hidden="true" />
          </button>}
          {upcomingDays[0] && <button className="mobile-next-occasion" type="button" onClick={() => onNavigate('calendar')}>
            <time dateTime={upcomingDays[0].date}><strong>{upcomingDays[0].date.slice(8)}</strong><small>{new Date(`${upcomingDays[0].date}T00:00:00`).toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</small></time>
            <span><small>Next upcoming day</small><strong>{upcomingDays[0].events.map((event) => event.name).join(', ')}</strong></span><FiChevronRight aria-hidden="true" />
          </button>}
          <nav className="mobile-home-shortcuts" aria-label="Home shortcuts">
            <button type="button" onClick={() => onNavigate('manage')}><i><FiFileText /></i><span>Drafts</span></button>
            <button type="button" onClick={() => onNavigate('calendar')}><i><FiClock /></i><span>Pending</span></button>
            <button type="button" onClick={() => onNavigate('messaging')}><i><FiMessageSquare /></i><span>Comments</span></button>
            <button type="button" onClick={() => onNavigate('manage')}><i><FiTrash2 /></i><span>Bin</span></button>
          </nav>
          {attentionItems.length > 0 && <section className="hd-card hd-attention" aria-label="Attention needed">
            <PanelTitle title="Attention Needed" />
            <ul>{attentionItems.slice(0, 3).map(({ icon: Icon, title: itemTitle, detail, module }) => <li key={itemTitle}><button type="button" onClick={() => onNavigate(module)}><i><Icon /></i><span><strong>{itemTitle}</strong><small>{detail}</small></span><FiChevronRight /></button></li>)}</ul>
          </section>}
          <section className="hd-card hd-top-posts">
            <div className="hd-card-head"><PanelTitle title="Top 10 Recent Posts" /><small>Last 30 days · ranked by views · opens on the platform</small></div>
            <div className={cx('hd-post-row', !topRecentPosts.length && 'empty')}>
              {topRecentPosts.map((post) => {
                const platform = post.platform || post.platforms?.[0] || 'facebook';
                const link = publishedPostUrl(post);
                const Tile = link ? 'a' : 'button';
                return <Tile key={post.id || post.externalPostId} {...(link ? { href: link, target: '_blank', rel: 'noopener noreferrer', title: `Open on ${platformById(platform).label}` } : { type: 'button', onClick: () => onNavigate('manage') })}><PostThumbnail apiBase={API_BASE} post={post} workspaceId={workspaceId} /><b className="dashboard-post-platform"><PlatformLogo id={platform} label={platformById(platform).label} /></b><span><strong>{shortPostText(post.title || post.content || 'Published post', 7)}</strong><small><FiEye /> {postViews(post).toLocaleString('en-IN')}</small></span></Tile>;
              })}
              {!topRecentPosts.length && <div className="empty-box centered-empty"><FiFileText /><strong>{summary.issue ? 'Top posts unavailable' : 'No ranked recent posts'}</strong><small>{summary.issue ? 'Restore profile access to fetch post views and rank your recent posts.' : 'No posts with available views were returned for this period.'}</small></div>}
            </div>
          </section>
        </div>
        <aside className="hd-side">
          <section className="hd-card hd-best-time">
            <PanelTitle title="Perfect Time to Post" />
            {bestPostingWindow.hasHistory
              ? <p className="hd-best-line"><strong>Today {bestPostingWindow.label}</strong> is best for your audience</p>
              : <p className="hd-best-line muted">Not enough data yet</p>}
            <small className="hd-best-note">{bestPostingWindow.hasHistory
              ? `Based on views of ${bestPostingWindow.sampleSize} posts from the last 30 days.`
              : `Needs at least ${MIN_POSTS_FOR_TIMING} recent posts with views. We never guess a time.`}</small>
            <div className="hd-heat" role="img" aria-label={bestPostingWindow.hasHistory ? `Posting activity by day and time. Best window today: ${bestPostingWindow.label}` : 'Posting activity by day and time. Not enough data yet.'}>
              <div className="hd-heat-head" aria-hidden="true"><span />{HEAT_SLOTS.map((hour, index) => <span key={hour}>{index % 2 === 0 ? hourLabel(hour).replace(' ', '').replace('AM', 'a').replace('PM', 'p') : ''}</span>)}</div>
              {HEAT_DAYS.map((dayName, day) => <div className={cx('hd-heat-row', day === bestPostingWindow.today && 'today')} key={dayName}>
                <em>{dayName[0]}</em>
                {HEAT_SLOTS.map((hour, slot) => <i key={hour} className={cx(day === bestPostingWindow.today && slot === bestPostingWindow.bestSlot && 'best')} title={`${dayName} ${slotLabel(hour)}`} style={{ '--heat': bestPostingWindow.hasHistory ? Math.min(1, bestPostingWindow.grid[day][slot] / bestPostingWindow.peak) : 0 }} />)}
              </div>)}
            </div>
          </section>
          <section className="hd-card hd-upcoming">
            <PanelTitle title="Upcoming Posts" />
            <div className="dashboard-upcoming-list hd-upcoming-list">{upcomingPosts.map((post) => {
              const platforms = (Array.isArray(post.platforms) ? post.platforms : [post.platform]).filter(Boolean);
              return <button key={post.id || post.externalPostId} type="button" onClick={() => onNavigate('calendar')}><PostThumbnail apiBase={API_BASE} post={post} workspaceId={workspaceId} /><span><strong>{shortPostText(post.title || post.content || 'Scheduled post', 8)}</strong><small>{new Date(post.scheduledTime).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</small><em>{platforms.slice(0, 4).map((id) => <PlatformLogo key={id} id={id} label={platformById(id).label} />)}</em></span><FiEdit3 /></button>;
            })}</div>
            {!upcomingPosts.length && <div className="hd-empty"><FiCalendar aria-hidden="true" /><strong>Nothing scheduled</strong><small>Your next planned posts will appear here.</small><button className="btn subtle" type="button" onClick={() => onNavigate('calendar')}>Open calendar</button></div>}
          </section>
        </aside>
      </div>
    </section>
  );
}

const MANAGE_PAGE_FIRST = 80;
const MANAGE_PAGE_MORE = 30;

function ManagePosts({ posts = [], view = 'published', setView, onResume, setNotice, setPublishedPosts, workspaceId }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [layout, setLayout] = useState('grid');
  const [rangeDays, setRangeDays] = useState(30);
  const [shownCount, setShownCount] = useState(MANAGE_PAGE_FIRST);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [recentDeleted, setRecentDeleted] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [providerPosts, setProviderPosts] = useState([]);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerIssue, setProviderIssue] = useState('');
  useEffect(() => {
    let active = true;
    setRecentDeleted([]);
    setEditingId(null);
    setDraft('');
    axios.get(`${API_BASE}/api/posts/deleted`, { withCredentials: true, headers: { 'x-workspace-id': workspaceId } })
      .then((response) => { if (active) setRecentDeleted(response.data.posts || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [workspaceId]);
  useEffect(() => {
    let active = true;
    const cacheKey = `${workspaceId}:all:${rangeDays}`;
    const collectProviderPosts = (data = {}) => {
      const posts = [
        ...(data.networks || []).flatMap((network) => (network.accounts || []).flatMap((account) => account.topPosts || [])),
        ...(data.topPosts || [])
      ];
      return Array.from(new Map(posts.map((post) => [`${post.platform}:${post.id}`, post])).values())
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
    };
    const cached = analyticsMemoryCache.get(cacheKey);
    setProviderPosts(collectProviderPosts(cached?.data));
    setProviderIssue(analyticsHealth(cached?.data).issue);
    if (cached && Date.now() - cached.cachedAt < ANALYTICS_CACHE_TTL_MS) {
      setProviderPosts(collectProviderPosts(cached.data));
      setProviderLoading(false);
      return () => { active = false; };
    }
    setProviderLoading(true);
    axios.get(`${API_BASE}/api/analytics/summary`, { params: { days: rangeDays, platform: 'all' }, withCredentials: true, headers: { 'x-workspace-id': workspaceId }, timeout: 20000 })
      .then(({ data }) => {
        if (!active) return;
        analyticsMemoryCache.set(cacheKey, { cachedAt: Date.now(), data });
        setProviderPosts(collectProviderPosts(data));
        setProviderIssue(analyticsHealth(data).issue);
      })
      .catch(() => { if (active) setProviderIssue('Connected posts could not be refreshed. Previously loaded and saved posts remain available.'); })
      .finally(() => { if (active) setProviderLoading(false); });
    return () => { active = false; };
  }, [workspaceId, rangeDays]);
  const workspaceRequest = { withCredentials: true, headers: { 'x-workspace-id': workspaceId } };
  const publishedOnly = useMemo(() => posts.filter((post) => String(post.status || '').toLowerCase() === 'published'), [posts]);
  const draftPosts = useMemo(() => posts.filter((post) => String(post.status || '').toLowerCase() === 'draft'), [posts]);
  const visiblePosts = useMemo(() => {
    if (!providerPosts.length) return publishedOnly;
    const savedByProviderId = new Map();
    publishedOnly.forEach((post) => String(post.externalPostId || '').split(',').map((id) => id.trim()).filter(Boolean).forEach((id) => savedByProviderId.set(`${post.platforms?.[0] || ''}:${id}`, post)));
    const merged = providerPosts.map((providerPost) => {
      const saved = savedByProviderId.get(`${providerPost.platform}:${providerPost.id}`);
      return {
        ...(saved || {}),
        ...providerPost,
        id: saved?.id || providerPost.id,
        providerPostId: providerPost.id,
        externalPostId: saved?.externalPostId || providerPost.id,
        platforms: saved?.platforms || [providerPost.platform],
        pageId: saved?.pageId || providerPost.accountId,
        pageName: providerPost.accountName || saved?.pageName,
        status: 'published'
      };
    });
    const providerKeys = new Set(providerPosts.map((post) => `${post.platform}:${post.id}`));
    return [...merged, ...publishedOnly.filter((post) => !String(post.externalPostId || '').split(',').some((id) => providerKeys.has(`${post.platforms?.[0] || ''}:${id.trim()}`)))];
  }, [providerPosts, publishedOnly]);
  const filteredPosts = useMemo(() => visiblePosts.filter((post) => {
    const platforms = (Array.isArray(post.platforms) ? post.platforms : [post.platform]).filter(Boolean);
    const matchesPlatform = platformFilter === 'all' || platforms.includes(platformFilter);
    const haystack = [post.title, post.content, post.message, post.pageName, post.accountName].filter(Boolean).join(' ').toLowerCase();
    return matchesPlatform && (!searchQuery.trim() || haystack.includes(searchQuery.trim().toLowerCase()));
  }), [platformFilter, searchQuery, visiblePosts]);

  useEffect(() => { setShownCount(MANAGE_PAGE_FIRST); }, [platformFilter, rangeDays, searchQuery, workspaceId]);
  const shownPosts = filteredPosts.slice(0, shownCount);

  const startEdit = (post) => {
    setEditingId(post.id || post.externalPostId);
    setDraft(post.content || post.message || '');
  };
  const saveEdit = async (post) => {
    const key = post.id || post.externalPostId;
    try {
      const response = await axios.patch(`${API_BASE}/api/posts/${key}`, { content: draft }, workspaceRequest);
      setPublishedPosts((current) => current.map((item) => ((item.id || item.externalPostId) === key ? response.data.post : item)));
      setEditingId(null);
      setNotice({ type: 'success', message: 'Saved workspace record updated. Already-published platform content is unchanged.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Post record could not be updated.' });
    }
  };
  const deletePost = async (post) => {
    const key = post.id || post.externalPostId;
    try {
      const response = await axios.delete(`${API_BASE}/api/posts/${key}`, workspaceRequest);
      setPublishedPosts((current) => current.filter((item) => (item.id || item.externalPostId) !== key));
      const deletedPost = response.data.post || post;
      setRecentDeleted((current) => [deletedPost, ...current.filter((item) => (item.id || item.externalPostId) !== key)].slice(0, 5));
      setDeleteTarget(null);
      setNotice({ type: 'success', message: 'Saved workspace record deleted. Already-published platform content is unchanged.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Post record could not be deleted.' });
    }
  };
  const sharePost = async (post) => {
    const url = publishedPostUrl(post);
    if (!url) {
      setNotice({ type: 'warning', message: 'This provider did not return a shareable post link.' });
      return;
    }
    try {
      if (navigator.share) await navigator.share({ title: post.title || 'Published post', text: shortPostText(post.content || post.message || '', 20), url });
      else {
        await navigator.clipboard.writeText(url);
        setNotice({ type: 'success', message: 'Post link copied.' });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') setNotice({ type: 'warning', message: 'Post could not be shared from this device.' });
    }
  };
  return <><section className="manage-posts-layout compact-managed">
    <div className="glass-panel manage-posts-main">
      <div className="manage-posts-head">
        <div><h2>Published Posts</h2><p>Last {rangeDays} days from every connected profile</p></div>
        <div className="managed-toolbar">
          <div className="layout-switch" role="group" aria-label="Post layout"><button type="button" title="Grid view" aria-label="Grid view" aria-pressed={layout === 'grid'} onClick={() => setLayout('grid')}><FiGrid /></button><button type="button" title="List view" aria-label="List view" aria-pressed={layout === 'list'} onClick={() => setLayout('list')}><FiList /></button></div>
        </div>
      </div>
      <div className="managed-filter-row"><label><FiSearch /><input aria-label="Search posts" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search posts" /></label><select aria-label="Filter posts by platform" value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}><option value="all">All platforms</option>{['facebook', 'instagram', 'linkedin', 'youtube', 'threads', 'twitter', 'google'].map((platform) => <option key={platform} value={platform}>{platformById(platform).label}</option>)}</select><select aria-label="Time range" value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>{[7, 30, 90, 365].map((days) => <option key={days} value={days}>{days === 365 ? '365 Days' : `${days} Days`}</option>)}</select></div>
      <div className={`managed-post-list managed-${layout}`}>
        {providerIssue && <div className="provider-analytics-status compact-provider-status" role="status"><strong>Provider sync needs attention</strong><span>{providerIssue}</span></div>}
        {providerLoading && !visiblePosts.length && <Skeleton rows={5} label="Loading connected posts" />}
        {shownPosts.map((post) => {
          const key = post.id || post.externalPostId;
          const postPlatforms = Array.isArray(post.platforms) ? post.platforms : [post.platform || 'facebook'];
          const isEditing = editingId === key;
          const link = publishedPostUrl(post) || (post.externalPostId ? `${API_BASE}/api/posts/${encodeURIComponent(key)}/open?workspaceId=${encodeURIComponent(workspaceId)}` : '');
          const viewCount = post.views ?? post.viewCount ?? post.metrics?.views ?? post.summary?.views;
          const postTitle = String(post.title || post.content || post.message || 'Untitled post').trim();
          const canChange = /^[a-f\d]{24}$/i.test(String(key));
          const platformLogos = <div className="managed-platforms" aria-label="Published platforms">{postPlatforms.map((id) => <span className="platform-logo" key={id} title={platformById(id).label}><PlatformLogo id={id} label={platformById(id).label} /></span>)}</div>;
          const actionButtons = isEditing
            ? <><button type="button" title="Save description" aria-label="Save description" onClick={() => saveEdit(post)}><FiCheck /></button><button type="button" title="Cancel editing" aria-label="Cancel editing" onClick={() => setEditingId(null)}><FiX /></button></>
            : <>
              <button type="button" title="Share post" aria-label="Share post" onClick={() => sharePost(post)}><FiShare2 /></button>
              <button type="button" title="Edit saved description" aria-label="Edit saved description" onClick={() => canChange ? startEdit(post) : setNotice({ type: 'warning', message: 'This provider post is read-only until it is imported into the workspace.' })}><FiEdit3 /></button>
              <button className="danger-icon" type="button" title="Delete saved record" aria-label="Delete saved record" onClick={() => canChange ? setDeleteTarget(post) : setNotice({ type: 'warning', message: 'Provider-only posts cannot be deleted from EastenSocial.' })}><FiTrash2 /></button>
            </>;
          const viewsLine = <small className="managed-post-views"><FiEye aria-hidden="true" /> {viewCount == null ? 'Views unavailable' : `${Number(viewCount).toLocaleString('en-IN')} views`}</small>;
          if (layout === 'list') {
            const words = postTitle.split(/\s+/);
            const listTitle = words.length > 70 ? `${words.slice(0, 70).join(' ')}…` : postTitle;
            return <article className="managed-post-row" key={key}>
              <div className="managed-row-thumb">{link ? <a href={link} target="_blank" rel="noreferrer" aria-label="Open post"><PostThumbnail post={post} workspaceId={workspaceId} apiBase={API_BASE} /></a> : <PostThumbnail post={post} workspaceId={workspaceId} apiBase={API_BASE} />}</div>
              <div className="managed-row-body">
                {isEditing ? <textarea aria-label="Edit saved description" className="managed-edit-box" value={draft} onChange={(event) => setDraft(event.target.value)} /> : link ? <a className="managed-row-title" href={link} target="_blank" rel="noreferrer" title={postTitle}>{listTitle}</a> : <strong className="managed-row-title" title={postTitle}>{listTitle}</strong>}
                {viewsLine}
              </div>
              <div className="managed-row-platforms">{platformLogos}</div>
              <div className="managed-row-actions">{actionButtons}</div>
            </article>;
          }
          return <article className="managed-post-card" key={key}>
            <div className="managed-post-media">
              {link ? <a className="managed-media-link" href={link} target="_blank" rel="noreferrer" aria-label={`Open post: ${postTitle.slice(0, 60)}`}><PostThumbnail post={post} workspaceId={workspaceId} apiBase={API_BASE} /></a> : <PostThumbnail post={post} workspaceId={workspaceId} apiBase={API_BASE} />}
              {platformLogos}
              <div className="managed-actions">{actionButtons}</div>
            </div>
            <div className="managed-post-content">
              {isEditing ? <textarea aria-label="Edit saved description" className="managed-edit-box" value={draft} onChange={(event) => setDraft(event.target.value)} /> : <strong className="managed-post-title" title={postTitle}>{postTitle.slice(0, 24)}</strong>}
              {viewsLine}
            </div>
          </article>;
        })}
        {!filteredPosts.length && <div className="empty-box compact-empty"><strong>{visiblePosts.length ? 'No posts match these filters' : 'No published posts yet'}</strong></div>}
        {filteredPosts.length > shownCount && <button className="managed-see-more" type="button" onClick={() => setShownCount((count) => count + MANAGE_PAGE_MORE)}>See more ({Math.min(MANAGE_PAGE_MORE, filteredPosts.length - shownCount)} of {filteredPosts.length - shownCount} remaining)</button>}
      </div>
    </div>
    <aside className="manage-posts-sidebar">
      <div className="glass-panel manage-side-card">
        <PanelTitle title="Drafts" action={`${draftPosts.length}`} />
        <div className="manage-side-list">
          {draftPosts.slice(0, 5).map((post) => <article className="manage-draft-row" key={post.id || post.externalPostId}><button type="button" onClick={() => onResume(post)}><FiFileText /><span><strong>{post.title || 'Untitled draft'}</strong><small>{new Date(post.createdAt || Date.now()).toLocaleDateString('en-IN')}</small></span></button><button className="icon-action danger" type="button" aria-label={`Delete ${post.title || 'draft'}`} title="Delete draft" onClick={() => setDeleteTarget(post)}><FiTrash2 /></button></article>)}
          {!draftPosts.length && <small>No drafts saved yet.</small>}
        </div>
      </div>
      <div className="glass-panel manage-side-card">
        <PanelTitle title="Recent Deleted Posts" action={`${recentDeleted.length}/5`} />
        <div className="manage-side-list deleted-list">
          {recentDeleted.map((post) => <span key={post.id || post.externalPostId}><FiTrash2 /><b>{post.title || post.pageName || 'Deleted post'}</b></span>)}
          {!recentDeleted.length && <small>Deleted post records will appear here.</small>}
        </div>
      </div>
    </aside>
  </section>{deleteTarget && <ConfirmDialog title="Delete saved post?" message="This removes the workspace record. The already-published platform post will remain unchanged." confirmLabel="Delete" onCancel={() => setDeleteTarget(null)} onConfirm={() => deletePost(deleteTarget)} />}</>;
}

const occasionRank = (event) => (/^national/i.test(event.category) ? 0 : /festival|religio|sikh|hindu|muslim|christian|jain|buddhist|regional|indigenous/i.test(event.category) ? 1 : event.priority === 'HIGH' ? 2 : 3);

function CalendarView({ onEditPost, onFestivalCreate, publishedPosts = [], setNotice, setPublicCalendar, setPublishedPosts, workspace, workspaceId }) {
  const [movedDays, setMovedDays] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(calendarYear, calendarMonthIndex, 1));
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCalendarPost, setSelectedCalendarPost] = useState(null);
  const [reviewComments, setReviewComments] = useState([]);
  const [rescheduleDraft, setRescheduleDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const monthOffset = (selectedMonth.getFullYear() - calendarYear) * 12 + selectedMonth.getMonth() - calendarMonthIndex;
  const monthLabel = selectedMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const occasions = useMemo(() => occasionsForYear(selectedMonth.getFullYear(), { state: workspace?.businessState }).filter((event) => Number(event.date.slice(5, 7)) === selectedMonth.getMonth() + 1), [selectedMonth, workspace?.businessState]);
  const selectedOccasions = occasions.filter((event) => Number(event.date.slice(8)) === selectedDay);
  useEffect(() => { setSelectedDay(null); setSelectedCalendarPost(null); setRescheduleDraft(''); setMovedDays({}); }, [workspaceId]);
  const calendarEntries = useMemo(() => {
    const savedEntries = publishedPosts.map((post) => savedPostToCalendarEntry(post, selectedMonth)).filter(Boolean);
    return savedEntries.map((post) => ({
      ...post,
      day: movedDays[post.id || post.title] || post.day
    }));
  }, [movedDays, publishedPosts, selectedMonth]);
  const workspaceRequest = useMemo(() => ({ withCredentials: true, headers: { 'x-workspace-id': workspaceId } }), [workspaceId]);
  const reviewByPost = useMemo(() => {
    const map = {};
    reviewComments.forEach((review) => { map[String(review.postId)] = review; });
    return map;
  }, [reviewComments]);
  const selectedDayPosts = selectedDay ? calendarEntries.filter((post) => post.day === selectedDay) : [];

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/api/calendar/reviews`, workspaceRequest)
      .then((response) => {
        if (!active) return;
        setPublicCalendar(Boolean(response.data.active));
        setReviewComments(response.data.reviews || []);
      })
      .catch(() => active && setReviewComments([]));
    return () => { active = false; };
  }, [setPublicCalendar, workspaceId, workspaceRequest]);

  const moveCalendarPost = async (post, targetDay) => {
    if (!post || !targetDay) return;
    if (post.postStatus === 'published') {
      setNotice({ type: 'warning', message: 'Published posts cannot be dragged. Open the original post from View.' });
      return;
    }
    setMovedDays((current) => ({ ...current, [post.id || post.title]: targetDay }));
    const savedPost = publishedPosts.find((item) => String(item.id) === String(post.id));
    const sourceDate = new Date(savedPost?.scheduledTime || savedPost?.createdAt || Date.now());
    const nextDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), targetDay, sourceDate.getHours(), sourceDate.getMinutes(), 0, 0);
    try {
      const response = await axios.patch(`${API_BASE}/api/posts/${post.id}`, { scheduledTime: nextDate.toISOString() }, workspaceRequest);
      setPublishedPosts((rows) => rows.map((item) => String(item.id) === String(post.id) ? response.data.post : item));
      setNotice({ type: 'success', message: `${post.title} rescheduled to ${nextDate.toLocaleString('en-IN')}.` });
    } catch (error) {
      setMovedDays((current) => ({ ...current, [post.id || post.title]: post.day }));
      setNotice({ type: 'error', message: error.response?.data?.error || 'Post could not be rescheduled.' });
    }
  };
  const deleteCalendarPost = async (target = selectedCalendarPost) => {
    if (!target) return;
    try {
      await axios.delete(`${API_BASE}/api/posts/${target.id}`, workspaceRequest);
      setPublishedPosts((rows) => rows.filter((item) => String(item.id) !== String(target.id)));
      setSelectedCalendarPost(null);
      setDeleteTarget(null);
      setNotice({ type: 'success', message: 'Post deleted from this workspace.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Post could not be deleted.' });
    }
  };
  const openReschedule = (post) => {
    setSelectedCalendarPost(post);
    setRescheduleDraft(toLocalDateTimeInput(post.scheduledTime || post.publishedAt || post.originalPost?.scheduledTime || post.originalPost?.createdAt));
  };
  const saveCalendarReschedule = async () => {
    if (!selectedCalendarPost || !rescheduleDraft) return;
    try {
      const nextDate = new Date(rescheduleDraft);
      const response = await axios.patch(`${API_BASE}/api/posts/${selectedCalendarPost.id}`, { scheduledTime: nextDate.toISOString() }, workspaceRequest);
      setPublishedPosts((rows) => rows.map((item) => String(item.id) === String(selectedCalendarPost.id) ? response.data.post : item));
      setSelectedCalendarPost(null);
      setRescheduleDraft('');
      setNotice({ type: 'success', message: 'Post schedule updated.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Schedule could not be updated.' });
    }
  };
  const calendarPostLink = (post) => publishedPostUrl(post.originalPost || post)
    || (post.postStatus === 'published' && post.id ? `${API_BASE}/api/posts/${encodeURIComponent(post.id)}/open?workspaceId=${encodeURIComponent(workspaceId)}` : '');

  return (<>
    <section className="calendar-workspace">
      <div className="glass-panel calendar-module">
        <div className="calendar-top">
          <div>
            <p className="kicker">Calendar</p>
            <h2>{monthLabel}</h2>
          </div>
          <div className="calendar-actions month-actions">
            <button className="icon-btn" type="button" title="Previous month" aria-label="Previous month" disabled={monthOffset <= 0} onClick={() => setSelectedMonth((date) => new Date(date.getFullYear(), date.getMonth() - 1, 1))}><FiChevronLeft /></button>
            <button className="btn subtle" type="button" onClick={() => { setSelectedMonth(new Date(calendarYear, calendarMonthIndex, 1)); setNotice({ type: 'success', message: 'Showing current month.' }); }}>Today</button>
            <button className="icon-btn" type="button" title="Next month" aria-label="Next month" disabled={monthOffset >= 3} onClick={() => setSelectedMonth((date) => new Date(date.getFullYear(), date.getMonth() + 1, 1))}><FiChevronRight /></button>
          </div>
        </div>

        <div className="calendar-grid" style={{ '--calendar-weeks': Math.ceil(monthDays(selectedMonth).length / 7) }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <strong className="weekday" key={day}>{day}</strong>
          ))}
          {monthDays(selectedMonth).map((day, index) => {
            const posts = calendarEntries.filter((post) => post.day === day);
            const dayEvents = day ? occasions.filter((event) => Number(event.date.slice(8)) === day).sort((a, b) => occasionRank(a) - occasionRank(b)) : [];
            return (
              <div
                className={cx('day-cell', dayEvents.length > 0 && 'has-occasion', day === calendarToday.getDate() && selectedMonth.getMonth() === calendarMonthIndex && selectedMonth.getFullYear() === calendarYear && 'today')}
                key={`${day || 'blank'}-${index}`}
                onClick={() => day && setSelectedDay(day)}
                onDragOver={(event) => day && event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const postKey = event.dataTransfer.getData('text/plain');
                  const movingPost = calendarEntries.find((post) => String(post.id || post.title) === postKey);
                  moveCalendarPost(movingPost, day);
                }}
              >
                {day && <button className="calendar-date-trigger" type="button" title={dayEvents.length ? dayEvents.map((event) => event.name).join(' / ') : undefined} aria-label={`${day} ${monthLabel}${dayEvents.length ? `, ${dayEvents.length} special ${dayEvents.length === 1 ? 'event' : 'events'}` : ''}`} onClick={(event) => { event.stopPropagation(); setSelectedDay(day); }}><span className="day-number">{day}</span>{dayEvents.length > 0 && <span className="day-events-dot" aria-hidden="true" />}</button>}
                {day && <button className="calendar-day-add" type="button" title="Create on this date" aria-label={`Create post on ${day} ${monthLabel}`} onClick={(event) => { event.stopPropagation(); onEditPost({ scheduledTime: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day, 10, 0, 0, 0).toISOString() }); }}><FiPlus /></button>}
                {dayEvents.length > 0 && <span className="cal-occasion" title={dayEvents.map((event) => event.name).join(' / ')}><em>{dayEvents[0].name}</em>{dayEvents.length > 1 && <b>+{dayEvents.length - 1}</b>}</span>}
                {posts.length > 0 && <div className="cal-chips">
                  {posts.slice(0, 5).map((post) => {
                    const platform = platformById(post.platform);
                    const review = reviewByPost[String(post.id)];
                    const reviewClass = review?.status || post.approvalStatus || (post.status === 'Ready' ? 'approved' : post.status === 'Needs media' ? 'declined' : 'changes');
                    return <button
                      className={cx('cal-chip', reviewClass, post.postStatus === 'published' && 'locked')}
                      draggable={post.postStatus !== 'published'}
                      key={`chip-${post.day}-${post.id || post.title}`}
                      title={`${post.time} · ${post.title}`}
                      onClick={(event) => { event.stopPropagation(); setSelectedDay(day); setSelectedCalendarPost(post); }}
                      onDragStart={(event) => event.dataTransfer.setData('text/plain', String(post.id || post.title))}
                      style={{ '--platform': platform.color }}
                      type="button"
                    ><b className="calendar-platform-logo"><PlatformLogo id={platform.id} label={platform.label} /></b><i>{post.time}</i><span>{post.title}</span></button>;
                  })}
                  {posts.length > 5 && <button className="cal-chip-more" type="button" onClick={(event) => { event.stopPropagation(); setSelectedDay(day); }}>+{posts.length - 5} more</button>}
                </div>}
                {posts.slice(0, 1).map((post) => {
                  const platform = platformById(post.platform);
                  const review = reviewByPost[String(post.id)];
                  const reviewClass = review?.status || post.approvalStatus || (post.status === 'Ready' ? 'approved' : post.status === 'Needs media' ? 'declined' : 'changes');
                  const reviewLabel = review?.status === 'changes' ? 'Changes' : review?.status ? `${review.status.charAt(0).toUpperCase()}${review.status.slice(1)}` : post.status;
                  return (
                    <button
                      className={cx('cal-post', post.source === 'mongo' && 'mongo-post', reviewClass, post.postStatus === 'published' && 'locked')}
                      draggable={post.postStatus !== 'published'}
                      key={`${post.day}-${post.id || post.title}`}
                      onClick={(event) => { event.stopPropagation(); setSelectedDay(day); setSelectedCalendarPost(post); }}
                      onDragStart={(event) => event.dataTransfer.setData('text/plain', String(post.id || post.title))}
                      style={{ '--platform': platform.color }}
                      type="button"
                    >
                      <span className="calendar-post-thumb">{post.originalPost?.thumbnailUrl || post.thumbnailUrl ? <img src={post.originalPost?.thumbnailUrl || post.thumbnailUrl} alt="" /> : <PlatformLogo id={platform.id} label={platform.label} />}</span>
                      <small><b className="calendar-platform-logo"><PlatformLogo id={platform.id} label={platform.label} /></b> {post.time}</small>
                      <strong>{post.title}</strong>
                      <span>{reviewLabel}</span>
                      <em className="cal-status-icons">{reviewClass === 'approved' ? <FiCheck /> : reviewClass === 'declined' ? <FiX /> : reviewClass === 'changes' ? <FiMessageSquare /> : <FiClock />}</em>
                    </button>
                  );
                })}
                {posts.length > 1 && <button className="calendar-more" type="button" onClick={(event) => { event.stopPropagation(); setSelectedDay(day); }} aria-label={`View all ${posts.length} posts on ${day} ${monthLabel}`}>+{posts.length - 1} more</button>}
                {posts.length > 0 && <span className="mobile-calendar-markers" aria-hidden="true">{posts.slice(0, 4).map((post) => {
                  const review = reviewByPost[String(post.id)];
                  const status = review?.status || post.approvalStatus || (post.status === 'Ready' ? 'approved' : post.status === 'Needs media' ? 'declined' : 'changes');
                  return <i className={status} key={`marker-${post.id || post.title}`}>{status === 'approved' ? '✓' : status === 'declined' ? '×' : ''}</i>;
                })}</span>}
              </div>
            );
          })}
        </div>
        {!calendarEntries.length && (
          <div className="empty-box centered-empty calendar-empty-month"><FiCalendar /><strong>No posts this month</strong><small>Schedule or publish a post and it will appear here automatically.</small><button className="btn primary" type="button" onClick={() => onEditPost({ scheduledTime: new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1, 10, 0, 0, 0).toISOString() })}>Create post</button></div>
        )}
      </div>

      <aside className="calendar-side">
        <div className="glass-panel">
          <PanelTitle title="Client changes" action={`${reviewComments.length}`} />
          {reviewComments.slice().reverse().slice(0, 8).map((item, index) => (
            <div className={cx('review-comment', item.status)} key={`${item.postId}-${item.createdAt || index}`}>
              <strong>{item.status === 'changes' ? 'Changes requested' : item.status}</strong>
              <small>{item.comment || `${item.reviewerName || 'Client'} reviewed this post.`}</small>
            </div>
          ))}
          {!reviewComments.length && <div className="empty-box compact-empty"><strong>No client reviews yet</strong><small>Approvals and change comments will appear here.</small></div>}
        </div>

        <div className="glass-panel">
          <PanelTitle title={selectedCalendarPost ? selectedCalendarPost.title : 'No post selected'} />
          {selectedCalendarPost ? (
            <div className="calendar-selected">
              {(() => {
                const schedule = scheduleDisplay(selectedCalendarPost.scheduledTime || selectedCalendarPost.publishedAt);
                return <div className="calendar-schedule-summary">
                  <span className={cx('calendar-status-badge', selectedCalendarPost.postStatus)}>{selectedCalendarPost.status}</span>
                  <span><FiCalendar /> {schedule.date}</span>
                  <span><FiClock /> {schedule.time}</span>
                  {selectedCalendarPost.postStatus === 'scheduled' && <strong>{schedule.relative}</strong>}
                  <small>{(selectedCalendarPost.platforms || [selectedCalendarPost.platform]).map((id) => platformById(id).label).join(' · ')}</small>
                  {selectedCalendarPost.postStatus === 'failed' && selectedCalendarPost.originalPost?.errorMessage && <p className="calendar-publish-error"><FiAlertCircle /> {selectedCalendarPost.originalPost.errorMessage}</p>}
                </div>;
              })()}
              <div className="calendar-icon-actions">
                {selectedCalendarPost.postStatus === 'published' && calendarPostLink(selectedCalendarPost) ? <a className="icon-btn" href={calendarPostLink(selectedCalendarPost)} target="_blank" rel="noreferrer" title="View original post" aria-label="View original post"><FiEye /></a> : <button className="icon-btn" type="button" title="Edit post" aria-label="Edit post" onClick={() => onEditPost(selectedCalendarPost.originalPost || selectedCalendarPost)}><FiEdit3 /></button>}
                {selectedCalendarPost.postStatus !== 'published' && <button className="icon-btn" type="button" title="Reschedule post" aria-label="Reschedule post" onClick={() => openReschedule(selectedCalendarPost)}><FiClock /></button>}
                <button className="icon-btn danger-icon" type="button" title="Delete saved post" aria-label="Delete saved post" onClick={() => setDeleteTarget(selectedCalendarPost)}><FiX /></button>
              </div>
              {rescheduleDraft && selectedCalendarPost.postStatus !== 'published' && <div className="calendar-reschedule"><input type="datetime-local" value={rescheduleDraft} onChange={(event) => setRescheduleDraft(event.target.value)} /><button className="btn primary" type="button" onClick={saveCalendarReschedule}>Save time</button></div>}
            </div>
          ) : (
            <div className="empty-box compact-empty"><strong>Select a post</strong><small>Click any calendar post to edit/delete.</small></div>
          )}
        </div>
      </aside>
      {selectedDay && (
        <div className="calendar-day-modal" role="dialog" aria-modal="true" aria-label={`${selectedDay} ${monthLabel} schedule`} onClick={() => { setSelectedDay(null); setRescheduleDraft(''); }}>
          <section onClick={(event) => event.stopPropagation()}>
            <header><div><p className="kicker">Schedule</p><h3>{selectedDay} {monthLabel}</h3></div><button type="button" aria-label="Close schedule" onClick={() => { setSelectedDay(null); setRescheduleDraft(''); }}><FiX /></button></header>
            <div className="calendar-day-agenda">
              {selectedOccasions.map((event) => <div className="occasion-detail" key={event.id}><span><strong>{event.name}</strong><small>{event.category}{event.dateNote && !/^Fixed$/i.test(event.dateNote) ? ` · ${event.dateNote}` : ''}</small></span><button className="icon-btn occasion-create" type="button" title={`Create AI post for ${event.name}`} aria-label={`Create AI post for ${event.name}`} onClick={() => onFestivalCreate(event)}><FiPlus /></button></div>)}
              {selectedDayPosts.map((post) => {
                const review = reviewByPost[String(post.id)];
                const reviewClass = review?.status || post.approvalStatus || 'pending';
                const schedule = scheduleDisplay(post.scheduledTime || post.publishedAt);
                const platformNames = (post.platforms || [post.platform]).map((id) => platformById(id).label).join(' · ');
                return <article key={`agenda-${post.id}`} className={cx('agenda-post', reviewClass)}>
                  <div><strong>{post.title}</strong><div className="agenda-schedule-meta"><span className={cx('calendar-status-badge', post.postStatus)}>{post.status}</span><span><FiCalendar /> {schedule.date}</span><span><FiClock /> {schedule.time}</span>{post.postStatus === 'scheduled' && <b>{schedule.relative}</b>}<small>{platformNames}</small></div><p>{post.content || 'No caption saved for this post.'}</p>{post.postStatus === 'failed' && post.originalPost?.errorMessage && <em className="calendar-publish-error"><FiAlertCircle /> {post.originalPost.errorMessage}</em>}{review?.comment && <em><FiMessageSquare /> {review.comment}</em>}</div>
                  <div className="calendar-icon-actions">
                    {post.postStatus === 'published' && calendarPostLink(post) ? <a className="icon-btn" href={calendarPostLink(post)} target="_blank" rel="noreferrer" title="View original post" aria-label="View original post"><FiEye /></a> : <button className="icon-btn" type="button" title="Edit post" aria-label="Edit post" onClick={() => onEditPost(post.originalPost || post)}><FiEdit3 /></button>}
                    {post.postStatus !== 'published' && <button className="icon-btn" type="button" title="Reschedule post" aria-label="Reschedule post" onClick={() => openReschedule(post)}><FiClock /></button>}
                    <button className="icon-btn danger-icon" type="button" title="Delete saved post" aria-label="Delete saved post" onClick={() => setDeleteTarget(post)}><FiX /></button>
                  </div>
                </article>;
              })}
              {!selectedDayPosts.length && <div className="empty-box compact-empty"><strong>No posts on this date</strong><small>Use the plus button on a date to start a scheduled post.</small></div>}
            </div>
            {selectedCalendarPost && rescheduleDraft && selectedCalendarPost.postStatus !== 'published' && <div className="calendar-reschedule modal-reschedule"><input type="datetime-local" value={rescheduleDraft} onChange={(event) => setRescheduleDraft(event.target.value)} /><button className="btn primary" type="button" onClick={saveCalendarReschedule}>Save schedule</button></div>}
          </section>
        </div>
      )}
    </section>
    {deleteTarget && <ConfirmDialog title="Delete scheduled post?" message="This removes the post from this workspace calendar." confirmLabel="Delete" onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteCalendarPost(deleteTarget)} />}
  </>);
}

function Messaging({ inboxFilter, initialMode = 'messages', selectedInboxId, setNotice, setInboxFilter, setSelectedInboxId, workspaceId, onNavigate }) {
  const [replyDraft, setReplyDraft] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [networkFilter, setNetworkFilter] = useState('All platforms');
  const [inboxKind, setInboxKind] = useState('messages');
  const [inboxMode, setInboxMode] = useState(initialMode);
  useEffect(() => setInboxMode(initialMode), [initialMode]);
  const [inboxSearch, setInboxSearch] = useState('');
  const [inboxSort, setInboxSort] = useState('Newest first');
  const [providerInbox, setProviderInbox] = useState([]);
  const [providerInboxConfigured, setProviderInboxConfigured] = useState(false);
  const [automationRules, setAutomationRules] = useState([]);
  const inboxFlags = useRef({});
  useEffect(() => { inboxFlags.current = {}; setReplyDraft(''); setShowEmojiPicker(false); setAutomationRules([]); }, [workspaceId]);
  const [, setInboxAccounts] = useState([]);
  const [providerInboxIssue, setProviderInboxIssue] = useState('');
  const [inboxCursors, setInboxCursors] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const expandedInboxRef = useRef(false);
  const inboxRequestRef = useRef(0);
  const inboxFilterRef = useRef(networkFilter);
  inboxFilterRef.current = networkFilter;
  const inboxKindRef = useRef(inboxKind);
  inboxKindRef.current = inboxKind;
  const pendingInboxRef = useRef(new Set());
  const [inboxLoading, setInboxLoading] = useState(true);
  const [sendingReply, setSendingReply] = useState(false);
  const [automationDraft, setAutomationDraft] = useState({ name: '', keywords: '', reply: '', platforms: ['facebook', 'instagram'], triggers: ['message'] });
  const [editingAutomationId, setEditingAutomationId] = useState('');
  const [automationMenuId, setAutomationMenuId] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [removeConversationTarget, setRemoveConversationTarget] = useState(null);
  const inboxWorkspaceRef = useRef(workspaceId);
  inboxWorkspaceRef.current = workspaceId;
  const activeItems = providerInbox.filter((item) => !item.hidden && (item.type === 'comment' ? 'comments' : 'messages') === inboxKind && (networkFilter === 'All platforms' || item.network === networkFilter));
  const selectedInbox = activeItems.find((item) => String(item.id) === String(selectedInboxId)) || activeItems[0] || null;
  const activeMessageId = selectedInbox?.id;
  const visibleInbox = conversationList(providerInbox
    .filter((item) => {
      const text = `${item.from || ''} ${item.body || ''} ${item.network || ''}`.toLowerCase();
      const matchesText = !inboxSearch.trim() || text.includes(inboxSearch.trim().toLowerCase());
      const matchesStatus = inboxFilter === 'All'
        || item.status === inboxFilter
        || (inboxFilter === 'Starred' && item.starred);
      return !item.hidden && matchesText && matchesStatus && (networkFilter === 'All platforms' || item.network === networkFilter);
    })
    .sort((a, b) => {
      if (inboxSort === 'Unread first') {
        const unreadDelta = (b.status === 'Unread' ? 1 : 0) - (a.status === 'Unread' ? 1 : 0);
        if (unreadDelta) return unreadDelta;
      }
      if (inboxSort === 'Oldest first') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }), inboxKind);
  const selectedConversation = selectedInbox && inboxKind === 'messages'
    ? activeItems.filter((item) => conversationKey(item) === conversationKey(selectedInbox)).sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
    : selectedInbox ? [selectedInbox] : [];
  useEffect(() => {
    setReplyDraft(activeMessageId ? localStorage.getItem(`easten-reply-${activeMessageId}`) || '' : '');
  }, [activeMessageId]);
  useEffect(() => {
    setLabelDraft(selectedInbox?.label || '');
    setNotesDraft(selectedInbox?.notes || '');
  }, [selectedInbox?.id, selectedInbox?.label, selectedInbox?.notes]);
  useEffect(() => { expandedInboxRef.current = false; inboxRequestRef.current += 1; setProviderInbox([]); setInboxAccounts([]); setInboxCursors({}); setProviderInboxIssue(''); setInboxLoading(true); }, [workspaceId]);
  useEffect(() => { expandedInboxRef.current = false; setInboxCursors({}); setProviderInboxIssue(''); }, [networkFilter, inboxKind]);

  const loadProviderInbox = useCallback(async () => {
    if (!workspaceId || String(workspaceId).startsWith('workspace-')) return;
    const providerParam = { Facebook: 'facebook', Instagram: 'instagram', Threads: 'threads' }[networkFilter] || '';
    const requestId = inboxRequestRef.current;
    const requestKey = `${workspaceId}:${networkFilter}:${inboxKind}`;
    if (pendingInboxRef.current.has(requestKey)) return;
    pendingInboxRef.current.add(requestKey);
    setInboxLoading(true);
    const isCurrentRequest = () => inboxRequestRef.current === requestId
      && inboxWorkspaceRef.current === workspaceId
      && inboxFilterRef.current === networkFilter && inboxKindRef.current === inboxKind;
    const readProvider = async (provider) => {
      const response = await axios.get(`${API_BASE}/api/inbox/messages`, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId },
        params: { provider, kind: inboxKind },
        timeout: 30000
      });
      return response.data;
    };

    if (!providerParam) {
      const results = {};
      const applyProgress = () => {
        if (!isCurrentRequest()) return;
        const payloads = Object.values(results);
        Object.entries(results).forEach(([provider, data]) => {
          if (data.failed) return;
          setProviderInbox((current) => expandedInboxRef.current
            ? mergeProviderMessages([current, (data.messages || []).map((item) => ({ ...item, ...inboxFlags.current[item.id] }))])
            : replaceProviderMessages(current, provider, inboxKind,
              (data.messages || []).map((item) => ({ ...item, ...inboxFlags.current[item.id] })), data.issue));
        });
        setInboxAccounts(mergeProviderAccounts(payloads.map((data) => data.accounts || [])));
        setProviderInboxConfigured(payloads.some((data) => Boolean(data.configured)));
        setProviderInboxIssue(providerIssues(results));
        setInboxCursors(Object.fromEntries(Object.entries(results)
          .filter(([, data]) => data.nextCursor)
          .map(([provider, data]) => [provider, data.nextCursor])));
        setInboxLoading(false);
      };

      await Promise.allSettled(['facebook', 'instagram', 'threads'].map(async (provider) => {
        try {
          results[provider] = await readProvider(provider);
        } catch (error) {
          results[provider] = {
            failed: true,
            configured: Boolean(error.response?.data?.configured),
            accounts: error.response?.data?.accounts || [],
            messages: [],
            issue: error.response?.data?.issue || error.response?.data?.error || 'Provider inbox could not be refreshed.'
          };
        }
        applyProgress();
      }));
      pendingInboxRef.current.delete(requestKey);
      return;
    }

    try {
      const data = await readProvider(providerParam);
      if (!isCurrentRequest()) return;
      const rows = (data.messages || []).map((item) => ({ ...item, ...inboxFlags.current[item.id] }));
      setProviderInbox((current) => expandedInboxRef.current ? mergeProviderMessages([current, rows]) : replaceProviderMessages(current, providerParam, inboxKind, rows, data.issue));
      if (!expandedInboxRef.current) setInboxCursors(data.nextCursor ? { [providerParam]: data.nextCursor } : {});
      setProviderInboxConfigured(Boolean(data.configured));
      setInboxAccounts(data.accounts || []);
      setProviderInboxIssue(friendlyProviderIssue(data.issue));
    } catch (error) {
      if (!isCurrentRequest()) return;
      setProviderInboxIssue(friendlyProviderIssue(error.response?.data?.issue || error.response?.data?.error) || 'Provider inbox could not be refreshed.');
      if (error.response?.status !== 404) setNotice({ type: 'warning', message: error.response?.data?.error || 'Provider inbox could not be refreshed.' });
    } finally { pendingInboxRef.current.delete(requestKey); if (isCurrentRequest()) setInboxLoading(false); }
  }, [inboxKind, networkFilter, setNotice, workspaceId]);

  const loadMoreMessages = async () => {
    const cursorEntries = Object.entries(inboxCursors).filter(([, cursor]) => cursor);
    if (!cursorEntries.length || loadingMore) return;
    expandedInboxRef.current = true;
    setLoadingMore(true);
    try {
      const responses = await Promise.allSettled(cursorEntries.map(async ([provider, cursor]) => {
        const { data } = await axios.get(`${API_BASE}/api/inbox/messages`, {
          withCredentials: true, headers: { 'x-workspace-id': workspaceId },
          params: { provider, kind: inboxKind, cursor }, timeout: 30000
        });
        return { provider, data };
      }));
      if (inboxWorkspaceRef.current !== workspaceId || inboxFilterRef.current !== networkFilter || inboxKindRef.current !== inboxKind) return;
      const loaded = responses.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      const rows = loaded.flatMap(({ data }) => (data.messages || []).map((item) => ({ ...item, ...inboxFlags.current[item.id] })));
      setProviderInbox((current) => mergeProviderMessages([current, rows]));
      setInboxCursors((current) => {
        const next = { ...current };
        loaded.forEach(({ provider, data }) => {
          if (data.nextCursor) next[provider] = data.nextCursor;
          else delete next[provider];
        });
        return next;
      });
      if (responses.some((result) => result.status === 'rejected')) {
        setNotice({ type: 'warning', message: 'Some older provider conversations could not be loaded. Try again.' });
      }
    } catch (error) {
      setNotice({ type: 'warning', message: error.response?.data?.issue || 'Older conversations could not be loaded. Try again.' });
    } finally { setLoadingMore(false); }
  };

  useEffect(() => {
    loadProviderInbox();
    const timer = window.setInterval(loadProviderInbox, 30000);
    return () => window.clearInterval(timer);
  }, [loadProviderInbox]);

  const loadAutomations = useCallback(async () => {
    if (!workspaceId || String(workspaceId).startsWith('workspace-')) return;
    try {
      const response = await axios.get(`${API_BASE}/api/inbox/automations`, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      if (inboxWorkspaceRef.current !== workspaceId) return;
      setAutomationRules(response.data.rules || []);
    } catch (error) {
      if (error.response?.status !== 404) setNotice({ type: 'warning', message: error.response?.data?.error || 'Automation rules could not be loaded.' });
    }
  }, [setNotice, workspaceId]);

  useEffect(() => { loadAutomations(); }, [loadAutomations]);

  const selectProviderMessage = async (item) => {
    setSelectedInboxId(item.id);
    if (item.status !== 'Unread') return;
    try {
      await axios.patch(`${API_BASE}/api/inbox/messages/${item.id}`, { status: 'Read' }, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      setProviderInbox((rows) => rows.map((row) => String(row.id) === String(item.id) ? { ...row, status: 'Read' } : row));
      inboxFlags.current[item.id] = { ...inboxFlags.current[item.id], status: 'Read' };
    } catch (error) {
      setNotice({ type: 'warning', message: error.response?.data?.error || 'Message status could not be updated.' });
    }
  };

  const updateConversationFlag = async (item, patch) => {
    const previous = { status: item.status, starred: Boolean(item.starred), label: item.label || '', notes: item.notes || '', hidden: Boolean(item.hidden) };
    inboxFlags.current[item.id] = { ...inboxFlags.current[item.id], ...patch };
    setProviderInbox((rows) => rows.map((row) => row.id === item.id ? { ...row, ...patch } : row));
    try {
      const response = await axios.patch(`${API_BASE}/api/inbox/messages/${item.id}`, patch, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      const saved = { status: response.data.status, starred: Boolean(response.data.starred), label: response.data.label || '', notes: response.data.notes || '', hidden: Boolean(response.data.hidden) };
      inboxFlags.current[item.id] = { ...inboxFlags.current[item.id], ...saved };
      setProviderInbox((rows) => rows.map((row) => row.id === item.id ? { ...row, ...saved } : row));
    } catch (error) {
      inboxFlags.current[item.id] = { ...inboxFlags.current[item.id], ...previous };
      setProviderInbox((rows) => rows.map((row) => row.id === item.id ? { ...row, ...previous } : row));
      setNotice({ type: 'warning', message: error.response?.data?.error || 'Message flag could not be saved.' });
    }
  };

  const saveReply = () => {
    if (!selectedInbox) return;
    if (!replyDraft.trim()) {
      setNotice({ type: 'warning', message: 'Write a reply before saving it.' });
      return;
    }

    localStorage.setItem(`easten-reply-${selectedInbox.id}`, replyDraft.trim());
    setNotice({ type: 'success', message: 'Reply draft saved.' });
  };
  const sendReply = async () => {
    if (!selectedInbox) return;
    if (!replyDraft.trim()) {
      setNotice({ type: 'warning', message: 'Write a reply before sending it.' });
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API_BASE}/api/inbox/messages/${selectedInbox.id}/reply`, { text: replyDraft.trim() }, { withCredentials: true, headers: { 'x-workspace-id': workspaceId } });
      setReplyDraft('');
      localStorage.removeItem(`easten-reply-${selectedInbox.id}`);
      setNotice({ type: 'success', message: 'Reply sent.' });
    } catch (error) { setNotice({ type: 'warning', message: error.response?.data?.error || 'Delivery could not be confirmed. Check the native inbox.' }); }
    finally { setSendingReply(false); loadProviderInbox(); }
  };
  const saveAutomation = async () => {
    try {
      const payload = {
        ...automationDraft,
        keywords: automationDraft.keywords.split(',')
      };
      const config = { withCredentials: true, headers: { 'x-workspace-id': workspaceId } };
      const response = editingAutomationId
        ? await axios.patch(`${API_BASE}/api/inbox/automations/${editingAutomationId}`, payload, config)
        : await axios.post(`${API_BASE}/api/inbox/automations`, payload, config);
      setAutomationRules((current) => editingAutomationId
        ? current.map((rule) => rule.id === editingAutomationId ? response.data.rule : rule)
        : [response.data.rule, ...current]);
      setAutomationDraft({ name: '', keywords: '', reply: '', platforms: ['facebook', 'instagram'], triggers: ['message'] });
      setEditingAutomationId('');
      setNotice({ type: 'success', message: editingAutomationId ? 'Automation rule updated.' : 'Automation rule saved.' });
    } catch (error) {
      setNotice({ type: 'warning', message: error.response?.data?.error || 'Automation rule could not be saved.' });
      if (error.response?.status === 409) await loadAutomations();
    }
  };
  const editAutomation = (rule) => {
    setEditingAutomationId(rule.id);
    setAutomationDraft({ name: rule.name, keywords: rule.keywords.join(', '), reply: rule.reply, platforms: rule.platforms, triggers: rule.triggers?.length ? rule.triggers : ['message'] });
  };
  const toggleAutomation = async (rule) => {
    try {
      const response = await axios.patch(`${API_BASE}/api/inbox/automations/${rule.id}`, { active: !(rule.active && rule.deliveryEnabled) }, { withCredentials: true, headers: { 'x-workspace-id': workspaceId } });
      setAutomationRules((current) => current.map((item) => item.id === rule.id ? response.data.rule : item));
    } catch (error) {
      setNotice({ type: 'warning', message: error.response?.data?.error || 'Automation rule could not be updated.' });
    }
  };
  const deleteAutomation = async (rule) => {
    try {
      await axios.delete(`${API_BASE}/api/inbox/automations/${rule.id}`, { withCredentials: true, headers: { 'x-workspace-id': workspaceId } });
      setAutomationRules((current) => current.filter((item) => item.id !== rule.id));
      setNotice({ type: 'success', message: 'Automation rule deleted.' });
    } catch (error) {
      setNotice({ type: 'warning', message: error.response?.data?.error || 'Automation rule could not be deleted.' });
    }
  };

  return (<>
    <section className="inbox-layout inbox-v2">
      {inboxMode === 'messages' && <div className="inbox-message-main">
        <div className="glass-panel inbox-list">
        <div className="inbox-kind-tabs" role="tablist" aria-label="Inbox type">
          <button type="button" role="tab" aria-selected={inboxKind === 'messages'} className={cx(inboxKind === 'messages' && 'active')} onClick={() => { setInboxKind('messages'); setSelectedInboxId(null); }}>Messages</button>
          <button type="button" role="tab" aria-selected={inboxKind === 'comments'} className={cx(inboxKind === 'comments' && 'active')} onClick={() => { setInboxKind('comments'); setSelectedInboxId(null); }}>Comments</button>
        </div>
        <div className="inbox-search-row">
          <select aria-label="Message platform filter" value={networkFilter} onChange={(event) => setNetworkFilter(event.target.value)}>{['All platforms', 'Instagram', 'Facebook', 'Threads'].map((filter) => <option key={filter}>{filter}</option>)}</select>
          <select aria-label="Message sort" value={inboxSort} onChange={(event) => setInboxSort(event.target.value)}>{['Newest first', 'Oldest first', 'Unread first'].map((filter) => <option key={filter}>{filter}</option>)}</select>
          <button className="icon-btn inbox-refresh" type="button" title={`Refresh ${inboxKind}`} aria-label="Sync messages" disabled={inboxLoading} onClick={loadProviderInbox}><FiRefreshCw /></button>
        </div>
        <div className="inbox-filter-line">
          <label className="inbox-search-wrap"><FiMessageSquare /><input className="inbox-search" value={inboxSearch} onChange={(event) => setInboxSearch(event.target.value)} placeholder="Search inbox" aria-label="Search inbox" />{inboxSearch && <button type="button" aria-label="Clear inbox search" title="Clear search" onClick={() => setInboxSearch('')}><FiX /></button>}</label>
          <div className="inbox-status-filters" aria-label="Message filters">{['All', 'Unread', 'Follow up', 'Starred'].map((filter) => <button className={cx(inboxFilter === filter && 'active')} aria-pressed={inboxFilter === filter} key={filter} onClick={() => setInboxFilter(filter)} type="button">{filter}</button>)}</div>
          {selectedInbox?.label && <span className="active-label-chip"><FiTag />{selectedInbox.label}</span>}
        </div>
        {inboxLoading && !visibleInbox.length && <Skeleton label={`Loading ${inboxKind}`} />}
        {providerInboxIssue && <p className="inbox-refresh-issue" role="status">{visibleInbox.length ? 'Showing last loaded results. ' : ''}{providerInboxIssue}</p>}
        <div className="inbox-message-scroll" role="region" aria-label={inboxKind === 'comments' ? 'Comments' : 'Messages'} tabIndex={0}>
        {visibleInbox.map((item) => {
          const platform = platformById(String(item.provider || item.network || '').toLowerCase());
          return (
            <button
              className={cx('inbox-item', selectedInbox && conversationKey(selectedInbox) === conversationKey(item) && 'active')}
              aria-pressed={Boolean(selectedInbox && conversationKey(selectedInbox) === conversationKey(item))}
              key={item.id}
              onClick={() => selectProviderMessage(item)}
              style={{ '--platform': platform.color }}
              type="button"
            >
              <strong>{item.from}</strong>
              <b className="message-platform-icon" title={platform.label}><PlatformLogo id={platform.id} label={platform.label} /></b>
              <p>{item.type === 'comment' && <strong>Public comment: </strong>}{item.body}</p>
              <em>{item.status === 'Follow up' ? 'Follow Up' : item.status}</em>
              <time>{new Date(item.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</time>
            </button>
          );
        })}
        {!visibleInbox.length && !inboxLoading && <div className="empty-box centered-empty inbox-empty"><FiMessageSquare /><strong>{inboxKind === 'comments' ? 'No comments available' : 'No conversations available'}</strong><small>{providerInboxIssue || (providerInboxConfigured ? 'No results for this platform and filter yet.' : 'Connect a channel with inbox access to begin.')}</small>{!providerInboxConfigured && <button className="btn primary" type="button" onClick={() => onNavigate?.('profiles')}>Connect channels</button>}</div>}
        {Object.values(inboxCursors).some(Boolean) && <button className="secondary-btn" type="button" disabled={loadingMore} onClick={loadMoreMessages}>{loadingMore ? 'Loading...' : 'Load more conversations'}</button>}
        </div>
        </div>
        <div className="glass-panel conversation colorful-conversation">
        {selectedInbox ? (
          <>
            <header className="conversation-header"><div><strong>{selectedInbox.from}</strong><small>{selectedInbox.network}</small></div><div><button className={cx('icon-btn', selectedInbox.starred && 'active')} type="button" aria-pressed={Boolean(selectedInbox.starred)} title="Star conversation" onClick={() => updateConversationFlag(selectedInbox, { starred: !selectedInbox.starred })}><FiStar /></button><button className="icon-btn danger-icon" type="button" title="Remove from EastenSocial inbox" onClick={() => setRemoveConversationTarget(selectedInbox)}><FiTrash2 /></button></div></header>
            <div className="conversation-body">
              {inboxKind === 'comments' && <p className="delivery-status">Public reply. Your response will be visible on the post.</p>}
              {selectedInbox.postUrl && <a href={selectedInbox.postUrl} target="_blank" rel="noopener noreferrer">View original post</a>}
              {selectedConversation.map((item, index) => {
                const date = new Date(item.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' });
                const previousDate = index ? new Date(selectedConversation[index - 1].createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '';
                return <React.Fragment key={item.id}>{date !== previousDate && <div className="conversation-day">{date}</div>}<div className="bubble inbound">{item.body}<time>{new Date(item.createdAt).toLocaleTimeString('en-IN', { timeStyle: 'short' })}</time></div>{(item.attachments || []).filter((attachment) => attachment.url).map((attachment, attachmentIndex) => <a key={attachmentIndex} href={attachment.url} target="_blank" rel="noopener noreferrer">Open {attachment.type} attachment</a>)}{item.delivery && <><div className="bubble outbound">{item.delivery.text}</div><p className="delivery-status">{item.delivery.status} {item.delivery.issue}</p></>}</React.Fragment>;
              })}
            </div>
            <div className="reply-box">
              <textarea maxLength={2000} value={replyDraft} onChange={(event) => setReplyDraft(event.target.value)} placeholder="Write a reply..." />
              <div className="reply-actions"><button className="icon-btn" type="button" title="Add attachment" aria-label="Add attachment" onClick={() => setNotice({ type: 'warning', message: 'Attachments will be enabled after provider media-send permissions are approved.' })}><FiPaperclip /></button><span className="emoji-picker-wrap"><button className={cx('icon-btn', showEmojiPicker && 'active')} type="button" title="Add emoji" aria-label="Add emoji" aria-expanded={showEmojiPicker} onClick={() => setShowEmojiPicker((current) => !current)}><FiSmile /></button>{showEmojiPicker && <span className="emoji-picker" role="menu" aria-label="Choose an emoji">{['😀', '😊', '👍', '🙏', '❤️', '🎉', '✅', '📞'].map((emoji) => <button key={emoji} type="button" role="menuitem" onClick={() => { setReplyDraft((current) => `${current}${current && !current.endsWith(' ') ? ' ' : ''}${emoji}`); setShowEmojiPicker(false); }}>{emoji}</button>)}</span>}</span><button className="btn subtle" type="button" onClick={saveReply}>Save</button><button className="btn primary icon-text-btn" type="button" disabled={sendingReply || !selectedInbox.canReply || Boolean(selectedInbox.delivery) || !replyDraft.trim()} onClick={sendReply}><FiSend />{sendingReply ? 'Sending' : 'Send'}</button></div>
              {!selectedInbox.canReply && <p className="delivery-status">Reply window closed. A new incoming message is required.</p>}
            </div>
          </>
        ) : <div className="empty-box centered-empty conversation-empty"><FiMessageSquare /><strong>{providerInboxConfigured ? 'No conversation selected' : 'Inbox sync is not configured yet'}</strong><small>{providerInboxConfigured ? 'Select a conversation from the list to read and reply.' : 'Facebook, Instagram and Threads messages are read live after permissions are active.'}</small><button className="btn primary" type="button" onClick={() => onNavigate?.(providerInboxConfigured ? 'messaging' : 'profiles')}>{providerInboxConfigured ? 'Refresh inbox' : 'Connect channels'}</button></div>}
        </div>
      </div>}

      {inboxMode === 'messages' && <aside className="glass-panel conversation-context">
        <PanelTitle title="Details" />
        {selectedInbox ? <>
          <label><span>Label</span><div className="context-inline"><FiTag /><input maxLength="40" value={labelDraft} onChange={(event) => setLabelDraft(event.target.value)} placeholder="Add custom label" /></div></label>
          <button className="btn subtle" type="button" disabled={labelDraft === (selectedInbox.label || '')} onClick={() => updateConversationFlag(selectedInbox, { label: labelDraft })}>Save label</button>
          <label><span>Notes</span><textarea maxLength="1000" value={notesDraft} onChange={(event) => setNotesDraft(event.target.value)} placeholder="Private agency notes" /></label>
          <button className="btn primary" type="button" disabled={notesDraft === (selectedInbox.notes || '')} onClick={() => updateConversationFlag(selectedInbox, { notes: notesDraft })}>Save notes</button>
          <div className="context-status"><span>Status</span><button type="button" className={cx(selectedInbox.status === 'Follow up' && 'active')} onClick={() => updateConversationFlag(selectedInbox, { status: selectedInbox.status === 'Follow up' ? 'Read' : 'Follow up' })}><FiAlertCircle /> Follow up</button></div>
        </> : <div className="empty-box compact-empty"><strong>No conversation selected</strong><small>Select a message to add a label or private note.</small></div>}
      </aside>}

      {inboxMode === 'automations' && <header className="automation-head"><div><h2>Automations</h2><p>Reply automatically to messages and comments that contain your keywords. New rules start paused.</p></div></header>}
      {inboxMode === 'automations' && <div className="automation-split">
        <div className="automation-main-column">
          <div className="glass-panel automation-templates">
            <PanelTitle title="Automation templates" />
            <div className="automation-template-grid">{[
              ['Auto reply', 'Greet new conversations', ['welcome', 'hello'], 'Hi, thanks for contacting us. We have received your message and will reply shortly.', ['message']],
              ['Frequently asked questions', 'Answer common questions', ['fees', 'price', 'details'], 'Thanks for your question. Here are the details you requested:', ['message']],
              ['Comment to message', 'Continue comments in DM', ['info', 'details'], 'Thanks for your comment. We have sent the details here.', ['comment']],
              ['Away message', 'Set response expectations', ['hello', 'hi'], 'Thanks for reaching out. Our team is away right now and will reply during business hours.', ['message']]
            ].map(([name, description, keywords, reply, triggers]) => <button key={name} type="button" onClick={() => { setEditingAutomationId(''); setAutomationDraft({ name, keywords: keywords.join(', '), reply, platforms: ['facebook', 'instagram'], triggers }); }}><FiMessageSquare /><span><strong>{name}</strong><small>{description}</small></span><FiChevronRight /></button>)}</div>
          </div>
          <div className="glass-panel automation-create-panel">
          <PanelTitle title={editingAutomationId ? 'Edit automation' : 'Create automation'} />
          <div className="automation-rule-form">
            <div className="automation-field-row">
              <label><span>Rule name</span><input value={automationDraft.name} onChange={(event) => setAutomationDraft((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Office address" /></label>
              <label><span>Keywords</span><input value={automationDraft.keywords} onChange={(event) => setAutomationDraft((current) => ({ ...current, keywords: event.target.value }))} placeholder="location, address, maps" /></label>
            </div>
            <label className="automation-reply-field"><span>Reply message</span><textarea value={automationDraft.reply} onChange={(event) => setAutomationDraft((current) => ({ ...current, reply: event.target.value }))} placeholder="What should be sent back?" /></label>
            <div className="automation-options">
              <div className="automation-option-group"><span>Platforms</span><div>{['facebook', 'instagram'].map((platform) => <label className={automationDraft.platforms.includes(platform) ? 'selected' : ''} key={platform}><input type="checkbox" checked={automationDraft.platforms.includes(platform)} onChange={() => setAutomationDraft((current) => ({ ...current, platforms: current.platforms.includes(platform) ? current.platforms.filter((item) => item !== platform) : [...current.platforms, platform] }))} /><PlatformLogo id={platform} label={platform === 'facebook' ? 'Facebook' : 'Instagram'} /><span>{platform === 'facebook' ? 'Facebook' : 'Instagram'}</span></label>)}</div></div>
              <div className="automation-option-group"><span>Runs for</span><div>{[['message', 'Direct messages'], ['comment', 'Comments']].map(([trigger, label]) => <label className={automationDraft.triggers.includes(trigger) ? 'selected' : ''} key={trigger}><input type="checkbox" checked={automationDraft.triggers.includes(trigger)} onChange={() => setAutomationDraft((current) => ({ ...current, triggers: current.triggers.includes(trigger) ? current.triggers.filter((item) => item !== trigger) : [...current.triggers, trigger] }))} /><span>{label}</span></label>)}</div></div>
            </div>
            <div className="automation-form-actions">
              <small>New rules start paused. Enable them after review.</small>
              <div>{editingAutomationId && <button className="btn subtle" type="button" onClick={() => { setEditingAutomationId(''); setAutomationDraft({ name: '', keywords: '', reply: '', platforms: ['facebook', 'instagram'], triggers: ['message'] }); }}>Cancel</button>}<button className="btn primary" type="button" disabled={!automationDraft.name.trim() || !automationDraft.keywords.trim() || !automationDraft.reply.trim() || !automationDraft.platforms.length || !automationDraft.triggers.length} onClick={saveAutomation}>{editingAutomationId ? 'Update rule' : 'Add rule'}</button></div>
            </div>
          </div>
          </div>
        </div>
        <aside className="glass-panel automation-panel automation-only-panel">
          <PanelTitle title="Your automations" action={`${automationRules.length}/50`} />
          <div className="automation-rule-list">{automationRules.map((rule) => <div className={cx('rule-card', rule.active && rule.deliveryEnabled && 'active')} key={rule.id}><button className="automation-rule-body" type="button" onClick={() => editAutomation(rule)}><strong>{rule.name}<em className={cx('rule-status', rule.active && rule.deliveryEnabled && 'on')}>{rule.active && rule.deliveryEnabled ? 'Active' : 'Paused'}</em></strong><small>{(rule.triggers?.length ? rule.triggers : ['message']).map((item) => item === 'message' ? 'Messages' : 'Comments').join(' + ')} · {rule.keywords.join(', ')}</small></button><button className={cx('automation-state-toggle', rule.active && rule.deliveryEnabled && 'active')} type="button" aria-label={`${rule.active && rule.deliveryEnabled ? 'Pause' : 'Enable'} ${rule.name}`} onClick={() => toggleAutomation(rule)}><span /></button><div className="automation-overflow"><button className="icon-btn" type="button" aria-label={`Options for ${rule.name}`} onClick={() => setAutomationMenuId((current) => current === rule.id ? '' : rule.id)}><FiMoreHorizontal /></button>{automationMenuId === rule.id && <div><button type="button" onClick={() => { editAutomation(rule); setAutomationMenuId(''); }}>Edit</button><button className="danger-link" type="button" onClick={() => { deleteAutomation(rule); setAutomationMenuId(''); }}>Delete</button></div>}</div></div>)}{!automationRules.length && <div className="empty-box compact-empty"><strong>No automations yet</strong><small>Choose a template or create a custom rule.</small></div>}</div>
        </aside>
      </div>}
    </section>
    {removeConversationTarget && <ConfirmDialog title="Remove conversation?" message="This hides the conversation from EastenSocial. The original provider message will not be deleted." confirmLabel="Remove" onCancel={() => setRemoveConversationTarget(null)} onConfirm={async () => { await updateConversationFlag(removeConversationTarget, { hidden: true }); setRemoveConversationTarget(null); }} />}
  </>);
}

export function chartAxisMaximum(values) {
  const maximum = Math.max(0, ...values.filter((value) => value != null && Number.isFinite(Number(value))).map(Number));
  if (!maximum) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(maximum));
  const ceiling = [1, 2, 2.5, 5, 10].find((factor) => factor * magnitude >= maximum) * magnitude;
  return Math.max(5, Math.ceil(ceiling / 5) * 5);
}

export function GrowthLineChart({ series, days, liveAt = null }) {
  const [hover, setHover] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState(0);
  const svgRef = useRef(null);
  const chartRef = useRef(null);
  useEffect(() => { setZoom(1); setOffset(0); setHover(null); }, [days]);
  useEffect(() => {
    const element = chartRef.current;
    const wheel = (event) => {
      event.preventDefault();
      setHover(null);
      setZoom((current) => Math.max(1, Math.min(3, current + (event.deltaY < 0 ? 0.25 : -0.25))));
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, []);
  const width = 960;
  const height = 390;
  const chart = { left: 68, right: 930, top: 40, bottom: 308 };
  const allValues = series.flatMap((item) => item.values || []);
  const yMax = chartAxisMaximum(allValues);
  const bucketCount = Math.max(1, series[0]?.values?.length || 1);
  const visibleCount = Math.max(2, Math.ceil(bucketCount / zoom));
  const start = Math.min(offset, Math.max(0, bucketCount - visibleCount));
  const end = Math.min(bucketCount - 1, start + visibleCount - 1);
  const x = (index) => chart.left + ((index - start) / Math.max(1, end - start)) * (chart.right - chart.left);
  const y = (value) => chart.bottom - Math.min(1, Number(value || 0) / yMax) * (chart.bottom - chart.top);
  const smoothSegment = (points) => {
    const fixed = (value) => value.toFixed(1);
    if (points.length < 3) return points.map(([px, py], index) => `${index ? 'L' : 'M'} ${fixed(px)} ${fixed(py)}`).join(' ');
    let path = `M ${fixed(points[0][0])} ${fixed(points[0][1])}`;
    for (let index = 1; index < points.length - 1; index += 1) {
      const [cx, cy] = points[index];
      const [nx, ny] = points[index + 1];
      path += ` Q ${fixed(cx)} ${fixed(cy)} ${fixed((cx + nx) / 2)} ${fixed((cy + ny) / 2)}`;
    }
    const last = points[points.length - 1];
    return `${path} L ${fixed(last[0])} ${fixed(last[1])}`;
  };
  const pathFor = (values = []) => {
    const segments = [];
    values.slice(start, end + 1).forEach((value, index) => {
      if (value == null) { segments.push([]); return; }
      if (!segments.length) segments.push([]);
      segments[segments.length - 1].push([x(index + start), y(value)]);
    });
    return segments.filter((segment) => segment.length).map(smoothSegment).join(' ');
  };
  const areaFor = (values = []) => values.length && values.every((value) => value != null)
    ? `${pathFor(values)} L ${x(end).toFixed(1)} ${chart.bottom} L ${chart.left} ${chart.bottom} Z`
    : '';
  const labelFor = (index) => {
    const date = new Date(Date.now() - days * 86400000 + index * days / bucketCount * 86400000);
    return date.toLocaleDateString('en-IN', days >= 180 ? { month: 'short', year: '2-digit' } : { day: 'numeric', month: 'short' });
  };
  const labelIndexes = Array.from({ length: bucketCount }, (_, index) => index).filter((index) => {
    if (index < start || index > end) return false;
    return index === start || index === end || (index - start) % Math.max(1, Math.ceil(visibleCount / 6)) === 0;
  });
  const ariaSummary = series.map((item) => `${item.label}: ${(item.values || []).reduce((sum, value) => sum + Number(value || 0), 0).toLocaleString('en-IN')}`).join(', ');
  const yTicks = [0, 0.2, 0.4, 0.6, 0.8, 1];
  const axisLabel = (value) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 }).format(value);

  return (
    <div ref={chartRef} className="growth-line-chart" onMouseLeave={() => setHover(null)}>
      {liveAt && <span className="live-chart-badge" title={`Updated ${new Date(liveAt).toLocaleString('en-IN')}`}><i />Live</span>}
      <div className="chart-navigation"><button type="button" aria-label="Zoom out chart" disabled={zoom <= 1} onClick={() => setZoom(Math.max(1, zoom - 0.5))}>−</button><button type="button" aria-label="Zoom in chart" disabled={zoom >= 3} onClick={() => setZoom(Math.min(3, zoom + 0.5))}>+</button><button type="button" onClick={() => { setZoom(1); setOffset(0); }}>Reset</button></div>
      {hover && <div className="growth-tooltip" style={{ left: hover.x, top: hover.y }}>
        <strong>{hover.label}</strong><span>{hover.value.toLocaleString('en-IN')}</span>
      </div>}
      <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Growth chart for last ${days} days. ${ariaSummary}`}>
        {yTicks.map((tick) => <g key={tick}>
          <line className="grid-line" x1={chart.left} x2={chart.right} y1={chart.bottom - tick * (chart.bottom - chart.top)} y2={chart.bottom - tick * (chart.bottom - chart.top)} />
          <text x={chart.left - 12} y={chart.bottom - tick * (chart.bottom - chart.top) + 4} textAnchor="end">{axisLabel(Math.round(yMax * tick))}</text>
        </g>)}
        <defs>{series.map((item, index) => <linearGradient id={`growth-fill-${index}`} key={item.id} x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={item.color} stopOpacity="0.28" /><stop offset="100%" stopColor={item.color} stopOpacity="0" /></linearGradient>)}</defs>
        {series.map((item, index) => item.values?.length > 1 && <path className="growth-area" key={`area-${item.id}`} d={areaFor(item.values)} fill={`url(#growth-fill-${index})`} />)}
        {series.map((item) => <g key={item.id} style={{ '--series-color': item.color }}>
          <path className="growth-line" d={pathFor(item.values)} stroke={item.color} />
          {(item.values || []).map((value, index) => value != null && index >= start && index <= end && <circle key={`${item.id}-${index}`} className="growth-point" cx={x(index)} cy={y(value)} r="5" fill={item.color} tabIndex="0" aria-label={`${item.label}, ${labelFor(index)}: ${value}`} onMouseEnter={() => { const scale = (svgRef.current?.getBoundingClientRect().width || width) / width; setHover({ label: `${item.label} · ${labelFor(index)}`, value: Number(value || 0), x: Math.max(0, Math.min(x(index) * scale - 50, width * scale - 200)), y: Math.max(0, y(value) * scale - 48) }); }} onFocus={() => setHover({ label: `${item.label} · ${labelFor(index)}`, value: Number(value || 0), x: 12, y: 8 })} onBlur={() => setHover(null)} />)}
        </g>)}
        <line className="axis-line" x1={chart.left} x2={chart.right} y1={chart.bottom} y2={chart.bottom} />
        <line className="axis-line" x1={chart.left} x2={chart.left} y1={chart.top} y2={chart.bottom} />
        {labelIndexes.map((index) => <text className="x-label" key={index} x={x(index)} y="352" textAnchor={index === 0 ? 'start' : index === bucketCount - 1 ? 'end' : 'middle'}>{labelFor(index)}</text>)}
      </svg>
      {zoom > 1 && <input className="chart-pan" aria-label="Move chart date window" type="range" min="0" max={Math.max(0, bucketCount - visibleCount)} value={start} onChange={(event) => { setOffset(Number(event.target.value)); setHover(null); }} />}
      <div className="growth-legend">{series.map((item) => <span key={item.id}><i style={{ background: item.color }} />{item.label}</span>)}<em>Scroll to zoom · {Math.round(zoom * 100)}%</em></div>
    </div>
  );
}

function Reports({ businessName = '', platformOptions = [], publishedPosts = [], reportPlatform, reportRange, setNotice, setReportPlatform, setReportRange, workspaceId }) {
  const [providerAnalytics, setProviderAnalytics] = useState({ loading: true, networks: [], topPosts: [], generatedAt: null, error: null });
  const [reportAccount, setReportAccount] = useState('all');
  const [chartMetric, setChartMetric] = useState('Posts');
  const [refreshToken, setRefreshToken] = useState(() => Date.now());
  const [customDates, setCustomDates] = useState(() => ({
    from: new Date(Date.now() - 29 * 86400000).toLocaleDateString('en-CA'),
    to: new Date().toLocaleDateString('en-CA')
  }));
  const [draftDates, setDraftDates] = useState(customDates);
  const [openDemographic, setOpenDemographic] = useState('age');
  useEffect(() => {
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') setRefreshToken(Date.now()); }, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);
  const customRange = reportRange === 'Custom';
  const [cutoff, periodEnd] = useMemo(() => customRange
    ? [new Date(`${customDates.from}T00:00:00`), new Date(Math.min(refreshToken, new Date(`${customDates.to}T00:00:00`).getTime() + 86400000))]
    : [new Date(refreshToken - ({ '7D': 7, '30D': 30, '90D': 90, '6M': 180, '1Y': 365 }[reportRange] || 30) * 86400000), new Date(refreshToken)],
  [customRange, customDates.from, customDates.to, reportRange, refreshToken]);
  const rangeDays = customRange ? Math.max(1, Math.ceil((periodEnd - cutoff) / 86400000)) : ({ '7D': 7, '30D': 30, '90D': 90, '6M': 180, '1Y': 365 }[reportRange] || 30);
  const connectedPlatforms = platformOptions.filter((platform) => platform.connected);
  const reportChoices = ['All', ...connectedPlatforms.map((platform) => platform.label)];
  const selectedPlatform = connectedPlatforms.find((platform) => platform.label === reportPlatform);
  useEffect(() => setReportAccount('all'), [reportPlatform, workspaceId]);
  const postDate = (post) => new Date(post.publishedAt || post.scheduledTime || post.createdAt || 0);
  const postPlatforms = (post) => (Array.isArray(post.platforms) ? post.platforms : [post.platform]).filter(Boolean);
  const rangePosts = publishedPosts.filter((post) => {
    const date = postDate(post);
    const isInRange = !Number.isNaN(date.getTime()) && date >= cutoff;
    const matchesAccount = reportAccount === 'all' || String(post.pageId || post.accountId || '') === String(reportAccount)
      || (post.destinations || []).some((item) => String(item.accountId) === String(reportAccount));
    return isInRange && date < periodEnd && matchesAccount && (!selectedPlatform || postPlatforms(post).includes(selectedPlatform.id));
  });
  const publishedCount = rangePosts.filter((post) => post.status === 'published').length;
  const bucketCount = rangeDays <= 30 ? rangeDays : rangeDays >= 365 ? 12 : 12;
  const bucketSize = rangeDays / bucketCount;
  const publishingSeries = connectedPlatforms
    .filter((platform) => !selectedPlatform || platform.id === selectedPlatform.id)
    .map((platform) => ({
      id: platform.id,
      label: platform.label,
      color: platform.color,
      values: Array.from({ length: bucketCount }, (_, index) => {
        const start = new Date(cutoff.getTime() + index * bucketSize * 86400000);
        const end = new Date(cutoff.getTime() + (index + 1) * bucketSize * 86400000);
        return rangePosts.filter((post) => {
          const date = postDate(post);
          return post.status === 'published' && date >= start && date < end && postPlatforms(post).includes(platform.id);
        }).length;
      })
    }));
  const recentPosts = rangePosts
    .filter((post) => post.status === 'published')
    .sort((a, b) => postDate(b) - postDate(a))
    .slice(0, 10);
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    if (!workspaceId) return undefined;
    const cacheKey = `${workspaceId}:${selectedPlatform?.id || 'all'}:${customRange ? `${customDates.from}:${customDates.to}` : rangeDays}`;
    const cached = analyticsMemoryCache.get(cacheKey);
    const hasFreshCache = cached && Date.now() - cached.cachedAt < ANALYTICS_CACHE_TTL_MS;
    if (hasFreshCache) {
      setProviderAnalytics({ ...cached.data, loading: false, error: null });
    } else {
      setProviderAnalytics({ networks: cached?.data.networks || [], topPosts: cached?.data.topPosts || [], generatedAt: cached?.data.generatedAt || null, loading: true, error: null });
    }
    const loadAnalytics = (attempt = 0) => axios.get(`${API_BASE}/api/analytics/summary`, {
      params: { days: rangeDays, ...(customRange ? { from: cutoff.toISOString(), to: periodEnd.toISOString() } : {}), platform: selectedPlatform?.id || 'all', fresh: Date.now() },
      withCredentials: true,
      headers: { 'x-workspace-id': workspaceId },
      timeout: 25000
    })
      .then((response) => {
        const data = {
          loading: false,
          networks: response.data.networks || [],
          topPosts: response.data.topPosts || [],
          generatedAt: response.data.generatedAt || null,
          stale: response.data.stale || false,
          error: null
        };
        analyticsMemoryCache.set(cacheKey, { cachedAt: Date.now(), data });
        if (!cancelled) setProviderAnalytics(data);
      })
      .catch((error) => {
        if (!cancelled && error.response?.status === 401 && attempt === 0) {
          retryTimer = window.setTimeout(() => loadAnalytics(1), 800);
          return;
        }
        if (!cancelled) {
          if (cached) {
            setProviderAnalytics({ ...cached.data, loading: false, stale: true, error: error.response?.data?.error || 'Live analytics could not be refreshed.' });
          } else {
            setProviderAnalytics({
              loading: false,
              networks: [],
              topPosts: [],
              generatedAt: null,
              error: error.response?.data?.error || error.message || 'Live analytics request failed.'
            });
          }
        }
      });
    loadAnalytics();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [rangeDays, customRange, customDates.from, customDates.to, cutoff, periodEnd, selectedPlatform?.id, workspaceId, refreshToken]);
  const analyticsNetwork = providerAnalytics.networks.find((network) => network.platform === selectedPlatform?.id);
  const accountChoices = selectedPlatform ? (analyticsNetwork?.accounts || []) : [];
  const selectedAccount = accountChoices.find((account) => String(account.id) === String(reportAccount));
  const scopedNetworks = providerAnalytics.networks
    .filter((network) => !selectedPlatform || network.platform === selectedPlatform.id)
    .map((network) => ({
      ...network,
      accounts: (network.accounts || []).filter((account) => reportAccount === 'all' || String(account.id) === String(reportAccount))
    }))
    .filter((network) => network.accounts.length > 0);
  const scopedAccounts = scopedNetworks.flatMap((network) => (network.accounts || []).map((account) => ({ ...account, platform: network.platform })));
  const analyticsReadyCount = scopedAccounts.filter((account) => account.status === 'ready').length;
  const analyticsIssues = scopedNetworks.flatMap((network) => {
    const accounts = network.accounts?.length ? network.accounts : [{ name: network.platform, message: network.message }];
    return accounts.flatMap((account) => {
      if (account.status === 'ready') return [];
      const messages = account.message ? [account.message] : [];
      return messages.map((message) => ({ platform: network.platform, name: account.name || account.username || 'Account', message }));
    });
  });
  const visibleNetworks = scopedNetworks
    .map((network) => ({ ...network, accounts: (network.accounts || []).filter((account) => account.summary || account.status === 'ready') }))
    .filter((network) => network.accounts.length > 0);
  const providerMetricSeries = visibleNetworks.map((network) => {
    const candidates = {
      facebook: [
        { key: 'views', label: 'Views', color: '#1877f2' },
        { key: 'reach', label: 'Reach', color: '#469e6b' },
        { key: 'page_post_engagements', label: 'Engagements', color: '#273e84' }
      ],
      instagram: [
        { key: 'views', label: 'Views', color: '#e1306c' },
        { key: 'reach', label: 'Reach', color: '#469e6b' },
        { key: 'profile_views', label: 'Profile views', color: '#273e84' },
        { key: 'media_engagements', label: 'Engagements', color: '#273e84' }
      ],
      threads: [
        { key: 'views', label: 'Views', color: '#111111' },
        { key: 'engagements', label: 'Engagements', color: '#469e6b' }
      ],
      youtube: [{ key: 'views', label: 'Views', color: '#ff0000' }]
    }[network.platform] || [];
    const metric = candidates.find((candidate) => candidate.label === chartMetric && network.accounts.some((account) => (account.series?.[candidate.key] || []).length));
    if (!metric) return null;
    const values = Array.from({ length: bucketCount }, () => null);
    (network.accounts || []).forEach((account) => {
      (account.series?.[metric.key] || []).forEach((point) => {
        const pointDate = new Date(point.end_time || point.date || 0);
        if (Number.isNaN(pointDate.getTime()) || pointDate < cutoff || pointDate >= periodEnd) return;
        const elapsedDays = Math.max(0, (pointDate.getTime() - cutoff.getTime()) / (24 * 60 * 60 * 1000));
        const bucket = Math.min(bucketCount - 1, Math.floor(elapsedDays / bucketSize));
        const rawValue = point?.value?.value ?? point?.value;
        const numeric = rawValue == null ? NaN : Number(rawValue);
        if (Number.isFinite(numeric)) values[bucket] = (values[bucket] ?? 0) + numeric;
      });
    });
    return { id: network.platform, label: `${platformById(network.platform).label} ${metric.label}`, color: platformById(network.platform).color, values };
  }).filter(Boolean);
  const reportPublishingSeries = providerPublishingSeries(visibleNetworks, publishingSeries, cutoff.getTime(), periodEnd.getTime(), bucketCount);
  const hasPublishingSeries = reportPublishingSeries.some((series) => series.values.some((value) => Number(value) > 0));
  const chartUsesPublishingFallback = chartMetric !== 'Posts' && !providerMetricSeries.length && hasPublishingSeries;
  const visibleSeries = chartMetric === 'Posts' ? reportPublishingSeries : providerMetricSeries;
  const activityTotal = visibleSeries.reduce((total, series) => total + series.values.reduce((sum, value) => sum + value, 0), 0);
  const readyAccounts = visibleNetworks.flatMap((network) => (network.accounts || []).map((account) => ({ ...account, platform: network.platform })));
  const followersAvailable = readyAccounts.some((account) => account.followers !== null && account.followers !== undefined);
  const totalFollowers = readyAccounts.reduce((total, account) => total + Number(account.followers || 0), 0);
  const accountsWithReach = readyAccounts.filter((account) => account.summary?.reach != null && account.summary?.reachAvailable !== false && Number.isFinite(Number(account.summary?.reach)));
  const totalReach = accountsWithReach.reduce((total, account) => total + Number(account.summary.reach), 0);
  const accountsWithViews = readyAccounts.filter((account) => account.summary?.views != null && account.summary?.viewsAvailable !== false && Number.isFinite(Number(account.summary?.views)));
  const totalViews = accountsWithViews.reduce((total, account) => total + Number(account.summary.views), 0);
  const totalEngagement = readyAccounts.reduce((total, account) => total + Number(account.summary?.engagement || 0), 0);
  const totalFollowerChange = readyAccounts.reduce((total, account) => total + Number(account.summary?.followerChange || 0), 0);
  const followerGainAvailable = readyAccounts.some((account) => account.summary?.followerChange != null);
  const engagementAvailable = readyAccounts.some((account) => account.summary?.engagement != null);
  const missingMetaMetricAccess = readyAccounts.some((account) => ['facebook', 'instagram'].includes(account.platform)
    && (account.summary?.reachAvailable === false || account.summary?.viewsAvailable === false));
  const demographicAccount = readyAccounts.find((account) => account.demographics?.available)
    || readyAccounts.find((account) => account.platform === 'instagram')
    || readyAccounts[0];
  const demographics = demographicAccount?.demographics || null;
  const demographicAccounts = readyAccounts.filter((account) => account.demographics?.available && ['age', 'gender', 'country', 'city'].some((dimension) => (account.demographics[dimension] || []).length));
  const demographicEmptyTitle = demographicAccount?.platform === 'facebook'
    ? 'Facebook audience demographics are limited'
    : 'Demographics not available yet';
  const demographicEmptyMessage = demographicAccount?.platform === 'facebook'
    ? demographics?.message || 'Facebook age and gender are restricted in the current Page Insights API. EastenSocial now checks the available follower city and country metrics when Meta grants Page Insights access.'
    : demographics?.message || 'Connect an eligible Instagram professional account and grant instagram_manage_insights.';
  const providerTopPosts = providerReportPosts(providerAnalytics.networks, providerAnalytics.topPosts, selectedPlatform?.id, reportAccount);
  const postViews = (post) => Number(post.views ?? post.viewCount ?? post.metrics?.views ?? post.summary?.views ?? 0);
  const hasPostViews = (post) => [post.views, post.viewCount, post.metrics?.views, post.summary?.views].some((value) => value != null && Number.isFinite(Number(value)));
  const totalProviderPosts = readyAccounts.reduce((total, account) => total + Number(account.postsCount || 0), 0);
  const postingDates = readyAccounts.flatMap((account) => account.postingDates || []);
  const activePostingDays = Math.min(rangeDays, postingDates.length
    ? new Set(postingDates).size
    : readyAccounts.reduce((total, account) => total + Number(account.activePostingDays || 0), 0));
  const displayedTopPosts = (providerTopPosts.length ? providerTopPosts : recentPosts)
    .slice()
    .sort((a, b) => postViews(b) - postViews(a))
    .slice(0, 10);
  const highlights = (() => {
    if (!visibleSeries.length) return null;
    const buckets = Math.max(1, visibleSeries[0].values.length);
    const totals = Array.from({ length: buckets }, (_, index) => visibleSeries.reduce((total, item) => total + Number(item.values[index] || 0), 0));
    const bestIndex = totals.indexOf(Math.max(...totals));
    const half = Math.floor(buckets / 2);
    const earlier = totals.slice(0, half).reduce((total, value) => total + value, 0);
    const later = totals.slice(half).reduce((total, value) => total + value, 0);
    const label = (index) => new Date(cutoff.getTime() + index * bucketSize * 86400000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const leader = visibleSeries.slice().sort((a, b) => b.values.reduce((sum, value) => sum + Number(value || 0), 0) - a.values.reduce((sum, value) => sum + Number(value || 0), 0))[0];
    return { average: activityTotal / buckets, bestLabel: label(bestIndex), bestValue: totals[bestIndex], change: earlier ? ((later - earlier) / earlier) * 100 : null, leader: leader.label, per: bucketSize <= 1 ? 'day' : 'period' };
  })();
  const rangeLabel = customRange ? `${customDates.from} to ${customDates.to}` : ({ '7D': '7 Days', '30D': '30 Days', '90D': '90 Days', '6M': '6 Months', '1Y': '1 Year' }[reportRange] || `${rangeDays} Days`);
  const postLink = (post) => publishedPostUrl(post) || '';
  const reportPost = (post) => ({
    title: post.title || post.content || post.message || 'Published post',
    platformLabel: post.platform ? platformById(post.platform).label : '',
    account: post.accountName || post.pageName || '',
    date: postDate(post).toISOString(),
    views: hasPostViews(post) ? postViews(post) : null,
    url: postLink(post)
  });
  const seriesTotal = (series) => series.values.reduce((sum, value) => sum + Number(value || 0), 0);
  const platformTotals = visibleSeries.slice().sort((a, b) => seriesTotal(b) - seriesTotal(a));
  // Everything the exported PDF and the shared client link are built from.
  const createReport = () => ({
    businessName: businessName || 'Your business',
    generatedAt: new Date().toISOString(),
    rangeLabel,
    scopeLabel: selectedAccount?.name || selectedPlatform?.label || 'All platforms',
    period: { from: cutoff.toISOString(), to: new Date(periodEnd.getTime() - (customRange ? 1 : 0)).toISOString() },
    summary: {
      followers: followersAvailable ? totalFollowers : null,
      followerChange: followerGainAvailable ? totalFollowerChange : null,
      views: accountsWithViews.length ? totalViews : null,
      reach: accountsWithReach.length ? totalReach : null,
      engagement: engagementAvailable ? totalEngagement : null,
      activeDays: activePostingDays,
      posts: totalProviderPosts || publishedCount
    },
    chart: { metric: chartMetric, series: visibleSeries.map((series) => ({ label: series.label, color: series.color, values: series.values })) },
    platformTotals: platformTotals.map((series) => ({ label: series.label, color: series.color, value: seriesTotal(series) })),
    topPosts: displayedTopPosts.map(reportPost),
    platforms: scopedNetworks.map((network) => ({
      label: platformById(network.platform).label,
      color: platformById(network.platform).color,
      topPosts: providerTopPosts.filter((post) => post.platform === network.platform).slice().sort((a, b) => postViews(b) - postViews(a)).slice(0, 10).map(reportPost),
      accounts: (network.accounts || []).map((account) => ({
        name: account.name || account.username || 'Account',
        followers: account.followers ?? null,
        followerChange: account.summary?.followerChange ?? null,
        views: account.summary?.viewsAvailable === false ? null : account.summary?.views ?? null,
        reach: account.summary?.reachAvailable === false ? null : account.summary?.reach ?? null,
        engagement: account.summary?.engagement ?? null,
        posts: account.postsCount ?? null,
        demographics: account.demographics?.available ? { age: account.demographics.age || [], gender: account.demographics.gender || [], country: account.demographics.country || [], city: account.demographics.city || [] } : null
      }))
    }))
  });
  const loadReportBuilder = () => new Promise((resolve, reject) => {
    if (window.EastenReport) { resolve(window.EastenReport); return; }
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/report-html.js`;
    script.onload = () => (window.EastenReport ? resolve(window.EastenReport) : reject(new Error('Report builder missing')));
    script.onerror = () => reject(new Error('Report builder could not load'));
    document.head.appendChild(script);
  });
  const [reportBusy, setReportBusy] = useState('');
  // The report opens in its own tab and the print dialog offers "Save as PDF" (keeps links clickable and every language readable).
  const exportReport = async () => {
    const tab = window.open('', '_blank');
    setReportBusy('export');
    try {
      const builder = await loadReportBuilder();
      const html = builder.buildReportHtml(createReport(), { autoPrint: true });
      if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
        setNotice({ type: 'success', message: 'Report is ready. Choose “Save as PDF” in the print window.' });
      } else {
        const url = URL.createObjectURL(new Blob([builder.buildReportHtml(createReport())], { type: 'text/html' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `eastensocial-report-${rangeLabel.replace(/\s+/g, '-').toLowerCase()}.html`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 500);
        setNotice({ type: 'warning', message: 'Your browser blocked the new tab, so the report was downloaded instead. Open it and print it as PDF.' });
      }
    } catch (error) {
      if (tab) tab.close();
      setNotice({ type: 'error', message: 'The report could not be prepared. Try again.' });
    } finally {
      setReportBusy('');
    }
  };
  const shareReportLink = async () => {
    setReportBusy('share');
    try {
      const { data } = await axios.post(`${API_BASE}/api/reports/share`, { payload: createReport() }, { withCredentials: true, headers: { 'x-workspace-id': workspaceId } });
      try { await navigator.clipboard.writeText(data.url); setNotice({ type: 'success', message: 'Client link copied. It shows this report as it looks now and works for 30 days.' }); }
      catch (clipboardError) { window.prompt('Copy this client link (works for 30 days):', data.url); }
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'The client link could not be created.' });
    } finally {
      setReportBusy('');
    }
  };
  // Audience of whatever is selected above: every shown account's breakdown is added together.
  const audience = ['age', 'gender', 'country', 'city'].map((dimension) => {
    const totals = new Map();
    demographicAccounts.forEach((account) => (account.demographics[dimension] || []).forEach((row) => totals.set(row.label, (totals.get(row.label) || 0) + Number(row.value || 0))));
    const rows = [...totals].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, dimension === 'age' || dimension === 'gender' ? 8 : 6);
    return { dimension, title: { age: 'Age groups', gender: 'Gender', country: 'Top countries', city: 'Top cities' }[dimension], rows, sum: [...totals.values()].reduce((sum, value) => sum + value, 0) };
  });
  const audienceTint = selectedPlatform?.color || '#1e9e6f';

  return (
    <section className="reports-grid">
      <div className="glass-panel report-main">
        <div className="panel-head">
          <div><p className="kicker">Reports</p><h2>Performance overview</h2></div>
          <div className="report-controls">
            <button className="report-refresh" type="button" title="Refresh live analytics" aria-label="Refresh live analytics" disabled={providerAnalytics.loading} onClick={() => setRefreshToken(Date.now())}>{providerAnalytics.loading ? '...' : '↻'}</button>
            <select value={reportChoices.includes(reportPlatform) ? reportPlatform : 'All'} onChange={(event) => setReportPlatform(event.target.value)}>
              {reportChoices.map((item) => <option key={item}>{item}</option>)}
            </select>
            {selectedPlatform && accountChoices.length > 0 && (
              <select className="report-account-select" value={accountChoices.some((account) => String(account.id) === String(reportAccount)) ? reportAccount : 'all'} onChange={(event) => setReportAccount(event.target.value)}>
                <option value="all">All {selectedPlatform.label} accounts</option>
                {accountChoices.map((account) => <option key={account.id} value={account.id}>{account.name || account.username || account.id}</option>)}
              </select>
            )}
            <div className="toggle-pair">
              {['7D', '30D', '90D', '6M', '1Y', 'Custom'].map((range) => (
                <button className={cx(reportRange === range && 'active')} key={range} onClick={() => setReportRange(range)} type="button">{range}</button>
              ))}
            </div>
            {customRange && <form className="report-custom-dates" onSubmit={(event) => {
              event.preventDefault();
              const start = new Date(`${draftDates.from}T00:00:00`);
              const end = new Date(`${draftDates.to}T00:00:00`);
              if (!draftDates.from || !draftDates.to || end < start || end > new Date() || end - start >= 366 * 86400000) {
                setNotice({ type: 'warning', message: 'Select a valid date range of up to one year ending today or earlier.' });
                return;
              }
              setCustomDates(draftDates);
            }}>
              <label>From <input aria-label="Report start date" type="date" max={draftDates.to} value={draftDates.from} onChange={(event) => setDraftDates((dates) => ({ ...dates, from: event.target.value }))} /></label>
              <label>To <input aria-label="Report end date" type="date" min={draftDates.from} max={new Date().toLocaleDateString('en-CA')} value={draftDates.to} onChange={(event) => setDraftDates((dates) => ({ ...dates, to: event.target.value }))} /></label>
              <button className="btn" type="submit">Apply</button>
            </form>}
          </div>
          <div className="report-actions">
            <button className="btn primary" type="button" disabled={providerAnalytics.loading || reportBusy === 'export'} onClick={exportReport}><FiFileText aria-hidden="true" /> {reportBusy === 'export' ? 'Preparing…' : 'Export Report'}</button>
            <button className="icon-btn report-share" type="button" title="Copy a view-only link for your client" aria-label="Copy a view-only report link for your client" disabled={providerAnalytics.loading || reportBusy === 'share'} onClick={shareReportLink}><FiLink /></button>
          </div>
        </div>

        {providerAnalytics.loading ? <Skeleton rows={4} label="Loading live analytics" /> : <div className="hd-kpis reports-kpis">
          <DashboardMetricCard icon={FiHeart} label="Followers" series={aggregateAccountTrend(readyAccounts, ['follower_count', 'page_daily_follows', 'page_fan_adds'])} value={followersAvailable ? totalFollowers.toLocaleString('en-IN') : '—'} />
          <DashboardMetricCard icon={FiUsers} label="Reach" series={aggregateAccountTrend(readyAccounts, ['reach'])} note={accountsWithReach.length ? '' : 'Provider metric unavailable'} value={accountsWithReach.length ? totalReach.toLocaleString('en-IN') : '—'} />
          <DashboardMetricCard icon={FiEye} label="Views" series={aggregateAccountTrend(readyAccounts, ['views', 'page_media_view', 'impressions'])} note={accountsWithViews.length ? '' : 'Provider metric unavailable'} value={accountsWithViews.length ? totalViews.toLocaleString('en-IN') : '—'} />
          <DashboardMetricCard icon={FiMessageSquare} label="Engagement" series={aggregateAccountTrend(readyAccounts, ['total_interactions', 'page_post_engagements', 'media_engagements'])} value={engagementAvailable ? totalEngagement.toLocaleString('en-IN') : '—'} />
        </div>}

        {(providerAnalytics.loading || selectedAccount || selectedPlatform) && <div className="provider-analytics-status compact-provider-status">
          <strong>{selectedAccount ? `${selectedAccount.name} analytics` : selectedPlatform ? `${selectedPlatform.label} analytics` : 'Combined provider analytics'}</strong>
          <span>
            {providerAnalytics.loading
              ? 'Checking live provider access...'
              : selectedAccount
                ? (selectedAccount.message || (selectedAccount.status === 'ready' ? 'Live analytics are ready for this account.' : 'This account needs provider analytics access.'))
                : selectedPlatform
                  ? (analyticsNetwork?.message || 'This connected network has not granted analytics access yet.')
                  : `${analyticsReadyCount} account(s) returned live metrics for this period.`}
          </span>
        </div>}

        {providerAnalytics.error && (
          <div className="provider-analytics-diagnostics friendly-diagnostics" role="status">
            <div><FiAlertCircle /><span><strong>Live analytics are temporarily unavailable</strong><small>Your saved workspace data is safe. Try the provider again.</small></span></div>
            <button className="btn subtle" type="button" onClick={() => setRefreshToken(Date.now())}>Retry</button>
          </div>
        )}

        {providerAnalytics.stale && <p className="report-saved-note" role="status">Showing your last saved numbers{providerAnalytics.generatedAt ? ` · ${new Date(providerAnalytics.generatedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}. They refresh automatically.</p>}
        {!providerAnalytics.error && analyticsIssues.length > 0 && (
          <div className="provider-analytics-status compact-provider-status" role="status">
            <strong>{analyticsIssues.length} profile{analyticsIssues.length === 1 ? '' : 's'} need access</strong>
            <span>Reconnect the affected profile after its analytics permission is approved.</span>
          </div>
        )}

        {!providerAnalytics.error && missingMetaMetricAccess && (
          <div className="provider-analytics-status analytics-access-note compact-provider-status" role="status">
            <strong>Some Meta metrics are unavailable</strong>
            <span>Some Meta metrics need approved Insights permissions. Facebook age/gender is restricted by Meta's current Page Insights API.</span>
          </div>
        )}

        <div className="chart-card report-chart">
          <div className="chart-topline"><select aria-label="Chart metric" value={chartMetric} onChange={(event) => setChartMetric(event.target.value)}>{['Views', 'Reach', 'Engagements', 'Posts'].map((metric) => <option key={metric}>{metric}</option>)}</select><strong>{activityTotal.toLocaleString('en-IN')}</strong></div>
          {chartUsesPublishingFallback && <p className="chart-fallback-note">Daily {chartMetric.toLowerCase()} history is unavailable for this selection. Available period totals remain in the summary above.</p>}
          {providerAnalytics.loading ? <Skeleton rows={5} label="Loading chart" /> : visibleSeries.length ? <GrowthLineChart series={visibleSeries} days={rangeDays} liveAt={providerAnalytics.generatedAt} /> : <div className="empty-box centered-empty"><FiBarChart2 /><strong>No chart data yet</strong><small>Connect a profile with analytics access to see this graph.</small></div>}
        </div>

        {highlights && <dl className="report-highlights-strip" aria-label="Highlights">
          <div><dt>Best {highlights.per}</dt><dd>{highlights.bestLabel}<small>{Math.round(highlights.bestValue).toLocaleString('en-IN')} {chartMetric.toLowerCase()}</small></dd></div>
          <div><dt>Average per {highlights.per}</dt><dd>{Math.round(highlights.average).toLocaleString('en-IN')}<small>{chartMetric.toLowerCase()}</small></dd></div>
          <div><dt>Compared with earlier half</dt><dd className={highlights.change == null ? '' : highlights.change >= 0 ? 'up' : 'down'}>{highlights.change == null ? '—' : `${highlights.change >= 0 ? '▲ +' : '▼ −'}${Math.abs(highlights.change).toFixed(1)}%`}</dd></div>
          <div><dt>Leading platform</dt><dd>{highlights.leader}</dd></div>
        </dl>}

        <section className="platform-strip-panel" aria-label="Platform performance">
          <h3>Platform performance</h3>
          {platformTotals.length ? <div className="platform-strip">
            {platformTotals.map((series) => {
              const total = seriesTotal(series);
              const top = Math.max(seriesTotal(platformTotals[0]), 1);
              return <div className="platform-strip-item" key={series.id || series.label} style={{ '--platform': series.color }}>
                <span><strong>{series.label}</strong><em>{total.toLocaleString('en-IN')}</em></span>
                <i className="thin-line"><b style={{ width: `${Math.max(3, (total / top) * 100)}%` }} /></i>
              </div>;
            })}
          </div> : <p className="platform-strip-empty">Connect a profile with analytics access to compare platforms.</p>}
        </section>

        <p className="side-note">Live data from the connected platforms for the selected period.</p>
      </div>

      <aside className="glass-panel report-demographics" aria-label="Demographics">
        <h3>Demographics</h3>
        <small className="report-demo-scope">{selectedAccount?.name || selectedPlatform?.label || 'All platforms'}</small>
        {audience.length ? audience.map((group) => (
          <div className="report-demo-group" key={group.dimension}>
            <button className="report-demo-heading" type="button" aria-expanded={openDemographic === group.dimension || (!audience.some((item) => item.dimension === openDemographic) && audience[0]?.dimension === group.dimension)} aria-controls={`report-demo-${group.dimension}`} onClick={() => setOpenDemographic(group.dimension)}>{group.title}<FiChevronDown aria-hidden="true" /></button>
            <div id={`report-demo-${group.dimension}`} hidden={!(openDemographic === group.dimension || (!audience.some((item) => item.dimension === openDemographic) && audience[0]?.dimension === group.dimension))}>{group.rows.length ? group.rows.map((row) => {
              const max = Math.max(...group.rows.map((item) => item.value), 1);
              return <div className="report-demo-row" key={row.label} style={{ '--platform': audienceTint }}>
                <span><strong>{row.label}</strong><em>{group.sum ? Math.round((row.value / group.sum) * 100) : 0}%</em></span>
                <i className="thin-line"><b style={{ width: `${Math.max(3, (row.value / max) * 100)}%` }} /></i>
              </div>;
            }) : <p className="report-demo-unavailable">{demographicEmptyMessage}</p>}</div>
          </div>
        )) : <div className="empty-box audience-empty"><strong>{demographicEmptyTitle}</strong><small>{demographicEmptyMessage}</small></div>}
      </aside>
    </section>
  );
}

const teamPermissionLabels = {
  'content.view': 'View content', 'content.create': 'Create and edit', 'content.publish': 'Publish and schedule',
  'content.delete': 'Delete content', 'calendar.manage': 'Manage calendar', 'inbox.reply': 'Reply in Inbox',
  'automations.manage': 'Manage automations', 'ads.manage': 'Manage Ads', 'reports.view': 'View reports', 'assets.manage': 'Manage assets'
};

function TeamManagement({ setNotice, workspaces, onOpenChat }) {
  const allPermissions = Object.keys(teamPermissionLabels);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [memberWorkspaceId, setMemberWorkspaceId] = useState('');
  const [memberAssignments, setMemberAssignments] = useState({});
  const [memberTab, setMemberTab] = useState('details');
  const { tasks, setTasks } = useTeamTasks(API_BASE);
  const emptyForm = () => ({ name: '', email: '', designation: '', assignments: {} });
  const [form, setForm] = useState(emptyForm);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/team`, { withCredentials: true });
      const rows = response.data.members || [];
      setMembers(rows);
      setSelectedMemberId((current) => current || rows[0]?.id || '');
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Team could not be loaded.' });
    } finally { setLoading(false); }
  }, [setNotice]);
  useEffect(() => { loadTeam(); }, [loadTeam]);
  const selectedMember = members.find((member) => member.id === selectedMemberId) || null;
  useEffect(() => {
    if (!selectedMember) {
      setMemberAssignments({});
      setMemberWorkspaceId('');
      return;
    }
    const assignments = Object.fromEntries((selectedMember.assignments || []).map((item) => [item.workspaceId, [...(item.permissions || [])]]));
    setMemberAssignments(assignments);
    setMemberWorkspaceId((current) => current && workspaces.some((workspace) => workspace.id === current) ? current : selectedMember.assignments?.[0]?.workspaceId || workspaces[0]?.id || '');
  }, [selectedMember, workspaces]);

  const toggleWorkspace = (workspaceId) => setForm((current) => {
    const assignments = { ...current.assignments };
    if (assignments[workspaceId]) delete assignments[workspaceId];
    else assignments[workspaceId] = [...allPermissions];
    return { ...current, assignments };
  });
  const toggleWorkspacePermission = (workspaceId, permission) => setForm((current) => {
    const permissions = current.assignments[workspaceId] || [];
    const next = permissions.includes(permission) ? permissions.filter((item) => item !== permission) : [...permissions, permission];
    return { ...current, assignments: { ...current.assignments, [workspaceId]: next } };
  });
  const toggleFullAccess = (workspaceId) => setForm((current) => {
    const currentPermissions = current.assignments[workspaceId] || [];
    return { ...current, assignments: { ...current.assignments, [workspaceId]: currentPermissions.length === allPermissions.length ? [] : [...allPermissions] } };
  });
  const addEmployee = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        designation: form.designation,
        assignments: Object.entries(form.assignments).map(([workspaceId, permissions]) => ({ workspaceId, permissions }))
      };
      const response = await axios.post(`${API_BASE}/api/team`, payload, { withCredentials: true });
      const temporaryPassword = response.data.temporaryPassword;
      setNotice({ type: 'success', message: temporaryPassword ? `Employee created. One-time password: ${temporaryPassword}` : 'Employee assignments updated.' });
      setForm(emptyForm());
      setInviteOpen(false);
      await loadTeam();
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Employee could not be added.' });
    } finally { setSaving(false); }
  };
  const setMemberStatus = async (member, status) => {
    try {
      await axios.patch(`${API_BASE}/api/team/${member.id}`, { designation: member.designation, assignments: member.assignments.map((item) => ({ workspaceId: item.workspaceId, permissions: item.permissions || [] })), status }, { withCredentials: true });
      setNotice({ type: 'success', message: status === 'suspended' ? 'Employee access suspended.' : 'Employee access restored.' });
      await loadTeam();
    } catch (error) { setNotice({ type: 'error', message: error.response?.data?.error || 'Employee status could not be changed.' }); }
  };

  const toggleMemberWorkspace = (workspaceId) => setMemberAssignments((current) => {
    const next = { ...current };
    if (next[workspaceId]) delete next[workspaceId];
    else next[workspaceId] = [...allPermissions];
    return next;
  });
  const toggleMemberPermission = (workspaceId, permission) => setMemberAssignments((current) => {
    const permissions = current[workspaceId] || [];
    return { ...current, [workspaceId]: permissions.includes(permission) ? permissions.filter((item) => item !== permission) : [...permissions, permission] };
  });
  const toggleMemberFullAccess = (workspaceId) => setMemberAssignments((current) => ({ ...current, [workspaceId]: (current[workspaceId] || []).length === allPermissions.length ? [] : [...allPermissions] }));
  const saveMemberAccess = async () => {
    if (!selectedMember) return;
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/api/team/${selectedMember.id}`, {
        designation: selectedMember.designation,
        assignments: Object.entries(memberAssignments).map(([workspaceId, permissions]) => ({ workspaceId, permissions })),
        status: selectedMember.accountStatus
      }, { withCredentials: true });
      setNotice({ type: 'success', message: `${selectedMember.name}'s workspace access was updated.` });
      await loadTeam();
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Employee permissions could not be saved.' });
    } finally { setSaving(false); }
  };

  return <section className="team-management">
    <header className="team-page-head"><div><p>ACCESS CONTROL</p><h2>Team</h2><span>Assign people to workspaces, then grant only the access they need.</span></div><button className="btn success" type="button" onClick={() => setInviteOpen((current) => !current)}><FiPlus />{inviteOpen ? 'Close invite' : 'Invite Employee'}</button></header>
    <div className="team-stats" aria-label="Team summary">
      <article><span>Employees</span><strong>{members.length}</strong></article>
      <article><span>Active</span><strong>{members.filter((member) => member.accountStatus !== 'suspended').length}</strong></article>
      <article><span>Open tasks</span><strong>{tasks.filter((task) => task.status !== 'done').length}</strong></article>
      <article className={tasks.some((task) => task.overdue) ? 'warn' : ''}><span>Overdue</span><strong>{tasks.filter((task) => task.overdue).length}</strong></article>
    </div>
    <div className="team-layout">
      <div className="glass-panel team-member-list">
        <div className="team-list-heading"><span><h3>Employees</h3><small>{members.filter((member) => member.accountStatus !== 'suspended').length} active · {members.filter((member) => member.accountStatus === 'suspended').length} suspended</small></span><b>{members.length}</b></div>
        {loading && <Skeleton lines={4} />}
        {!loading && !members.length && <div className="empty-box"><FiUsers /><strong>No employees yet</strong><small>Invite your first employee and assign their workspaces.</small></div>}
        {members.map((member) => <button className={cx('team-member-row', selectedMemberId === member.id && !inviteOpen && 'active')} key={member.id} type="button" onClick={() => { setSelectedMemberId(member.id); setInviteOpen(false); }}><span className="team-member-avatar">{String(member.name || 'E').slice(0, 1).toUpperCase()}</span><span><strong>{member.name}</strong><small>{member.designation || 'Employee'} · {member.email}</small><span className="team-assignment-chips">{member.assignments.slice(0, 3).map((item) => <b key={item.workspaceId}>{item.workspaceName}</b>)}</span></span><em className={member.accountStatus === 'suspended' ? 'suspended' : 'active'}>{member.accountStatus === 'suspended' ? 'Suspended' : 'Active'}</em><span className="team-open-tasks" title="Open tasks">{tasks.filter((task) => task.assigneeId === member.id && task.status !== 'done').length}</span><FiChevronRight /></button>)}
      </div>
      {inviteOpen ? <form className="glass-panel team-invite-form" onSubmit={addEmployee}>
        <h3>Add Employee</h3>
        <div className="team-form-grid"><label><span>Name</span><input required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label><label><span>Email</span><input required type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} /></label><label className="wide"><span>Designation</span><input value={form.designation} onChange={(event) => setForm((current) => ({ ...current, designation: event.target.value }))} placeholder="e.g. Social Media Executive" /></label></div>
        <fieldset><legend>Assigned Workspaces</legend><div className="team-workspace-list">{workspaces.map((workspace) => {
          const permissions = form.assignments[workspace.id];
          const selected = Boolean(permissions);
          const fullAccess = selected && permissions.length === allPermissions.length;
          return <section className={cx('team-workspace-access', selected && 'selected')} key={workspace.id}>
            <label className="team-workspace-toggle"><input type="checkbox" checked={selected} onChange={() => toggleWorkspace(workspace.id)} /><span><strong>{workspace.name}</strong><small>{selected ? fullAccess ? 'Full access' : `${permissions.length} permissions selected` : 'Not assigned'}</small></span></label>
            {selected && <div className="team-permission-panel"><label className="team-full-access"><input type="checkbox" checked={fullAccess} onChange={() => toggleFullAccess(workspace.id)} /><span><strong>Full access</strong><small>Allow every current team permission</small></span></label><div className="team-check-grid">{allPermissions.map((permission) => <label key={permission}><input type="checkbox" checked={permissions.includes(permission)} onChange={() => toggleWorkspacePermission(workspace.id, permission)} /><span>{teamPermissionLabels[permission]}</span></label>)}</div></div>}
          </section>;
        })}</div></fieldset>
        <button className="btn primary" disabled={saving || !Object.keys(form.assignments).length} type="submit"><FiPlus /> {saving ? 'Adding...' : 'Add Employee'}</button>
      </form> : <aside className="glass-panel team-member-panel">
        {selectedMember ? <><header><span className="team-member-avatar large">{String(selectedMember.name || 'E').slice(0, 1).toUpperCase()}</span><span><h3>{selectedMember.name}</h3><small>{selectedMember.designation || 'Employee'} · {selectedMember.email}</small></span><button className="btn subtle team-message-btn" type="button" onClick={onOpenChat}><FiMessageSquare aria-hidden="true" /> Chat</button><button className="btn subtle" type="button" onClick={() => setMemberStatus(selectedMember, selectedMember.accountStatus === 'suspended' ? 'active' : 'suspended')}>{selectedMember.accountStatus === 'suspended' ? 'Restore' : 'Suspend'}</button></header>
          <div className="team-tabs" role="tablist" aria-label="Employee sections">{[['details', 'Details'], ['tasks', 'Tasks'], ['access', 'Access']].map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={memberTab === id} className={memberTab === id ? 'active' : ''} onClick={() => setMemberTab(id)}>{label}</button>)}</div>
          <section className={cx('team-tab', memberTab === 'details' && 'active')} data-tab="details">
            <dl className="team-details">
              <div><dt>Role</dt><dd>{selectedMember.designation || 'Employee'}</dd></div>
              <div><dt>Email</dt><dd>{selectedMember.email}</dd></div>
              <div><dt>Status</dt><dd>{selectedMember.accountStatus === 'suspended' ? 'Suspended' : 'Active'}</dd></div>
              <div><dt>Last sign-in</dt><dd>{selectedMember.lastLoginAt ? new Date(selectedMember.lastLoginAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : 'Not signed in yet'}</dd></div>
              <div><dt>Open tasks</dt><dd>{tasks.filter((task) => task.assigneeId === selectedMember.id && task.status !== 'done').length}</dd></div>
              <div><dt>Completed</dt><dd>{tasks.filter((task) => task.assigneeId === selectedMember.id && task.status === 'done').length}</dd></div>
            </dl>
            <h4>Assigned workspaces</h4>
            <div className="team-assignment-chips">{(selectedMember.assignments || []).map((item) => <b key={item.workspaceId}>{item.workspaceName}</b>)}{!(selectedMember.assignments || []).length && <small>Not assigned to any workspace.</small>}</div>
          </section>
          <section className={cx('team-tab', memberTab === 'tasks' && 'active')} data-tab="tasks">
            <TeamTaskList apiBase={API_BASE} tasks={tasks.filter((task) => task.assigneeId === selectedMember.id)} setTasks={(updater) => setTasks((rows) => { const mine = rows.filter((task) => task.assigneeId === selectedMember.id); const others = rows.filter((task) => task.assigneeId !== selectedMember.id); const next = typeof updater === 'function' ? updater(mine) : updater; return [...next, ...others]; })} assignee={selectedMember} workspaces={workspaces} canManage setNotice={setNotice} emptyText={`Assign the first task to ${selectedMember.name}.`} />
          </section>
          <section className={cx('team-tab', memberTab === 'access' && 'active')} data-tab="access">
            <label className="team-workspace-picker"><span>Workspace permissions</span><select value={memberWorkspaceId} onChange={(event) => setMemberWorkspaceId(event.target.value)}>{workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}</select></label>{memberWorkspaceId && <div className="team-member-permissions"><label className="team-workspace-toggle"><input type="checkbox" checked={Boolean(memberAssignments[memberWorkspaceId])} onChange={() => toggleMemberWorkspace(memberWorkspaceId)} /><span><strong>Assigned to this workspace</strong><small>Employee can open this workspace after sign-in</small></span></label>{memberAssignments[memberWorkspaceId] && <><label className="team-full-access"><input type="checkbox" checked={memberAssignments[memberWorkspaceId].length === allPermissions.length} onChange={() => toggleMemberFullAccess(memberWorkspaceId)} /><span><strong>Full access</strong><small>Allow every current permission</small></span></label><div className="team-check-grid">{allPermissions.map((permission) => <label key={permission}><input type="checkbox" checked={memberAssignments[memberWorkspaceId].includes(permission)} onChange={() => toggleMemberPermission(memberWorkspaceId, permission)} /><span>{teamPermissionLabels[permission]}</span></label>)}</div></>}</div>}<button className="btn primary team-save-access" disabled={saving} type="button" onClick={saveMemberAccess}>{saving ? 'Saving...' : 'Save Permissions'}</button>
          </section>
          </> : <div className="empty-box"><FiUsers /><strong>Select an employee</strong><small>Choose a team member to manage workspace access.</small></div>}
      </aside>}
    </div>
  </section>;
}

function Profiles({ connectedPages, connectFacebook, connectGoogle, connectInstagram, connectExternal, connectedChannelCount, channelLimit, canAddChannel, currentUser, externalStatus, facebookStatus, googleStatus, instagramStatus, lastSync, setNotice, workspaceId }) {
  const [selectedProfileId, setSelectedProfileId] = useState('facebook');
  const [removalTarget, setRemovalTarget] = useState(null);
  const [removalPassword, setRemovalPassword] = useState('');
  const [removalError, setRemovalError] = useState('');
  const [removing, setRemoving] = useState(false);
  const [waChannel, setWaChannel] = useState(null);
  const [waDialog, setWaDialog] = useState(false);
  const [waForm, setWaForm] = useState({ label: '', baseUrl: '' });
  const [waSaving, setWaSaving] = useState(false);
  const [waError, setWaError] = useState('');
  const waRequest = useMemo(() => ({ withCredentials: true, headers: { 'x-workspace-id': workspaceId } }), [workspaceId]);
  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/api/integrations`, waRequest)
      .then(({ data }) => { if (active) setWaChannel((data.connected || []).find((item) => item.provider === 'whatsapp_channel') || null); })
      .catch(() => { if (active) setWaChannel(null); });
    return () => { active = false; };
  }, [waRequest]);
  const saveWaChannel = async (event) => {
    event.preventDefault();
    setWaSaving(true);
    setWaError('');
    try {
      const { data } = await axios.put(`${API_BASE}/api/integrations/whatsapp_channel`, waForm, waRequest);
      setWaChannel(data.integration || { provider: 'whatsapp_channel', ...waForm });
      setWaDialog(false);
      setNotice({ type: 'success', message: 'WhatsApp Channel added to this workspace.' });
    } catch (error) {
      setWaError(error.response?.data?.error || 'The channel could not be saved.');
    } finally {
      setWaSaving(false);
    }
  };
  const removeWaChannel = async () => {
    if (!window.confirm('Remove the WhatsApp Channel from this workspace?')) return;
    try {
      await axios.delete(`${API_BASE}/api/integrations/whatsapp_channel`, waRequest);
      setWaChannel(null);
      setNotice({ type: 'success', message: 'WhatsApp Channel removed.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'The channel could not be removed.' });
    }
  };
  const instagramAccounts = instagramStatus?.accounts || [];
  const linkedinAccounts = externalStatus?.connections?.linkedin || [];
  const threadsAccounts = externalStatus?.connections?.threads || [];
  const googleLocations = googleStatus?.googleBusiness?.locations || [];
  const youtubeChannels = googleStatus?.youtube?.channels || [];
  const publishingReadyPages = connectedPages.filter((page) => page.capabilities?.publishing?.ready === true);
  const facebookFullyReady = connectedPages.filter((page) => ['publishing', 'analytics', 'inbox'].every((key) => page.capabilities?.[key]?.ready));
  const instagramFullyReady = instagramAccounts.filter((account) => ['publishing', 'analytics', 'inbox'].every((key) => account.capabilities?.[key]?.ready));
  const metaCapabilityRows = (capabilities, instagram = false) => {
    if (!capabilities) return [];
    return [
      ['Publishing', capabilities.publishing],
      ['Analytics', capabilities.analytics],
      ['Inbox', capabilities.inbox],
      ...(!instagram ? [['Ads', capabilities.ads], ['Leads', capabilities.leads]] : [])
    ].filter(([, value]) => value);
  };

  const displayProfiles = profileCards.map((profile) => {
    if (profile.id === 'facebook') {
      return {
        ...profile,
        handle: connectedPages.length ? `${publishingReadyPages.length}/${connectedPages.length} publishing · ${facebookFullyReady.length}/${connectedPages.length} fully ready` : 'Not connected',
        health: facebookFullyReady.length === connectedPages.length && connectedPages.length ? 'Active' : publishingReadyPages.length ? 'Limited' : connectedPages.length ? 'Needs access' : 'Disconnected'
      };
    }
    if (profile.id === 'instagram') {
      return { ...profile, handle: instagramAccounts.length ? `${instagramFullyReady.length}/${instagramAccounts.length} account${instagramAccounts.length === 1 ? '' : 's'} fully ready` : 'Not connected', health: instagramFullyReady.length === instagramAccounts.length && instagramAccounts.length ? 'Active' : instagramAccounts.length ? 'Limited' : 'Disconnected' };
    }
    if (profile.id === 'google') {
      const hasLogin = Boolean(googleStatus?.connected);
      return {
        ...profile,
        handle: googleLocations.length ? `${googleLocations.length} business location${googleLocations.length === 1 ? '' : 's'}` : hasLogin && googleStatus?.googleBusiness?.needsApiApproval ? 'Google approval pending' : hasLogin ? 'Google connected, no location found' : 'Not connected',
        health: googleLocations.length ? 'Active' : hasLogin ? 'Needs access' : 'Disconnected'
      };
    }
    if (profile.id === 'youtube') {
      const hasYoutubeLogin = Boolean(googleStatus?.youtube?.hasScope);
      return {
        ...profile,
        handle: youtubeChannels.length ? `${youtubeChannels.length} channel${youtubeChannels.length === 1 ? '' : 's'}` : hasYoutubeLogin ? 'YouTube connected, channel check pending' : 'Not connected',
        health: youtubeChannels.length ? 'Active' : hasYoutubeLogin ? 'Needs access' : 'Disconnected'
      };
    }
    if (profile.id === 'whatsapp_channel') {
      return { ...profile, handle: waChannel ? waChannel.label || 'Channel added' : 'Not added', health: waChannel ? 'Active' : 'Not added' };
    }
    if (profile.id === 'linkedin') {
      const active = linkedinAccounts.filter((account) => !account.needsReconnect);
      return { ...profile, handle: active.length ? `${active.length} account${active.length === 1 ? '' : 's'}` : linkedinAccounts.length ? 'Reconnect required' : 'Not connected', health: active.length ? 'Active' : linkedinAccounts.length ? 'Reconnect' : 'Disconnected' };
    }
    if (profile.id === 'threads') {
      const configured = Boolean(externalStatus?.providers?.threads?.configured);
      const active = threadsAccounts.filter((account) => !account.needsReconnect);
      return {
        ...profile,
        handle: active.length ? `${active.length} profile${active.length === 1 ? '' : 's'}` : threadsAccounts.length ? 'Reconnect required' : configured ? 'Not connected' : 'Developer setup required',
        health: active.length ? 'Active' : threadsAccounts.length ? 'Reconnect' : configured ? 'Disconnected' : 'Setup required',
        comingSoon: !configured
      };
    }
    return profile;
  });

  const providerAccounts = {
    facebook: connectedPages.map((page) => ({
      id: page.pageId,
      provider: 'facebook-page',
      name: page.name || 'Facebook Page',
      detail: page.capabilities?.publishing?.ready === false ? page.capabilities.publishing.issue || 'Publishing permission is incomplete.' : `Page ID ${page.pageId}`,
      ready: page.capabilities?.publishing?.ready === true,
      capabilities: page.capabilities,
      removable: true
    })),
    instagram: instagramAccounts.map((account) => ({ id: account.instagramBusinessId, provider: 'instagram-account', name: account.username ? `@${account.username}` : account.name || 'Instagram account', detail: account.pageName || `Account ID ${account.instagramBusinessId}`, ready: account.capabilities?.publishing?.ready !== false, capabilities: account.capabilities, removable: true })),
    google: googleLocations.map((location) => ({ id: location.parent, provider: 'google-business', name: location.title || location.storeCode || 'Business location', detail: location.googleEmail || location.parent, ready: true })),
    youtube: youtubeChannels.map((channel) => ({ id: channel.id, provider: 'youtube', name: channel.title || 'YouTube channel', detail: channel.googleEmail || channel.id, ready: true })),
    linkedin: linkedinAccounts.map((account) => ({ id: account.externalAccountId, provider: 'linkedin', name: account.displayName || account.username || 'LinkedIn account', detail: account.needsReconnect ? 'Reconnect required' : account.metadata?.accountType === 'organization' ? 'Organization publishing connection active' : 'Member publishing connection active', ready: !account.needsReconnect, removable: true })),
    threads: threadsAccounts.map((account) => ({ id: account.externalAccountId, provider: 'threads', name: account.username ? `@${account.username}` : account.displayName || 'Threads profile', detail: account.needsReconnect ? 'Reconnect required' : 'Text publishing connection active', ready: !account.needsReconnect, removable: true })),
    whatsapp_channel: waChannel ? [{ id: 'whatsapp_channel', provider: 'whatsapp_channel', name: waChannel.label || 'WhatsApp Channel', detail: waChannel.baseUrl || '', ready: true }] : [],
    twitter: [],
    snapchat: []
  };

  const connectProfile = (profile) => {
    if (profile.comingSoon) {
      setNotice({ type: 'neutral', message: `${profile.platform} is visible for roadmap clarity, but its production API connection is not enabled yet.` });
    } else if (profile.id === 'facebook') connectFacebook();
    else if (profile.id === 'instagram') connectInstagram();
    else if (profile.id === 'google') connectGoogle('google-business');
    else if (profile.id === 'youtube') connectGoogle('youtube');
    else if (profile.id === 'linkedin') connectExternal('linkedin');
    else if (profile.id === 'threads') connectExternal('threads');
    else if (profile.id === 'whatsapp_channel') { setWaForm({ label: waChannel?.label || '', baseUrl: waChannel?.baseUrl || '' }); setWaError(''); setWaDialog(true); }
  };

  const requestAccountRemoval = (profile, account = null) => {
    const providerMap = { facebook: 'facebook', instagram: 'instagram', google: 'google-business', youtube: 'youtube', linkedin: 'linkedin', threads: 'threads' };
    setRemovalTarget(account ? { ...account, profile } : { profile, provider: providerMap[profile.id], name: `${profile.platform} connection`, wholeProvider: true });
    setRemovalPassword('');
    setRemovalError('');
  };

  const confirmRemoval = async (event) => {
    event.preventDefault();
    if (!removalTarget) return;
    setRemovalError('');
    try {
      setRemoving(true);
      const endpoint = removalTarget.wholeProvider
        ? `${API_BASE}/api/connections/${removalTarget.provider}`
        : `${API_BASE}/api/connections/${removalTarget.provider}/${encodeURIComponent(removalTarget.id)}`;
      await axios.delete(endpoint, { withCredentials: true, headers: { 'x-workspace-id': workspaceId }, data: { password: removalPassword } });
      setNotice({ type: 'success', message: `${removalTarget.name} removed from this workspace.` });
      setRemovalTarget(null);
      window.setTimeout(() => window.location.reload(), 450);
    } catch (error) {
      setRemovalError(error.response?.data?.error || `${removalTarget.name} could not be removed.`);
    } finally {
      setRemoving(false);
    }
  };

  const selectedProfile = displayProfiles.find((profile) => profile.id === selectedProfileId) || displayProfiles[0];
  const selectedAccounts = providerAccounts[selectedProfile.id] || [];
  const providerIssue = selectedProfile.id === 'google'
    ? googleStatus?.googleBusiness?.error
    : selectedProfile.id === 'youtube' ? googleStatus?.youtube?.error : selectedProfile.id === 'facebook' ? lastSync?.errorMessage : '';

  return (
    <>
      <section className="profiles-grid profile-account-workbench">
        <div className="glass-panel profile-live">
          <PanelTitle title="Publishing platforms" action={`${connectedChannelCount} / ${currentUser.unlimitedChannels ? '∞' : channelLimit}`} />
          <div className={cx('channel-allowance', !canAddChannel && 'limit-reached')}>
            <div><strong>{currentUser.plan} allowance</strong><span>{canAddChannel ? 'Select a platform to manage its accounts.' : 'Channel limit reached. Contact your admin.'}</span></div>
            <div className="allowance-track"><i style={{ width: `${currentUser.unlimitedChannels ? 24 : Math.min((connectedChannelCount / Math.max(channelLimit, 1)) * 100, 100)}%` }} /></div>
          </div>
          <div className="profile-cards profile-provider-list">
            {displayProfiles.map((profile) => (
              <button className={cx('profile-card profile-provider-row', selectedProfileId === profile.id && 'selected')} key={profile.id} style={{ '--platform': profile.color }} type="button" onClick={() => setSelectedProfileId(profile.id)}>
                <span className="profile-platform-logo"><PlatformLogo id={profile.id} label={profile.platform} /></span>
                <span className="profile-provider-copy"><strong>{profile.platform}</strong><small>{profile.handle}</small></span>
                <em className={cx(profile.health === 'Active' && 'ready', ['Limited', 'Needs access'].includes(profile.health) && 'warning')}>{profile.health}</em>
                <span className="profile-row-arrow" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </div>

        <aside className="glass-panel profile-account-panel">
          <div className="profile-account-head">
            <div><span className="profile-platform-logo"><PlatformLogo id={selectedProfile.id} label={selectedProfile.platform} /></span><div><small>Selected platform</small><h2>{selectedProfile.platform}</h2></div></div>
            {selectedProfile.comingSoon
              ? <span className="profile-coming-soon aligned">Coming soon</span>
              : <button className="btn primary" type="button" onClick={() => connectProfile(selectedProfile)}>{selectedProfile.id === 'whatsapp_channel' ? (selectedAccounts.length ? 'Edit channel' : 'Add channel') : selectedAccounts.length ? 'Add account' : 'Connect'}</button>}
          </div>
          <p className="profile-account-summary">{selectedProfile.comingSoon ? 'Production API onboarding is not available yet.' : `${selectedAccounts.length} account${selectedAccounts.length === 1 ? '' : 's'} connected in this workspace.`}</p>
          {providerIssue && <p className="sync-warning">{providerIssue}</p>}
          {selectedProfile.id === 'facebook' && connectedPages.length > 0 && publishingReadyPages.length < connectedPages.length && <p className="profile-access-note">Publishing access is not verified for every selected Page. If Meta App Review is pending, wait for Advanced Access approval. Otherwise, grant Page content access and reconnect once.</p>}
          {selectedProfile.id === 'whatsapp_channel' && <p className="profile-access-note">WhatsApp does not allow apps to post into Channels. Add your channel link here to keep it with this workspace's assets; you still post to the channel from WhatsApp.</p>}
          <div className="profile-account-scroll">
            {selectedAccounts.map((account) => (
              <article className={cx('provider-account-card', !account.ready && 'access-blocked')} key={`${account.provider}-${account.id}`}>
                <span className="account-provider-mark"><PlatformLogo id={selectedProfile.id} label={selectedProfile.platform} /></span>
                <div><strong>{account.name}</strong><small>{account.detail}</small>{account.capabilities && <div className="meta-capability-list">{metaCapabilityRows(account.capabilities, selectedProfile.id === 'instagram').map(([label, capability]) => <span className={capability.ready ? 'ready' : 'limited'} key={label} title={capability.issue || `${label} ready`}><i>{capability.ready ? <FiCheck /> : <FiAlertCircle />}</i>{label}</span>)}</div>}</div>
                <span className={cx('account-readiness', account.ready ? 'ready' : 'blocked')}>{account.ready ? 'Ready' : 'Access needed'}</span>
                {account.removable && <button className="danger-link" type="button" onClick={() => requestAccountRemoval(selectedProfile, account)}>Remove</button>}
              </article>
            ))}
            {!selectedAccounts.length && <div className="empty-box compact-empty"><strong>No {selectedProfile.platform} accounts</strong><small>{selectedProfile.comingSoon ? 'This integration will be enabled after provider approval.' : 'Use Connect to authorize the first account for this workspace.'}</small></div>}
          </div>
          {!selectedProfile.comingSoon && selectedProfile.id !== 'whatsapp_channel' && selectedAccounts.length > 0 && <div className="profile-account-footer"><button className="btn subtle" type="button" onClick={() => connectProfile(selectedProfile)}>Edit selection</button><button className="danger-link" type="button" onClick={() => requestAccountRemoval(selectedProfile)}>Disconnect {selectedProfile.platform}</button></div>}
          {selectedProfile.id === 'whatsapp_channel' && waChannel && <div className="profile-account-footer"><button className="danger-link" type="button" onClick={removeWaChannel}>Remove channel</button></div>}
          {selectedProfile.id === 'google' && googleStatus?.googleBusiness?.needsApiApproval && <p className="profile-access-note">Google login is stored, but Business Profile API access approval is still pending for this Cloud project.</p>}
          {selectedProfile.id === 'facebook' && (connectedPages.length > 0 || lastSync) && <small className="profile-sync-foot">{connectedPages.length > 0 ? `Current access: ${connectedPages.length} Page(s) connected · ${publishingReadyPages.length} publishing ready` : `Last Meta sync: ${lastSync.pagesFound || 0} Pages found · ${lastSync.publishablePages ?? 0} publishing ready`}</small>}
        </aside>
      </section>
      {waDialog && <div className="connection-remove-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !waSaving) setWaDialog(false); }}>
        <form className="connection-remove-dialog" onSubmit={saveWaChannel}>
          <span className="profile-platform-logo"><PlatformLogo id="whatsapp_channel" label="WhatsApp Channel" /></span>
          <h2>{waChannel ? 'Edit' : 'Add'} WhatsApp Channel</h2>
          <p>Open your channel in WhatsApp, copy its link and paste it here.</p>
          <label><span>Channel name</span><input autoFocus required maxLength={120} value={waForm.label} onChange={(event) => setWaForm((current) => ({ ...current, label: event.target.value }))} placeholder="e.g. Easten Updates" /></label>
          <label><span>Channel link</span><input required type="url" value={waForm.baseUrl} onChange={(event) => setWaForm((current) => ({ ...current, baseUrl: event.target.value }))} placeholder="https://whatsapp.com/channel/..." /></label>
          {waError && <p className="auth-error">{waError}</p>}
          <div><button className="btn subtle" type="button" onClick={() => setWaDialog(false)} disabled={waSaving}>Cancel</button><button className="btn primary" type="submit" disabled={waSaving}>{waSaving ? 'Saving...' : 'Save channel'}</button></div>
        </form>
      </div>}
      {removalTarget && <div className="connection-remove-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !removing) setRemovalTarget(null); }}>
        <form className="connection-remove-dialog" onSubmit={confirmRemoval}>
          <span className="profile-platform-logo"><PlatformLogo id={removalTarget.profile.id} label={removalTarget.profile.platform} /></span>
          <p className="site-eyebrow">Protected action</p>
          <h2>Remove {removalTarget.name}?</h2>
          <p>This removes the connection only from the current workspace. Enter your current EastenSocial password to confirm.</p>
          <label><span>Current password</span><input autoFocus type="password" autoComplete="current-password" value={removalPassword} onChange={(event) => setRemovalPassword(event.target.value)} required /></label>
          {removalError && <p className="auth-error">{removalError}</p>}
          <div><button className="btn subtle" type="button" onClick={() => setRemovalTarget(null)} disabled={removing}>Cancel</button><button className="btn primary" type="submit" disabled={removing || !removalPassword}>{removing ? 'Removing...' : 'Confirm removal'}</button></div>
        </form>
      </div>}
    </>
  );
}

function BulkScheduling({ bulkRows, setNotice, setBulkRows, workspaceId }) {
  const [isSaving, setIsSaving] = useState(false);
  const parseCsvLine = (line) => {
    const cells = [];
    let cell = '';
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      if (character === '"' && line[index + 1] === '"' && quoted) {
        cell += '"';
        index += 1;
      } else if (character === '"') {
        quoted = !quoted;
      } else if (character === ',' && !quoted) {
        cells.push(cell.trim());
        cell = '';
      } else {
        cell += character;
      }
    }
    cells.push(cell.trim());
    return cells;
  };
  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const lines = (await file.text()).replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) throw new Error('CSV needs a header row and at least one post row.');
      const headers = parseCsvLine(lines[0]).map((header) => header.toLowerCase());
      const required = ['date', 'platform', 'caption'];
      const missing = required.filter((header) => !headers.includes(header));
      if (missing.length) throw new Error(`Missing CSV columns: ${missing.join(', ')}`);
      const rows = lines.slice(1).map((line, index) => {
        const values = parseCsvLine(line);
        const read = (name) => values[headers.indexOf(name)] || '';
        return {
          id: `${Date.now()}-${index}`,
          date: read('date'),
          time: read('time'),
          platform: read('platform'),
          title: read('title'),
          caption: read('caption'),
          media: read('media') || 'none',
          status: 'Imported'
        };
      });
      setBulkRows(rows);
      setNotice({ type: 'success', message: `${rows.length} CSV rows imported for review.` });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'CSV could not be imported.' });
    }
  };
  const downloadSample = () => {
    const csv = 'date,time,platform,title,caption,media\n2026-09-01,10:30,facebook|instagram,September launch,"Campaign caption here",launch.jpg\n';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = 'eastensocial-bulk-scheduling-sample.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const updateRow = (id, field, value) => setBulkRows((rows) => rows.map((row) => row.id === id ? { ...row, [field]: value } : row));
  const removeRow = (id) => setBulkRows((rows) => rows.filter((row) => row.id !== id));
  const scheduleReadyRows = async () => {
    if (!bulkRows.length) {
      setNotice({ type: 'warning', message: 'Import a CSV before scheduling bulk posts.' });
      return;
    }
    try {
      setIsSaving(true);
      const response = await axios.post(`${API_BASE}/api/posts/bulk`, { rows: bulkRows, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, {
        withCredentials: true,
        headers: { 'x-workspace-id': workspaceId }
      });
      const rowErrors = new Map((response.data.errors || []).map((item) => [Number(item.row) - 1, item.error]));
      setBulkRows((rows) => rows.map((row, index) => ({
        ...row,
        status: rowErrors.get(index) || 'Scheduled',
        hasError: rowErrors.has(index)
      })));
      setNotice({
        type: response.data.partial ? 'warning' : 'success',
        message: response.data.partial
          ? `${response.data.accepted} rows scheduled. ${response.data.errors.length} rows need correction.`
          : `${response.data.accepted} bulk posts saved to this workspace calendar.`
      });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Bulk posts could not be saved.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="bulk-grid">
      <div className="glass-panel">
        <PanelTitle title="Upload, map, approve" action="CSV + media" />
        <div className="upload-zone">
          <strong>Import a CSV campaign</strong>
          <span>Required columns: date, platform, caption. Optional: time and media.</span>
          <div>
            <label className="btn primary">Import CSV<input type="file" accept=".csv,text/csv" onChange={importCsv} hidden /></label>
            <button className="btn subtle" type="button" onClick={downloadSample}>Download sample CSV</button>
          </div>
        </div>

        <div className="bulk-options">
          <label><span>Default platforms</span><select><option>Facebook + Instagram</option><option>All connected profiles</option></select></label>
          <label><span>Queue rule</span><select><option>Best time per platform</option><option>Exact CSV time</option></select></label>
          <label><span>Approval</span><select><option>Client approval required</option><option>Auto approve</option></select></label>
        </div>
      </div>

      <div className="glass-panel">
        <PanelTitle title={`${bulkRows.length} posts ready`} action="Review" />
        <div className="bulk-table">
          {bulkRows.map((row) => (
            <div className={cx('bulk-row', row.hasError && 'has-error')} key={row.id}>
              <input type="date" value={row.date || ''} onChange={(event) => updateRow(row.id, 'date', event.target.value)} />
              <input type="time" value={row.time || ''} onChange={(event) => updateRow(row.id, 'time', event.target.value)} />
              <input value={row.platform || ''} onChange={(event) => updateRow(row.id, 'platform', event.target.value)} aria-label="Platforms" />
              <input value={row.title || ''} maxLength="60" onChange={(event) => updateRow(row.id, 'title', event.target.value)} placeholder="Title" />
              <textarea value={row.caption || ''} onChange={(event) => updateRow(row.id, 'caption', event.target.value)} aria-label="Caption" />
              <small>{row.media || 'No media'}</small>
              <em>{row.status}</em>
              <button className="danger-link" type="button" onClick={() => removeRow(row.id)}>Remove</button>
            </div>
          ))}
        </div>
        <button className="btn primary bulk-save" type="button" disabled={isSaving || !bulkRows.length} onClick={scheduleReadyRows}>{isSaving ? 'Saving...' : 'Save to calendar'}</button>
      </div>
    </section>
  );
}

function MetaTargetPicker({ accountId, label, onChange, selected = [], type, workspaceId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId || (!query.trim() && !['behavior', 'custom'].includes(type))) { setResults([]); setError(''); return undefined; }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_BASE}/api/ads/meta/targeting-search`, {
          withCredentials: true,
          headers: { 'x-workspace-id': workspaceId },
          params: { accountId, type, q: query.trim() },
          timeout: 15000
        });
        setResults((response.data.results || []).filter((item) => !selected.some((entry) => entry.id === item.id)));
      } catch (requestError) {
        setResults([]);
        setError(requestError.response?.data?.error || 'Meta search is unavailable for this account.');
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [accountId, query, selected, type, workspaceId]);

  return <div className="meta-target-picker">
    <span>{label}</span>
    <div className="meta-target-chips">{selected.map((item) => <button type="button" key={`${item.id}:${item.name}`} onClick={() => onChange(selected.filter((entry) => entry.id !== item.id))}>{item.name}<FiX /></button>)}</div>
    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search Meta ${label.toLowerCase()}`} disabled={!accountId} />
    {(loading || results.length > 0) && <div className="meta-target-results">{loading ? <small>Searching Meta...</small> : results.map((item) => <button type="button" key={`${item.id}:${item.name}`} onClick={() => { onChange([...selected, item]); setQuery(''); setResults([]); }}><strong>{item.name}</strong><small>{item.type || type}{item.countryCode ? ` · ${item.countryCode}` : ''}</small></button>)}</div>}
    {error && <small className="meta-target-error">{error}</small>}
  </div>;
}

function Ads({ setNotice, workspaceId }) {
  const [dashboard, setDashboard] = useState({ drafts: adCampaigns, providers: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [adStep, setAdStep] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [savedAudiencesLoading, setSavedAudiencesLoading] = useState(false);
  const [metaForms, setMetaForms] = useState([]);
  const [metaFormsLoading, setMetaFormsLoading] = useState(false);
  const [isPublishingMeta, setIsPublishingMeta] = useState(false);
  const [leadFormDraft, setLeadFormDraft] = useState({ name: '', headline: '', privacyPolicyUrl: '', followUpUrl: '', locale: 'EN_US', questions: ['FULL_NAME', 'EMAIL', 'PHONE'] });
  const [adMediaFile, setAdMediaFile] = useState(null);
  const [adMediaPreview, setAdMediaPreview] = useState('');
  const [adUploadProgress, setAdUploadProgress] = useState(0);
  const [editingDraftId, setEditingDraftId] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [campaignTab, setCampaignTab] = useState('live');
  const [form, setForm] = useState({
    provider: 'meta', name: '', objective: 'Leads', dailyBudget: '1200', currency: 'INR',
    providerAccountId: '', buyingType: 'Auction', specialAdCategory: 'None', conversionLocation: 'Website', performanceGoal: 'Maximize results',
    pageId: '', instagramActorId: '', destinationUrl: '', displayUrl: '', primaryText: '', headline: '', description: '', callToAction: 'Learn More',
    mediaFileName: '', mediaUrl: '', mediaMimeType: '', audienceMode: 'create', savedAudienceId: '', savedAudienceName: '',
    audienceName: '', audience: '', locations: '', locationTargets: [], excludedLocations: '', ageMin: '18', ageMax: '65', genders: ['All'],
    languages: '', languageTargets: [], interests: '', interestTargets: [], behaviors: [], behaviorTargets: [], customAudiences: '', customAudienceTargets: [], excludedAudiences: '', excludedAudienceTargets: [], placementMode: 'automatic',
    placements: ['Facebook Feed', 'Instagram Feed', 'Facebook Stories', 'Instagram Stories', 'Instagram Reels'], devices: ['Mobile', 'Desktop'],
    optimizationEvent: 'Default', budgetType: 'daily', bidStrategy: 'Highest volume', bidAmount: '', pixelId: '', leadFormId: '', urlParameters: '',
    startAt: '', endAt: ''
  });

  const workspaceRequest = useMemo(() => ({
    withCredentials: true,
    headers: { 'x-workspace-id': workspaceId },
    timeout: 20000
  }), [workspaceId]);

  const loadDashboard = useCallback(async ({ quiet = false } = {}) => {
    if (!workspaceId) return;
    if (!quiet) setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/ads/dashboard`, workspaceRequest);
      setDashboard(response.data);
      setDashboardError('');
    } catch (error) {
      const message = error.response?.data?.error || 'Ads workspace could not be loaded.';
      setDashboardError(message);
      setNotice({ type: 'error', message });
    } finally {
      setIsLoading(false);
    }
  }, [setNotice, workspaceId, workspaceRequest]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const toggleFormList = (field, value) => setForm((current) => {
    const values = current[field] || [];
    return { ...current, [field]: values.includes(value) ? values.filter((item) => item !== value) : [...values, value] };
  });
  const provider = dashboard.providers?.[form.provider] || {};
  const providerAccounts = useMemo(
    () => dashboard.providers?.[form.provider]?.accounts || [],
    [dashboard.providers, form.provider]
  );
  const selectedProviderAccount = providerAccounts.find((account) => account.id === form.providerAccountId);
  const metaIdentities = dashboard.providers?.meta?.identities || [];

  useEffect(() => {
    if (!providerAccounts.length || providerAccounts.some((account) => account.id === form.providerAccountId)) return;
    const preferredAccount = providerAccounts.find((account) => account.linked) || providerAccounts[0];
    setForm((current) => ({ ...current, providerAccountId: preferredAccount.id }));
  }, [form.providerAccountId, providerAccounts]);

  useEffect(() => () => { if (adMediaPreview) URL.revokeObjectURL(adMediaPreview); }, [adMediaPreview]);

  useEffect(() => {
    if (form.provider !== 'meta' || !selectedProviderAccount?.linked || adStep !== 2) return;
    let cancelled = false;
    setSavedAudiencesLoading(true);
    axios.get(`${API_BASE}/api/ads/meta/saved-audiences`, { ...workspaceRequest, params: { accountId: selectedProviderAccount.id } })
      .then(({ data }) => { if (!cancelled) setSavedAudiences(data.audiences || []); })
      .catch((error) => { if (!cancelled) setNotice({ type: 'warning', message: error.response?.data?.error || 'Saved audiences could not be loaded.' }); })
      .finally(() => { if (!cancelled) setSavedAudiencesLoading(false); });
    return () => { cancelled = true; };
  }, [adStep, form.provider, selectedProviderAccount?.id, selectedProviderAccount?.linked, setNotice, workspaceRequest]);

  useEffect(() => {
    if (form.provider !== 'meta' || !form.pageId || form.conversionLocation !== 'Instant Form') return;
    let cancelled = false;
    setMetaFormsLoading(true);
    axios.get(`${API_BASE}/api/ads/meta/pages/${encodeURIComponent(form.pageId)}/forms`, workspaceRequest)
      .then(({ data }) => { if (!cancelled) setMetaForms(data.forms || []); })
      .catch((error) => { if (!cancelled) setNotice({ type: 'warning', message: error.response?.data?.error || 'Instant Forms could not be loaded.' }); })
      .finally(() => { if (!cancelled) setMetaFormsLoading(false); });
    return () => { cancelled = true; };
  }, [form.conversionLocation, form.pageId, form.provider, setNotice, workspaceRequest]);

  const createSavedAudience = async () => {
    if (!form.audienceName.trim()) { setNotice({ type: 'warning', message: 'Enter an audience name before saving.' }); return; }
    try {
      const response = await axios.post(`${API_BASE}/api/ads/meta/saved-audiences`, { ...form, accountId: form.providerAccountId, name: form.audienceName }, workspaceRequest);
      setSavedAudiences((current) => [response.data.audience, ...current]);
      setForm((current) => ({ ...current, audienceMode: 'saved', savedAudienceId: response.data.audience.id, savedAudienceName: response.data.audience.name }));
      setNotice({ type: 'success', message: 'Audience saved to the selected Meta ad account.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Audience could not be saved to Meta.' });
    }
  };

  const createInstantForm = async () => {
    try {
      const response = await axios.post(`${API_BASE}/api/ads/meta/pages/${encodeURIComponent(form.pageId)}/forms`, leadFormDraft, workspaceRequest);
      setMetaForms((current) => [response.data.form, ...current]);
      updateForm('leadFormId', response.data.form.id);
      setLeadFormDraft((current) => ({ ...current, name: '', headline: '' }));
      setNotice({ type: 'success', message: 'Instant Form created on the selected Meta Page.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Instant Form could not be created.' });
    }
  };

  const selectAdMedia = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (adMediaPreview) URL.revokeObjectURL(adMediaPreview);
    setAdMediaFile(file);
    setAdMediaPreview(URL.createObjectURL(file));
    setAdUploadProgress(0);
    setForm((current) => ({ ...current, mediaFileName: '', mediaUrl: '', mediaMimeType: file.type }));
  };

  const uploadAdMedia = async () => {
    if (!adMediaFile) return { fileName: form.mediaFileName, mediaUrl: form.mediaUrl, mimeType: form.mediaMimeType };
    const response = await axios.post(`${API_BASE}/api/ads/media/upload`, adMediaFile, {
      ...workspaceRequest,
      headers: { ...workspaceRequest.headers, 'Content-Type': adMediaFile.type || 'application/octet-stream' },
      timeout: 0,
      onUploadProgress: (progress) => setAdUploadProgress(progress.total ? Math.round((progress.loaded / progress.total) * 100) : 0)
    });
    return response.data;
  };

  const editDraft = (draft) => {
    const commaValue = (value) => Array.isArray(value) ? value.join(', ') : value || '';
    setForm((current) => ({
      ...current, ...draft,
      locations: commaValue(draft.locations), excludedLocations: commaValue(draft.excludedLocations),
      languages: commaValue(draft.languages), interests: commaValue(draft.interests),
      customAudiences: commaValue(draft.customAudiences), excludedAudiences: commaValue(draft.excludedAudiences),
      genders: draft.genders || ['All'], behaviors: draft.behaviors || [], placements: draft.placements || current.placements, devices: draft.devices || current.devices
    }));
    setEditingDraftId(draft.id);
    setAdMediaFile(null);
    setAdMediaPreview(draft.mediaUrl || '');
    setAdUploadProgress(0);
    setAdStep(0);
    setComposerOpen(true);
  };

  const startNewCampaign = () => {
    if (adMediaPreview) URL.revokeObjectURL(adMediaPreview);
    setEditingDraftId('');
    setAdMediaFile(null);
    setAdMediaPreview('');
    setAdUploadProgress(0);
    setAdStep(0);
    setComposerOpen(true);
  };

  const saveDraft = async ({ keepOpen = false, quiet = false } = {}) => {
    if (!form.name.trim()) {
      setNotice({ type: 'warning', message: 'Enter a campaign name before continuing.' });
      return null;
    }
    setIsSaving(true);
    try {
      const uploaded = await uploadAdMedia();
      const payload = { ...form, mediaFileName: uploaded.fileName || '', mediaUrl: uploaded.mediaUrl || '', mediaMimeType: uploaded.mimeType || form.mediaMimeType };
      const response = editingDraftId
        ? await axios.put(`${API_BASE}/api/ads/drafts/${editingDraftId}`, payload, workspaceRequest)
        : await axios.post(`${API_BASE}/api/ads/drafts`, payload, workspaceRequest);
      setDashboard((current) => ({
        ...current,
        drafts: editingDraftId
          ? (current.drafts || []).map((draft) => draft.id === editingDraftId ? response.data.draft : draft)
          : [response.data.draft, ...(current.drafts || [])]
      }));
      if (keepOpen) {
        setForm((current) => ({ ...current, mediaFileName: payload.mediaFileName, mediaUrl: payload.mediaUrl, mediaMimeType: payload.mediaMimeType }));
        setEditingDraftId(response.data.draft.id);
        setAdMediaFile(null);
        setAdMediaPreview(payload.mediaUrl || adMediaPreview);
        setAdUploadProgress(0);
        return response.data.draft;
      }
      setForm((current) => ({ ...current, name: '', primaryText: '', headline: '', description: '', mediaFileName: '', mediaUrl: '', mediaMimeType: '', destinationUrl: '' }));
      if (adMediaPreview) URL.revokeObjectURL(adMediaPreview);
      setAdMediaFile(null);
      setAdMediaPreview('');
      setAdUploadProgress(0);
      setEditingDraftId('');
      setAdStep(0);
      setComposerOpen(false);
      if (!quiet) setNotice({ type: 'success', message: editingDraftId ? 'Campaign draft updated. No ad was launched.' : 'Campaign draft saved to this workspace. No ad was launched and no budget was spent.' });
      return response.data.draft;
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Campaign draft could not be saved.' });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const publishMetaCampaign = async () => {
    setConfirmAction(null);
    setIsPublishingMeta(true);
    try {
      const draft = await saveDraft({ keepOpen: true, quiet: true });
      if (!draft) return;
      const response = await axios.post(`${API_BASE}/api/ads/drafts/${draft.id}/publish-meta`, {}, { ...workspaceRequest, timeout: 90000 });
      setDashboard((current) => ({ ...current, drafts: (current.drafts || []).map((item) => item.id === draft.id ? response.data.draft : item) }));
      setNotice({ type: 'success', message: 'Meta Campaign, Ad Set, Creative and Ad were created in PAUSED status. No spending has started.' });
      await loadDashboard({ quiet: true });
    } catch (error) {
      const partial = error.response?.data?.partialSetup;
      setNotice({ type: 'error', message: `${error.response?.data?.error || 'Meta campaign could not be created.'}${partial ? ' The partial setup remains saved as a draft.' : ''}` });
    } finally {
      setIsPublishingMeta(false);
    }
  };

  const deleteDraft = async (draft) => {
    try {
      await axios.delete(`${API_BASE}/api/ads/drafts/${draft.id}`, workspaceRequest);
      setDashboard((current) => ({ ...current, drafts: (current.drafts || []).filter((item) => item.id !== draft.id) }));
      setConfirmAction(null);
      setNotice({ type: 'success', message: 'Campaign draft deleted.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Draft could not be deleted.' });
    }
  };

  const connectGoogleAds = () => {
    window.location.href = `${API_BASE}/auth/google?service=google-ads&workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const connectMetaAds = () => {
    window.location.href = `${API_BASE}/auth/facebook?workspaceId=${encodeURIComponent(workspaceId)}`;
  };

  const setAccountLink = async (shouldLink) => {
    if (!selectedProviderAccount) return;
    setIsLoading(true);
    try {
      if (shouldLink) {
        await axios.post(`${API_BASE}/api/ads/accounts`, {
          provider: form.provider,
          accountId: selectedProviderAccount.id
        }, workspaceRequest);
      } else {
        await axios.delete(`${API_BASE}/api/ads/accounts/${form.provider}/${encodeURIComponent(selectedProviderAccount.id)}`, workspaceRequest);
      }
      await loadDashboard({ quiet: true });
      setNotice({ type: 'success', message: shouldLink ? 'Ad account linked to this workspace.' : 'Ad account unlinked from this workspace.' });
      setConfirmAction(null);
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Ad account link could not be updated.' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateMetaCampaignStatus = async (campaign, status) => {
    setConfirmAction(null);
    try {
      await axios.post(`${API_BASE}/api/ads/meta/campaigns/${encodeURIComponent(campaign.id)}/status`, { status }, workspaceRequest);
      await loadDashboard({ quiet: true });
      setNotice({ type: 'success', message: status === 'ACTIVE' ? 'Campaign activated. Meta may now begin spending the configured budget.' : 'Campaign paused.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.error || 'Campaign status could not be updated.' });
    }
  };

  const allLiveCampaigns = [
    ...(dashboard.providers?.meta?.campaigns || []),
    ...(dashboard.providers?.google?.campaigns || [])
  ];
  const adResults = allLiveCampaigns.reduce((summary, campaign) => ({
    spend: summary.spend + Number(campaign.spend || 0),
    reach: summary.reach + Number(campaign.reach || campaign.impressions || 0),
    leads: summary.leads + Number(campaign.leads || 0),
    clicks: summary.clicks + Number(campaign.clicks || 0)
  }), { spend: 0, reach: 0, leads: 0, clicks: 0 });
  const formatAccountMoney = (value, currency = 'INR') => {
    if (value == null || value === '') return 'Unavailable';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(value) / 100);
  };
  const accountSpendCapRemaining = selectedProviderAccount?.spendCap && Number(selectedProviderAccount.spendCap) > 0
    ? Math.max(Number(selectedProviderAccount.spendCap) - Number(selectedProviderAccount.amountSpent || 0), 0)
    : null;
  const openMetaBilling = () => {
    if (!selectedProviderAccount?.id) return;
    const accountId = String(selectedProviderAccount.accountId || selectedProviderAccount.id).replace(/^act_/, '');
    window.open(`https://business.facebook.com/billing_hub/payment_settings?asset_id=${encodeURIComponent(accountId)}`, '_blank', 'noopener,noreferrer');
  };

  const metaBudget = (campaign) => {
    const amount = Number(campaign.daily_budget || campaign.lifetime_budget || 0);
    if (!amount) return '—';
    return `${(amount / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}${campaign.daily_budget ? '/day' : ' total'}`;
  };
  const campaignStatusOf = (campaign) => String(campaign.effective_status || campaign.status || '').toUpperCase();
  const money = (value) => (value == null || value === '' ? '—' : `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
  const adsManagerUrl = (campaign) => {
    const accountId = String(campaign.accountId || '').replace(/^act_/, '');
    return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${encodeURIComponent(accountId)}&selected_campaign_ids=${encodeURIComponent(campaign.id)}`;
  };

  return (<>
    <section className="ads-control-center">
      <header className="ads-page-head">
        <div><p className="kicker">ADS</p><h2>Campaign control center</h2><small>{dashboard.providers?.meta?.businessScope?.names?.length ? `${dashboard.providers.meta.businessScope.names.join(', ')} · ` : ''}Build drafts and review live Meta or Google campaigns for this workspace.</small></div>
        <div className="ads-head-actions">
          <button className="icon-action" type="button" onClick={() => loadDashboard()} aria-label="Refresh ad accounts" title="Refresh"><FiRefreshCw /></button>
          <button className="btn primary ads-create-btn" type="button" onClick={startNewCampaign}><FiPlus aria-hidden="true" /> Create Campaign</button>
        </div>
      </header>

      <div className="ad-provider-strip">
        {['meta', 'google'].map((providerId) => {
          const item = dashboard.providers?.[providerId] || {};
          const linkedCount = (item.accounts || []).filter((account) => account.linked).length;
          return (
            <button className={`ad-provider-card ${form.provider === providerId ? 'selected' : ''}`} type="button" key={providerId} onClick={() => updateForm('provider', providerId)}>
              <span className={`provider-dot ${providerId}`} />
              <span><strong>{providerId === 'meta' ? 'Meta Ads' : 'Google Ads'}</strong><small>{item.ready ? `${linkedCount} assigned to this workspace` : item.issue || 'Checking access...'}</small></span>
              {item.ready ? <FiCheck className="provider-ready" /> : <FiAlertCircle className="provider-warning" />}
            </button>
          );
        })}
      </div>

      {dashboardError && <div className="ads-connection-alert"><FiAlertCircle /><span><strong>Ads data is unavailable</strong><small>{dashboardError}</small></span><button className="btn subtle" type="button" onClick={() => loadDashboard()}>Retry</button></div>}

      <section className="ad-account-toolbar" aria-label="Selected advertising account">
        <div><span>Ad account</span><select value={form.providerAccountId} onChange={(event) => updateForm('providerAccountId', event.target.value)} disabled={!providerAccounts.length}><option value="">{isLoading ? 'Loading accounts...' : 'Choose an account'}</option>{providerAccounts.map((account) => <option key={account.id} value={account.id}>{account.linked ? 'Workspace · ' : 'Available · '}{account.businessName ? `${account.businessName} · ` : ''}{account.name || account.id}</option>)}</select></div>
        <span className={cx('ad-account-health', selectedProviderAccount?.linked && provider.ready ? 'ready' : 'warning')}>{selectedProviderAccount?.linked ? <><FiCheck /> Connected to workspace</> : selectedProviderAccount ? <><FiAlertCircle /> Assignment required</> : <><FiAlertCircle /> No account selected</>}</span>
        {selectedProviderAccount && !selectedProviderAccount.linked && <button className="btn primary" type="button" onClick={() => setAccountLink(true)}>Use this account</button>}
        {!provider.connected && <button className="btn primary" type="button" onClick={form.provider === 'meta' ? connectMetaAds : connectGoogleAds}>Connect {form.provider === 'meta' ? 'Meta Ads' : 'Google Ads'}</button>}
        {provider.connected && !provider.ready && <button className="btn subtle" type="button" onClick={form.provider === 'meta' ? connectMetaAds : connectGoogleAds}>Repair access</button>}
        <small>{provider.issue || (selectedProviderAccount?.linked ? 'Campaigns, audiences and billing details sync from this account.' : 'Assign one account before building or fetching campaign data.')}</small>
      </section>

      {form.provider === 'meta' && selectedProviderAccount && <section className="meta-funding-panel" aria-label="Meta ad account funding">
        <header><div><p className="kicker">BILLING</p><h3>Available Funds Info</h3></div><button className="btn primary" type="button" onClick={openMetaBilling}>Add Funds on Meta</button></header>
        <div>
          <article><span>Meta balance</span><strong>{selectedProviderAccount.funding?.displayAmount || formatAccountMoney(selectedProviderAccount.balance, selectedProviderAccount.currency)}</strong><small>Provider-reported balance</small></article>
          <article><span>Amount spent</span><strong>{formatAccountMoney(selectedProviderAccount.amountSpent, selectedProviderAccount.currency)}</strong><small>{selectedProviderAccount.currency || 'Account currency'}</small></article>
          <article><span>Spend cap remaining</span><strong>{accountSpendCapRemaining == null ? 'No cap / unavailable' : formatAccountMoney(accountSpendCapRemaining, selectedProviderAccount.currency)}</strong><small>Based on Meta spend cap</small></article>
          <article><span>Funding method</span><strong>{selectedProviderAccount.funding?.displayString || (selectedProviderAccount.hasFundingSource ? 'Connected on Meta' : 'Not returned')}</strong><small>Manage securely on Meta</small></article>
        </div>
      </section>}

      <section className="ad-results-strip" aria-label="Ad results">
        <header><div><p className="kicker">LIVE PERFORMANCE</p><h3>Ad Results</h3></div><small>Combined results from linked Meta and Google campaigns</small></header>
        <div><article><span>Spend</span><strong>₹{adResults.spend.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></article><article><span>Reach</span><strong>{adResults.reach.toLocaleString('en-IN')}</strong></article><article><span>Leads</span><strong>{adResults.leads.toLocaleString('en-IN')}</strong></article><article><span>Clicks</span><strong>{adResults.clicks.toLocaleString('en-IN')}</strong></article></div>
      </section>

      <div className="ads-workbench">
        <div className={cx('ad-compose-wrap', composerOpen && 'open')} onMouseDown={(event) => { if (event.target === event.currentTarget) setComposerOpen(false); }}>
        <div className="glass-panel ad-compose-panel" role="dialog" aria-label="Create ad campaign">
          <div className="panel-head"><div><h2>{editingDraftId ? 'Edit campaign setup' : 'Create ad campaign'}</h2><small>Build the complete setup here, then create it on Meta in PAUSED status for a final review.</small></div>{editingDraftId && <button className="btn subtle" type="button" onClick={() => { setEditingDraftId(''); setAdStep(0); }}>Cancel edit</button>}<button className="icon-action ad-compose-close" type="button" aria-label="Close" title="Close" onClick={() => setComposerOpen(false)}><FiX /></button></div>
          <nav className="ad-stepper" aria-label="Campaign setup steps">
            {['Campaign', 'Creative', 'Audience', 'Placements', 'Budget', 'Review'].map((label, index) => <button className={cx(index === adStep && 'active', index < adStep && 'complete')} type="button" key={label} onClick={() => setAdStep(index)}><span>{index < adStep ? <FiCheck /> : index + 1}</span>{label}</button>)}
          </nav>
          <div className="ad-form-grid">
            {adStep === 0 && <>
              <label className="wide"><span>Campaign name</span><input value={form.name} maxLength="120" onChange={(event) => updateForm('name', event.target.value)} placeholder="September admission leads" /></label>
              <label><span>Objective</span><select value={form.objective} onChange={(event) => updateForm('objective', event.target.value)}><option>Leads</option><option>Sales</option><option>Website traffic</option><option>Awareness</option><option>Engagement</option><option>App promotion</option></select></label>
              <label><span>Ad account</span><select value={form.providerAccountId} onChange={(event) => updateForm('providerAccountId', event.target.value)}><option value="">Choose after connection</option>{providerAccounts.map((account) => <option key={account.id} value={account.id}>{account.linked ? 'Linked · ' : ''}{account.businessName ? `${account.businessName} · ` : ''}{account.name || account.id}</option>)}</select></label>
              {form.provider === 'meta' && <label><span>Facebook Page identity</span><select value={form.pageId} onChange={(event) => { const identity = metaIdentities.find((item) => item.pageId === event.target.value); setForm((current) => ({ ...current, pageId: event.target.value, instagramActorId: identity?.instagramActorId || '' })); }}><option value="">Choose Page</option>{metaIdentities.map((identity) => <option key={identity.pageId} value={identity.pageId}>{identity.name}{identity.instagramUsername ? ` · @${identity.instagramUsername}` : ''}</option>)}</select></label>}
              <label><span>Buying type</span><select value={form.buyingType} onChange={(event) => updateForm('buyingType', event.target.value)}><option>Auction</option><option>Reservation</option></select></label>
              <label><span>Special Ad Category</span><select value={form.specialAdCategory} onChange={(event) => updateForm('specialAdCategory', event.target.value)}><option>None</option><option>Housing</option><option>Employment</option><option>Credit</option><option>Social issues, elections or politics</option></select></label>
              <label><span>Conversion location</span><select value={form.conversionLocation} onChange={(event) => updateForm('conversionLocation', event.target.value)}><option>Website</option><option>Instant Form</option><option>Calls</option><option>Messenger</option><option>Instagram Direct</option><option>WhatsApp</option><option>App</option></select></label>
              <label><span>Performance goal</span><select value={form.performanceGoal} onChange={(event) => updateForm('performanceGoal', event.target.value)}><option>Maximize results</option><option>Maximize conversion value</option><option>Maximize landing page views</option><option>Maximize link clicks</option><option>Maximize reach</option><option>Maximize impressions</option></select></label>
            </>}
            {adStep === 1 && <>
              <label className="wide ad-media-upload"><span>Ad media</span><input type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" onChange={selectAdMedia} /><div className="ad-media-drop"><FiPaperclip /><span><strong>{adMediaFile?.name || 'Select image or video'}</strong><small>Original creative is securely attached to this workspace draft.</small></span></div></label>
              {adMediaPreview && <div className="ad-media-preview wide">{String(adMediaFile?.type || form.mediaMimeType).startsWith('video/') ? <video src={adMediaPreview} controls /> : <img src={adMediaPreview} alt="Ad creative preview" />}<small>{adUploadProgress ? `Uploading ${adUploadProgress}%` : 'Ready to upload when draft is saved'}</small></div>}
              <label className="wide"><span>Primary text</span><textarea rows="4" maxLength="2200" value={form.primaryText} onChange={(event) => updateForm('primaryText', event.target.value)} placeholder="Main ad copy shown above the creative" /></label>
              <label><span>Headline</span><input maxLength="255" value={form.headline} onChange={(event) => updateForm('headline', event.target.value)} placeholder="A short, clear offer" /></label>
              <label><span>Description</span><input maxLength="500" value={form.description} onChange={(event) => updateForm('description', event.target.value)} placeholder="Supporting detail" /></label>
              <label><span>Call to action</span><select value={form.callToAction} onChange={(event) => updateForm('callToAction', event.target.value)}><option>Learn More</option><option>Apply Now</option><option>Book Now</option><option>Contact Us</option><option>Get Quote</option><option>Send Message</option><option>Sign Up</option><option>Shop Now</option><option>WhatsApp</option></select></label>
              <label><span>Destination URL</span><input type="url" value={form.destinationUrl} onChange={(event) => updateForm('destinationUrl', event.target.value)} placeholder="https://example.com/landing-page" /></label>
              <label className="wide"><span>Display URL (optional)</span><input type="url" value={form.displayUrl} onChange={(event) => updateForm('displayUrl', event.target.value)} placeholder="https://example.com" /></label>
              {form.provider === 'meta' && form.conversionLocation === 'Instant Form' && <div className="instant-form-builder wide">
                <header><div><strong>Meta Instant Form</strong><small>Select an existing form or create one without leaving EastenSocial.</small></div></header>
                <label><span>Existing form</span><select value={form.leadFormId} onChange={(event) => updateForm('leadFormId', event.target.value)}><option value="">{metaFormsLoading ? 'Loading forms...' : 'Choose form'}</option>{metaForms.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.status}</option>)}</select></label>
                <div className="instant-form-fields">
                  <label><span>New form name</span><input value={leadFormDraft.name} onChange={(event) => setLeadFormDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Admissions enquiry" /></label>
                  <label><span>Headline</span><input value={leadFormDraft.headline} onChange={(event) => setLeadFormDraft((current) => ({ ...current, headline: event.target.value }))} placeholder="Request a callback" /></label>
                  <label><span>Privacy policy URL</span><input type="url" value={leadFormDraft.privacyPolicyUrl} onChange={(event) => setLeadFormDraft((current) => ({ ...current, privacyPolicyUrl: event.target.value }))} placeholder="https://example.com/privacy" /></label>
                  <label><span>Thank-you URL</span><input type="url" value={leadFormDraft.followUpUrl} onChange={(event) => setLeadFormDraft((current) => ({ ...current, followUpUrl: event.target.value }))} placeholder="https://example.com/thank-you" /></label>
                </div>
                <fieldset className="ad-check-field"><legend>Form questions</legend>{[['FULL_NAME', 'Full name'], ['EMAIL', 'Email'], ['PHONE', 'Phone'], ['WHATSAPP_NUMBER', 'WhatsApp'], ['CITY', 'City'], ['STATE', 'State'], ['COMPANY_NAME', 'Company'], ['JOB_TITLE', 'Job title']].map(([value, label]) => <label key={value}><input type="checkbox" checked={leadFormDraft.questions.includes(value)} onChange={() => setLeadFormDraft((current) => ({ ...current, questions: current.questions.includes(value) ? current.questions.filter((item) => item !== value) : [...current.questions, value] }))} />{label}</label>)}</fieldset>
                <button className="btn subtle" type="button" disabled={!form.pageId || !leadFormDraft.name || !leadFormDraft.privacyPolicyUrl} onClick={createInstantForm}><FiPlus /> Create Instant Form on Meta</button>
              </div>}
            </>}
            {adStep === 2 && <>
              <div className="ad-mode-switch wide"><button type="button" className={form.audienceMode === 'create' ? 'active' : ''} onClick={() => updateForm('audienceMode', 'create')}>Create Audience</button><button type="button" className={form.audienceMode === 'saved' ? 'active' : ''} onClick={() => updateForm('audienceMode', 'saved')}>Saved Audience</button></div>
              {form.audienceMode === 'saved' ? <label className="wide"><span>Saved audience</span><select value={form.savedAudienceId} onChange={(event) => { const audience = savedAudiences.find((item) => item.id === event.target.value); setForm((current) => ({ ...current, savedAudienceId: event.target.value, savedAudienceName: audience?.name || '' })); }}><option value="">{savedAudiencesLoading ? 'Loading audiences...' : 'Choose saved audience'}</option>{savedAudiences.map((audience) => <option key={audience.id} value={audience.id}>{audience.name}</option>)}</select><small>{!savedAudiencesLoading && !savedAudiences.length ? 'No saved audience returned by this Meta ad account.' : 'Audience targeting will come from Meta.'}</small></label> : <>
                <label className="wide"><span>Audience name</span><input value={form.audienceName} onChange={(event) => updateForm('audienceName', event.target.value)} placeholder="Punjab parents · Admissions 2026" /></label>
                <MetaTargetPicker accountId={form.providerAccountId} label="Locations" type="location" workspaceId={workspaceId} selected={form.locationTargets} onChange={(value) => updateForm('locationTargets', value)} />
                <label><span>Exclude locations</span><input value={form.excludedLocations} onChange={(event) => updateForm('excludedLocations', event.target.value)} placeholder="Cities or regions to exclude" /></label>
                <label><span>Age range</span><div className="age-inputs"><input type="number" min="13" max="65" value={form.ageMin} onChange={(event) => updateForm('ageMin', event.target.value)} aria-label="Minimum age" /><span>to</span><input type="number" min="13" max="65" value={form.ageMax} onChange={(event) => updateForm('ageMax', event.target.value)} aria-label="Maximum age" /></div></label>
                <fieldset className="ad-check-field"><legend>Gender</legend>{['All', 'Women', 'Men'].map((item) => <label key={item}><input type="checkbox" checked={form.genders.includes(item)} onChange={() => toggleFormList('genders', item)} />{item}</label>)}</fieldset>
                <MetaTargetPicker accountId={form.providerAccountId} label="Languages" type="language" workspaceId={workspaceId} selected={form.languageTargets} onChange={(value) => updateForm('languageTargets', value)} />
                <MetaTargetPicker accountId={form.providerAccountId} label="Interests" type="interest" workspaceId={workspaceId} selected={form.interestTargets} onChange={(value) => updateForm('interestTargets', value)} />
                <div className="wide"><MetaTargetPicker accountId={form.providerAccountId} label="Behaviours" type="behavior" workspaceId={workspaceId} selected={form.behaviorTargets} onChange={(value) => updateForm('behaviorTargets', value)} /></div>
                <MetaTargetPicker accountId={form.providerAccountId} label="Custom / Lookalike audiences" type="custom" workspaceId={workspaceId} selected={form.customAudienceTargets} onChange={(value) => updateForm('customAudienceTargets', value)} />
                <MetaTargetPicker accountId={form.providerAccountId} label="Exclude audiences" type="custom" workspaceId={workspaceId} selected={form.excludedAudienceTargets} onChange={(value) => updateForm('excludedAudienceTargets', value)} />
                <div className="wide ad-audience-save"><small>Save these Meta-valid selections for future campaigns.</small><button className="btn subtle" type="button" onClick={createSavedAudience}>Save Audience to Meta</button></div>
              </>}
            </>}
            {adStep === 3 && <>
              <div className="ad-mode-switch wide"><button type="button" className={form.placementMode === 'automatic' ? 'active' : ''} onClick={() => updateForm('placementMode', 'automatic')}>Advantage+ Placements</button><button type="button" className={form.placementMode === 'manual' ? 'active' : ''} onClick={() => updateForm('placementMode', 'manual')}>Manual Placements</button></div>
              {form.placementMode === 'automatic' ? <div className="ad-info-box wide"><FiCheck /><span><strong>Automatic delivery</strong><small>Provider can distribute budget across eligible placements for the selected objective and creative.</small></span></div> : <fieldset className="ad-placement-grid wide"><legend>Select placements</legend>{['Facebook Feed', 'Instagram Feed', 'Facebook Stories', 'Instagram Stories', 'Facebook Reels', 'Instagram Reels', 'Messenger Inbox', 'Audience Network'].map((item) => <label key={item}><input type="checkbox" checked={form.placements.includes(item)} onChange={() => toggleFormList('placements', item)} /><span>{item}</span></label>)}</fieldset>}
              <fieldset className="ad-check-field wide"><legend>Devices</legend>{['Mobile', 'Desktop'].map((item) => <label key={item}><input type="checkbox" checked={form.devices.includes(item)} onChange={() => toggleFormList('devices', item)} />{item}</label>)}</fieldset>
              <label className="wide"><span>Optimization event</span><select value={form.optimizationEvent} onChange={(event) => updateForm('optimizationEvent', event.target.value)}><option>Default</option><option>Conversions</option><option>Landing page views</option><option>Link clicks</option><option>Leads</option><option>Reach</option><option>Impressions</option><option>ThruPlay</option></select></label>
            </>}
            {adStep === 4 && <>
              <label><span>Budget type</span><select value={form.budgetType} onChange={(event) => updateForm('budgetType', event.target.value)}><option value="daily">Daily budget</option><option value="lifetime">Lifetime budget</option></select></label>
              <label><span>{form.budgetType === 'daily' ? 'Daily budget' : 'Lifetime budget'}</span><div className="money-input"><b>{form.currency}</b><input type="number" min="1" step="1" value={form.dailyBudget} onChange={(event) => updateForm('dailyBudget', event.target.value)} /></div></label>
              <label><span>Bid strategy</span><select value={form.bidStrategy} onChange={(event) => updateForm('bidStrategy', event.target.value)}><option>Highest volume</option><option>Cost per result goal</option><option>Bid cap</option><option>ROAS goal</option></select></label>
              {form.bidStrategy !== 'Highest volume' && <label><span>Bid / cost control</span><input type="number" min="0" value={form.bidAmount} onChange={(event) => updateForm('bidAmount', event.target.value)} /></label>}
              <label><span>Start</span><input type="datetime-local" value={form.startAt} onChange={(event) => updateForm('startAt', event.target.value)} /></label>
              <label><span>End</span><input type="datetime-local" value={form.endAt} onChange={(event) => updateForm('endAt', event.target.value)} /></label>
              <label><span>Meta Pixel / Dataset ID</span><input value={form.pixelId} onChange={(event) => updateForm('pixelId', event.target.value)} placeholder="Optional tracking dataset" /></label>
              <label><span>Instant Form ID</span><input value={form.leadFormId} onChange={(event) => updateForm('leadFormId', event.target.value)} placeholder="Required for Instant Form leads" /></label>
              <label className="wide"><span>URL parameters</span><input value={form.urlParameters} onChange={(event) => updateForm('urlParameters', event.target.value)} placeholder="utm_source=meta&utm_campaign=admissions" /></label>
            </>}
            {adStep === 5 && <div className="ad-review wide">
              <div><span>Campaign</span><strong>{form.name || 'Untitled campaign'}</strong><small>{form.objective} · {form.conversionLocation} · {form.specialAdCategory}</small></div>
              <div><span>Creative</span><strong>{form.headline || 'Headline not added'}</strong><small>{adMediaFile?.name || form.mediaFileName || 'No media selected'} · {form.callToAction}</small></div>
              <div><span>Audience</span><strong>{form.audienceMode === 'saved' ? form.savedAudienceName || 'Saved audience' : form.audienceName || 'New audience'}</strong><small>{form.audienceMode === 'saved' ? 'Meta saved targeting' : `${form.locationTargets.map((item) => item.name).join(', ') || 'India'} · Age ${form.ageMin}-${form.ageMax}`}</small></div>
              <div><span>Delivery</span><strong>{form.budgetType === 'daily' ? `${form.currency} ${form.dailyBudget}/day` : `${form.currency} ${form.dailyBudget} lifetime`}</strong><small>{form.placementMode === 'automatic' ? 'Advantage+ placements' : `${form.placements.length} manual placements`} · {form.bidStrategy}</small></div>
              <p><FiAlertCircle /> Save Draft keeps setup in EastenSocial. Create on Meta builds Campaign, Ad Set, Creative and Ad in PAUSED status, so spend remains off.</p>
            </div>}
          </div>
          <div className="ad-form-actions">
            {form.provider === 'meta' && !provider.ready && <button className="btn subtle" type="button" onClick={connectMetaAds}>{provider.connected ? 'Reconnect Meta Ads' : 'Connect Meta Ads'}</button>}
            {form.provider === 'google' && !provider.ready && <button className="btn subtle" type="button" onClick={connectGoogleAds}>{provider.connected ? 'Reconnect Google Ads' : 'Connect Google Ads'}</button>}
            {provider.ready && selectedProviderAccount?.linked && <button className="btn subtle" type="button" onClick={() => setConfirmAction({ type: 'unlink', account: selectedProviderAccount })}>Unlink account</button>}
            <span className="ad-step-actions">{adStep > 0 && <button className="btn subtle" type="button" onClick={() => setAdStep((step) => step - 1)}>Back</button>}{adStep < 5 ? <button className="btn primary" type="button" disabled={!selectedProviderAccount?.linked} onClick={() => setAdStep((step) => step + 1)}>Continue</button> : <><button className="btn subtle" type="button" disabled={isSaving || isPublishingMeta || !selectedProviderAccount?.linked} onClick={() => saveDraft()}>{isSaving ? 'Saving...' : 'Save Draft'}</button>{form.provider === 'meta' && <button className="btn primary" type="button" disabled={isSaving || isPublishingMeta || !selectedProviderAccount?.linked} onClick={() => setConfirmAction({ type: 'publish-meta' })}>{isPublishingMeta ? 'Creating on Meta...' : 'Create on Meta'}</button>}</>}</span>
          </div>
        </div>

        </div>

        <aside className="ads-funds" aria-label="Ad account funds">
          <h3>Funds</h3>
          {form.provider === 'meta' && selectedProviderAccount ? <>
            <div className="ads-funds-balance"><span>Available balance</span><strong>{selectedProviderAccount.funding?.displayAmount || formatAccountMoney(selectedProviderAccount.balance, selectedProviderAccount.currency)}</strong></div>
            <dl>
              <div><dt>Amount spent</dt><dd>{formatAccountMoney(selectedProviderAccount.amountSpent, selectedProviderAccount.currency)}</dd></div>
              <div><dt>Spend cap left</dt><dd>{accountSpendCapRemaining == null ? 'No cap' : formatAccountMoney(accountSpendCapRemaining, selectedProviderAccount.currency)}</dd></div>
              <div><dt>Payment method</dt><dd>{selectedProviderAccount.funding?.displayString || (selectedProviderAccount.hasFundingSource ? 'Added on Meta' : 'Not returned')}</dd></div>
            </dl>
            <button className="btn primary" type="button" onClick={openMetaBilling}><FiPlus aria-hidden="true" /> Add Funds</button>
            <small>Payments are made securely on Meta.</small>
          </> : <p>{form.provider === 'meta' ? 'Choose an ad account to see its funds.' : 'Google Ads billing is managed inside your Google Ads account.'}</p>}
        </aside>

        <div className="glass-panel ads-table-panel">
          <div className="ads-tabs" role="tablist" aria-label="Campaign lists">
            <button type="button" role="tab" aria-selected={campaignTab === 'live'} className={campaignTab === 'live' ? 'active' : ''} onClick={() => setCampaignTab('live')}>Campaigns <b>{allLiveCampaigns.length}</b></button>
            <button type="button" role="tab" aria-selected={campaignTab === 'drafts'} className={campaignTab === 'drafts' ? 'active' : ''} onClick={() => setCampaignTab('drafts')}>Draft Campaigns <b>{dashboard.drafts?.length || 0}</b></button>
          </div>
          {campaignTab === 'live' ? <div className="ads-table-scroll"><table className="ads-table">
            <thead><tr><th>Campaign</th><th>Status</th><th>Budget</th><th>Results</th><th>Spend</th><th>Reach</th><th>Clicks</th><th>CTR</th><th>CPC</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {allLiveCampaigns.map((campaign) => {
                const status = campaignStatusOf(campaign);
                const active = status === 'ACTIVE';
                return <tr key={`${campaign.provider}:${campaign.id}`}>
                  <td><span className={`provider-dot ${campaign.provider}`} /><div><strong>{campaign.name}</strong><small>{campaign.accountName || campaign.accountId}{campaign.objective ? ` · ${String(campaign.objective).replace(/^OUTCOME_/, '').toLowerCase()}` : ''}</small></div></td>
                  <td><b className={`campaign-status ${status.toLowerCase()}`}>{status === 'ACTIVE' ? 'Active' : status === 'PAUSED' ? 'Paused' : status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')}</b></td>
                  <td>{campaign.provider === 'meta' ? metaBudget(campaign) : '—'}</td>
                  <td>{campaign.leads ? `${campaign.leads.toLocaleString('en-IN')} leads` : '—'}</td>
                  <td>{money(campaign.spend)}</td>
                  <td>{campaign.reach ? Number(campaign.reach).toLocaleString('en-IN') : '—'}</td>
                  <td>{campaign.clicks ? Number(campaign.clicks).toLocaleString('en-IN') : '—'}</td>
                  <td>{campaign.ctr ? `${Number(campaign.ctr).toFixed(2)}%` : '—'}</td>
                  <td>{campaign.cpc ? `₹${Number(campaign.cpc).toFixed(2)}` : '—'}</td>
                  <td className="ads-row-actions">
                    {campaign.provider === 'meta' && <button className="icon-action" type="button" title={active ? 'Pause campaign' : 'Activate campaign'} aria-label={active ? `Pause ${campaign.name}` : `Activate ${campaign.name}`} onClick={() => setConfirmAction({ type: 'campaign-status', campaign, status: active ? 'PAUSED' : 'ACTIVE' })}>{active ? <FiPause /> : <FiPlay />}</button>}
                    {campaign.provider === 'meta' && <a className="icon-action" href={adsManagerUrl(campaign)} target="_blank" rel="noreferrer" title="Open in Meta Ads Manager" aria-label={`Open ${campaign.name} in Meta Ads Manager`}><FiExternalLink /></a>}
                  </td>
                </tr>;
              })}
            </tbody>
          </table>{!isLoading && !allLiveCampaigns.length && <div className="empty-box compact-empty"><strong>No live campaigns</strong><small>Campaigns created on Meta or Google for this workspace will appear here.</small></div>}</div>
          : <div className="ads-table-scroll"><table className="ads-table drafts">
            <thead><tr><th>Draft</th><th>Platform</th><th>Objective</th><th>Budget</th><th>Last edited</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {(dashboard.drafts || []).map((draft) => <tr key={draft.id}>
                <td><span className={`provider-dot ${draft.provider}`} /><div><strong>{draft.name}</strong><small>{draft.status === 'draft' || !draft.status ? 'Draft' : draft.status}</small></div></td>
                <td>{draft.provider === 'meta' ? 'Meta Ads' : 'Google Ads'}</td>
                <td>{draft.objective || '—'}</td>
                <td>{draft.currency} {Number(draft.dailyBudget || 0).toLocaleString('en-IN')}/day</td>
                <td>{draft.updatedAt ? new Date(draft.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                <td className="ads-row-actions">
                  <button className="icon-action" type="button" onClick={() => editDraft(draft)} aria-label={`Edit ${draft.name}`} title="Edit draft"><FiEdit3 /></button>
                  <button className="icon-action danger" type="button" onClick={() => setConfirmAction({ type: 'draft', draft })} aria-label={`Delete ${draft.name}`} title="Delete draft"><FiTrash2 /></button>
                </td>
              </tr>)}
            </tbody>
          </table>{!(dashboard.drafts || []).length && <div className="empty-box compact-empty"><strong>No drafts</strong><small>Click Create Campaign and save it as a draft to continue later.</small></div>}</div>}
        </div>

        <div className="glass-panel ads-list-panel">
          <div className="panel-head"><div><h2>Campaigns</h2><small>{isLoading ? 'Refreshing provider data...' : `${allLiveCampaigns.length} live · ${dashboard.drafts?.length || 0} draft`}</small></div></div>
          <div className="campaign-list modern-campaign-list">
            {allLiveCampaigns.map((campaign) => (
              <div className="campaign-row modern" key={`${campaign.provider}:${campaign.id}`}>
                <span className={`provider-dot ${campaign.provider}`} />
                <div><strong>{campaign.name}</strong><small>{campaign.accountName || campaign.accountId} · {campaign.objective || 'Campaign'}</small><span className="campaign-metrics"><i>Spend {campaign.spend != null ? Number(campaign.spend).toLocaleString('en-IN') : '—'}</i><i>Leads {campaign.leads ?? '—'}</i><i>ROAS {campaign.roas ? Number(campaign.roas).toFixed(2) : '—'}</i><i>CPC {campaign.cpc ? Number(campaign.cpc).toFixed(2) : '—'}</i><i>CTR {campaign.ctr ? `${Number(campaign.ctr).toFixed(2)}%` : '—'}</i></span></div>
                <b className={`campaign-status ${String(campaign.effective_status || campaign.status).toLowerCase()}`}>{campaign.effective_status || campaign.status}</b>
                {campaign.provider === 'meta' && <button className="btn subtle campaign-status-action" type="button" onClick={() => setConfirmAction({ type: 'campaign-status', campaign, status: String(campaign.effective_status || campaign.status).toUpperCase() === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' })}>{String(campaign.effective_status || campaign.status).toUpperCase() === 'ACTIVE' ? 'Pause' : 'Activate'}</button>}
              </div>
            ))}
            {(dashboard.drafts || []).map((draft) => (
              <div className="campaign-row modern" key={draft.id}>
                <span className={`provider-dot ${draft.provider}`} />
                <div><strong>{draft.name}</strong><small>{draft.provider === 'meta' ? 'Meta Ads' : 'Google Ads'} · {draft.currency} {Number(draft.dailyBudget).toLocaleString('en-IN')}/day</small></div>
                <b className="campaign-status draft">Draft</b>
                <button className="icon-action" type="button" onClick={() => editDraft(draft)} aria-label={`Edit ${draft.name}`} title="Edit draft"><FiEdit3 /></button>
                <button className="icon-action danger" type="button" onClick={() => setConfirmAction({ type: 'draft', draft })} aria-label={`Delete ${draft.name}`} title="Delete draft"><FiTrash2 /></button>
              </div>
            ))}
            {!isLoading && !allLiveCampaigns.length && !(dashboard.drafts || []).length && <div className="empty-box compact-empty"><strong>No campaigns yet</strong><small>Connect an ads provider or save the first campaign draft.</small></div>}
          </div>
        </div>
      </div>
    </section>
    {confirmAction?.type === 'draft' && <ConfirmDialog title="Delete campaign draft?" message={`“${confirmAction.draft.name}” will be removed from this workspace.`} confirmLabel="Delete" onCancel={() => setConfirmAction(null)} onConfirm={() => deleteDraft(confirmAction.draft)} />}
    {confirmAction?.type === 'unlink' && <ConfirmDialog title="Unlink ad account?" message={`“${confirmAction.account.name || confirmAction.account.id}” will no longer be visible in this workspace.`} confirmLabel="Unlink" onCancel={() => setConfirmAction(null)} onConfirm={() => setAccountLink(false)} />}
    {confirmAction?.type === 'publish-meta' && <ConfirmDialog title="Create PAUSED campaign on Meta?" message="Meta Campaign, Ad Set, Creative and Ad will be created in PAUSED status. No spend starts until the campaign is activated later." confirmLabel="Create paused campaign" onCancel={() => setConfirmAction(null)} onConfirm={publishMetaCampaign} />}
    {confirmAction?.type === 'campaign-status' && <ConfirmDialog title={confirmAction.status === 'ACTIVE' ? 'Activate Meta campaign?' : 'Pause Meta campaign?'} message={confirmAction.status === 'ACTIVE' ? 'Activation can start delivery and budget spend on Meta according to the campaign schedule.' : 'Meta will stop new delivery after the pause is applied.'} confirmLabel={confirmAction.status === 'ACTIVE' ? 'Activate campaign' : 'Pause campaign'} onCancel={() => setConfirmAction(null)} onConfirm={() => updateMetaCampaignStatus(confirmAction.campaign, confirmAction.status)} />}
  </>);
}

// Section headings are plain titles: the small green hint labels and the count chips
// (10, 10/10, 2/30...) were decoration, so only real controls are rendered on the right.
function PanelTitle({ title, action }) {
  const control = action != null && typeof action === 'object';
  return (
    <div className="panel-head">
      <div><h2>{title}</h2></div>
      {control && <span>{action}</span>}
    </div>
  );
}

function ConfirmDialog({ confirmLabel = 'Confirm', message, onCancel, onConfirm, title }) {
  return <div className="confirm-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
    <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-copy">
      <span className="confirm-dialog-icon"><FiAlertCircle /></span>
      <h2 id="confirm-dialog-title">{title}</h2>
      <p id="confirm-dialog-copy">{message}</p>
      <footer><button className="btn subtle" type="button" onClick={onCancel}>Cancel</button><button className="btn danger-confirm" type="button" onClick={onConfirm}>{confirmLabel}</button></footer>
    </section>
  </div>;
}

export default App;
